import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import OrgStructureModal from './OrgStructureModal';
import EmpNoRuleModal from './EmpNoRuleModal';
import DisclosureScopeModal from './DisclosureScopeModal';
import { logDownload, logPrint } from '../utils/eventLog';
import { SysMailGuideNote } from '../components/SysMailAddress';

// ── 인사관리 ─────────────────────────────────────────────────────────────
// 다양한 인사관리 프로그램(그리팅/플렉스/시프티 등)의 표준 모듈을 참고해 폭넓게 구성했습니다.
// 사원정보 / 조직도 / 근태관리 / 휴가관리 / 인사발령 / 급여관리 / 평가관리 / 교육관리 /
// 경조사관리 — 9개 탭. 메뉴 자체 읽기/쓰기는 사원정보 > 권한설정(hr-management), 모듈별
// 민감한 동작은 세부설정으로 한 번 더 제어됩니다(급여 전체조회/휴가승인/각종 등록 등).
//
// "계정관리" 탭은 삭제되었습니다. 조직구성/공개범위 설정은 조직도 탭으로, 계정별 승인/
// 회원구분/부서/직급/권한설정은 사원정보 탭으로 각각 기능을 그대로 옮겼습니다(데이터/권한
// 체계는 손대지 않음). 예전 화면(AdminUsers.js, /admin/users 경로)은 기존 즐겨찾기/새로고침이
// 깨지지 않도록 코드는 그대로 남겨두었지만, 더 이상 메뉴에서 연결되지 않습니다.
// "채용관리" 탭은 요청에 따라 완전히 삭제되었습니다(백엔드 라우트 포함).

const TABS = [
  { key:'employees',   label:'직원현황' },
  { key:'orgchart',    label:'조직도' },
  { key:'attendance',  label:'근태관리' },
  { key:'leave',       label:'휴가관리' },
  { key:'appointment', label:'인사발령' },
  { key:'payroll',     label:'급여관리' },
  { key:'evaluation',  label:'평가관리' },
  { key:'training',    label:'교육관리' },
  { key:'event',       label:'경조사관리' },
];

// 인사관리 탭은 아니지만 같은 "사용여부(옵션)" 저장소(hr_menu_options)를 함께 쓰는 항목.
// 홈 화면의 근태체크(출근/퇴근/외근/출장/복귀) 위젯 노출 여부 — Home.js가 GET /hr/menu-options
// 결과에서 이 key가 꺼져 있는지(enabled=0) 확인해서 위젯을 숨긴다.
const EXTRA_OPTIONS = [
  { key:'home_attendance_widget', label:'🏠 홈 화면 근태체크(출근/퇴근/외근/출장/복귀) 위젯' },
];

// 인사관리 탭별 "권한상 이름" — 계정관리 > 권한설정의 hr-management > 상세설정 > 'category_read_scope'
// (등록된 목록별 읽기권한, list형)에 저장되는 값과 정확히 일치해야 한다. TABS의 label(이모지 포함,
// 탭 버튼 표시용)과는 별개로, 이모지를 뺀 순수 한글 탭 이름만 권한 값으로 쓴다(백엔드
// backend/routes/hr.js의 HR_TAB_LABELS와 반드시 동일한 문자열을 유지할 것).
const HR_TAB_LABELS = {
  employees:   '직원현황',
  orgchart:    '조직도',
  attendance:  '근태관리',
  leave:       '휴가관리',
  appointment: '인사발령',
  payroll:     '급여관리',
  evaluation:  '평가관리',
  training:    '교육관리',
  event:       '경조사관리',
};

// 다른 메뉴(출고관리/영업관리 등)와 동일한 디자인 규격으로 통일:
// 버튼 10px 16px/14px, 입력창 10px 14px/14px, 표 헤더 #f8fafc 배경 + 14px 회색 글자 +
// 가운데 정렬, 본문 행 14px + #f0f0f0 구분선 — Sales.js ShipmentTable 스타일과 동일.
export const C = { primary:'#185FA5', border:'#e5e7eb', bg:'#f8fafc', text:'#333', sub:'#888' };

const btn = (bg, color='#fff') => ({
  background:bg, color, border:'none', borderRadius:8, padding:'10px 16px',
  fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
});
export const input = { padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };
export const th = { padding:'10px 14px', fontSize:14, fontWeight:600, color:'#666', background:'#f8fafc', textAlign:'center', whiteSpace:'nowrap', borderBottom:'1px solid #e5e7eb' };
export const td = { padding:'10px 14px', fontSize:14, color:C.text, borderTop:'1px solid #f0f0f0', textAlign:'center' };
const navBtnStyle = { background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' };

// 목록 인쇄 — Certs.js의 목록 인쇄(popup + window.print())와 동일한 방식. 서버를 거치지 않고
// 현재 화면에 보이는 목록 그대로 새 창에 표를 그려서 인쇄한다.
function printListPopup({ title, columns, rows, menuLabel }) {
  logPrint(menuLabel, title);
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const headHtml = `<tr>${columns.map(c => `<th>${c}</th>`).join('')}</tr>`;
  const bodyHtml = rows.length
    ? rows.map(r => `<tr>${r.map(v => `<td>${v === null || v === undefined || v === '' ? '-' : v}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${columns.length}" style="color:#aaa;padding:24px 0;">등록된 내역이 없습니다.</td></tr>`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>
      @page { size: A4 landscape; margin: 14mm; }
      body { font-family:'Malgun Gothic',sans-serif; color:#222; }
      h2 { font-size:18px; margin:0 0 4px; }
      .meta { font-size:11px; color:#888; margin-bottom:14px; }
      table { width:100%; border-collapse:collapse; font-size:12px; }
      th, td { border:1px solid #ccc; padding:6px 8px; text-align:center; }
      th { background:#f1f5f9; }
    </style></head>
    <body><h2>${title}</h2><div class="meta">출력일시: ${dateStr}</div>
    <table><thead>${headHtml}</thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  const pw = window.open('', '_blank', 'width=1000,height=700');
  if (!pw) return;
  pw.document.write(html);
  pw.document.close();
  pw.focus();
  setTimeout(() => { pw.print(); pw.close(); }, 400);
}

// 목록 다운로드 — 서버가 만들어주는 CSV(엑셀에서 바로 열림)를 blob으로 받아 저장한다.
async function downloadListCsv(url, filename, menuLabel) {
  try {
    const res = await API.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
    logDownload(menuLabel, filename);
  } catch (e) {
    alert('다운로드 중 오류가 발생했습니다.');
  }
}

function Section({ title, action, children }) {
  return (
    <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
      {(title || action) && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontSize:16, fontWeight:600 }}>{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// 각 탭의 상단 컨트롤(검색/필터/인쇄/다운로드/등록 버튼 등)을 화면 상단(👥 인사관리 제목과 같은 줄,
// 우측)에 표시하기 위한 포털. 탭은 한 번에 하나만 마운트되므로(HRManagement의 조건부 렌더),
// 탭이 바뀌면 이전 탭의 버튼은 자동으로 사라지고 새 탭의 버튼이 그 자리에 나타난다.
function HeaderPortal({ target, children }) {
  if (!target) return null;
  return createPortal(children, target);
}

// 목록 정렬(오름차순/내림차순) 공용 훅 — 컬럼 헤더 클릭으로 토글한다.
function useSort(list, defaultKey = null, defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sorted = useMemo(() => {
    if (!sortKey) return list;
    const copy = [...list];
    copy.sort((a, b) => {
      let va = a ? a[sortKey] : null;
      let vb = b ? b[sortKey] : null;
      if (va === null || va === undefined) va = '';
      if (vb === null || vb === undefined) vb = '';
      let cmp;
      const na = Number(va), nb = Number(vb);
      if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
      else cmp = String(va).localeCompare(String(vb), 'ko');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [list, sortKey, sortDir]);
  return { sorted, sortKey, sortDir, toggleSort };
}

// 정렬 가능한 목록 헤더 — 아이콘 없이 컬럼명 글자 자체를 클릭하면 오름차순/내림차순이 토글된다.
// 현재 정렬 기준인 컬럼은 글자 색상만으로 구분한다. sortKey가 없는 컬럼(작업/버튼 열 등)은
// 클릭해도 아무 동작을 하지 않는 일반 헤더로 표시된다.
function SortTh({ label, sortKey, currentKey, currentDir, onSort, rowSpan, style }) {
  if (!sortKey) return <th rowSpan={rowSpan} style={{ ...th, ...style }}>{label}</th>;
  const active = currentKey === sortKey;
  return (
    <th rowSpan={rowSpan} style={{ ...th, cursor:'pointer', userSelect:'none', color: active ? C.primary : th.color, ...style }}
      title={active ? (currentDir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      onClick={() => onSort(sortKey)}>
      {label}
    </th>
  );
}

function EmptyRow({ span, text='등록된 내역이 없습니다.' }) {
  return <tr><td colSpan={span} style={{ ...td, textAlign:'center', color:'#aaa', padding:'40px 0', fontSize:14 }}>{text}</td></tr>;
}

// 검색 + 모두보기(페이지당 개수) 공용 컨트롤 — 사원정보 탭에 있던 검색/모두보기와 동일한 UI/동작을
// 다른 모든 탭에도 그대로 적용하기 위한 공용 컴포넌트. 이 목록들은 이미 전량 로드돼 있으므로
// (서버측 search 파라미터가 없는 소규모 목록들) 검색은 클라이언트에서 바로 필터링한다.
function SearchPageBar({ search, setSearch, pageSize, setPageSize, placeholder='이름/부서 검색' }) {
  return (
    <>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder} style={{ ...input, width:200 }} />
      <select value={pageSize} onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
        style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555',
          outline:'none', cursor:'pointer', background:'#fff' }}>
        <option value="10">10개씩</option>
        <option value="30">30개씩</option>
        <option value="50">50개씩</option>
        <option value="100">100개씩</option>
        <option value="all">모두보기</option>
      </select>
    </>
  );
}

// 공용 페이지네이션(모두보기가 아닐 때만 표시) — 사원정보 탭의 이전/다음 버튼과 동일한 스타일.
function Pagination({ page, setPage, totalPages }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:14 }}>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
          color: page === 1 ? '#ccc' : '#555', fontSize:14, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>◀</button>
      <span style={{ fontSize:14, color:'#666' }}>{page} / {totalPages}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
          color: page === totalPages ? '#ccc' : '#555', fontSize:14, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>▶</button>
    </div>
  );
}

// 검색어(search)로 목록(rows)을 클라이언트에서 필터링 — fields에 나열된 각 필드 값에 부분일치하면 통과.
function filterBySearch(rows, search, fields) {
  const q = search.trim();
  if (!q) return rows;
  return rows.filter(r => fields.some(f => String(r[f] || '').includes(q)));
}

// 연/월 선택(달력) 상태 — 근태관리에서 쓰던 것과 동일한 방식을 여러 탭에서 재사용하기 위한 공용 훅.
function useMonthPicker() {
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const shiftMonth = (delta) => {
    let m = selMonth + delta, y = selYear;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setSelMonth(m); setSelYear(y);
  };
  const month = `${selYear}-${String(selMonth).padStart(2, '0')}`;
  return { selYear, selMonth, setSelYear, setSelMonth, shiftMonth, month };
}

// 연/월 선택(달력) 위젯 — 근태관리 탭에 있던 것과 동일한 UI(이전/다음 화살표 + 클릭시 12개월 팝업 + 이번달 바로가기).
// 2026-08-29 추가 — onYearView(선택) prop: 값을 넘기면 12개월 그리드 바로 위에 "{연도}년 전체보기"
// 버튼이 추가로 나타난다. 급여관리(PayrollTab)에서만 이 prop을 넘겨 쓰며, 인사발령/평가관리/
// 교육관리/경조사관리 등 이 컴포넌트를 쓰는 다른 4개 탭은 prop을 넘기지 않으므로(undefined)
// 기존 동작(버튼 없음)이 그대로 유지된다.
function MonthPicker({ selYear, selMonth, setSelYear, setSelMonth, shiftMonth, onYearView }) {
  const now = new Date();
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(selYear);
  const monthPickerRef = useRef(null);
  useEffect(() => { if (showMonthPicker) setPickerYear(selYear); }, [showMonthPicker, selYear]);
  useEffect(() => {
    if (!showMonthPicker) return;
    const onClickOutside = (e) => { if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) setShowMonthPicker(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showMonthPicker]);
  return (
    <div ref={monthPickerRef} style={{ position:'relative', display:'flex', alignItems:'center', gap:8 }}>
      <button onClick={() => shiftMonth(-1)} style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28, fontSize:13, cursor:'pointer', color:'#555' }}>◀</button>
      <span onClick={() => setShowMonthPicker(v => !v)}
        style={{ fontSize:16, fontWeight:700, color:'#000', minWidth:100, textAlign:'center', cursor:'pointer' }}>
        {selYear}년 {selMonth}월
      </span>
      <button onClick={() => shiftMonth(1)} style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28, fontSize:13, cursor:'pointer', color:'#555' }}>▶</button>
      {!(selYear === now.getFullYear() && selMonth === now.getMonth() + 1) && (
        <button onClick={() => { setSelYear(now.getFullYear()); setSelMonth(now.getMonth() + 1); }}
          style={{ fontSize:12, padding:'5px 10px', background:'#E6F1FB', color:'#185FA5', border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
          이번달
        </button>
      )}
      {showMonthPicker && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, background:'#fff',
          border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 4px 14px rgba(0,0,0,0.12)', padding:12, zIndex:20, width:220 }}
          onClick={e => e.stopPropagation()}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <button onClick={() => setPickerYear(y => y - 1)} style={navBtnStyle}>◀</button>
            <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
            <button onClick={() => setPickerYear(y => y + 1)} style={navBtnStyle}>▶</button>
          </div>
          {onYearView && (
            <button onClick={() => { onYearView(pickerYear); setShowMonthPicker(false); }}
              style={{ width:'100%', marginBottom:8, padding:'7px 0', borderRadius:6, border:'1px solid #185FA5',
                background:'#E6F1FB', color:'#185FA5', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              📊 {pickerYear}년 전체보기
            </button>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
            {Array.from({ length:12 }, (_, i) => i + 1).map(m => {
              const active = pickerYear === selYear && m === selMonth;
              return (
                <button key={m}
                  onClick={() => { setSelYear(pickerYear); setSelMonth(m); setShowMonthPicker(false); }}
                  style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                    borderColor: active ? '#185FA5' : '#ddd',
                    background:  active ? '#E6F1FB' : '#fff',
                    color:       active ? '#185FA5' : '#555',
                    fontSize:14, cursor:'pointer' }}>
                  {m}월
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// 페이지당 개수(모두보기) select만 단독으로 필요할 때(달력과 함께 쓸 때) 쓰는 축약 컴포넌트.
function PageSizeSelect({ pageSize, setPageSize }) {
  return (
    <select value={pageSize} onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
      style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555', outline:'none', cursor:'pointer', background:'#fff' }}>
      <option value="10">10개씩</option><option value="30">30개씩</option><option value="50">50개씩</option>
      <option value="100">100개씩</option><option value="all">모두보기</option>
    </select>
  );
}

export function Modal({ title, onClose, children, width=460, headerActions=null, headerPad='18px 22px 0' }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:2000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:14, width, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:headerPad, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:16, fontWeight:700, margin:0 }}>{title}</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {headerActions}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#999', cursor:'pointer' }}>✕</button>
          </div>
        </div>
        <div style={{ padding:'16px 22px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:12, color:'#666', marginBottom:5, fontWeight:600 }}>{label}</div>
      {children}
    </div>
  );
}

function EmpSelect({ employees, value, onChange, placeholder='대상자 선택' }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...input, width:'100%' }}>
      <option value="">{placeholder}</option>
      {employees.map(e => (
        <option key={e.id} value={e.id}>{e.department || '-'} · {e.position || '-'} · {e.name}</option>
      ))}
    </select>
  );
}

// ── 계정관리(구) 기능 이관 — 승인/회원구분/부서/직급/권한설정 ──────────────────────
// 예전 계정관리 탭(AdminUsers.js)에서 쓰던 권한설정 모달(일반설정/세부설정)을 사원정보 탭에서도
// 그대로 쓸 수 있도록 가져왔습니다. 데이터/권한 체계(백엔드 API)는 전혀 손대지 않고, 화면 위치만
// 옮긴 것입니다. 채용관리 탭이 삭제되어 hr-management의 recruit_write 세부항목은 뺐습니다.
const NO_WRITE_MENUS = ['home', 'display'];

// list형(목록별) 상세설정 항목의 선택지를 메뉴별로 어떻게 가져올지 정의합니다. 같은 메뉴 안에서
// 읽기/쓰기 두 항목이 같은 목록을 공유하는 경우(계약관리/인증관리/스토리지)가 많아서, 항목별이
// 아니라 optionsKey 단위로 한 번만 조회해서 캐시합니다.
const LIST_OPTION_FETCHERS = {
  contracts: () => API.get('/contracts/categories').then(r => (r.data || []).map(c => c.name)),
  certs: () => API.get('/certs/categories').then(r => (r.data || []).map(c => c.name)),
  storage: () => API.get('/storage/list').then(r => (r.data.items || []).filter(it => it.isDir).map(it => it.name)),
  'hr-management': () => Promise.resolve(Object.values(HR_TAB_LABELS)),
};

const DETAIL_DEFS = {
  // 홈화면 — 근태체크(출퇴근) 위젯 관련 읽기 세부항목. 인사관리 > 사원정보 > 옵션에서
  // "🏠 홈 화면 근태체크 위젯"(tab_key: home_attendance_widget)을 사용으로 켜둔 경우에만
  // 아래 '출퇴근' 항목이 이 화면(상세설정)에 나타난다. 꺼져 있으면 홈화면 메뉴 자체에
  // 상세설정 항목이 없는 것으로 표시된다(DetailPermissionModal의 homeAttendanceEnabled 참고).
  home: {
    read: [
      { key: 'checkin', label: '출퇴근', requiresOption: 'home_attendance_widget' },
    ],
    write: [],
  },
  notices: {
    read:  [{ key:'comment_allowed', label:'댓글 허용' }],
    write: [
      { key:'display_expose', label:'전광판 노출' },
      { key:'delete_allowed', label:'삭제가능' },
    ],
  },
  tasks: {
    read:  [
      { key:'download_allowed', label:'다운로드' },
      { key:'request_read_all', label:'업무요청 조회' },
    ],
    write: [
      { key:'register_allowed',      label:'업무등록' },
      { key:'delete_allowed',        label:'업무목록 삭제' },
      { key:'status_change_allowed', label:'상태변경' },
      { key:'minutes_write_allowed', label:'회의록 작성' },
      { key:'request_write_allowed', label:'업무요청' },
      { key:'category_manage',       label:'관리항목 등록' },
    ],
  },
  contracts: {
    read: [
      { key:'download_allowed',    label:'다운로드' },
      { key:'category_read_scope', label:'등록된 목록별로 읽기권한', type:'list', optionsKey:'contracts' },
    ],
    write: [
      { key:'category_manage',      label:'목록등록' },
      { key:'category_write_scope', label:'등록된 목록별로 쓰기권한', type:'list', optionsKey:'contracts' },
    ],
  },
  certs: {
    read: [
      { key:'category_read_scope', label:'인증항목별 읽기', type:'list', optionsKey:'certs' },
      { key:'download_allowed',    label:'다운로드' },
    ],
    write: [
      { key:'category_manage',      label:'인증항목 등록' },
      { key:'manage_item_manage',   label:'관리항목 등록' },
      { key:'category_write_scope', label:'각 인증항목별 쓰기권한', type:'list', optionsKey:'certs' },
    ],
  },
  'sales-activities': {
    read:  [
      { key:'detail_view_allowed',  label:'목록 상세보기' },
      { key:'support_view_allowed', label:'영업지원 보기' },
    ],
    write: [
      { key:'register_allowed',    label:'영업활동 등록' },
      { key:'update_allowed',      label:'영업활동 업데이트' },
      { key:'list_delete_allowed', label:'영업목록 삭제' },
      { key:'quotation_register_allowed', label:'견적서 등록' },
      { key:'quotation_update_allowed',   label:'견적서 수정' },
      { key:'quotation_delete_allowed',   label:'견적서 삭제' },
    ],
  },
  sales: {
    read: [
      { key:'delivery_detail_visible', label:'납품출고 상세보기' },
      { key:'transfer_detail_visible', label:'공장-본사 이관출고 상세보기' },
      { key:'defect_detail_visible',   label:'교환/반품내역 상세보기' },
    ],
    write: [
      { key:'register_allowed',          label:'납품출고 등록' },
      { key:'edit_allowed',              label:'납품출고 수정' },
      { key:'list_delete_allowed',       label:'납품출고 삭제' },
      { key:'transfer_register_allowed', label:'공장-본사 이관출고 등록' },
      { key:'transfer_edit_allowed',     label:'공장-본사 이관출고 수정' },
      { key:'transfer_delete_allowed',   label:'공장-본사 이관출고 삭제' },
      { key:'defect_register_allowed',   label:'교환/반품 등록' },
      { key:'defect_edit_allowed',       label:'교환/반품 수정' },
      { key:'defect_dispose_allowed',    label:'교환/반품 폐기' },
      { key:'status_hold_allowed',       label:'출고보류 승인' },
      { key:'pay_confirm_allowed',       label:'결재확인 승인' },
      { key:'status_pending_allowed',    label:'출고확인 승인' },
      { key:'status_complete_allowed',   label:'출고완료 승인' },
    ],
  },
  productions: {
    read: [
      { key:'plan_list_visible',     label:'생산계획 목록보기' },
      { key:'status_list_visible',   label:'생산현황 목록보기' },
      { key:'plan_detail_visible',   label:'생산계획 상세보기' },
      { key:'status_detail_visible', label:'생산현황 상세보기' },
    ],
    write: [
      { key:'plan_register_allowed',      label:'생산계획 등록' },
      { key:'list_delete_allowed',        label:'생산계획 삭제' },
      { key:'status_delete_action',       label:'생산현황 삭제' },
      { key:'status_delete_allowed',      label:'생산현황 삭제요청' },
      { key:'admin_approval_action',      label:'생산계획 관리승인' },
      { key:'production_approval_action', label:'생산계획 생산승인' },
    ],
  },
  inventory: {
    read: [
      { key:'finished_goods_read', label:'완제품 목록보기' },
      { key:'parts_read',          label:'부품 목록보기' },
      { key:'consumables_read',    label:'소모품 목록보기' },
      { key:'history_read',        label:'수정이력 조회' },
    ],
    write: [
      { key:'product_group_manage',  label:'제품군 등록/삭제' },
      { key:'product_option_manage', label:'제품옵션 추가/삭제' },
      { key:'parts_write',           label:'부품 등록/삭제' },
      { key:'parts_edit',            label:'부품수정' },
      { key:'consumables_write',     label:'소모품 등록/삭제' },
      { key:'consumables_edit',      label:'소모품 수정' },
      { key:'finished_goods_write',  label:'완제품 재고현황 수정' },
      { key:'defect_register',       label:'완제품 불량현황 수정' },
      { key:'history_delete',        label:'수정이력 조회/삭제' },
    ],
  },
  storage: {
    read:  [{ key:'accessible_folders', label:'폴더별로 읽기권한', type:'list', optionsKey:'storage' }],
    write: [{ key:'writable_folders',   label:'읽기권한이 있는 폴더 쓰기', type:'list', optionsKey:'storage' }],
  },
  'hr-management': {
    read: [
      { key:'category_read_scope', label:'등록된 목록별 읽기권한', type:'list', optionsKey:'hr-management' },
      { key:'payroll_read',        label:'급여 전체 조회 (끄면 본인 급여만 조회)' },
    ],
    write: [
      { key:'category_write_scope', label:'등록된 목록별 쓰기권한', type:'list', optionsKey:'hr-management' },
      { key:'option_write',      label:'옵션수정' },
      { key:'employee_write',    label:'사원정보 등록/수정' },
      { key:'attendance_write',  label:'근태관리 등록/수정' },
      { key:'leave_approve',     label:'휴가 승인/반려 + 연차 부여' },
      { key:'appointment_write', label:'인사발령 등록' },
      { key:'payroll_write',     label:'급여 등록/수정' },
      { key:'evaluation_write',  label:'평가 등록/수정' },
      { key:'training_write',    label:'교육 등록/참가자 관리' },
      { key:'event_write',       label:'경조사 등록' },
      { key:'delete_allowed',    label:'각 목록 삭제 가능' },
    ],
  },
  'e-approval': {
    read:  [],
    write: [],
  },
  mypage: {
    read:  [],
    write: [{ key:'dedicated_account_manage', label:'전용계정 관리' }],
  },
};

const formatUserLine = (u) => {
  const parts = [u.company, u.department, u.position, u.name].filter(Boolean);
  return `${parts.join(' ㅣ ')} (${u.username})`;
};
const thStyle = { padding:'12px 16px', textAlign:'center', fontSize:13, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb', whiteSpace:'nowrap' };
const tdStyle = { padding:'12px 16px', fontSize:14, borderBottom:'1px solid #f0f0f0', textAlign:'center' };
export const quickBtn = { padding:'6px 12px', fontSize:12, fontWeight:600, color:'#555', background:'#f8fafc', border:'1px solid #ddd', borderRadius:6, cursor:'pointer' };

export default function HRManagement() {
  const { user } = useAuth();
  const perm = usePermissions();
  const location = useLocation();
  const [tab, setTab] = useState('employees');
  // 2026-08-28 추가 — 근로소득 간이세액표 리마인더("지금 확인" 버튼)에서 navigate(..., { state:{
  // openIncomeTaxTable:true } })로 진입했을 때, 급여관리 탭으로 이동한 뒤 4대보험 요율 관리
  // 모달 → 근로소득 간이세액표 등록 모달까지 자동으로 열어준다.
  useEffect(() => { if (location.state?.openIncomeTaxTable) setTab('payroll'); }, [location.state]);
  const [employees, setEmployees] = useState([]);
  const [msg, setMsg] = useState('');
  // 인사관리 탭 메뉴 사용여부(옵션) — 꺼진(enabled=0) tab_key 집합만 들고 있는다. 행이 없는
  // tab_key는 기본 사용(꺼지지 않음)으로 취급한다. 사원정보(직원현황) 탭은 옵션에서 끌 수 없다
  // (관리 화면 자체이므로 항상 켜둔다).
  const [disabledTabs, setDisabledTabs] = useState(new Set());
  const [showMenuOptions, setShowMenuOptions] = useState(false);
  const loadMenuOptions = () => API.get('/hr/menu-options')
    .then(r => setDisabledTabs(new Set((r.data || []).filter(o => !o.enabled).map(o => o.tab_key))))
    .catch(() => {});
  useEffect(() => { loadMenuOptions(); }, []);
  // 탭 메뉴 사용여부(disabledTabs, 앱 전역 옵션) 뿐 아니라, "등록된 목록별 읽기권한"
  // (category_read_scope)도 함께 확인해서 사용자별로 볼 수 있는 탭만 보여준다.
  // (최고관리자/로딩 중에는 perm.hasListItem이 항상 true를 반환한다)
  const visibleTabs = TABS.filter(t => (t.key === 'employees' || !disabledTabs.has(t.key))
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS[t.key]));
  // 지금 보고 있는 탭이 방금 꺼졌다면(다른 탭에서 옵션을 바꾼 경우는 없지만 방어적으로) 사원정보로 되돌린다.
  useEffect(() => { if (tab !== 'employees' && disabledTabs.has(tab)) setTab('employees'); }, [disabledTabs]); // eslint-disable-line
  // 각 탭의 상단 컨트롤(인쇄/다운로드/등록 버튼 등)을 여기(제목과 같은 줄, 우측)에 모아서
  // 보여준다 — 탭 내부에서 HeaderPortal로 이 DOM 노드에 렌더링한다(모든 탭 공통 적용).
  const [headerActionsEl, setHeaderActionsEl] = useState(null);
  const headerActionsRef = (el) => { if (el && el !== headerActionsEl) setHeaderActionsEl(el); };
  // 검색창/모두보기(페이지당 개수) 같은 목록 필터 컨트롤은 탭 메뉴 줄의 우측(스크린샷 지정 위치)에
  // 별도로 모아서 보여준다 — 사원정보의 검색/모두보기, 인사발령의 모두보기가 여기로 들어간다.
  const [tabBarActionsEl, setTabBarActionsEl] = useState(null);
  const tabBarActionsRef = (el) => { if (el && el !== tabBarActionsEl) setTabBarActionsEl(el); };

  const loadEmployees = (search) => API.get('/hr/employees', { params: search ? { search } : {} })
    .then(r => setEmployees(r.data)).catch(() => {});
  useEffect(() => { loadEmployees(); }, []);

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(''), 2500); };

  // 다른 메뉴(출고관리/생산관리)의 제품군 탭과 동일한 스타일: 회색 밑줄 바 위에
  // 활성 탭만 파란색(#185FA5) 글자 + 2px 밑줄, 비활성은 회색(#888).
  // 사원정보 탭은 라벨 옆에 인원수를 (N) 형태로 붙여 보여준다(본문 목록 제목의 인원수 표기를 대신함).
  const tabLabel = (t) => t.key === 'employees' ? `${t.label} (${employees.length})` : t.label;
  const tabBtn = (t) => (
    <button key={t.key} onClick={() => setTab(t.key)}
      style={{ padding:'10px 20px', background:'none', border:'none', cursor:'pointer',
        fontSize:14, fontWeight:600, color: tab===t.key ? C.primary : '#888',
        borderBottom: tab===t.key ? `2px solid ${C.primary}` : '2px solid transparent',
        whiteSpace:'nowrap', marginBottom:-2 }}>
      {tabLabel(t)}
    </button>
  );

  return (
    <div style={{ padding:24, maxWidth:1690 }}>
      {/* 헤더 — 출고관리/영업관리와 동일한 제목(20px/600) + 설명(13px 회색) 구성.
          우측에는 현재 탭의 상단 컨트롤(검색/필터/인쇄/다운로드/등록 버튼 등)이 HeaderPortal로
          렌더링되는 공용 슬롯을 둔다(모든 탭 공통 적용). */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, gap:12, flexWrap:'wrap' }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>👥 인사관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>사원정보 · 조직도 · 근태 · 휴가 · 발령 · 급여 · 채용 · 평가 · 교육 · 경조사를 관리합니다.</p>
        </div>
        <div className="hdr-actions" ref={headerActionsRef} style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }} />
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:20, borderBottom:'2px solid #e5e7eb' }}>
        <div style={{ display:'flex', flexWrap:'wrap' }}>{visibleTabs.map(tabBtn)}</div>
        <div ref={tabBarActionsRef} style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', paddingBottom:6 }} />
      </div>

      {tab === 'employees'   && <EmployeesTab employees={employees} reload={loadEmployees} perm={perm} user={user} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} openMenuOptions={() => setShowMenuOptions(true)} />}
      {tab === 'orgchart'    && !disabledTabs.has('orgchart')    && <OrgChartTab user={user} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}
      {tab === 'attendance'  && !disabledTabs.has('attendance')  && <AttendanceTab employees={employees} perm={perm} user={user} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}
      {tab === 'leave'       && !disabledTabs.has('leave')       && <LeaveTab employees={employees} perm={perm} user={user} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}
      {tab === 'appointment' && !disabledTabs.has('appointment') && <AppointmentTab employees={employees} perm={perm} showMsg={showMsg} reloadEmployees={loadEmployees} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}
      {tab === 'payroll'     && !disabledTabs.has('payroll')     && <PayrollTab employees={employees} perm={perm} user={user} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} autoOpenIncomeTaxTable={!!location.state?.openIncomeTaxTable} />}
      {tab === 'evaluation'  && !disabledTabs.has('evaluation')  && <EvaluationTab employees={employees} perm={perm} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}
      {tab === 'training'    && !disabledTabs.has('training')    && <TrainingTab employees={employees} perm={perm} user={user} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}
      {tab === 'event'       && !disabledTabs.has('event')       && <EventTab employees={employees} perm={perm} showMsg={showMsg} headerActionsEl={headerActionsEl} tabBarActionsEl={tabBarActionsEl} />}

      {showMenuOptions && (
        <MenuOptionsModal disabledTabs={disabledTabs} onClose={() => setShowMenuOptions(false)}
          onSaved={() => { loadMenuOptions(); setShowMenuOptions(false); }} />
      )}
    </div>
  );
}

// 인사관리 탭 사용여부 설정 모달 — 사원정보(직원현황) 탭에서 "옵션" 버튼으로 연다.
// 사원정보 탭 자신은 목록에서 제외(항상 사용). 체크 해제한 탭은 저장 즉시 탭 메뉴에서 사라진다.
function MenuOptionsModal({ disabledTabs, onClose, onSaved }) {
  const [checked, setChecked] = useState(() => {
    const init = {};
    TABS.forEach(t => { if (t.key !== 'employees') init[t.key] = !disabledTabs.has(t.key); });
    EXTRA_OPTIONS.forEach(t => { init[t.key] = !disabledTabs.has(t.key); });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const toggle = (key) => setChecked(v => ({ ...v, [key]: !v[key] }));
  const save = () => {
    setSaving(true);
    const options = Object.keys(checked).map(tab_key => ({ tab_key, enabled: checked[tab_key] }));
    API.put('/hr/menu-options', { options })
      .then(() => onSaved())
      .catch(() => alert('저장 중 오류가 발생했습니다.'))
      .finally(() => setSaving(false));
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:2000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:14, width:360, maxWidth:'90vw', padding:'22px 24px' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:16, fontWeight:700, marginBottom:4 }}>⚙️ 탭 메뉴 사용여부</div>
        <div style={{ fontSize:12, color:'#999', marginBottom:16 }}>체크를 해제하면 해당 탭/위젯이 화면에서 숨겨집니다.</div>
        <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
          {TABS.filter(t => t.key !== 'employees').map(t => (
            <label key={t.key} style={{ display:'flex', alignItems:'center', gap:10, fontSize:14, cursor:'pointer' }}>
              <input type="checkbox" checked={!!checked[t.key]} onChange={() => toggle(t.key)} style={{ width:16, height:16 }} />
              {t.label}
            </label>
          ))}
          <div style={{ borderTop:'1px solid #eee', margin:'4px 0' }} />
          {EXTRA_OPTIONS.map(t => (
            <label key={t.key} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, cursor:'pointer' }}>
              <input type="checkbox" checked={!!checked[t.key]} onChange={() => toggle(t.key)} style={{ width:16, height:16 }} />
              {t.label}
            </label>
          ))}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={save} disabled={saving} style={{ flex:1, padding:'10px 0', borderRadius:8, border:'none',
            background: saving ? '#aaa' : C.primary, color:'#fff', fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button onClick={onClose} disabled={saving} style={{ flex:1, padding:'10px 0', borderRadius:8, border:'1px solid #ddd',
            background:'#f8fafc', color:'#555', fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 1) 사원정보
// ═══════════════════════════════════════════════════════════════
const EMPLOYMENT_TYPES = ['정규직', '계약직', '일용직'];
// 급여(실수령액/연봉) 입력 — 계약관리(Contracts.js)의 금액 콤마 입력과 동일한 규칙(1000단위 구분).
const onlyDigits  = (s) => String(s || '').replace(/[^\d]/g, '');
const formatComma = (s) => { const d = onlyDigits(s); return d ? Number(d).toLocaleString('ko-KR') : ''; };
const moneyDisplay = (n) => (n === null || n === undefined || n === '') ? '-' : Number(n).toLocaleString('ko-KR');

// 2026-08: 목록에 사번을 부서 앞에 추가하고, 권한설정/계정관리 열은 삭제 — 이 두 기능은
// 계정관리(AdminUsers.js) 화면의 회원 리스트에서 대신 처리하도록 이관되었다(아래 참고).
const EMP_LIST_COLUMNS = ['등록일','사번','부서','직급','이름','연락처','비상연락처','채용구분','재직상태','입사일','계약만료일','서류등록','상세'];

// 서류등록 상태 — 요청사항: "서류등록 완료를 계약/서약/동의/등본/통장 이렇게 표현하고 완료되면
// 색상만 변경". 예전에는 "완료"/"미등록: 근로계약, 등본" 처럼 하나의 배지에 텍스트로 요약해
// 보여줬는데, 이제는 5개 문서 각각을 항상 짧은 라벨(계약/서약/동의/등본/통장)로 나열하고 그
// 문서가 등록완료된 경우에만 색상(초록)으로 바뀐다 — 인사기록카드(EmployeeCardView) 문서버튼
// 색상 판정 기준과 동일하게: 근로계약(전자)/근로서약서/개인정보동의서는 "전자서명 완료" 여부,
// 등본/통장사본은 "파일 등록" 여부로 판단한다. 근로계약(스캔)은 서명 개념이 없는 종이계약
// 업로드이므로 파일 존재만으로도 완료로 인정한다(라인 아래 done 함수 참고).
const EMP_DOC5 = [
  { key: 'labor_contract', label: '계약', done: e => !!(e.doc_labor_contract || e.labor_contract_signed) },
  { key: 'pledge', label: '서약', done: e => !!e.pledge_signed },
  { key: 'privacy_consent', label: '동의', done: e => !!e.privacy_consent_signed },
  { key: 'resident_reg', label: '등본', done: e => !!e.doc_resident_reg },
  { key: 'bankbook', label: '통장', done: e => !!e.doc_bankbook },
];
// 인쇄/출력용 평문 표기(엑셀·PDF는 컬러 배지를 쓸 수 없으므로 완료/미완료 텍스트로 나열).
function empDocStatus(e) {
  return { text: EMP_DOC5.map(d => `${d.label}:${d.done(e) ? '완료' : '미완료'}`).join(' ') };
}

// 사원등록/수정 공통 — 사번/채용구분/입사일/퇴사일/계약만료일 입력 필드 블록(두 모달에서 재사용).
// 재직상태는 화면에 별도로 표시하지 않는다 — 퇴사일이 입력되면 자동으로 "퇴직", 비어있으면
// "재직"으로 저장 시점에 계산해서 보낸다(save/submitRegister 참고). 목록 등 다른 화면에서는
// 여전히 employment_status 값을 그대로 사용한다.
// dept/companyPrefix: 사원등록 화면에서만 전달됨 — 선택된 부서에 조직도 > "🔢 사번설정" 규칙이
// 있으면(dept.empno_digits != null) 그 규칙(회사약어+부서약어+입사년도+순번)으로 아직 아무에게도
// 배정되지 않은 다음 순번 후보를 최대 10개까지 계산해 드롭다운으로 보여준다(buildEmpNoCandidates).
// 규칙이 없으면 예전처럼 "자동부여 – 추후 재정의" 안내만 표시한다.
// 2026-08 변경 — "사번: 미할당된 사번으로 20개까지 선택할 수 있도록 수정". 예전에는 서버가
// 등록 시점에 채번한 값을 미리 보여주기만 하는 읽기전용 안내문(empNoPreview)이었는데, 인사기록카드
// 상세보기-수정 화면과 동일하게 실제 선택 가능한 드롭다운으로 바꾼다. 선택한 값은 f.employee_no에
// 담겨 등록 요청(POST /hr/employees)에 그대로 실려가고, 서버(assignEmployeeNo)가 그 값을 최종
// 사번으로 확정하며 부서의 다음 채번 순번도 그에 맞춰 갱신한다(hr.js 참고).
// 2026-08 재변경 — "사번: 할당되지 않은 사번 10개까지만 드롭다운으로 보여줌". 후보가 너무 많으면
// 오히려 고르기 불편하다는 피드백에 따라 20개 → 10개로 축소.
function EmployeeHrFields({ f, setF, disabled, dept, companyPrefix }) {
  const candidates = buildEmpNoCandidates(dept, companyPrefix, f.hire_date, f.employee_no, 10);
  return (
    <>
      {/* 요청사항: "채용구분, 사번" 한 줄 순서로 배치(예전에는 사번이 먼저였다). */}
      <Field label="채용구분">
        <select style={{ ...input, width:'100%' }} value={f.employment_type || '정규직'} disabled={disabled}
          onChange={e => setF(v => ({ ...v, employment_type: e.target.value }))}>
          {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="사번">
        {candidates ? (
          <select style={{ ...input, width:'100%' }} value={f.employee_no || ''} disabled={disabled}
            onChange={e => setF(v => ({ ...v, employee_no: e.target.value }))}>
            {!f.employee_no && <option value="">사번 선택</option>}
            {candidates.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <input style={{ ...input, width:'100%', background:'#f8fafc', color:'#aaa' }}
            value="자동부여 – 추후 재정의" disabled readOnly />
        )}
      </Field>
      <Field label="입사일"><input type="date" style={{ ...input, width:'100%' }} value={f.hire_date || ''} disabled={disabled}
        onChange={e => setF(v => ({ ...v, hire_date: e.target.value }))} /></Field>
      <Field label="퇴사일"><input type="date" style={{ ...input, width:'100%' }} value={f.resign_date || ''} disabled={disabled}
        onChange={e => setF(v => ({ ...v, resign_date: e.target.value }))} /></Field>
      {f.employment_type === '계약직' ? (
        <Field label="계약만료일"><input type="date" style={{ ...input, width:'100%' }} value={f.contract_end_date || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, contract_end_date: e.target.value }))} /></Field>
      ) : <div />}
    </>
  );
}

// 요청사항: "인사기록카드 상세보기-수정 화면에서 사번을 드롭다운으로 선택 (순차적으로 자동생성)".
// 해당 사원의 소속 부서에 사번설정 규칙(회사약어/부서약어/자릿수/다음번호)이 설정되어 있으면,
// 그 규칙 + 사원의 입사년도를 기준으로 다음에 올 수 있는 사번을 순서대로 여러 개(count) 미리
// 계산해 드롭다운 후보로 제공한다. 이미 저장되어 있던 값(currentValue)이 후보 목록에 없으면
// 목록 맨 앞에 추가해 실수로 값이 사라지지 않게 한다. 부서에 규칙이 아직 없으면(digits 미설정)
// null을 반환하고, 호출부에서는 이 경우 예전처럼 자유 입력(input)으로 표시한다.
function buildEmpNoCandidates(dept, companyPrefix, hireDate, currentValue, count = 5) {
  if (!dept || dept.empno_digits == null) return null;
  const d = hireDate ? new Date(hireDate) : new Date();
  const year = isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
  const yearYY = String(year).slice(-2);
  const base = (companyPrefix || '') + (dept.empno_prefix || '') + yearYY;
  const startSeq = dept.empno_next || 1;
  const list = [];
  for (let i = 0; i < count; i++) {
    list.push(base + String(startSeq + i).padStart(dept.empno_digits, '0'));
  }
  if (currentValue && !list.includes(currentValue)) list.unshift(currentValue);
  return list;
}

// ── 인사기록카드(신상카드) 서식 ────────────────────────────────────────────
// 첨부된 종이양식(인사기록카드)을 참고해 사원등록/상세 모달에 공통으로 쓰이는 확장 필드들.
// 증명사진/근로계약서/주민등록등본/통장사본은 D:\data_Storage\10. hr\<부서_직급_이름>\ 폴더로
// 저장되며, 인증(로그인) 없이 접근 가능했던 기존 정적 /uploads 경로 대신 로그인이 필요한
// 백엔드 라우트(GET /hr/file/:filename)에서 제공된다. <img src="...">나 window.open(url)처럼
// 인증 헤더 없이 직접 접근하는 방식은 쓸 수 없으므로, API 클라이언트로 blob을 받아온 뒤 로컬
// 객체URL을 만들어 사용한다(아래 AuthedPhoto/openHrFile).
function AuthedPhoto({ filename, alt, style, placeholder }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!filename) { setUrl(null); return undefined; }
    let objUrl = null;
    let cancelled = false;
    API.get(`/hr/file/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [filename]);
  if (!url) return placeholder !== undefined ? placeholder : null;
  return <img src={url} alt={alt} style={style} />;
}
function openHrFile(filename) {
  if (!filename) return;
  API.get(`/hr/file/${encodeURIComponent(filename)}`, { responseType: 'blob' })
    .then(r => {
      const objUrl = URL.createObjectURL(r.data);
      window.open(objUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    })
    .catch(() => alert('파일을 불러오지 못했습니다.'));
}
// 근로계약서(전자) 미리보기 전용 파일 열람 — 확정 저장되지 않은 임시 미리보기 PDF를
// GET /hr/labor-contract-preview/:filename 에서 받아온다(openHrFile과 동일한 인증 blob 방식).
function openLaborContractPreview(filename) {
  if (!filename) return;
  API.get(`/hr/labor-contract-preview/${encodeURIComponent(filename)}`, { responseType: 'blob' })
    .then(r => {
      const objUrl = URL.createObjectURL(r.data);
      window.open(objUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    })
    .catch(() => alert('미리보기를 불러오지 못했습니다.'));
}
const BLOOD_TYPES = ['A', 'B', 'O', 'AB'];
const HEALTH_LEVELS = ['건강', '보통', '위험'];
const LEVEL3 = ['상', '중', '하'];
const GENDERS = ['남', '여'];
// 주민등록번호 뒷자리 첫 숫자로 성별 자동판별 — 1,3(남자) / 2,4(여자). 그 외(5~8, 외국인 등)는 판별하지 않는다.
function genderFromResidentNo(residentNo) {
  const digits = String(residentNo || '').replace(/\D/g, '');
  if (digits.length < 7) return null;
  const g = digits[6];
  if (g === '1' || g === '3') return '남';
  if (g === '2' || g === '4') return '여';
  return null;
}
// 주민등록번호 앞 6자리(생년월일) + 뒷자리 첫 숫자(세기 판별)로 생년월일(YYYY-MM-DD)을 자동계산.
// 1,2→1900년대 / 3,4→2000년대 / 5,6→1900년대(외국인) / 7,8→2000년대(외국인) / 9,0→1800년대.
function birthDateFromResidentNo(residentNo) {
  const digits = String(residentNo || '').replace(/\D/g, '');
  if (digits.length < 7) return null;
  const yy = digits.slice(0, 2), mm = digits.slice(2, 4), dd = digits.slice(4, 6);
  const centuryMap = { '1':'19', '2':'19', '3':'20', '4':'20', '5':'19', '6':'19', '7':'20', '8':'20', '9':'18', '0':'18' };
  const century = centuryMap[digits[6]];
  if (!century) return null;
  const mmNum = Number(mm), ddNum = Number(dd);
  if (mmNum < 1 || mmNum > 12 || ddNum < 1 || ddNum > 31) return null;
  return `${century}${yy}-${mm}-${dd}`;
}

// 증명사진 업로드 — 선택 즉시 서버에 올리고 파일명만 폼(state)에 보관한다(등록/저장은 별도로 진행).
export function PhotoUploadField({ f, setF, disabled, uploadUrl = '/hr/employees/photo-upload' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const doUpload = (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('photo', file);
    API.post(uploadUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => setF(v => ({ ...v, photo: r.data.photo })))
      .catch(() => alert('사진 업로드에 실패했습니다.'))
      .finally(() => setUploading(false));
  };
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
      <div onClick={() => !disabled && inputRef.current?.click()}
        style={{ width:90, height:120, border:'1px solid #ddd', borderRadius:6, background:'#f8fafc',
          display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden',
          cursor: disabled ? 'default' : 'pointer', fontSize:11, color:'#aaa', textAlign:'center', lineHeight:1.4 }}>
        {f.photo
          ? <AuthedPhoto filename={f.photo} alt="증명사진" style={{ width:'100%', height:'100%', objectFit:'cover' }}
              placeholder={<span style={{ fontSize:11, color:'#aaa' }}>불러오는 중...</span>} />
          : (uploading ? '업로드중...' : <span>사진<br />(3x4)</span>)}
      </div>
      {!disabled && (
        <>
          <input ref={inputRef} type="file" accept="image/*" style={{ display:'none' }}
            onChange={e => { doUpload(e.target.files[0]); e.target.value = ''; }} />
          <button type="button" onClick={() => inputRef.current?.click()} style={{ ...quickBtn, padding:'4px 10px' }}>사진 선택</button>
        </>
      )}
    </div>
  );
}

// 주민등록등본 업로드 → AI 자동입력. AI 어시스턴트의 OCR 자동입력(genericOcr.js + 로컬 AI)과 등본
// 표 형식/주민번호 패턴에 특화된 기존 정규식 인식(residentRegOcr.js)을 서버에서 함께 돌려 서로 비는
// 값을 보충한 뒤, 그 결과(주민등록번호/주소/가족사항, 필요시 이름)를 입력폼에 바로 채워넣는다.
// 업로드 파일은 인식 전용 임시 파일로만 쓰이고 서버에 영구 저장되지 않는다.
function ResidentRegOcrButton({ f, setF, disabled, employeeName, allowNameFill, onConfidence }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const doUpload = (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('employeeName', employeeName || '');
    API.post('/hr/employees/resident-reg-ocr', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => {
        const d = r.data || {};
        const self = d.self || {};
        setF(v => {
          const next = { ...v };
          if (self.resident_no) {
            next.resident_no = self.resident_no;
            const gender = genderFromResidentNo(self.resident_no);
            const birthDate = birthDateFromResidentNo(self.resident_no);
            if (gender) next.gender = gender;
            if (birthDate) next.birth_date = birthDate;
          } else if (self.birth_date) {
            next.birth_date = self.birth_date;
          }
          if (d.address) next.address = d.address;
          // 2026-08 변경: 성명(한자)도 주민등록번호처럼 이미 값이 있어도 새로 인식된 값으로 갱신한다
          // (등본 재업로드는 최신화 의도이므로, 기존 값 보존보다 자동 갱신이 맞다는 요청에 따름).
          if (self.name_hanja) next.name_hanja = self.name_hanja;
          if (allowNameFill && !String(v.name || '').trim() && self.name) next.name = self.name;
          if (Array.isArray(d.family) && d.family.length) {
            // "본인" 행은 가족사항이 아니므로 제외한다(비전 AI가 프롬프트를 무시하고 포함시키는
            // 경우에 대한 방어 필터 — 정규식/PDF 경로는 애초에 본인을 family에 넣지 않음).
            const familyNoSelf = d.family.filter(m => (m.relation || '').trim() !== '본인');
            const existing = v.family || [];
            const key = (m) => `${(m.name || '').trim()}|${(m.relation || '').trim()}`;
            const existingKeys = new Set(existing.map(key));
            const added = familyNoSelf.filter(m => m.name && !existingKeys.has(key(m)));
            next.family = existingKeys.size ? [...existing, ...added] : familyNoSelf;
          }
          return next;
        });
        if (onConfidence) onConfidence(d.confidence || null);
        const matched = [];
        if (d.matched && d.matched.basic) matched.push('기본인식');
        if (d.matched && d.matched.ai) matched.push('AI인식');
        alert(`등본 인식이 완료되어 입력폼에 자동으로 채워졌습니다${matched.length ? ` (${matched.join(' + ')})` : ''}. 반영된 내용을 확인 후 필요시 직접 수정해주세요.`);
      })
      .catch(err => alert(err.response?.data?.message || '등본 인식에 실패했습니다.'))
      .finally(() => setUploading(false));
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*,.pdf" style={{ display:'none' }}
        onChange={e => { doUpload(e.target.files[0]); e.target.value = ''; }} />
      <button type="button" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}
        style={{ ...quickBtn, padding:'4px 10px', whiteSpace:'nowrap', opacity: (disabled || uploading) ? 0.6 : 1 }}>
        {uploading ? '인식 중...' : '📄 등본으로 자동입력'}
      </button>
    </>
  );
}

// 통장사본 업로드 → 은행명/예금주/계좌번호 자동인식. 사원등록 폼은 아직 저장된 사원(userId)이
// 없어 상세보기의 문서업로드 자동반영(POST /employees/:userId/document/bankbook)을 쓸 수 없으므로,
// 등본 자동입력(ResidentRegOcrButton)과 동일하게 파일만 올려 인식 결과를 즉시 돌려받는 standalone
// 라우트(POST /hr/employees/bankbook-ocr)를 새로 추가해 재사용한다. 인식된 값은 이미 값이 있어도
// 그대로 덮어쓴다(재업로드는 최신화 의도라는 등본 자동입력과 동일한 정책).
function BankbookOcrButton({ f, setF, disabled, onConfidence }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const doUpload = (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    API.post('/hr/employees/bankbook-ocr', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => {
        const d = r.data || {};
        setF(v => ({
          ...v,
          bank_name: d.bank_name || v.bank_name,
          bank_holder: d.account_holder || v.bank_holder,
          bank_account: d.account_number || v.bank_account,
        }));
        if (onConfidence) onConfidence(d.confidence || null);
        alert('통장사본 인식이 완료되어 급여통장 입력칸에 자동으로 채워졌습니다. 반영된 내용을 확인 후 필요시 직접 수정해주세요.');
      })
      .catch(err => alert(err.response?.data?.message || '통장사본 인식에 실패했습니다.'))
      .finally(() => setUploading(false));
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*,.pdf" style={{ display:'none' }}
        onChange={e => { doUpload(e.target.files[0]); e.target.value = ''; }} />
      <button type="button" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}
        style={{ ...quickBtn, padding:'4px 10px', whiteSpace:'nowrap', opacity: (disabled || uploading) ? 0.6 : 1 }}>
        {uploading ? '인식 중...' : '📷 통장사본 가져오기'}
      </button>
    </>
  );
}

// 2026-09 OCR 신뢰도 UI 공용 임계값 — 명함(Contacts.js)/업체관리(Companies.js)와 동일하게 60.
// null/undefined(=OCR로 매칭 불가, 틀렸다는 뜻은 아님)는 중립으로 두고, 숫자값이 임계값 미만일
// 때만 경고 표시한다.
const HR_LOW_CONFIDENCE_THRESHOLD = 60;
function isLowConfidence(c, value) {
  if (!value) return false;
  if (typeof c !== 'number') return false;
  return c < HR_LOW_CONFIDENCE_THRESHOLD;
}

// 반복입력 표(가족사항/학력사항/경력사항/자격사항 공용) — 행 추가/삭제 가능한 미니 표.
// rowConfidence: 등본 자동입력 가족사항 전용(옵션) — 행 인덱스에 맞춰 confidence.family[i](성명 매칭
// 신뢰도)를 전달하면, 해당 행의 "성명" 입력칸에 낮은 신뢰도 경고(붉은 테두리)를 표시한다.
export function RepeatableRows({ label, rows, setRows, columns, disabled, rowConfidence }) {
  const list = rows || [];
  const addRow = () => setRows(rs => [...(rs || []), {}]);
  const removeRow = (i) => setRows(rs => (rs || []).filter((_, idx) => idx !== i));
  const updateCell = (i, key, val) => setRows(rs => (rs || []).map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div style={{ fontSize:12, color:'#666', fontWeight:600 }}>{label}</div>
        {!disabled && <button type="button" onClick={addRow} style={{ ...quickBtn, padding:'3px 10px' }}>+ 행추가</button>}
      </div>
      <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            {columns.map(c => <th key={c.key} style={{ ...th, fontSize:11, padding:'6px 8px' }}>{c.label}</th>)}
            {!disabled && <th style={{ ...th, width:32 }} />}
          </tr></thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={columns.length + (disabled ? 0 : 1)} style={{ ...td, color:'#aaa', fontSize:12 }}>등록된 내역이 없습니다.</td></tr>
            )}
            {list.map((r, i) => (
              <tr key={i}>
                {columns.map(c => (
                  <td key={c.key} style={{ ...td, padding:4, ...(c.type === 'checkbox' ? { textAlign:'center' } : {}) }}>
                    {c.type === 'select' ? (
                      <select disabled={disabled} value={r[c.key] || ''} onChange={e => updateCell(i, c.key, e.target.value)}
                        style={{ ...input, width:'100%', padding:'6px 8px', fontSize:12 }}>
                        <option value="">-</option>
                        {c.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : c.type === 'checkbox' ? (
                      <input type="checkbox" disabled={disabled} checked={!!r[c.key]} onChange={e => updateCell(i, c.key, e.target.checked)}
                        style={{ width:16, height:16, cursor: disabled ? 'default' : 'pointer' }} />
                    ) : (
                      <input disabled={disabled} value={r[c.key] || ''} onChange={e => updateCell(i, c.key, e.target.value)}
                        style={{ ...input, width:'100%', padding:'6px 8px', fontSize:12,
                          ...(c.key === 'name' && isLowConfidence(rowConfidence && rowConfidence[i], r[c.key])
                            ? { border:'1.5px solid #ef4444', background:'#fff5f5' } : {}) }} />
                    )}
                  </td>
                ))}
                {!disabled && (
                  <td style={{ ...td, padding:4 }}>
                    <button type="button" onClick={() => removeRow(i)} style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:14 }}>✕</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// is_dependent(등록) — 급여관리에서 연봉 선택시 소득세 등을 자동계산할 때 부양가족 수에
// 포함할지 여부를 가족 구성원별로 직접 지정하는 체크박스. 인사기록카드 작성자가 실제
// 세법상 부양가족 요건(소득·나이 등)을 판단해 체크해야 하며, 시스템이 자동 판별하지 않는다.
// is_disabled(장애인) — 2026-08-28 "부양가족 나이에 따른 요율(공제)도 반영" 요청 구현.
// 급여관리 자동계산이 장애인 추가공제(연 200만원 소득공제)를 반영할 때 쓴다. 나이(생년월일)
// 만으로는 판단할 수 없어 등록(부양가족) 체크와 마찬가지로 작성자가 직접 체크해야 한다.
// (자녀세액공제 8~20세, 경로우대공제 70세 이상은 birth_date로 시스템이 자동 계산하므로
// 별도 체크박스가 필요없다.)
export const FAMILY_COLUMNS = [
  { key:'relation', label:'관계' }, { key:'name', label:'성명' }, { key:'birth_date', label:'생년월일' },
  { key:'education', label:'학력' }, { key:'occupation', label:'직업' },
  { key:'living_together', label:'동거여부', type:'select', options:['동거', '비동거'] },
  { key:'is_dependent', label:'등록(부양가족)', type:'checkbox' },
  { key:'is_disabled', label:'장애인', type:'checkbox' },
];
export const EDUCATION_COLUMNS = [
  { key:'period', label:'기간' }, { key:'school', label:'학교' }, { key:'major', label:'전공' }, { key:'note', label:'비고' },
];
export const CAREER_COLUMNS = [
  { key:'period', label:'기간' }, { key:'company', label:'직장명' }, { key:'final_position', label:'최종직위' }, { key:'duties', label:'담당업무' },
];
export const QUALIFICATION_COLUMNS = [
  { key:'name', label:'자격증명' }, { key:'acquired_date', label:'취득일자' }, { key:'foreign_lang', label:'외국어' },
  { key:'reading', label:'독해력', type:'select', options: LEVEL3 },
  { key:'writing', label:'작문', type:'select', options: LEVEL3 },
  { key:'speaking', label:'회화', type:'select', options: LEVEL3 },
];

// 인사기록카드 확장필드 — 사원등록/상세 모달 공통(사진/신상정보/건강사항/병역 + 가족·학력·경력·자격 반복입력).
// employeeName: 등본 자동입력(기본 기능)이 "본인" 행을 식별할 때 쓰는 현재 이름(등록모달은 f.name,
// 수정모달은 대상 사원의 users.name을 부모가 넘겨준다). allowNameFill: 등록모달처럼 f에 최상위 name
// 필드가 있을 때만 true로 넘겨, 등본에서 읽은 이름으로 빈 이름칸을 자동으로 채울 수 있게 한다.
// enableResidentRegOcr: 등본 자동입력 버튼 노출 여부 — 인사관리(hr-management) 쓰기권한이 있는
// 화면(사원등록/수정)에서만 true로 켠다. 이 컴포넌트는 마이페이지 "추가정보"(본인 셀프서비스,
// hr-management 메뉴 권한이 없는 일반 직원도 접근) 화면에서도 재사용되는데, 새 인식 라우트는
// hr-management 쓰기권한을 요구하므로 기본값 false로 두어 그 화면에서는 노출하지 않는다.
// confidence: 2026-09 등본 자동입력 OCR 신뢰도 UI(옵션) — ResidentRegOcrButton의 onConfidence로
// 받은 값({ address, self:{resident_no,birth_date,name_hanja}, family:[...] })을 그대로 전달하면
// 성명(한자)/주민등록번호/주소 입력행과 가족사항 표의 해당 행이 낮은 신뢰도일 때 붉게 표시된다.
export function HrCardFields({ f, setF, disabled, photoUploadUrl, employeeName, allowNameFill, enableResidentRegOcr = false, confidence }) {
  const setRowsFor = (key) => (updater) => setF(v => ({ ...v, [key]: typeof updater === 'function' ? updater(v[key] || []) : updater }));
  const cSelf = (confidence && confidence.self) || {};
  return (
    <div style={{ gridColumn:'1 / -1' }}>
      <div style={{ display:'flex', gap:16, marginBottom:4 }}>
        <PhotoUploadField f={f} setF={setF} disabled={disabled} {...(photoUploadUrl ? { uploadUrl: photoUploadUrl } : {})} />
        <div style={{ flex:1, display:'grid', gridTemplateColumns:'90px 1fr 90px 1fr', gap:0 }}>
          <CardInputRow label="성명(한자)" warn={isLowConfidence(cSelf.name_hanja, f.name_hanja)}>
            <input style={cardInputStyle} value={f.name_hanja || ''} disabled={disabled}
              onChange={e => setF(v => ({ ...v, name_hanja: e.target.value }))} />
          </CardInputRow>
          <CardInputRow label="성별">
            <select style={{ ...cardInputStyle, cursor: disabled ? 'default' : 'pointer' }} value={f.gender || ''} disabled={disabled}
              onChange={e => setF(v => ({ ...v, gender: e.target.value }))}>
              <option value="">-</option>{GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </CardInputRow>
          <CardInputRow label="주민등록번호" warn={isLowConfidence(cSelf.resident_no, f.resident_no)}>
            <input style={cardInputStyle} value={f.resident_no || ''} disabled={disabled}
              placeholder="000000-0000000" onChange={e => {
                const val = e.target.value;
                const gender = genderFromResidentNo(val);
                const birthDate = birthDateFromResidentNo(val);
                setF(v => ({ ...v, resident_no: val, ...(gender ? { gender } : {}), ...(birthDate ? { birth_date: birthDate } : {}) }));
              }} />
          </CardInputRow>
          <CardInputRow label="휴대전화">
            <input style={cardInputStyle} value={f.mobile_phone || ''} disabled={disabled}
              onChange={e => setF(v => ({ ...v, mobile_phone: e.target.value }))} />
          </CardInputRow>
          <CardInputRow label="주소" span={3} warn={isLowConfidence(confidence && confidence.address, f.address)}>
            <input style={cardInputStyle} value={f.address || ''} disabled={disabled}
              placeholder="개인 자택 주소를 입력하세요" onChange={e => setF(v => ({ ...v, address: e.target.value }))} />
            {/* 요청사항: "인사기록카드 상세정보 글자 우측끝에 등본으로 자동입력 버튼을 이쪽으로
                이동" — 이 버튼은 더 이상 여기(주소 옆)에 렌더링하지 않는다. 사원등록 모달의
                "📇 인사기록카드 상세정보" 라벨 줄로 옮겼다(아래 registering 모달 JSX 참고). */}
          </CardInputRow>
        </div>
      </div>

      <CardSectionTitle>가족사항</CardSectionTitle>
      <RepeatableRows label="" rows={f.family} setRows={setRowsFor('family')} columns={FAMILY_COLUMNS} disabled={disabled}
        rowConfidence={confidence && confidence.family} />

      <CardSectionTitle>건강사항</CardSectionTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:0 }}>
        <CardInputRow label="취미"><input style={cardInputStyle} value={f.hobby || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, hobby: e.target.value }))} /></CardInputRow>
        <CardInputRow label="특기"><input style={cardInputStyle} value={f.specialty || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, specialty: e.target.value }))} /></CardInputRow>
        <CardInputRow label="종교"><input style={cardInputStyle} value={f.religion || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, religion: e.target.value }))} /></CardInputRow>
        <CardInputRow label="혈액형">
          <select style={{ ...cardInputStyle, cursor: disabled ? 'default' : 'pointer' }} value={f.blood_type || ''} disabled={disabled}
            onChange={e => setF(v => ({ ...v, blood_type: e.target.value }))}>
            <option value="">-</option>{BLOOD_TYPES.map(b => <option key={b} value={b}>{b}형</option>)}
          </select>
        </CardInputRow>
        <CardInputRow label="신장"><input style={cardInputStyle} value={f.height || ''} disabled={disabled} placeholder="cm"
          onChange={e => setF(v => ({ ...v, height: e.target.value }))} /></CardInputRow>
        <CardInputRow label="체중"><input style={cardInputStyle} value={f.weight || ''} disabled={disabled} placeholder="kg"
          onChange={e => setF(v => ({ ...v, weight: e.target.value }))} /></CardInputRow>
        <CardInputRow label="시력(좌/우)">
          <input style={{ ...cardInputStyle, width:'auto', flex:1 }} value={f.eyesight_left || ''} disabled={disabled} placeholder="좌"
            onChange={e => setF(v => ({ ...v, eyesight_left: e.target.value }))} />
          <span style={{ color:'#ccc' }}>/</span>
          <input style={{ ...cardInputStyle, width:'auto', flex:1 }} value={f.eyesight_right || ''} disabled={disabled} placeholder="우"
            onChange={e => setF(v => ({ ...v, eyesight_right: e.target.value }))} />
        </CardInputRow>
        <CardInputRow label="건강정도">
          <select style={{ ...cardInputStyle, cursor: disabled ? 'default' : 'pointer' }} value={f.health_level || ''} disabled={disabled}
            onChange={e => setF(v => ({ ...v, health_level: e.target.value }))}>
            <option value="">-</option>{HEALTH_LEVELS.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </CardInputRow>
        <CardInputRow label="질병" span={3}>
          <input style={cardInputStyle} value={f.disease || ''} disabled={disabled}
            onChange={e => setF(v => ({ ...v, disease: e.target.value }))} />
        </CardInputRow>
      </div>

      <CardSectionTitle>병역</CardSectionTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:0 }}>
        <CardInputRow label="병역구분">
          <select style={{ ...cardInputStyle, cursor: disabled ? 'default' : 'pointer' }} value={f.military_status || ''} disabled={disabled}
            onChange={e => setF(v => ({ ...v, military_status: e.target.value }))}>
            <option value="">-</option><option value="필">필</option><option value="미필">미필</option><option value="면제">면제</option>
          </select>
        </CardInputRow>
        <CardInputRow label="면제사유"><input style={cardInputStyle} value={f.military_exempt_reason || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, military_exempt_reason: e.target.value }))} /></CardInputRow>
        <CardInputRow label="군별"><input style={cardInputStyle} value={f.military_branch || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, military_branch: e.target.value }))} /></CardInputRow>
        <CardInputRow label="병과"><input style={cardInputStyle} value={f.military_category || ''} disabled={disabled}
          onChange={e => setF(v => ({ ...v, military_category: e.target.value }))} /></CardInputRow>
      </div>

      {/* 학력사항/경력사항 입력은 삭제되었습니다(요청에 따름) — 과거에 등록된 데이터는 상세보기(인사기록카드)에서 계속 조회할 수 있습니다. */}
      <CardSectionTitle>자격사항</CardSectionTitle>
      <RepeatableRows label="" rows={f.qualifications} setRows={setRowsFor('qualifications')} columns={QUALIFICATION_COLUMNS} disabled={disabled} />
    </div>
  );
}

// ── 인사기록카드 상세보기(읽기전용) — 첨부된 종이양식과 유사한 표 레이아웃으로 렌더링 ──────────
function CardRow({ label, value, span = 1 }) {
  return (
    <>
      <div style={{ gridColumn:`span 1`, background:'#f8fafc', border:'1px solid #ddd', padding:'8px 10px', fontSize:12, fontWeight:600, color:'#555', display:'flex', alignItems:'center' }}>{label}</div>
      <div style={{ gridColumn:`span ${span}`, border:'1px solid #ddd', padding:'8px 10px', fontSize:13, display:'flex', alignItems:'center', wordBreak:'break-all' }}>{value || '-'}</div>
    </>
  );
}
function CardSectionTitle({ children }) {
  return <div style={{ fontSize:13, fontWeight:700, margin:'18px 0 8px', color:C.primary }}>{children}</div>;
}
// 인사기록카드 상세보기(CardRow)와 완전히 동일한 라벨/값 셀 CSS를 그대로 쓰되, 값 칸 안에 실제
// 입력요소(input/select)를 넣어 편집 가능하게 만든 버전 — 입력폼(HrCardFields)에서 사용.
const cardInputStyle = { border:'none', outline:'none', background:'transparent', fontSize:13, width:'100%', padding:0, fontFamily:'inherit', color:'inherit' };
// warn: 2026-09 등본/통장사본 OCR 신뢰도 UI(명함/업체관리와 동일한 패턴) — OCR/AI로 채워진 값이
// 낮은 신뢰도일 때 이 입력행의 값 칸을 붉은 테두리로 표시해, 저장 전 확인이 필요함을 알려준다.
function CardInputRow({ label, span = 1, children, warn }) {
  return (
    <>
      <div style={{ gridColumn:`span 1`, background:'#f8fafc', border:'1px solid #ddd', padding:'8px 10px', fontSize:12, fontWeight:600, color:'#555', display:'flex', alignItems:'center' }}>{label}</div>
      <div style={{ gridColumn:`span ${span}`, border: warn ? '1.5px solid #ef4444' : '1px solid #ddd', background: warn ? '#fff5f5' : undefined, padding:'6px 10px', fontSize:13, display:'flex', alignItems:'center', gap:6 }}>{children}</div>
    </>
  );
}
function CardTable({ columns, rows, headerFontSize = 11 }) {
  return (
    <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:4 }}>
      <thead><tr>{columns.map(c => <th key={c.key} style={{ ...th, fontSize:headerFontSize, padding:'6px 8px' }}>{c.label}</th>)}</tr></thead>
      <tbody>
        {(!rows || rows.length === 0) && <tr><td colSpan={columns.length} style={{ ...td, color:'#aaa', fontSize:12 }}>등록된 내역이 없습니다.</td></tr>}
        {(rows || []).map((r, i) => (
          <tr key={r.id || i}>{columns.map(c => (
            <td key={c.key} style={{ ...td, fontSize:12, ...(c.type === 'checkbox' ? { textAlign:'center' } : {}) }}>
              {c.type === 'checkbox' ? (r[c.key] ? '✓' : '-') : (r[c.key] || '-')}
            </td>
          ))}</tr>
        ))}
      </tbody>
    </table>
  );
}
// 인사기록카드 첨부서류(근로계약서/주민등록등본/통장사본) 버튼 — 등록되어 있으면 클릭 시 새 탭으로
// 열람/다운로드하고, 없으면(쓰기권한이 있을 때만) 클릭해서 바로 업로드한다. 이미 등록된 상태에서도
// 쓰기권한이 있으면 옆의 🔄 아이콘으로 재업로드(교체)할 수 있다.
export function DocButton({ label, filename, canWrite, onUpload, onView, fill }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const has = !!filename;
  const pick = () => inputRef.current?.click();
  const handleFile = (file) => {
    if (!file) return;
    setUploading(true);
    Promise.resolve(onUpload(file)).catch(() => alert('업로드에 실패했습니다.')).finally(() => setUploading(false));
  };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:2, ...(fill ? { width:'100%' } : {}) }}>
      <button type="button"
        onClick={() => { if (has) { if (onView) onView(); else openHrFile(filename); } else if (canWrite) pick(); }}
        style={{ background:'#fff', color: has ? '#0F6E56' : '#999',
          border:`1px solid ${has ? '#0F6E56' : '#ddd'}`, borderRadius:8,
          padding:'5px 2px', fontSize:12, fontWeight:600,
          cursor: (has || canWrite) ? 'pointer' : 'default', whiteSpace:'nowrap',
          ...(fill ? { flex:1, width:'100%', boxSizing:'border-box' } : {}) }}>
        {has ? '📄 ' : '📎 '}{label}{uploading ? ' (업로드중...)' : ''}
      </button>
      {canWrite && has && !onView && (
        <button type="button" onClick={pick} title="재업로드"
          style={{ background:'none', border:'none', color:'#aaa', cursor:'pointer', fontSize:13, padding:'0 2px' }}>🔄</button>
      )}
      {canWrite && (
        <input ref={inputRef} type="file" style={{ display:'none' }}
          onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
      )}
    </div>
  );
}

// 문서보기 모달 — 첨부서류(근로계약서/주민등록등본/통장사본) 클릭 시 새 탭으로 바로 내려받던
// 방식 대신, 모달 안에서 미리보기(이미지/PDF는 인라인, 그 외 형식은 다운로드 안내)하고 우측
// 상단에 수정(재업로드)/삭제 버튼을 제공한다.
function DocViewerModal({ label, filename, onClose, onReplace, onDelete, canWrite, printHistory }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    setBlobUrl(null); setLoadFailed(false);
    API.get(`/hr/file/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setMimeType(r.data.type || '');
        setBlobUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [filename]);

  const handleReplaceFile = (file) => {
    if (!file) return;
    setBusy(true);
    Promise.resolve(onReplace(file)).catch(() => alert('업로드에 실패했습니다.')).finally(() => setBusy(false));
  };
  const handleDelete = () => {
    if (!window.confirm('이 문서를 삭제하시겠습니까?')) return;
    setBusy(true);
    Promise.resolve(onDelete()).catch(() => alert('삭제에 실패했습니다.')).finally(() => setBusy(false));
  };

  const headerActions = (
    <div style={{ display:'flex', gap:6 }}>
      {canWrite && (
        <>
          <button onClick={() => inputRef.current?.click()} disabled={busy}
            style={{ background:'#fff', color:C.primary, border:`1px solid ${C.primary}`, borderRadius:8,
              padding:'5px 10px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            ✏️ 수정
          </button>
          <input ref={inputRef} type="file" style={{ display:'none' }}
            onChange={e => { handleReplaceFile(e.target.files[0]); e.target.value = ''; }} />
          <button onClick={handleDelete} disabled={busy}
            style={{ background:'#fff', color:'#dc2626', border:'1px solid #dc2626', borderRadius:8,
              padding:'5px 10px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            🗑️ 삭제
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal title={`📄 ${label}`} onClose={onClose} width={720} headerActions={headerActions}>
      <div style={{ minHeight:400, display:'flex', alignItems:'center', justifyContent:'center',
        background:'#f8fafc', borderRadius:8, overflow:'hidden' }}>
        {!blobUrl && !loadFailed && <div style={{ color:'#aaa', fontSize:13, padding:40 }}>불러오는 중...</div>}
        {loadFailed && <div style={{ color:'#dc2626', fontSize:13, padding:40 }}>파일을 불러오지 못했습니다.</div>}
        {blobUrl && mimeType.startsWith('image/') && (
          <img src={blobUrl} alt={label} style={{ maxWidth:'100%', maxHeight:600, objectFit:'contain' }} />
        )}
        {blobUrl && mimeType === 'application/pdf' && (
          <iframe src={blobUrl} title={label} style={{ width:'100%', height:600, border:'none' }} />
        )}
        {blobUrl && !mimeType.startsWith('image/') && mimeType !== 'application/pdf' && (
          <div style={{ textAlign:'center', padding:40 }}>
            <div style={{ fontSize:13, color:'#888', marginBottom:14 }}>이 형식은 미리보기를 지원하지 않습니다.</div>
            <a href={blobUrl} download={filename}
              style={{ display:'inline-block', padding:'8px 18px', background:C.primary, color:'#fff',
                borderRadius:8, fontSize:13, fontWeight:600, textDecoration:'none' }}>
              ⬇️ 다운로드
            </a>
          </div>
        )}
      </div>
      {/* 요청사항: "출력시 출력된 이력을 인사기록카드-근로계약 메뉴에서 볼 수 있음" — 근로계약
          (스캔) 문서를 열람할 때만(printHistory prop이 넘어온 경우) 근로자가 계정관리 > 근로계약
          버튼으로 열람/출력한 이력을 함께 보여준다(mypage.js POST /labor-contract-scan/print가
          한 줄씩 남긴 hr_labor_contract_scan_logs). */}
      {printHistory && (
        <div style={{ marginTop:14 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#555', marginBottom:6 }}>📜 출력이력</div>
          {printHistory.length === 0 ? (
            <div style={{ fontSize:12, color:'#aaa' }}>출력 이력이 없습니다.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:150, overflowY:'auto' }}>
              {printHistory.map(h => (
                <div key={h.id} style={{ fontSize:12, color:'#555', padding:'5px 10px', background:'#f8fafc',
                  border:'1px solid #eee', borderRadius:6 }}>
                  {h.printed_at} · {h.printed_by}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// 근로계약서(전자)/근로서약서/개인정보 제공 동의서 전용 미리보기 모달 — 지금까지는 생성/열람
// 버튼을 누르면 window.open(blob URL)로 새 브라우저 탭에 파일을 띄웠는데, 이 방식은 PDF
// 뷰어가 비활성화된 환경(사내 배포 브라우저/웹뷰 등)에서는 미리보기 대신 곧바로 파일
// 다운로드로 동작해버려 "미리보기 없이 다운로드 버튼만 있다"는 문제가 있었다(요청사항).
// DocViewerModal과 동일하게 blob을 받아 모달 안 iframe/img로 직접 렌더링하면 브라우저의
// PDF 뷰어 설정과 무관하게 항상 미리보기가 보장된다. 첨부서류(등본/통장사본 등)처럼
// 수정/삭제 기능은 필요 없는 "생성된 문서 열람 전용" 화면이라 DocViewerModal보다 단순하다.
// apiPath를 넘기면 GET /hr/file/:filename 대신 다른 경로(예: 확정 전 임시 미리보기용
// GET /hr/labor-contract-preview/:filename)에서 blob을 받아온다.
export function FilePreviewModal({ title, filename, apiPath, onClose, width = 870 }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    setBlobUrl(null); setLoadFailed(false);
    API.get(apiPath || `/hr/file/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setMimeType(r.data.type || '');
        setBlobUrl(objUrl);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [filename, apiPath]);

  return (
    <Modal title={`👁️ ${title}`} onClose={onClose} width={width}>
      <div style={{ minHeight:400, display:'flex', alignItems:'center', justifyContent:'center',
        background:'#f8fafc', borderRadius:8, overflow:'hidden' }}>
        {!blobUrl && !loadFailed && <div style={{ color:'#aaa', fontSize:13, padding:40 }}>불러오는 중...</div>}
        {loadFailed && <div style={{ color:'#dc2626', fontSize:13, padding:40 }}>파일을 불러오지 못했습니다.</div>}
        {blobUrl && mimeType.startsWith('image/') && (
          <img src={blobUrl} alt={title} style={{ maxWidth:'100%', maxHeight:600, objectFit:'contain' }} />
        )}
        {blobUrl && mimeType === 'application/pdf' && (
          // 요청사항: "근로계약서 미리보기를 누르면 화면 비율을 100%로 보여줌" — 브라우저 내장
          // PDF 뷰어는 URL 뒤에 #zoom=100 을 붙이면 초기 확대/축소 비율을 100%로 강제할 수 있다.
          <iframe src={`${blobUrl}#zoom=100`} title={title} style={{ width:'100%', height:600, border:'none' }} />
        )}
        {blobUrl && !mimeType.startsWith('image/') && mimeType !== 'application/pdf' && (
          <div style={{ textAlign:'center', padding:40 }}>
            <div style={{ fontSize:13, color:'#888', marginBottom:14 }}>이 형식은 미리보기를 지원하지 않습니다.</div>
            <a href={blobUrl} download={filename}
              style={{ display:'inline-block', padding:'8px 18px', background:C.primary, color:'#fff',
                borderRadius:8, fontSize:13, fontWeight:600, textDecoration:'none' }}>
              ⬇️ 다운로드
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
}

const CERT_PURPOSE_OPTIONS = ['제출용', '대출용', '비자용', '보험용', '기타'];

// 재직증명서 발급 모달 — 발급목적/제출처/회사명/대표자/메모를 입력해 "발급"하면 서버(PDF+puppeteer)가
// A4 문서를 만들어 저장하고(발급이력 관리 목적), 응답받은 filename을 새 창(blob)으로 열어 보여준다
// — 별도 인쇄 버튼 없이, 새 창에 뜨는 PDF 뷰어의 인쇄 아이콘으로 바로 인쇄할 수 있다.
// 아래쪽에는 지금까지 발급된 이력을 목록으로 보여주고, 각 건은 "보기"로 다시 열어볼 수 있다.
function CertificateModal({ userId, employeeName, defaultJobDuty, onClose }) {
  const [purpose, setPurpose] = useState('제출용');
  const [purposeOther, setPurposeOther] = useState('');
  const [submitTo, setSubmitTo] = useState('');
  // 2026-08 변경: 재직증명서 발급 엔진이 워드 템플릿(Certificate_Form.docx) 방식으로 바뀌면서
  // 회사명/대표자명은 템플릿에 고정 문구로 박혀있어 더 이상 입력받지 않는다. 대신 템플릿의
  // "담당업무" 자리표시자에 채울 값을 새로 입력받는다(인사기록카드 직무 값이 있으면 기본값으로).
  const [jobDuty, setJobDuty] = useState(defaultJobDuty || '');
  const [memo, setMemo] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = () => {
    setLoadingHistory(true);
    API.get(`/hr/employees/${userId}/certificates`)
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  };
  useEffect(() => { loadHistory(); }, [userId]);

  const issue = () => {
    if (purpose === '기타' && !purposeOther.trim()) { alert('발급목적을 입력해주세요.'); return; }
    const finalPurpose = purpose === '기타' ? purposeOther.trim() : purpose;
    setIssuing(true);
    API.post(`/hr/employees/${userId}/certificates`, { purpose: finalPurpose, submitTo, jobDuty, memo })
      .then(r => {
        openHrFile(r.data.filename);
        loadHistory();
      })
      .catch(err => alert(err.response?.data?.message || '발급에 실패했습니다.'))
      .finally(() => setIssuing(false));
  };

  return (
    <Modal title={`📃 재직증명서 발급 — ${employeeName}`} onClose={onClose} width={560}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
        <Field label="발급목적">
          <select style={{ ...input, width:'100%' }} value={purpose} onChange={e => setPurpose(e.target.value)}>
            {CERT_PURPOSE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        {purpose === '기타' ? (
          <Field label="발급목적(직접입력)">
            <input style={{ ...input, width:'100%' }} value={purposeOther} onChange={e => setPurposeOther(e.target.value)} />
          </Field>
        ) : <div />}
        <Field label="제출처">
          <input style={{ ...input, width:'100%' }} placeholder="예: ○○은행" value={submitTo} onChange={e => setSubmitTo(e.target.value)} />
        </Field>
        <div />
        <Field label="담당업무">
          <input style={{ ...input, width:'100%' }} value={jobDuty} onChange={e => setJobDuty(e.target.value)} />
        </Field>
        <div />
        <div style={{ gridColumn:'1 / -1' }}>
          <Field label="메모(선택)">
            <input style={{ ...input, width:'100%' }} value={memo} onChange={e => setMemo(e.target.value)} />
          </Field>
        </div>
      </div>
      <button onClick={issue} disabled={issuing}
        style={{ ...btn(C.primary), width:'100%', padding:'10px 0', marginTop:6, opacity: issuing ? 0.6 : 1 }}>
        {issuing ? '발급 중...' : '🖨️ 발급 및 인쇄'}
      </button>

      <div style={{ marginTop:20, paddingTop:16, borderTop:'1px dashed #e5e7eb' }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>발급이력</div>
        {loadingHistory ? (
          <div style={{ fontSize:12, color:'#aaa' }}>불러오는 중...</div>
        ) : history.length === 0 ? (
          <div style={{ fontSize:12, color:'#aaa' }}>발급 이력이 없습니다.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto' }}>
            {history.map(h => (
              <div key={h.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'6px 10px', background:'#f8fafc', border:'1px solid #eee', borderRadius:6, fontSize:12 }}>
                <span>{h.issued_at} · {h.purpose || '-'}{h.submit_to ? ` (${h.submit_to})` : ''} · {h.issued_by}</span>
                <button onClick={() => openHrFile(h.filename)}
                  style={{ background:'#fff', color:C.primary, border:`1px solid ${C.primary}`, borderRadius:6,
                    padding:'3px 8px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                  보기
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// 근로계약서(전자) 발급 모달 — CertificateModal과 비슷한 구조이나, 2026-08 개편으로
// "계약년월일/연봉/직무/직급 입력 → 계약서 미리보기로 최종 확인 → 계약서 생성 및 인쇄 버튼
// 활성화" 순서가 추가됐다. 계약년/월/일은 자동계산하지 않고 관리자가 직접 입력한다(요청사항).
// 연봉/직급은 인사기록카드 값을 기본값으로 채워주되, 발급 시점에 다르게 입력할 수 있다.
// 발급이력은 관리자가 이 모달에서 "생성"하는 시점이 아니라, 근로자 본인이 마이페이지에서
// 실제로 열어 출력을 확인한 시점에 기록된다(요청사항) — 아래 목록에는 아직 근로자가 출력하지
// 않은 "발급대기중" 건도 함께 표시하되 상태를 구분해서 보여준다.
// 2026-08 추가 — "연봉이 해당년도 최저시급기준 연봉금액 미달이면 1일 근무시간/휴게시간을
// 근로기준법에 의해 자동 지정, 그 이상이면 8시간/60분" 요청사항 구현. 연도별 최저시급은
// 고용노동부 고시값을 매년 하나씩 등록해두고(목록에 없는 연도는 등록된 값 중 가장 가까운
// 과거 연도의 시급을 그대로 사용 — 신규 연도 고시 전까지의 임시 대체값), 아래 계산 함수가
// 실제 근무시간/휴게시간을 산출한다.
const MIN_HOURLY_WAGE_BY_YEAR = { 2023: 9620, 2024: 9860, 2025: 10030, 2026: 10320 };
function getMinHourlyWage(year) {
  const years = Object.keys(MIN_HOURLY_WAGE_BY_YEAR).map(Number).sort((a, b) => a - b);
  if (!years.length) return 0;
  const y = Number(year) || years[years.length - 1];
  const past = years.filter(yy => yy <= y);
  const pick = past.length ? Math.max(...past) : years[0];
  return MIN_HOURLY_WAGE_BY_YEAR[pick];
}

// 근로기준법 기준 1일 근무시간/휴게시간 자동계산.
// - 월 소정근로시간 환산: 1일 H시간 근무를 주5일+주휴(H시간)로 환산하면 주 6H시간이 되고,
//   여기에 월평균 주수(4.345주=365/7/12)를 곱하면 약 26.07×H시간이 된다(H=8일 때 약 209시간 —
//   흔히 쓰는 "1일 8시간·월 209시간" 산정방식과 동일한 계수).
// - 연봉이 해당년도 최저시급 기준 정규직(1일 8시간) 연봉 미만이면, 그 연봉으로도 최저시급을
//   충족할 수 있는 최대 정수 근무시간(1~8)을 역산해 1일 근무시간으로 자동 지정한다.
// - 휴게시간은 근로기준법 54조 기준(4시간 이상 30분, 8시간 이상 60분)으로 함께 지정한다.
function computeAutoWorkHours(annualSalary, year) {
  const minHourly = getMinHourlyWage(year);
  const annual = Number(annualSalary) || 0;
  const FULL_TIME_MONTHLY_HOURS = 209; // 1일 8시간·주 40시간 기준 통상 월 소정근로시간
  const MONTHLY_HOURS_PER_DAY_UNIT = 26.07; // 6H × 4.345주 (1일 H시간 기준 월 소정근로시간 계수)
  const fullTimeMinAnnual = minHourly * FULL_TIME_MONTHLY_HOURS * 12;
  if (!annual || !minHourly || annual >= fullTimeMinAnnual) {
    return { workHoursPerDay: 8, breakMinutes: 60 };
  }
  const monthlySalary = annual / 12;
  let h = Math.floor(monthlySalary / (minHourly * MONTHLY_HOURS_PER_DAY_UNIT));
  h = Math.max(1, Math.min(8, h));
  const breakMinutes = h >= 8 ? 60 : (h >= 4 ? 30 : 0);
  return { workHoursPerDay: h, breakMinutes };
}

function LaborContractModal({ userId, employeeName, defaultSalary, defaultSalaryNet, defaultPosition, defaultDepartment, defaultHireDate, docFilename, onUploadScan, onViewScan, onClose }) {
  const now = new Date();
  // 요청사항: "입사일, 계약일 한줄에 표시 — 입사일, 계약일 모두 입력가능해야함". 계약년/월/일
  // 3칸 입력 대신 입사일과 동일한 형태(단일 date input)로 통일하고, 입사일도 더 이상 읽기전용이
  // 아니라 이 화면에서 바로 입력/수정할 수 있게 한다(회원가입으로 등록되어 인사기록카드에
  // 입사일이 비어있던 사원도 여기서 바로 입력 가능). 계약서를 실제로 생성할 때 입력한 입사일이
  // 다르면 인사기록카드(hr_profiles.hire_date)에도 함께 반영된다(hr.js 참고).
  const [hireDate, setHireDate] = useState(defaultHireDate || '');
  const [contractDate, setContractDate] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  );
  const [salaryAnnual, setSalaryAnnual] = useState(onlyDigits(defaultSalary));
  // 요청사항: "연봉, 급여는 사원등록으로 등록되면 자동으로 입력됨 / 사원등록이 아닌 회원가입으로
  // 등록되면 수기입력, 연봉이 입력되면 급여는 자동으로 계산". 사원등록 시 이미 salary_net이
  // 저장되어 있으면 그 값을 그대로 보여주고, 없으면(회원가입으로만 만들어진 계정) 연봉을
  // 입력/변경할 때마다 서버에서 실시간으로 계산해 채운다(급여관리 "연봉 선택시 자동계산"과
  // 동일한 계산식 — 아래 salary-net-preview 참고). 급여(실수령액)는 항상 자동계산 값이라
  // 화면에서는 읽기전용으로 보여준다.
  const [salaryNet, setSalaryNet] = useState(defaultSalaryNet || '');
  const [calcingNet, setCalcingNet] = useState(false);
  // 부서/직급 모두 자유입력이 아니라 계정관리 > 조직구성에서 등록해둔 목록 중에서 선택한다
  // (조직도의 부서/직급 표기와 계약서 표기가 어긋나지 않도록). 목록은 회원가입 화면과 동일하게
  // 인증 없이 조회 가능한 GET /org/public을 사용한다(이 모달은 hr-management 쓰기권한만
  // 있으면 되는 화면이라 최고관리자 전용인 GET /org는 쓸 수 없음).
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [department, setDepartment] = useState(defaultDepartment || '');
  const [position, setPosition] = useState(defaultPosition || '');
  // 2026-08 추가 — "근로계약(전자)/근로계약(스캔) 통합" 개편: 부서/직급 아래에 1일 근무시간/
  // 휴게시간 입력폼을 추가한다. 근로기준법 기준 기본값(8시간/60분)으로 자동 채워지며, 계약서
  // 4.1/4.2조([근무시간]/[휴게시간])에 그대로 반영된다. 관리자가 값을 바꾸면 바뀐 값이 저장된다.
  const [workHoursPerDay, setWorkHoursPerDay] = useState('8');
  const [breakMinutes, setBreakMinutes] = useState('60');
  const [previewing, setPreviewing] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [requestingSig, setRequestingSig] = useState(false);
  // 2026-08 추가 — "수기계약" 버튼: 서명된 계약서 스캔본을 업로드하면 OCR로 상단 입력폼을
  // 자동으로 채운다(값은 반드시 사람이 확인 후 저장해야 하므로 화면에만 채우고 자동저장하지
  // 않는다). 수기계약은 hr_labor_contracts에 새 행을 만들지 않으므로(=서명요청 대상 자체가
  // 아님) 별도 상태만으로 충분하다.
  const [scanUploading, setScanUploading] = useState(false);
  const [scanOcrMsg, setScanOcrMsg] = useState('');
  const scanInputRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // 요청사항: "미리보기 없이 다운로드 버튼이 있는데 미리보기가 가능하게 수정" — 계약서 미리보기와
  // 계약서 생성 완료 직후 열람 모두 window.open(blob)(=브라우저 PDF뷰어 설정에 따라 곧바로
  // 다운로드로 동작할 수 있음) 대신 FilePreviewModal(모달 안 iframe)로 확실히 미리보기되게 한다.
  const [previewModal, setPreviewModal] = useState(null); // { title, filename?, apiPath? } | null
  // 요청사항: "저장하기를 누르면 미리보기 없이 저장만" — 저장 성공을 알리는 가벼운 인라인
  // 메시지(3초 후 자동 소거)로 대체한다. 다른 화면들의 showMsg 패턴과 동일하게 로컬 state로 구현.
  const [savedMsg, setSavedMsg] = useState('');
  useEffect(() => {
    if (!savedMsg) return;
    const t = setTimeout(() => setSavedMsg(''), 2500);
    return () => clearTimeout(t);
  }, [savedMsg]);

  useEffect(() => {
    API.get('/org/public').then(r => {
      setDepartmentOptions(r.data.departments || []);
      setPositionOptions(r.data.positions || []);
    }).catch(() => {});
  }, []);

  // 연봉이 바뀔 때마다(직접입력이든, 위 초기값이든) 급여(실수령액)를 자동으로 다시 계산한다.
  useEffect(() => {
    const annual = Number(salaryAnnual) || 0;
    if (!annual) { setSalaryNet(''); return; }
    setCalcingNet(true);
    const t = setTimeout(() => {
      API.get(`/hr/employees/${userId}/salary-net-preview`, { params: { annual_salary: annual } })
        .then(r => setSalaryNet(String(r.data.net_pay ?? '')))
        .catch(() => {})
        .finally(() => setCalcingNet(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryAnnual, userId]);

  // 요청사항(3차): "이 상태에서 저장이 가능해야 함" — 예전에는 미리보기를 먼저 눌러
  // 내용을 확인해야만(입력값과 미리보기 시점 스냅샷이 같아야) 저장하기/근로계약서 생성
  // 버튼이 활성화됐다. 미리보기를 거치지 않고도 필수 입력값(입사일/계약일/연봉/부서/직급)만
  // 채워지면 바로 저장할 수 있어야 한다는 요청에 따라, 미리보기 여부와 무관하게 필수값
  // 완성 여부만으로 판단하도록 완화한다. 미리보기 버튼은 그대로 남겨 원하면 저장 전에
  // 내용을 확인할 수 있게 한다.
  const canConfirm = /^\d{4}-\d{2}-\d{2}$/.test(contractDate || '') && !!hireDate.trim()
    && !!salaryAnnual && !!department.trim() && !!position.trim();
  // 서명완료로 폼이 잠겼을 때 입력 필드에 덧씌우는 회색조 스타일(읽기전용 급여(실수령액) 필드와
  // 동일한 톤으로 통일).
  const lockedInputStyle = { background:'#f3f4f6', color:'#888', cursor:'not-allowed' };

  const loadHistory = () => {
    setLoadingHistory(true);
    API.get(`/hr/employees/${userId}/labor-contract`)
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  };
  useEffect(() => { loadHistory(); }, [userId]);

  // 2026-08 추가 — 근로계약서(전자) 출력이력(hr_doc_print_logs, doc_type='labor_contract').
  // 위 발급이력(history)과 달리 이건 순수 열람 목적: 이 계약서가 실제로 언제, 누구에 의해
  // 출력됐는지(근로자 본인이 계정관리에서 출력한 것 포함) 반복해서 쌓이는 로그다. 관리자는
  // 이 화면에서 직접 출력을 트리거하지 않으므로(발급이력에는 이미 요청사항에 따라 보기/출력
  // 버튼이 없다) 여기서는 조회만 한다.
  const [printLogs, setPrintLogs] = useState(null); // null = 로딩중
  useEffect(() => {
    API.get(`/hr/employees/${userId}/doc-print-log`, { params: { docType: 'labor_contract' } })
      .then(r => setPrintLogs(r.data || []))
      .catch(() => setPrintLogs([]));
  }, [userId]);

  // 요청사항: "작성후 저장하기를 누르면 입력된 값이 저장되고 다시 열었을 때 저장된 값이
  // 보이게 구성되어야 함" — 입사일/연봉/부서/직급은 사원 기본정보(defaultXxx prop)를 통해
  // 채워지지만, 그 값들은 이 모달을 다시 열 때 부모 화면(EmployeeCardView)이 최신 정보를
  // 다시 불러왔을 때만 정확하다. 계약일은 애초에 기본값 prop 자체가 없어(매번 오늘 날짜로
  // 초기화) 예전에 입력해둔 계약일이 전혀 반영되지 않았다. 발급이력이 로드되면(최초 진입 시
  // 또는 저장/갱신 직후) 가장 최근 발급건(history[0])의 실제 저장값으로 폼을 다시 채워
  // 항상 "마지막으로 저장된 값"이 보이도록 한다.
  useEffect(() => {
    if (history.length === 0) {
      // 아직 발급된 근로계약서가 없는 사원 — 저장된 이력이 없으므로 현재 연봉/계약연도를
      // 기준으로 1일 근무시간/휴게시간을 자동계산해 채운다("연봉 금액이 해당년도 최저시급기준
      // 연봉금액 미달/이상" 요청사항). 이후 연봉을 직접 수정하면 handleSalaryChange가 다시
      // 계산하고, 관리자가 근무시간/휴게시간을 손으로 고치면 그 값이 그대로 유지된다.
      if (salaryAnnual) {
        const ym = /^(\d{4})-/.exec(contractDate || '');
        const year = ym ? ym[1] : String(now.getFullYear());
        const auto = computeAutoWorkHours(salaryAnnual, year);
        setWorkHoursPerDay(String(auto.workHoursPerDay));
        setBreakMinutes(String(auto.breakMinutes));
      }
      return;
    }
    const h = history[0];
    if (h.contract_year && h.contract_month && h.contract_day) {
      setContractDate(`${h.contract_year}-${String(h.contract_month).padStart(2, '0')}-${String(h.contract_day).padStart(2, '0')}`);
    }
    if (h.department) setDepartment(h.department);
    if (h.position_title) setPosition(h.position_title);
    if (h.salary_annual) setSalaryAnnual(String(h.salary_annual));
    if (h.work_hours_per_day) setWorkHoursPerDay(String(h.work_hours_per_day));
    if (h.break_minutes) setBreakMinutes(String(h.break_minutes));
  }, [history]);

  // 2026-08 추가 — 연봉을 직접 입력/수정할 때마다 그 금액과 계약연도 기준 최저시급을 비교해
  // 1일 근무시간/휴게시간을 자동으로 다시 계산한다("연봉 금액이 해당년도 최저시급기준 연봉금액
  // 미달인 경우 자동 지정, 그 이상인 경우 8시간/60분"). 계산 후에도 필드는 그대로 직접
  // 수정 가능하다 — 관리자가 다시 손으로 고치면 그 값이 저장된다.
  const handleSalaryChange = (rawVal) => {
    const digits = onlyDigits(rawVal);
    setSalaryAnnual(digits);
    const ym = /^(\d{4})-/.exec(contractDate || '');
    const year = ym ? ym[1] : String(now.getFullYear());
    const auto = computeAutoWorkHours(digits, year);
    setWorkHoursPerDay(String(auto.workHoursPerDay));
    setBreakMinutes(String(auto.breakMinutes));
  };

  // 계약일을 바꿔 "해당년도"가 달라지면(예: 연말에 다음해 계약일로 작성) 같은 연봉이라도
  // 최저시급 기준이 달라질 수 있으므로 현재 연봉 기준으로 다시 계산한다.
  const handleContractDateChange = (val) => {
    setContractDate(val);
    if (!salaryAnnual) return;
    const ym = /^(\d{4})-/.exec(val || '');
    const year = ym ? ym[1] : String(now.getFullYear());
    const auto = computeAutoWorkHours(salaryAnnual, year);
    setWorkHoursPerDay(String(auto.workHoursPerDay));
    setBreakMinutes(String(auto.breakMinutes));
  };

  // 계약일(YYYY-MM-DD)을 계약서 백엔드가 기대하는 contractYear/Month/Day 3분할로 변환한다
  // (백엔드 API는 그대로 두고 프론트 입력 방식만 date picker 한 칸으로 통일).
  const splitContractDate = () => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(contractDate || '');
    if (!m) return null;
    return { contractYear: m[1], contractMonth: String(Number(m[2])), contractDay: String(Number(m[3])) };
  };

  const validate = () => {
    if (!splitContractDate()) { alert('계약일을 입력해주세요.'); return false; }
    if (!hireDate.trim()) { alert('입사일을 입력해주세요.'); return false; }
    if (!salaryAnnual) { alert('연봉을 입력해주세요.'); return false; }
    if (!department.trim()) { alert('부서를 선택해주세요.'); return false; }
    if (!position.trim()) { alert('직급을 선택해주세요.'); return false; }
    return true;
  };

  const preview = () => {
    if (!validate()) return;
    setPreviewing(true);
    const { contractYear, contractMonth, contractDay } = splitContractDate();
    API.post(`/hr/employees/${userId}/labor-contract/preview`, {
      contractYear, contractMonth, contractDay, hireDate: hireDate.trim(),
      salaryAnnual: Number(salaryAnnual), department: department.trim(), position: position.trim(),
      workHoursPerDay: workHoursPerDay.trim() || '8', breakMinutes: breakMinutes.trim() || '60',
    })
      .then(r => {
        setPreviewModal({ title: `근로계약서 미리보기 — ${employeeName}`, apiPath: `/hr/labor-contract-preview/${encodeURIComponent(r.data.previewFilename)}` });
        // 2026-08 보강: docx 생성 자체는 성공했지만 PDF 변환(LibreOffice)이 실패한 경우, 서버가
        // pdfWarning으로 원인을 내려준다. 화면에서는 미리보기가 "지원하지 않는 형식"으로만 보여서
        // 왜 그런지 알 수 없었으므로, 모달을 띄운 직후 원인을 그대로 알림으로 보여준다.
        if (r.data.pdfWarning) alert('⚠️ PDF 미리보기를 만들지 못했습니다(문서 자체는 정상 생성됨).\n\n' + r.data.pdfWarning);
      })
      .catch(err => alert(err.response?.data?.message || '미리보기 생성에 실패했습니다.'))
      .finally(() => setPreviewing(false));
  };

  // 요청사항: "저장하기를 누르면 현재 미리보기가 나타나는데 미리보기 없이 저장만" — 지금까지는
  // 저장 성공 시 방금 생성된 파일을 FilePreviewModal로 자동으로 띄웠는데, 이제는 미리보기 모달을
  // 열지 않고 조용히 저장만 한다(저장 여부는 아래 짧은 인라인 메시지로 알려준다). 내용을
  // 확인하고 싶으면 위의 "👁️ 근로계약서 미리보기" 버튼을 별도로 눌러야 한다.
  const issue = () => {
    if (!validate() || !canConfirm) return;
    setIssuing(true);
    const { contractYear, contractMonth, contractDay } = splitContractDate();
    API.post(`/hr/employees/${userId}/labor-contract`, {
      contractYear, contractMonth, contractDay, hireDate: hireDate.trim(),
      salaryAnnual: Number(salaryAnnual), department: department.trim(), position: position.trim(),
      workHoursPerDay: workHoursPerDay.trim() || '8', breakMinutes: breakMinutes.trim() || '60',
    })
      .then(r => {
        setSavedMsg('저장되었습니다.');
        loadHistory();
        if (r.data.pdfWarning) alert('⚠️ PDF 미리보기를 만들지 못했습니다(문서 자체는 정상 생성됨).\n\n' + r.data.pdfWarning);
      })
      .catch(err => alert(err.response?.data?.message || '발급에 실패했습니다.'))
      .finally(() => setIssuing(false));
  };

  // 서명요청 — 방금(또는 가장 최근에) 생성된 계약서에 대해, 계약당사자(근로자)에게 전자서명을
  // 요청한다. 근로자가 마이페이지에서 서명을 완료하면 그 서명파일이 계약서에 합성되어 다시
  // 저장된다(hr.js/mypage.js 참고). 목록 맨 위(history[0])가 가장 최근 생성된 계약서다. 서명요청
  // 즉시 근로자에게 알림이 가고(소켓 + 다음 로그인 시 폴백 — LaborContractSignAlert.js 참고),
  // 계정관리(마이페이지) 헤더에 "근로계약" 버튼이 뜬다.
  const latest = history[0] || null;
  // 요청사항: "서명이 완료되면 작성폼을 수정이 안되게 수정" — 가장 최근 계약서가 이미
  // 서명완료 상태면 입력폼 전체(입사일/계약일/연봉/부서/직급/근무시간/휴게시간)를 잠그고
  // 저장하기/근로계약서 생성/수기계약 버튼도 비활성화한다. 값을 바꾸려면 위 헤더의
  // "🔄 갱신" 버튼을 눌러 새 계약서를 발급해야 하며, 그 순간부터 latest가 서명 전 상태인
  // 새 건으로 바뀌어 자동으로 다시 편집 가능해진다.
  const locked = latest?.signature_status === 'signed';
  const requestSignature = (id) => {
    setRequestingSig(true);
    API.post(`/hr/employees/${userId}/labor-contract/${id}/request-signature`)
      .then(() => loadHistory())
      .catch(err => alert(err.response?.data?.message || '서명요청에 실패했습니다.'))
      .finally(() => setRequestingSig(false));
  };

  // 요청사항: "발급이력은 건별로 삭제가능하게 수정" — 저장된 파일도 함께 삭제된다.
  const [deletingHistoryId, setDeletingHistoryId] = useState(null);
  const deleteHistoryItem = (id) => {
    if (!window.confirm('이 발급이력을 삭제하시겠습니까? 저장된 계약서 파일도 함께 삭제되며 되돌릴 수 없습니다.')) return;
    setDeletingHistoryId(id);
    API.delete(`/hr/employees/${userId}/labor-contract/${id}`)
      .then(() => loadHistory())
      .catch(err => alert(err.response?.data?.message || '삭제에 실패했습니다.'))
      .finally(() => setDeletingHistoryId(null));
  };

  // 요청사항(1차): "근로계약(전자)로 작성된 후 근로계약서 미리보기 오른쪽에 저장하기 버튼 추가".
  // 처음에는 "이미 생성된 파일을 내려받기"로 구현했는데, 그러면 화면에 입력해 둔 연봉/부서/직급
  // 값은 전혀 서버로 전송되지 않고(그냥 예전에 생성된 파일을 다시 다운로드만 함) PDF 파일만
  // 컴퓨터에 저장되어, "연봉/부서/직급을 입력하고 저장하기를 눌렀는데 화면(실제 데이터)에는
  // 반영이 안 되고 PDF만 저장된다"는 버그로 이어졌다.
  // 요청사항(2차): 저장하기는 실제로 지금 입력된 값을 저장(=발급)해야 한다. 아래 "근로계약서
  // 생성" 버튼(issue)과 완전히 동일한 동작으로 연결해, 두 버튼 중 어느 것을 눌러도 현재 입력값
  // (연봉/부서/직급 등)이 그대로 서버에 저장되고 발급이력에 반영되도록 한다. 파일을 다운로드하는
  // 부수효과는 제거했다(요청사항: "pdf 저장은 안해도 됨").
  const saveLatest = issue;

  // 요청사항: "한번 서명된 건에 대해서는 서명요청 버튼이 비활성화 되어야 함" — 예전에는
  // 서명완료 상태를 순수 텍스트(span)로만 보여줘 클릭이 안 됐을 뿐 버튼 모양은 아니었다.
  // 아래 갱신 버튼과 나란히 놓였을 때 상태를 한눈에 알아보기 쉽도록, 실제로 비활성화된
  // <button>으로 명확히 표시한다.
  const sigPill = (h) => {
    if (h.signature_status === 'signed') return (
      <button disabled title="이미 서명이 완료된 계약서입니다. 값을 수정했다면 왼쪽 '갱신' 버튼을 눌러주세요."
        style={{ background:'#fff', color:'#bbb', border:'1px solid #ddd', borderRadius:6,
          padding:'3px 8px', fontSize:11, fontWeight:600, cursor:'not-allowed' }}>
        ✅ 서명완료
      </button>
    );
    if (h.signature_status === 'requested') return <span style={{ fontSize:11, fontWeight:700, color:'#e0a05a' }}>✍️ 서명요청됨</span>;
    return (
      <button onClick={() => requestSignature(h.id)} disabled={requestingSig}
        style={{ background:'#fff', color:'#7c3aed', border:'1px solid #7c3aed', borderRadius:6,
          padding:'3px 8px', fontSize:11, fontWeight:600, cursor:'pointer', opacity: requestingSig ? 0.6 : 1 }}>
        ✍️ 서명요청
      </button>
    );
  };

  // 요청사항: "근로계약 서명이 완료된 건에 대해서는 서명요청 왼쪽에 갱신 버튼이 나타나야 함 /
  // 갱신버튼을 누르면 수정된 값을 이용하여 연봉계약이 갱신되고 다시 서명요청을 해야함" —
  // 갱신은 지금 화면에 입력된 값으로 새 계약서를 발급(issue)하는 것과 동일한 동작이다.
  // 새로 발급되면 발급이력 맨 위(history[0]=latest)가 이 새 건으로 바뀌면서 서명상태가
  // 없는 상태로 시작하므로, 위 sigPill이 자동으로 다시 "✍️ 서명요청" 버튼을 보여준다 —
  // 즉 갱신 후 관리자가 서명요청을 다시 눌러야 하는 흐름이 별도 코드 없이 그대로 만족된다.
  const renewContract = () => {
    if (!window.confirm('현재 입력된 값으로 근로계약을 갱신합니다. 갱신 후에는 근로자에게 서명요청을 다시 보내야 합니다. 계속하시겠습니까?')) return;
    issue();
  };

  // 2026-08 추가 — "수기계약" 버튼: 서명된 계약서 스캔본을 업로드한다. 등본/통장사본과 달리
  // 인식된 값을 hr_profiles에 조용히 저장하지 않고(계약 조건은 반드시 사람이 확인해야 하는
  // 값이라) 화면 입력폼에만 채워 넣는다 — 사용자가 확인 후 "저장하기"/"근로계약서 생성"을
  // 눌러야 실제로 반영된다. 수기계약은 hr_labor_contracts에 행을 만들지 않으므로 서명요청
  // 버튼/워크플로우와 완전히 무관하다(요청사항: "수기계약을 할 경우 서명요청은 필요없음").
  const handleScanFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !onUploadScan) return;
    setScanUploading(true);
    setScanOcrMsg('');
    onUploadScan(file)
      .then((result) => {
        const fields = result && result.ocr && result.ocr.fields;
        if (fields) {
          if (fields.hireDate) setHireDate(fields.hireDate);
          if (fields.salaryAnnual) setSalaryAnnual(onlyDigits(fields.salaryAnnual));
          if (fields.department) setDepartment(fields.department);
          if (fields.position) setPosition(fields.position);
          if (fields.workHoursPerDay) setWorkHoursPerDay(onlyDigits(fields.workHoursPerDay));
          if (fields.breakMinutes) setBreakMinutes(onlyDigits(fields.breakMinutes));
          setScanOcrMsg('OCR로 인식된 값이 자동입력되었습니다. 내용을 확인한 후 저장해주세요.');
        } else {
          setScanOcrMsg('스캔본이 업로드되었습니다. (자동인식된 값은 없습니다 — 직접 확인 후 입력해주세요)');
        }
      })
      .catch(err => alert(err.response?.data?.message || '수기계약 스캔본 업로드에 실패했습니다.'))
      .finally(() => setScanUploading(false));
  };

  // 요청사항: "작성된 근로계약은 상단 닫기버튼 좌측에 서명요청 버튼을 생성" + "근로계약서 생성이
  // 완료되면 우측상단 서명요청 버튼 활성화" — Modal의 headerActions는 항상 제목 옆·닫기(✕) 버튼
  // 바로 왼쪽에 렌더링된다(Modal 컴포넌트 참고). 버튼 자체는 항상 표시하되, 아직 근로계약서를
  // 하나도 생성하지 않은 상태(latest === null)에서는 비활성화(회색)로 보여주고, 생성이 완료되는
  // 즉시(loadHistory로 latest가 채워지는 순간) 눌러서 서명요청할 수 있게 활성화된다.
  const headerActions = (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      {latest?.signature_status === 'signed' && (
        <button onClick={renewContract} disabled={issuing}
          title="현재 입력된 값으로 근로계약을 갱신하고 다시 서명요청할 수 있게 합니다"
          style={{ background:'#fff', color:C.primary, border:`1px solid ${C.primary}`, borderRadius:6,
            padding:'3px 8px', fontSize:11, fontWeight:600, cursor: issuing ? 'default' : 'pointer', opacity: issuing ? 0.6 : 1 }}>
          🔄 갱신
        </button>
      )}
      {latest ? sigPill(latest) : (
        <button disabled title="근로계약서를 먼저 생성해주세요"
          style={{ background:'#fff', color:'#bbb', border:'1px solid #ddd', borderRadius:6,
            padding:'3px 8px', fontSize:11, fontWeight:600, cursor:'not-allowed' }}>
          ✍️ 서명요청
        </button>
      )}
    </div>
  );

  return (
    <Modal title={`📝 근로계약서 작성 — ${employeeName}`} onClose={onClose} width={620} headerActions={headerActions}>
      {/* 요청사항: 입사일 + 계약일을 한 줄(둘 다 입력가능), 연봉 + 급여(실수령액)를 한 줄
          (급여는 항상 자동계산), 부서 + 직급을 한 줄로 배치. 입사일은 계약서 본문
          "1.[계약기간]" 조항의 시작일(년/월/일)로 그대로 반영되고, 계약서 생성 시 인사기록카드
          (hr_profiles.hire_date)에도 함께 반영된다. 부서/직급을 선택해 계약서를 생성하면
          사원정보(users.department/position)에 그대로 반영되어 조직도에도 자동으로 나타나고,
          연봉은 급여관리에 이번 달 급여행으로 자동 등록된다(공제금액은 인사기록카드 부양가족
          기준 자동계산 — hr.js 참고). */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
        <Field label="입사일">
          <input type="date" style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={hireDate}
            disabled={locked} onChange={e => setHireDate(e.target.value)} />
        </Field>
        <Field label="계약일">
          <input type="date" style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={contractDate}
            disabled={locked} onChange={e => handleContractDateChange(e.target.value)} />
        </Field>
        <Field label="연봉">
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <input style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={formatComma(salaryAnnual)}
              disabled={locked} onChange={e => handleSalaryChange(e.target.value)} />
            <span style={{ fontSize:12, color:'#666', whiteSpace:'nowrap' }}>원</span>
          </div>
        </Field>
        <Field label="급여(실수령액)">
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <input style={{ ...input, width:'100%', background:'#f3f4f6', color:'#666' }}
              value={calcingNet ? '계산 중...' : formatComma(salaryNet)} disabled readOnly />
            <span style={{ fontSize:12, color:'#666', whiteSpace:'nowrap' }}>원</span>
          </div>
        </Field>
        <Field label="부서">
          <select style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={department}
            disabled={locked} onChange={e => setDepartment(e.target.value)}>
            <option value="">부서 선택</option>
            {/* defaultDepartment(사원 기본정보)뿐 아니라 발급이력에서 다시 채워진 현재 department
                값도 목록에 없으면(조직구성에서 나중에 삭제된 경우 등) 선택값이 사라지지 않도록
                끼워 넣는다. */}
            {department && !departmentOptions.includes(department) && (
              <option value={department}>{department}</option>
            )}
            {departmentOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="직급">
          <select style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={position}
            disabled={locked} onChange={e => setPosition(e.target.value)}>
            <option value="">직급 선택</option>
            {/* 현재 선택된 직급이 조직구성 목록에 없는 값이어도(예: 목록에서 나중에 삭제된 경우)
                선택값이 사라지지 않도록 목록에 없으면 맨 위에 추가로 끼워 넣는다. */}
            {position && !positionOptions.includes(position) && (
              <option value={position}>{position}</option>
            )}
            {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        {/* 2026-08 추가 — "부서, 직급 아래쪽에 1일 근무시간, 휴게시간 입력폼 추가": 근로기준법
            기준 기본값(8시간/60분)으로 자동 채워지며, 계약서 4.1/4.2조에 그대로 반영된다. */}
        <Field label="1일 근무시간">
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <input style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={workHoursPerDay}
              disabled={locked} onChange={e => setWorkHoursPerDay(onlyDigits(e.target.value))} />
            <span style={{ fontSize:12, color:'#666', whiteSpace:'nowrap' }}>시간</span>
          </div>
        </Field>
        <Field label="휴게시간">
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <input style={{ ...input, width:'100%', ...(locked ? lockedInputStyle : {}) }} value={breakMinutes}
              disabled={locked} onChange={e => setBreakMinutes(onlyDigits(e.target.value))} />
            <span style={{ fontSize:12, color:'#666', whiteSpace:'nowrap' }}>분</span>
          </div>
        </Field>
      </div>
      {/* 요청사항: "서명이 완료되면 작성폼을 수정이 안되게 수정" — 잠긴 상태에서는 위 안내
          대신 이 문구를 보여줘 왜 입력이 안 되는지, 어떻게 풀 수 있는지 알려준다. */}
      {locked ? (
        <div style={{ fontSize:11, color:'#0F6E56', marginTop:4, fontWeight:600 }}>
          ✅ 서명이 완료된 계약서입니다. 값을 수정하려면 위 "🔄 갱신" 버튼을 눌러주세요.
        </div>
      ) : (
        <div style={{ fontSize:11, color:'#999', marginTop:4 }}>
          연봉이 계약연도 최저시급 기준(1일 8시간) 연봉에 미달하면 근로기준법에 따라 1일 근무시간과
          휴게시간이 자동으로 단축 적용되고, 그 이상이면 기본값(1일 8시간, 휴게 60분)이 적용됩니다.
          필요 시 직접 수정할 수 있습니다.
        </div>
      )}

      {/* 요청사항(3차): "미리보기와 저장하기 버튼은 가로기준 50%씩 크기를 조절" — 두 버튼을
          flex:1로 동일 비율(50/50)로 배분한다. */}
      <div style={{ display:'flex', gap:8, marginTop:10 }}>
        <button onClick={preview} disabled={previewing}
          style={{ ...btn('#fff'), color:C.primary, border:`1px solid ${C.primary}`, flex:1, minWidth:0,
            padding:'10px 0', whiteSpace:'nowrap', opacity: previewing ? 0.6 : 1 }}>
          {previewing ? '미리보기 생성 중...' : '👁️ 근로계약서 미리보기'}
        </button>
        <button onClick={saveLatest} disabled={issuing || !canConfirm || locked}
          title={locked ? '서명이 완료되어 잠긴 계약서입니다. 갱신 버튼을 눌러주세요.' : canConfirm ? '입력한 내용을 근로계약서로 저장(발급)합니다' : '입사일/계약일/연봉/부서/직급을 모두 입력해주세요'}
          style={{ ...btn('#fff'), color: (canConfirm && !locked) ? '#0F6E56' : '#bbb', border:`1px solid ${(canConfirm && !locked) ? '#0F6E56' : '#ddd'}`,
            flex:1, minWidth:0, padding:'10px 0', whiteSpace:'nowrap', opacity: (issuing || !canConfirm || locked) ? 0.6 : 1,
            cursor: (issuing || !canConfirm || locked) ? 'not-allowed' : 'pointer' }}>
          {issuing ? '저장 중...' : '💾 저장하기'}
        </button>
      </div>
      {/* 요청사항: "저장하기를 누르면 미리보기 없이 저장만" — 저장 성공 시 잠깐 보였다 사라지는
          인라인 확인 메시지를 버튼 바로 아래에 표시한다. */}
      {savedMsg && (
        <div style={{ fontSize:12, color:'#0F6E56', fontWeight:600, marginTop:6 }}>
          ✅ {savedMsg}
        </div>
      )}
      {!canConfirm && !locked && (
        <div style={{ fontSize:11, color:'#e05a5a', marginTop:6 }}>
          입사일/계약일/연봉/부서/직급을 모두 입력해야 "저장하기"/"근로계약서 생성" 버튼이 활성화됩니다.
        </div>
      )}
      {/* 2026-08 추가 — "근로계약서 생성 우측에 수기계약 버튼을 추가": 서명된 계약서 스캔본을
          업로드하는 경로. 수기계약은 서명요청 워크플로우 대상이 아니며, 업로드 시 OCR로
          인식된 값이 있으면 위 입력폼에 자동으로 채워진다(저장은 별도로 눌러야 함). */}
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button onClick={issue} disabled={issuing || !canConfirm || locked}
          title={locked ? '서명이 완료되어 잠긴 계약서입니다. 갱신 버튼을 눌러주세요.' : undefined}
          style={{ ...btn(C.primary), flex:1, minWidth:0, padding:'10px 0',
            opacity: (issuing || !canConfirm || locked) ? 0.45 : 1, cursor: (issuing || !canConfirm || locked) ? 'not-allowed' : 'pointer' }}>
          {issuing ? '생성 중...' : '📝 근로계약서 생성'}
        </button>
        <button onClick={() => scanInputRef.current && scanInputRef.current.click()} disabled={scanUploading || locked}
          title={locked ? '서명이 완료되어 잠긴 계약서입니다. 갱신 버튼을 눌러주세요.' : undefined}
          style={{ ...btn('#fff'), color:'#7c3aed', border:'1px solid #7c3aed', flex:1, minWidth:0,
            padding:'10px 0', whiteSpace:'nowrap', opacity: (scanUploading || locked) ? 0.6 : 1,
            cursor: (scanUploading || locked) ? 'not-allowed' : 'pointer' }}>
          {scanUploading ? '업로드 중...' : '🖊️ 수기계약'}
        </button>
        <input ref={scanInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
          onChange={handleScanFileChange} />
      </div>
      {scanOcrMsg && (
        <div style={{ fontSize:12, color:'#7c3aed', fontWeight:600, marginTop:6 }}>ℹ️ {scanOcrMsg}</div>
      )}
      {docFilename && (
        <div style={{ fontSize:12, color:'#666', marginTop:6 }}>
          업로드된 수기계약 스캔본이 있습니다.{' '}
          <button type="button" onClick={onViewScan}
            style={{ background:'none', border:'none', color:C.primary, fontWeight:700, cursor:'pointer', padding:0, fontSize:12, textDecoration:'underline' }}>
            보기
          </button>
        </div>
      )}

      <div style={{ marginTop:20, paddingTop:16, borderTop:'1px dashed #e5e7eb' }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>발급이력</div>
        {loadingHistory ? (
          <div style={{ fontSize:12, color:'#aaa' }}>불러오는 중...</div>
        ) : history.length === 0 ? (
          <div style={{ fontSize:12, color:'#aaa' }}>발급 이력이 없습니다.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:220, overflowY:'auto' }}>
            {/* 요청사항: "발급이력에는 서명요청 버튼과 보기버튼 삭제" — 서명요청은 상단 헤더의
                버튼 하나로 일원화했고(가장 최근 계약서 기준), 문서 열람은 생성 직후 자동으로
                뜨는 미리보기 모달로 대체했으므로 목록의 각 행은 이제 순수 상태표시 전용이다.
                요청사항(2차): "발급대기중은 표시하지 않음" — 근로자가 아직 마이페이지에서
                열람/출력하지 않아 status가 'pending'인 건은 "[발급대기중]" 라벨을 보여주지 않고
                나머지 정보(계약일/부서/직급/서명상태)만 그대로 보여준다(행 자체를 숨기지는
                않는다 — 서명요청/서명완료 여부는 여전히 확인할 수 있어야 하므로).
                요청사항(2차): "이력 건별로 삭제가능하게 수정" — 각 행 우측에 삭제 버튼을 추가한다. */}
            {history.map(h => (
              <div key={h.id} style={{ padding:'6px 10px', background:'#f8fafc', border:'1px solid #eee',
                borderRadius:6, fontSize:12, display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                <div>
                  {h.status === 'issued' && <b style={{ color:C.primary }}>[출력완료 {h.printed_at}] </b>}
                  {h.contract_year}년 {h.contract_month}월 {h.contract_day}일
                  {/* 요청사항: "부서, 직급, 이름 순서로 모두 표현, 생성자는 생략" — 예전에는 직급과
                      "생성: 관리자이름"만 보여줬는데, 계약당사자 기준으로 부서/직급/이름을 모두
                      보여주고 계약서를 실제로 만든 관리자 표기는 뺀다. */}
                  {` · ${h.department || '-'} · ${h.position_title || '-'} · ${employeeName}`}
                  {h.signature_status === 'signed' && <span style={{ color:'#059669', marginLeft:6, fontWeight:700 }}>· ✅ 서명완료</span>}
                  {h.signature_status === 'requested' && <span style={{ color:'#e0a05a', marginLeft:6, fontWeight:700 }}>· ✍️ 서명요청됨</span>}
                </div>
                <button type="button" onClick={() => deleteHistoryItem(h.id)} disabled={deletingHistoryId === h.id}
                  title="이 발급이력 삭제"
                  style={{ background:'none', border:'none', color:'#dc2626', cursor: deletingHistoryId === h.id ? 'default' : 'pointer',
                    fontSize:13, padding:'2px 4px', flexShrink:0, opacity: deletingHistoryId === h.id ? 0.5 : 1 }}>
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop:16, paddingTop:12, borderTop:'1px dashed #e5e7eb' }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>🖨️ 출력이력</div>
        {printLogs === null ? (
          <div style={{ fontSize:12, color:'#aaa' }}>불러오는 중...</div>
        ) : printLogs.length === 0 ? (
          <div style={{ fontSize:12, color:'#aaa' }}>출력된 기록이 없습니다.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
            {printLogs.map(log => (
              <div key={log.id} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#777',
                padding:'6px 10px', background:'#fafafa', border:'1px solid #f0f0f0', borderRadius:6 }}>
                <span>{log.printed_at}</span>
                <span>{log.printed_by || '-'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {previewModal && (
        // 요청사항: "근로계약서/근로서약서/개인정보동의서 보기 화면 가로폭 870px" — FilePreviewModal의
        // 기본값도 이미 870으로 맞춰져 있어 굳이 지정하지 않아도 되지만, 값이 바뀌더라도 이 화면만은
        // 항상 870을 유지하도록 명시적으로 지정해둔다.
        <FilePreviewModal title={previewModal.title} filename={previewModal.filename} apiPath={previewModal.apiPath}
          width={870} onClose={() => setPreviewModal(null)} />
      )}
    </Modal>
  );
}

// 2026-08 추가 — 인사기록카드의 근로서약서/개인정보동의서 버튼을 누르면 곧바로 미리보기(또는
// "아직 생성된 문서가 없습니다" 알림)를 띄우던 것을, 이 두 문서의 "서명 및 출력이력" 목록을
// 먼저 보여주는 방식으로 바꿨다. GET /hr/employees/:userId/esign-docs는 이미 해당 사원의
// hr_esign_docs 전체 이력(대부분 doc_type당 1건이지만, 여러 건이 쌓일 수도 있음)을 id DESC로
// 내려주므로 그대로 doc_type만 걸러 목록으로 뿌린다. 각 행 우측에 출력하기(파일을 받아 숨은
// iframe으로 인쇄창을 바로 띄움 — MyPage.js printHrFile과 동일한 방식)/보기(FilePreviewModal)
// 버튼을 둔다. 문서가 아직 생성되지 않았으면(이력 자체가 없으면) 안내 문구만 보여준다.
function EsignAdminHistoryModal({ userId, docType, employeeName, onClose }) {
  const label = docType === 'pledge' ? '근로서약서' : '개인정보동의서';
  const [rows, setRows] = useState(null); // null = 로딩중
  const [preview, setPreview] = useState(null); // { title, filename } | null
  // 2026-08 추가 — 근로서약서/개인정보동의서 출력이력(hr_doc_print_logs). 관리자가 아래에서
  // 🖨️ 출력하기를 누를 때마다(또는 근로자가 계정관리에서 출력할 때마다) 한 줄씩 쌓인다.
  const [printLogs, setPrintLogs] = useState(null); // null = 로딩중

  useEffect(() => {
    API.get(`/hr/employees/${userId}/esign-docs`)
      .then(r => setRows((r.data || []).filter(row => row.doc_type === docType)))
      .catch(() => setRows([]));
  }, [userId, docType]);

  const loadPrintLogs = () => {
    API.get(`/hr/employees/${userId}/doc-print-log`, { params: { docType } })
      .then(r => setPrintLogs(r.data || []))
      .catch(() => setPrintLogs([]));
  };
  useEffect(loadPrintLogs, [userId, docType]);

  // MyPage.js의 printHrFile(근로자용 출력하기)과 동일한 방식 — blob을 받아 화면 밖 숨은
  // iframe에 띄운 뒤 그 iframe의 인쇄창을 바로 연다. 관리자가 여기서 출력할 때마다
  // doc-print-log에도 한 줄 남기고(반영 즉시 보이도록 이력 목록을 다시 불러온다).
  const printFile = (row) => {
    API.post(`/hr/employees/${userId}/doc-print-log`, { docType, refId: row.id, filename: row.filename })
      .then(loadPrintLogs).catch(() => {});
    API.get(`/hr/file/${encodeURIComponent(row.filename)}`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
        iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
        iframe.src = url;
        iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
        document.body.appendChild(iframe);
        setTimeout(() => { try { iframe.remove(); } catch (e) {} try { URL.revokeObjectURL(url); } catch (e) {} }, 60000);
      })
      .catch(() => alert('출력할 파일을 불러오지 못했습니다.'));
  };

  return (
    <>
      <Modal title={`📜 ${label} — 서명 및 출력이력`} onClose={onClose} width={620}>
        {rows === null ? (
          <div style={{ padding:30, textAlign:'center', color:'#aaa', fontSize:13 }}>불러오는 중...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding:30, textAlign:'center', color:'#aaa', fontSize:13 }}>
            아직 생성된 이력이 없습니다.<br />근로계약(전자) 발급 또는 근로계약(스캔) 업로드 시 자동으로 생성됩니다.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:420, overflowY:'auto' }}>
            {rows.map(row => {
              const signed = row.status === '서명완료';
              return (
                <div key={row.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  gap:10, padding:'10px 12px', background:'#f8fafc', border:'1px solid #eee', borderRadius:8 }}>
                  <div style={{ fontSize:12, color:'#555', lineHeight:1.6 }}>
                    <div>
                      {signed
                        ? <b style={{ color:'#059669' }}>✅ 서명완료</b>
                        : <b style={{ color:'#e0a05a' }}>✍️ 서명대기</b>}
                      <span style={{ marginLeft:8, color:'#888' }}>{employeeName}</span>
                    </div>
                    <div style={{ marginTop:2, color:'#888' }}>
                      요청일시: {row.requested_at || '-'}{row.requested_by ? ` · ${row.requested_by}` : ''}
                      {signed && row.signed_at ? ` · 서명일시: ${row.signed_at}` : ''}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                    <button type="button" disabled={!row.filename} onClick={() => printFile(row)}
                      style={{ background:'#fff', color: row.filename ? C.primary : '#ccc',
                        border:`1px solid ${row.filename ? C.primary : '#ddd'}`, borderRadius:6, padding:'5px 10px',
                        fontSize:12, fontWeight:600, cursor: row.filename ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
                      🖨️ 출력하기
                    </button>
                    <button type="button" disabled={!row.filename} onClick={() => setPreview({ title: label, filename: row.filename })}
                      style={{ background:'#fff', color: row.filename ? '#555' : '#ccc',
                        border:`1px solid ${row.filename ? '#ccc' : '#ddd'}`, borderRadius:6, padding:'5px 10px',
                        fontSize:12, fontWeight:600, cursor: row.filename ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
                      👁️ 보기
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 2026-08 추가 — 서명 이력과 별개로, 실제 이 문서가 몇 번 언제 누구에 의해 출력됐는지의
            반복 기록. 서명 이력(위)은 doc당 최대 1건(재발급시 갱신)인 반면, 이 목록은 출력할
            때마다 새 줄이 쌓인다. */}
        <div style={{ marginTop:16, paddingTop:12, borderTop:'1px solid #eee' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#555', marginBottom:8 }}>🖨️ 출력이력</div>
          {printLogs === null ? (
            <div style={{ padding:'10px 0', textAlign:'center', color:'#aaa', fontSize:12 }}>불러오는 중...</div>
          ) : printLogs.length === 0 ? (
            <div style={{ padding:'10px 0', textAlign:'center', color:'#aaa', fontSize:12 }}>출력된 기록이 없습니다.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
              {printLogs.map(log => (
                <div key={log.id} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#777',
                  padding:'6px 10px', background:'#fafafa', border:'1px solid #f0f0f0', borderRadius:6 }}>
                  <span>{log.printed_at}</span>
                  <span>{log.printed_by || '-'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
      {preview && (
        <FilePreviewModal title={preview.title} filename={preview.filename} width={870} onClose={() => setPreview(null)} />
      )}
    </>
  );
}

function EmployeeCardView({ userId, onClose, onEdit, onDelete, deleteDisabled }) {
  const [data, setData] = useState(null);
  const [viewDocType, setViewDocType] = useState(null); // 'labor_contract' | 'pledge' | 'privacy_consent' | 'resident_reg' | 'bankbook' | null
  const [requesting, setRequesting] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [showLaborContractModal, setShowLaborContractModal] = useState(false);
  const [scanPrintHistory, setScanPrintHistory] = useState(null);
  // 요청사항: "전자서명후에는 근로계약 버튼 색상이 컬러로 변환되어야 함" — hr_profiles.
  // contract_e_signed 컬럼은 예전 준비중 단계에서 만들어둔 자리표시자로 실제로는 항상 0으로
  // 남아있어(근로계약서(전자) 기능이 나중에 hr_labor_contracts 테이블 기반으로 실제 구현되면서
  // 이 컬럼을 갱신하는 코드가 없다) 신뢰할 수 없다. 대신 LaborContractModal이 쓰는 것과 동일한
  // 발급이력 API로 가장 최근 발급 건의 signature_status를 직접 조회해 판단한다.
  const [laborContractSigned, setLaborContractSigned] = useState(false);
  const loadLaborContractSignStatus = () => {
    API.get(`/hr/employees/${userId}/labor-contract`)
      .then(r => setLaborContractSigned((r.data || [])[0]?.signature_status === 'signed'))
      .catch(() => {});
  };
  // 요청사항: "근로서약서/개인정보동의서 전자서명 완료시 색상을 컬러로 변경" — 이 두 문서는
  // hr_profiles.doc_pledge/doc_privacy_consent(단순 첨부파일 슬롯)가 아니라 hr_esign_docs에서
  // 자동생성/서명완료되므로, 근로계약과 동일하게 별도 상태 API(GET .../esign-docs)로 doc_type별
  // 최신 1건(서명대기/서명완료, filename)을 조회해 색상과 미리보기 대상 파일을 함께 판단한다.
  const [esignDocs, setEsignDocs] = useState({}); // { pledge: {status, filename, ...}, privacy_consent: {...} }
  // 2026-08 변경 — "근로서약서/개인정보동의서 버튼을 누르면 서명 및 출력이력이 먼저 나오고
  // 우측에 출력하기/보기 버튼 생성": 버튼을 누르면 바로 미리보기(또는 미생성 알림)를 띄우던
  // 것을 EsignAdminHistoryModal(이력 목록 모달)을 여는 것으로 바꿨다. 실제 미리보기는 그
  // 모달 안에서 각 이력 행의 "보기" 버튼을 눌러야 뜬다.
  const [esignHistoryDocType, setEsignHistoryDocType] = useState(null); // 'pledge' | 'privacy_consent' | null
  const loadEsignDocsStatus = () => {
    API.get(`/hr/employees/${userId}/esign-docs`)
      .then(r => {
        const map = {};
        (r.data || []).forEach(row => { if (!map[row.doc_type]) map[row.doc_type] = row; }); // id DESC → 첫 등장이 최신
        setEsignDocs(map);
      })
      .catch(() => {});
  };
  const loadFullData = () => API.get(`/hr/employees/${userId}/full`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { loadFullData(); }, [userId]);
  useEffect(loadLaborContractSignStatus, [userId]);
  useEffect(loadEsignDocsStatus, [userId]);
  // 요청사항: "출력기록은 인사관리-직원현황-인사기록카드-근로계약(전자) 또는 (스캔)에서
  // 출력이력을 보여줌" — 근로계약(스캔) 문서 보기 모달을 열 때만 출력이력을 불러온다.
  useEffect(() => {
    if (viewDocType !== 'labor_contract') { setScanPrintHistory(null); return; }
    API.get(`/hr/employees/${userId}/labor-contract-scan-history`)
      .then(r => setScanPrintHistory(r.data || []))
      .catch(() => setScanPrintHistory([]));
  }, [viewDocType, userId]);
  const canWriteDocs = !!onEdit;
  // 등본/통장사본을 업로드하면 서버가 즉시 OCR로 인식해 인사기록카드의 빈 필드만 자동으로
  // 채운다(이미 입력되어 있던 값은 절대 덮어쓰지 않는다). 무언가 반영됐으면 전체 정보를 다시
  // 불러와 화면에 바로 보이게 하고, 무엇이 채워졌는지 간단히 안내한다.
  const uploadDoc = (docType) => (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/hr/employees/${userId}/document/${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => {
        setData(d => ({ ...d, [`doc_${docType}`]: r.data.filename }));
        const ocr = r.data.ocr;
        if (ocr && ocr.applied && ((ocr.appliedFields && ocr.appliedFields.length) || ocr.addedFamilyCount)) {
          API.get(`/hr/employees/${userId}/full`).then(rf => setData(rf.data)).catch(() => {});
          const parts = [];
          if (ocr.appliedFields && ocr.appliedFields.length) parts.push(`${ocr.appliedFields.length}개 항목`);
          if (ocr.addedFamilyCount) parts.push(`가족 ${ocr.addedFamilyCount}명`);
          alert(`OCR 인식 결과가 자동으로 반영되었습니다 (${parts.join(', ')}). 내용을 확인해주세요.`);
        }
      });
  };
  const deleteDoc = (docType) => () =>
    API.delete(`/hr/employees/${userId}/document/${docType}`)
      .then(() => { setData(d => ({ ...d, [`doc_${docType}`]: null })); setViewDocType(null); });

  // 2026-08 추가 — "근로계약(전자)/근로계약(스캔) 통합" 개편: LaborContractModal 안의
  // "수기계약" 버튼에서 쓰는 전용 업로드 함수. 공용 uploadDoc()과 달리 서버가 돌려준
  // {filename, ocr} 전체를 그대로 반환해(uploadDoc의 .then 체인은 반환값이 없다) 모달이 OCR로
  // 인식된 값을 화면 입력폼에 자동으로 채울 수 있게 한다.
  const uploadScanContract = (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/hr/employees/${userId}/document/labor_contract`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => {
        setData(d => ({ ...d, doc_labor_contract: r.data.filename }));
        return r.data;
      });
  };

  // 2026-08-2 변경: 근로서약서/개인정보동의서의 "⚡생성" 버튼(관리자가 직접 클릭해서
  // 등록된 서명으로 즉시 생성)은 삭제했다. 이제 두 문서는 근로계약(전자) 생성 또는
  // 근로계약(스캔) 업로드 시점에 자동으로 만들어지고, 근로자가 계정관리(마이페이지)에서
  // 직접 전자서명해야 완성된다(MyPage.js 근로서약서/개인정보동의서 버튼 참고). 이 화면의
  // 문서버튼은 순수 열람/업로드 슬롯으로만 남는다.

  // 2026-08 문서버튼 5종 → 7종 확장 — 근로서약서/개인정보동의서를 근로계약서(스캔)와 동일한
  // 단순 업로드 방식으로 추가. 객체 삽입 순서가 곧 화면에 뿌려지는 순서이므로(Object.keys),
  // 요청받은 최종 배치 순서(전자/스캔/서약서/동의서/등본/급여계좌/재직증명서)에 맞춰 이 5개를
  // 정렬해두면 전자작성(별도 하드코딩, 맨 앞)·재직증명서(별도 하드코딩, 맨 뒤)와 합쳐 그대로
  // 요청 순서가 완성된다.
  // 2026-08 변경 — "근로계약(전자), 근로계약(스캔) -> 근로계약 으로 통합": 두 버튼을 하나의
  // "근로계약" 버튼으로 합쳤다. 스캔 업로드/열람은 이제 LaborContractModal 안의 "수기계약"
  // 버튼/링크로 이동했으므로, labor_contract는 더 이상 이 목록(=문서버튼 자동 렌더링 대상)에
  // 넣지 않는다. 다만 DOC_FIELD와 DocViewerModal(뷰어)은 그대로 재사용해야 하므로 라벨만 따로
  // 상수로 남겨둔다(LABOR_CONTRACT_SCAN_LABEL).
  const LABOR_CONTRACT_SCAN_LABEL = '근로계약(스캔)';
  const DOC_LABELS = {
    pledge: '근로서약서', privacy_consent: '개인정보동의서',
    resident_reg: '주민등록등본', bankbook: '급여계좌',
  };
  const DOC_FIELD = {
    labor_contract: 'doc_labor_contract', pledge: 'doc_pledge', privacy_consent: 'doc_privacy_consent',
    resident_reg: 'doc_resident_reg', bankbook: 'doc_bankbook',
  };

  // "근로계약(전자)" — backend/templates/LaborContract_Form.docx 템플릿에 사원정보(이름/생년월일/
  // 주소)와 연봉, 관리자가 직접 입력한 계약년/월/일을 채우고 등록된 서명 PNG를 합성해 PDF로
  // 발급한다(LaborContractModal).
  const openElectronicContract = () => setShowLaborContractModal(true);

  // "작성요청" — 이 사원(마이페이지 사용자)에게 추가정보(주민등록등본/통장사본 등) 작성을
  // 요청한다. 요청받은 직원은 로그인 시(또는 이미 로그인 중이면 실시간으로) 알림모달을 보고
  // "작성하기"를 누르면 계정관리 > 추가정보 모달이 바로 열린다(HrInfoRequestAlert.js 참고).
  const requestInfo = () => {
    if (!window.confirm(`${data.name}님에게 추가정보 작성요청을 보내시겠습니까?`)) return;
    setRequesting(true);
    API.post(`/hr/employees/${userId}/request-info`)
      .then(() => alert('작성요청을 보냈습니다.'))
      .catch(err => alert(err.response?.data?.message || '요청 전송에 실패했습니다.'))
      .finally(() => setRequesting(false));
  };

  // 문서 버튼 7종(전자/스캔/근로서약서/개인정보동의서/등본/급여계좌/재직증명서)은 헤더가 아니라
  // 제목 바로 아래(본문 상단)에 별도 줄로 배치한다.
  // 문서버튼 7종을 가로 기준으로 가득 채우도록 각 버튼을 flex:1로 균등 배분한다.
  const fillBtnStyle = { background:'#fff', color:'#999', border:'1px solid #ddd', borderRadius:8,
    padding:'5px 2px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
    width:'100%', boxSizing:'border-box' };
  const docButtonsRow = data && (
    <div style={{ display:'flex', gap:5, marginBottom:16 }}>
      <div style={{ flex:1, minWidth:0 }}>
        {/* 요청사항: "전자서명후에는 근로계약 버튼 색상이 컬러로 변환되어야 함" — 근로자가
            전자서명을 마친 발급건이 있으면 다른 문서버튼들의 "등록됨" 색상(#0F6E56)과 동일하게
            표시하고, 아직이면 기존처럼 회색으로 둔다. */}
        <button type="button" onClick={openElectronicContract}
          style={laborContractSigned
            ? { ...fillBtnStyle, color:'#0F6E56', border:'1px solid #0F6E56' }
            : fillBtnStyle}>
          📝 근로계약
        </button>
      </div>
      {['pledge', 'privacy_consent'].map(docType => {
        // 요청사항 2/3: "전자서명 완료시 컬러로 변경" + "누르면 파일찾기 대신 미리보기로 바로 표시".
        // 근로서약서/개인정보동의서는 근로계약(전자/스캔) 생성 시 자동으로 hr_esign_docs에
        // 만들어지고 근로자가 계정관리에서 서명을 완료하므로, 여기서는 등본/통장사본처럼
        // 관리자가 직접 업로드하는 슬롯(DocButton)이 아니라 상태표시 + 미리보기 전용 버튼으로
        // 렌더링한다(등본/통장사본은 아래에서 계속 기존 DocButton을 사용).
        const row = esignDocs[docType];
        const signed = row?.status === '서명완료';
        return (
          <div key={docType} style={{ flex:1, minWidth:0 }}>
            <button type="button"
              onClick={() => setEsignHistoryDocType(docType)}
              style={signed ? { ...fillBtnStyle, color:'#0F6E56', border:'1px solid #0F6E56' } : fillBtnStyle}>
              {signed ? '📄 ' : '📎 '}{DOC_LABELS[docType]}
            </button>
          </div>
        );
      })}
      {['resident_reg', 'bankbook'].map(docType => (
        <div key={docType} style={{ flex:1, minWidth:0 }}>
          <DocButton label={DOC_LABELS[docType]} filename={data[DOC_FIELD[docType]]} canWrite={canWriteDocs}
            onUpload={uploadDoc(docType)} onView={() => setViewDocType(docType)} fill />
        </div>
      ))}
      <div style={{ flex:1, minWidth:0 }}>
        <button type="button" onClick={() => setShowCertModal(true)}
          style={{ ...fillBtnStyle, color:'#0F6E56', border:'1px solid #0F6E56' }}>
          📃 재직증명서
        </button>
      </div>
    </div>
  );

  const headerActions = (onEdit || onDelete) && (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
      {onEdit && (
        <button onClick={onEdit}
          style={{ background:'#fff', color:C.primary, border:`1px solid ${C.primary}`, borderRadius:8,
            padding:'5px 10px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
          ✏️ 수정
        </button>
      )}
      {onEdit && data && (
        <button onClick={requestInfo} disabled={requesting}
          style={{ background:'#fff', color:'#b45309', border:'1px solid #b45309', borderRadius:8,
            padding:'5px 10px', fontSize:12, fontWeight:600, cursor: requesting ? 'default' : 'pointer', whiteSpace:'nowrap' }}>
          📨 작성요청
        </button>
      )}
      {onDelete && (
        <button onClick={onDelete} disabled={deleteDisabled}
          style={{ background:'#fff', color: deleteDisabled ? '#ccc' : '#dc2626', border:`1px solid ${deleteDisabled ? '#ddd' : '#dc2626'}`,
            borderRadius:8, padding:'5px 10px', fontSize:12, fontWeight:600, cursor: deleteDisabled ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
          🗑️ 삭제
        </button>
      )}
    </div>
  );
  if (!data) return (
    <Modal title="📇 인사기록카드" onClose={onClose} width={860}>
      <div style={{ padding:40, textAlign:'center', color:'#aaa' }}>불러오는 중...</div>
    </Modal>
  );
  return (
    <>
    <Modal title={`📇 인사기록카드 — ${data.name}${data.employee_no ? ` (사번: ${data.employee_no})` : ''}`} onClose={onClose} width={860} headerActions={headerActions} headerPad="18px 22px 15px">
      {docButtonsRow}
      <div style={{ display:'flex', gap:18 }}>
        <div style={{ width:130, height:177, border:'1px solid #ddd', borderRadius:6, background:'#f8fafc', flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', fontSize:11, color:'#aaa' }}>
          {data.photo ? <AuthedPhoto filename={data.photo} alt="증명사진" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '사진 없음'}
        </div>
        <div style={{ flex:1, display:'grid', gridTemplateColumns:'90px 1fr 90px 1fr', gap:0 }}>
          <CardRow label="성명(한글)" value={data.name} />
          <CardRow label="성명(한자)" value={data.name_hanja} />
          <CardRow label="주민등록번호" value={data.resident_no} />
          <CardRow label="생년월일" value={data.birth_date ? `${data.birth_date}${data.gender ? ` (${data.gender})` : ''}` : ''} />
          <CardRow label="입사일자" value={data.hire_date} />
          <CardRow label="전화번호" value={data.phone} />
          <CardRow label="휴대전화" value={data.mobile_phone} />
          <CardRow label="E-mail" value={data.email} />
          <CardRow label="주소" value={data.address} span={3} />
        </div>
      </div>

      <CardSectionTitle>가족사항</CardSectionTitle>
      <CardTable columns={FAMILY_COLUMNS} rows={data.family} headerFontSize={12} />

      <CardSectionTitle>건강사항</CardSectionTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:0 }}>
        <CardRow label="취미" value={data.hobby} />
        <CardRow label="특기" value={data.specialty} />
        <CardRow label="종교" value={data.religion} />
        <CardRow label="혈액형" value={data.blood_type ? `${data.blood_type}형` : ''} />
        <CardRow label="신장" value={data.height ? `${data.height}cm` : ''} />
        <CardRow label="체중" value={data.weight ? `${data.weight}kg` : ''} />
        <CardRow label="시력(좌/우)" value={(data.eyesight_left || data.eyesight_right) ? `${data.eyesight_left || '-'} / ${data.eyesight_right || '-'}` : ''} />
        <CardRow label="건강정도" value={data.health_level} />
        <CardRow label="질병" value={data.disease} span={3} />
      </div>

      <CardSectionTitle>병역</CardSectionTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:0 }}>
        <CardRow label="병역구분" value={data.military_status} />
        <CardRow label="면제사유" value={data.military_exempt_reason} />
        <CardRow label="군별" value={data.military_branch} />
        <CardRow label="병과" value={data.military_category} />
      </div>

      <CardSectionTitle>학력사항</CardSectionTitle>
      <CardTable columns={EDUCATION_COLUMNS} rows={data.education_history} />

      <CardSectionTitle>경력사항</CardSectionTitle>
      <CardTable columns={CAREER_COLUMNS} rows={data.career_history} />

      <CardSectionTitle>자격사항</CardSectionTitle>
      <CardTable columns={QUALIFICATION_COLUMNS} rows={data.qualifications} />
    </Modal>

    {viewDocType && data[DOC_FIELD[viewDocType]] && (
      <DocViewerModal
        label={viewDocType === 'labor_contract' ? LABOR_CONTRACT_SCAN_LABEL : DOC_LABELS[viewDocType]}
        filename={data[DOC_FIELD[viewDocType]]} canWrite={canWriteDocs}
        onClose={() => setViewDocType(null)}
        onReplace={viewDocType === 'labor_contract' ? uploadScanContract : uploadDoc(viewDocType)}
        onDelete={deleteDoc(viewDocType)}
        printHistory={viewDocType === 'labor_contract' ? scanPrintHistory : null} />
    )}

    {showCertModal && (
      <CertificateModal userId={userId} employeeName={data.name} onClose={() => setShowCertModal(false)} />
    )}

    {showLaborContractModal && (
      <LaborContractModal userId={userId} employeeName={data.name} defaultSalary={data.salary_annual}
        defaultSalaryNet={data.salary_net}
        defaultPosition={data.position} defaultDepartment={data.department} defaultHireDate={data.hire_date}
        docFilename={data.doc_labor_contract} onUploadScan={uploadScanContract}
        onViewScan={() => setViewDocType('labor_contract')}
        onClose={() => { setShowLaborContractModal(false); loadLaborContractSignStatus(); loadFullData(); loadEsignDocsStatus(); }} />
    )}

    {esignHistoryDocType && (
      // 근로서약서/개인정보동의서 — "서명 및 출력이력" 목록 모달. 목록의 각 행에서 출력하기/보기를
      // 누르면 그 안에서 처리되며(자체 FilePreviewModal 포함), 여기서는 열고 닫기만 관리한다.
      <EsignAdminHistoryModal userId={userId} docType={esignHistoryDocType} employeeName={data.name}
        onClose={() => setEsignHistoryDocType(null)} />
    )}

    </>
  );
}

// 2026-08: 계정관리(마이페이지)의 회원 리스트에서도 회원구분 변경에 재사용하기 위해 export한다.
export const MEMBER_TYPE_OPTIONS = [
  { value: 'staff',      label: '사내임직원' },
  { value: 'partner',    label: '협력사' },
  { value: 'individual', label: '개인관리' },
];
export const toMemberType = (u) => {
  if (u?.member_type === 'partner') return 'partner';
  if (u?.member_type === 'individual') return 'individual';
  return 'staff';
};

// 사원정보 목록 컬럼 → 정렬 기준 필드 매핑(''은 정렬 불가한 작업/버튼 열)
const EMP_SORT_KEYS = ['created_at','employee_no','department','position','name','phone','emergency_phone','employment_type','employment_status','hire_date','contract_end_date', null, null];

function EmployeesTab({ employees, reload, perm, user, showMsg, headerActionsEl, tabBarActionsEl, openMenuOptions }) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all'); // 10 / 30 / 50 / 100 / 'all'(기본값)
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // employee row
  const [form, setForm] = useState({});
  const [registering, setRegistering] = useState(false);
  const [regForm, setRegForm] = useState({});
  // 2026-09 등본/통장사본 OCR 신뢰도 UI(명함/업체관리와 동일한 패턴) — 사원등록 폼에서만 쓴다.
  // (상세보기-수정의 문서업로드 자동반영 흐름은 별도 라우트/응답을 쓰고 화면에 반영하지 않으므로 대상 아님.)
  const [residentRegConfidence, setResidentRegConfidence] = useState(null);
  const [bankbookConfidence, setBankbookConfidence] = useState(null);
  const [cardViewId, setCardViewId] = useState(null); // 인사기록카드 상세보기 대상 사원 id
  const canWrite = perm.hasDetail('hr-management', 'employee_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.employees)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.employees);
  // "⚙️ 옵션"(탭 메뉴 사용여부 on/off) 버튼은 더 이상 employee_write가 아니라 전용 세부항목
  // 'option_write'로 제어한다. 사원정보 등록/수정 권한과는 별개 개념이다.
  const canManageOptions = perm.hasDetail('hr-management', 'option_write');

  // ── 계정관리(구)에서 이관된 기능: 승인대기 / 회원구분·부서·직급 변경 / 권한설정 ──
  // 최고관리자에게만 보이며, 실제 API 권한체계(백엔드 isSuperAdmin 게이트)는 그대로입니다.
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const [pendingUsers, setPendingUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  // 2026-08 추가 — "접두어를 두종류로 나눔: 회사약어(회사 전체 1개)+부서약어(부서별)" 요청사항.
  // 사번 미리보기(regEmpNoPreview)는 회사약어 + 부서약어(departments의 empno_prefix) + 순번
  // 순서로 조합해야 하므로, loadOrg()가 받아온 회사약어(GET /org 응답의 empnoCompanyPrefix)를
  // 별도 상태로 들고 있는다.
  const [companyEmpnoPrefix, setCompanyEmpnoPrefix] = useState('');
  const [memberTypeDraft, setMemberTypeDraft] = useState('staff');
  const [deptDraft, setDeptDraft] = useState('');
  const [posDraft, setPosDraft] = useState('');
  const [acctUser, setAcctUser] = useState(null); // 계정관리(회원구분/부서/직급) 모달 대상 사원
  // 2026-08: 계정관리 모달 안의 "🔐 권한설정" 버튼을 삭제하고 그 자리에 비밀번호 초기화
  // 버튼을 옮겼다 — 권한설정은 이미 목록의 별도 "권한설정" 버튼으로 접근 가능하므로 중복이었고,
  // 비밀번호 초기화는 계정관리 화면에서 바로 처리하는 편이 더 자연스럽다는 요청에 따른 변경.
  const [acctResetting, setAcctResetting] = useState(false);
  const [permMenus, setPermMenus] = useState([]);
  const [permRows, setPermRows] = useState([]);
  const [permDetailRows, setPermDetailRows] = useState([]);
  const [permUser, setPermUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [detailMenuKey, setDetailMenuKey] = useState(null);
  const [detailLiveOverride, setDetailLiveOverride] = useState(null);

  const loadPending = () => API.get('/admin/users').then(r => setPendingUsers(r.data.filter(u => u.approved === 0 && u.account_type !== 'display_only'))).catch(() => {});
  const loadOrg = () => API.get('/org').then(r => {
    setDepartments(r.data.departments || []);
    setPositions(r.data.positions || []);
    setCompanyEmpnoPrefix(r.data.empnoCompanyPrefix || '');
  }).catch(() => {});
  const loadPerms = () => API.get('/permissions').then(r => {
    setPermMenus(r.data.menus || []); setPermRows(r.data.permissions || []); setPermDetailRows(r.data.details || []);
  }).catch(() => {});
  useEffect(() => { if (isSuperAdmin) { loadPending(); loadOrg(); loadPerms(); } }, [isSuperAdmin]);

  const approvePending = (id) => API.patch(`/admin/users/${id}/approve`)
    .then(() => { showMsg('승인 완료!'); loadPending(); reload(search); })
    .catch(err => alert(err.response?.data?.message || '승인 처리 중 오류가 발생했습니다.'));
  const rejectPending = (id) => API.patch(`/admin/users/${id}/reject`)
    .then(() => { showMsg('승인 취소되었습니다.'); loadPending(); })
    .catch(err => alert(err.response?.data?.message || '처리 중 오류가 발생했습니다.'));

  // 계정관리(회원구분/부서/직급) 모달 — 대상은 editing(사원정보 수정)이 아니라 별도의 acctUser로
  // 관리한다(목록의 "계정관리" 버튼에서 직접 연다).
  const openAcctManage = (e) => {
    setAcctUser(e);
    setMemberTypeDraft(toMemberType(e));
    setDeptDraft(e.department || '');
    setPosDraft(e.position || '');
  };
  const changeMemberType = () => API.patch(`/admin/users/${acctUser.id}/member-type`, { memberType: memberTypeDraft })
    .then(() => { showMsg('회원구분이 변경되었습니다.'); reload(search); })
    .catch(err => alert(err.response?.data?.message || '변경 실패'));
  const changeDepartmentAcct = () => API.patch(`/admin/users/${acctUser.id}/department`, { department: deptDraft })
    .then(() => { showMsg('부서가 변경되었습니다.'); reload(search); })
    .catch(err => alert(err.response?.data?.message || '변경 실패'));
  const changePositionAcct = () => API.patch(`/admin/users/${acctUser.id}/position`, { position: posDraft })
    .then(() => { showMsg('직급이 변경되었습니다.'); reload(search); })
    .catch(err => alert(err.response?.data?.message || '변경 실패'));
  // 계정관리 모달의 비밀번호 초기화 — 원래 PermissionModal 하단에 있던 기능을 그대로 이식한 것으로,
  // 확인창 없이 클릭 즉시 고정 비밀번호(1q2w3e4r)로 초기화한다.
  const resetAcctPassword = () => {
    setAcctResetting(true);
    API.patch(`/admin/users/${acctUser.id}/reset-password`)
      .then(() => alert('비밀번호가 1q2w3e4r 로 초기화되었습니다.'))
      .catch(err => alert(err.response?.data?.message || '비밀번호 초기화에 실패했습니다.'))
      .finally(() => setAcctResetting(false));
  };

  // 2026-08: 승인대기 계정은 승인되기 전까지 이 목록(직원현황)에 나오면 안 된다는 요청에 따라,
  // 더 이상 pendingUsers를 목록에 합치지 않는다. 승인대기 계정은 화면 상단 "⏳ 승인 대기" 섹션에서만
  // 확인/승인/거부하고, 승인이 끝나야 비로소 employees(백엔드에서 이미 approved=1만 내려줌)에 포함되어
  // 이 목록에 나타난다.
  const { sorted: sortedEmployees, sortKey, sortDir, toggleSort } = useSort(employees, 'created_at', 'desc');
  useEffect(() => { setPage(1); }, [employees, pendingUsers, pageSize]);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedEmployees.length / pageSize));
  const pagedList  = pageSize === 'all' ? sortedEmployees : sortedEmployees.slice((page - 1) * pageSize, page * pageSize);

  const printList = () => printListPopup({
    title: '사원정보', menuLabel: '인사관리(사원정보)',
    columns: EMP_LIST_COLUMNS.slice(0, -1),
    rows: sortedEmployees.map(e => [
      e.created_at ? e.created_at.slice(0, 10) : '-', e.employee_no || '-', e.department || '-', e.position || '-', e.name,
      e.phone || '-', e.emergency_phone || '-', e.employment_type || '-', e.approved === 0 ? '승인대기' : (e.employment_status || '재직'),
      e.hire_date || '-', e.employment_type === '계약직' ? (e.contract_end_date || '-') : '-',
      empDocStatus(e).text,
    ]),
  });
  const downloadList = () => downloadListCsv('/hr/employees/export' + (search ? `?search=${encodeURIComponent(search)}` : ''), 'IMS_사원정보.csv', '인사관리(사원정보)');

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      // 요청사항: "채용구분과 사번 위에 부서, 직급 추가" — 이 화면에서 부서/직급도 함께 수정할
      // 수 있도록 users 값(e.department/e.position)으로 seed한다. 저장은 save()가 form 전체를
      // 그대로 PUT /hr/employees/:id로 보내고, 백엔드가 department/position 변경분을 users
      // 테이블에도 반영한다(hr.js 참고).
      department: e.department || '', position: e.position || '',
      employee_no: e.employee_no || '', hire_date: e.hire_date || '', resign_date: e.resign_date || '',
      employment_type: e.employment_type || '정규직',
      contract_end_date: e.contract_end_date || '',
      birth_date: e.birth_date || '',
      bank_name: e.bank_name || '',
      bank_holder: e.bank_holder || '',
      bank_account: e.bank_account || '', notes: e.notes || '',
      photo: e.photo || '', gender: e.gender || '', mobile_phone: e.mobile_phone || '',
      family: [], qualifications: [], latest_payroll: null,
    });
    // 인사기록카드 확장정보(신상/건강/병역 + 가족·자격)와 급여관리 연동 정보(latest_payroll)는
    // 목록 응답에 없으므로 상세 열람 시 별도 조회. 학력/경력 입력은 삭제되었으므로 더이상 불러오지 않는다
    // (기존에 등록된 값이 있어도 이 폼에서는 건드리지 않아 저장 시 자동으로 보존됨).
    API.get(`/hr/employees/${e.id}/full`).then(r => {
      const d = r.data;
      setForm(f => ({
        ...f,
        name_hanja: d.name_hanja || '', gender: d.gender || '', resident_no: d.resident_no || '', address: d.address || '',
        mobile_phone: d.mobile_phone || '', photo: d.photo || '',
        hobby: d.hobby || '', specialty: d.specialty || '', religion: d.religion || '',
        height: d.height || '', weight: d.weight || '', blood_type: d.blood_type || '',
        eyesight_left: d.eyesight_left || '', eyesight_right: d.eyesight_right || '',
        health_level: d.health_level || '', disease: d.disease || '',
        military_status: d.military_status || '', military_exempt_reason: d.military_exempt_reason || '',
        military_branch: d.military_branch || '', military_category: d.military_category || '',
        family: d.family || [], qualifications: d.qualifications || [],
        latest_payroll: d.latest_payroll || null,
        // 2026-08-29 추가 — 대표이사/임원 고용보험 적용제외 플래그(위 employment_insurance
        // 체크박스). p.*(hr_profiles 전체 컬럼)에 포함되어 내려온다.
        employment_insurance_exempt: Number(d.employment_insurance_exempt) === 1 ? 1 : 0,
      }));
    }).catch(() => {});
  };

  const save = () => {
    const { latest_payroll, ...rest } = form;
    const payload = {
      ...rest,
      // 재직상태는 더이상 직접 선택하지 않고 퇴사일 입력 여부로 자동 계산해서 저장한다.
      employment_status: form.resign_date ? '퇴직' : '재직',
      contract_end_date: form.employment_type === '계약직' ? form.contract_end_date : '',
    };
    API.put(`/hr/employees/${editing.id}`, payload)
      .then(() => { showMsg('사원정보가 저장되었습니다.'); setEditing(null); reload(search); })
      .catch(err => alert(err.response?.data?.message || '저장 실패'));
  };

  // 상세보기(인사기록카드) 화면 우측 상단 수정/삭제 — 수정은 기존 상세/수정 모달을 그대로 열고,
  // 삭제는 계정 자체를 삭제하는 최고관리자 전용 API(/admin/users/:id, superadmin 대상은 백엔드에서 차단)를 사용한다.
  const deleteEmployee = (e) => {
    if (!window.confirm(`${e.name} 사원을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    API.delete(`/admin/users/${e.id}`)
      .then(() => { showMsg('삭제되었습니다.'); setCardViewId(null); reload(search); })
      .catch(err => alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.'));
  };

  const openRegister = () => {
    setRegForm({
      // 버그수정: 예전에는 여기서 employee_no에 'EMP0001' 형태의 임시 채번값을 직접 넣어뒀는데,
      // 이 값이 buildEmpNoCandidates의 currentValue로 전달되면서(목록에 없는 값은 맨 앞에
      // 추가되는 로직 때문에) 실제로는 할당된 적 없는 "EMP0005" 같은 값이 드롭다운 맨 위에
      // 기본 선택되어 있는 것처럼 보이는 문제가 있었다. 사번은 부서 선택 후 EmployeeHrFields가
      // 계산하는 후보 목록에서 사용자가 직접 골라야 하므로 초기값은 빈 문자열로 둔다.
      username:'', password:'', name:'', department:'', position:'', phone:'', emergency_phone:'', email:'',
      employee_no: '', employment_type:'정규직',
      hire_date:'', resign_date:'', contract_end_date:'', salary_net:'', salary_annual:'',
      birth_date:'', bank_name:'', bank_holder:'', bank_account:'', notes:'',
      // 인사기록카드 확장필드
      name_hanja:'', gender:'', resident_no:'', address:'', mobile_phone:'', photo:'',
      hobby:'', specialty:'', religion:'', height:'', weight:'', blood_type:'',
      eyesight_left:'', eyesight_right:'', health_level:'', disease:'',
      military_status:'', military_exempt_reason:'', military_branch:'', military_category:'',
      family: [], qualifications: [],
    });
    setRegistering(true);
  };

  // 사번 미리보기 — 선택된 부서에 조직도 > "🔢 사번설정"에서 등록해둔 규칙(부서약어+자릿수)이
  // 있으면 화면에 다음 사번을 미리 보여준다. departments는 loadOrg()가 GET /org(SELECT * FROM
  // departments)로 이미 불러와둔 상태라 별도 API 호출 없이 그 자리에서 계산한다. 실제 값은
  // 등록 시점에 서버(assignEmployeeNo)가 최종 확정하므로 이 값은 어디까지나 안내용이다.
  // 2026-08 추가 — "접두어를 두종류로 나눔: 회사약어(회사 전체 1개)+부서약어(부서별)".
  // 최종 사번 = 회사약어(companyEmpnoPrefix) + 부서약어(dept.empno_prefix) + 순번.
  // 2026-08 추가 — "부서약어 뒤에 입사년도 뒤 두자리 자동반영". 사원등록 폼에 입력된 입사일
  // (regForm.hire_date)의 연도 뒤 2자리를 순번 앞에 넣는다. 입사일을 아직 입력하지 않았다면
  // 등록 시점의 현재 연도로 대체되므로(백엔드 assignEmployeeNo와 동일 규칙) 미리보기도 같은
  // 방식으로 계산한다.
  // 요청사항: "급여(실수령액) : 자동계산" — 계약연봉(regForm.salary_annual)이 바뀔 때마다
  // 근로계약(전자) 발급 폼과 동일한 계산식(4대보험+소득세, backend/utils/payrollDeductions.js)으로
  // 급여(실수령액)을 다시 계산해 채운다. 근로계약(전자) 화면은 이미 저장된 사원(userId)을 기준으로
  // hr_family_members에서 부양가족 수를 조회하는 라우트를 쓰지만, 사원등록 시점에는 아직 사원이
  // 생성되기 전이라 그 라우트를 쓸 수 없다 — 대신 아직 저장 전인 폼의 가족사항 입력값
  // (regForm.family, HrCardFields에서 입력)에서 "등록(부양가족)" 체크된 행 수를 세어 이 화면
  // 전용의 사번 미필요 버전 라우트(GET /hr/employees/salary-net-preview)에 dependent_count로
  // 실어 보낸다. 자동계산 값이라 입력칸은 읽기전용으로 둔다.
  const [calcingSalaryNet, setCalcingSalaryNet] = useState(false);
  useEffect(() => {
    const annual = Number(regForm.salary_annual) || 0;
    if (!annual) { setRegForm(f => (f.salary_net ? { ...f, salary_net: '' } : f)); return; }
    const dependentCount = 1 + (regForm.family || []).filter(m => m.is_dependent).length;
    setCalcingSalaryNet(true);
    const t = setTimeout(() => {
      API.get('/hr/employees/salary-net-preview', { params: { annual_salary: annual, dependent_count: dependentCount } })
        .then(r => setRegForm(f => ({ ...f, salary_net: String(r.data.net_pay ?? '') })))
        .catch(() => {})
        .finally(() => setCalcingSalaryNet(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regForm.salary_annual, regForm.family]);

  const submitRegister = () => {
    if (!regForm.username.trim() || !regForm.password.trim() || !regForm.name.trim()) {
      alert('아이디, 비밀번호, 이름은 필수입니다.'); return;
    }
    if (regForm.password.length < 6) { alert('비밀번호는 6자 이상이어야 합니다.'); return; }
    // 요청사항: "사번: 미할당된 사번으로 20개까지 선택할 수 있도록 수정" — 이제 사번은 자동으로
    // 조용히 채번되지 않고 드롭다운에서 직접 골라야 하므로(부서에 사번설정 규칙이 있는 경우),
    // 선택하지 않고 등록을 누르면 안내 후 막는다.
    const selDept = departments.find(d => d.name === regForm.department);
    if (selDept && selDept.empno_digits != null && !regForm.employee_no) {
      alert('사번을 선택해주세요.'); return;
    }
    const payload = {
      ...regForm,
      // 재직상태는 더이상 직접 선택하지 않고 퇴사일 입력 여부로 자동 계산해서 저장한다.
      employment_status: regForm.resign_date ? '퇴직' : '재직',
      salary_net: onlyDigits(regForm.salary_net),
      salary_annual: onlyDigits(regForm.salary_annual),
      contract_end_date: regForm.employment_type === '계약직' ? regForm.contract_end_date : '',
    };
    API.post('/hr/employees', payload)
      .then(() => { showMsg('사원이 등록되었습니다.'); setRegistering(false); reload(search); })
      .catch(err => alert(err.response?.data?.message || '등록 실패'));
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {isSuperAdmin && pendingUsers.length > 0 && (
        <Section title={`⏳ 승인 대기 (${pendingUsers.length}명)`}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {pendingUsers.map(u => (
              <div key={u.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                background:'#fffbeb', border:'1px solid #fef3c7', borderRadius:10, padding:'12px 16px', gap:10, flexWrap:'wrap' }}>
                <div style={{ fontSize:13 }}>
                  <span style={{ fontWeight:600 }}>{u.position && `${u.position} `}{u.name}</span>
                  <span style={{ color:'#888', margin:'0 6px' }}>@{u.username}</span>
                  <span style={{ color:'#aaa' }}>{u.department || '부서 미지정'} · {u.created_at?.slice(0, 10)}</span>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => approvePending(u.id)} style={{ ...btn(C.primary), padding:'6px 12px', fontSize:12 }}>✅ 승인</button>
                  <button onClick={() => rejectPending(u.id)} style={{ ...btn('#dc2626'), padding:'6px 12px', fontSize:12 }}>❌ 거부</button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

    <HeaderPortal target={headerActionsEl}>
      <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
      <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
      {canManageOptions && <button onClick={openMenuOptions} style={btn('#64748B')}>⚙️ 옵션</button>}
      {canWrite && <button onClick={openRegister} style={btn(C.primary)}>+ 사원등록</button>}
    </HeaderPortal>

    <HeaderPortal target={tabBarActionsEl}>
      <input value={search} onChange={e => setSearch(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && reload(search)}
        placeholder="이름/부서/직급/사번 검색" style={{ ...input, width:200 }} />
      <button onClick={() => reload(search)} style={btn('#f0f0f0', '#555')}>검색</button>
      <select value={pageSize} onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
        style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555',
          outline:'none', cursor:'pointer', background:'#fff' }}>
        <option value="10">10개씩</option>
        <option value="30">30개씩</option>
        <option value="50">50개씩</option>
        <option value="100">100개씩</option>
        <option value="all">모두보기</option>
      </select>
    </HeaderPortal>

    <Section>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            {EMP_LIST_COLUMNS.map((h,i) => (
              <SortTh key={i} label={h} sortKey={EMP_SORT_KEYS[i]} currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            ))}
          </tr></thead>
          <tbody>
            {pagedList.length === 0 && <EmptyRow span={EMP_LIST_COLUMNS.length} />}
            {pagedList.map(e => (
              <tr key={e.id}>
                <td style={td}>{e.created_at ? e.created_at.slice(0, 10) : '-'}</td>
                <td style={td}>{e.employee_no || '-'}</td>
                <td style={td}>{e.department || '-'}</td>
                <td style={td}>{e.position || '-'}</td>
                <td style={{ ...td, fontWeight:600 }}>{e.name}</td>
                <td style={td}>{e.phone || '-'}</td>
                <td style={td}>{e.emergency_phone || '-'}</td>
                <td style={td}>{e.employment_type || '-'}</td>
                <td style={td}>
                  {e.approved === 0 ? (
                    <span style={{ padding:'3px 10px', borderRadius:6, fontSize:13, fontWeight:700, background:'#fef3c7', color:'#b45309' }}>
                      승인대기
                    </span>
                  ) : (
                    <span style={{ padding:'3px 10px', borderRadius:6, fontSize:13, fontWeight:700,
                      background: e.employment_status === '퇴직' ? '#fee2e2' : e.employment_status === '휴직' ? '#fef3c7' : '#dcfce7',
                      color: e.employment_status === '퇴직' ? '#dc2626' : e.employment_status === '휴직' ? '#b45309' : '#16a34a' }}>
                      {e.employment_status || '재직'}
                    </span>
                  )}
                </td>
                <td style={td}>{e.hire_date || '-'}</td>
                <td style={td}>{e.employment_type === '계약직' ? (e.contract_end_date || '-') : '-'}</td>
                {/* 요청사항: "서류등록 완료를 계약/서약/동의/등본/통장 이렇게 표현하고 완료되면
                    색상만 변경" — 5개 라벨을 항상 보여주고, 완료된 것만 초록색으로 강조한다. */}
                <td style={{ ...td, textAlign:'center' }}>
                  <div style={{ display:'flex', justifyContent:'center', flexWrap:'wrap', gap:4 }}>
                    {EMP_DOC5.map(d => {
                      const done = d.done(e);
                      return (
                        <span key={d.key} style={{ padding:'3px 8px', borderRadius:6, fontSize:12, fontWeight:700,
                          background: done ? '#dcfce7' : '#f1f5f9', color: done ? '#16a34a' : '#94a3b8',
                          whiteSpace:'nowrap' }}>
                          {d.label}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td style={td}>
                  <button onClick={() => setCardViewId(e.id)}
                    style={{ background:'#fff', color:'#0F6E56', border:'1px solid #0F6E56', borderRadius:8,
                      padding:'5px 10px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                    상세보기
                  </button>
                </td>
                {/* 2026-08: 목록의 권한설정/계정관리 버튼 열을 삭제했다 — 두 기능 모두
                    계정관리(AdminUsers.js) 화면의 회원 리스트에서 처리하도록 이관되어
                    직원현황 목록에서는 더 이상 노출하지 않는다. */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 (모두보기가 아닐 때만) */}
      {pageSize !== 'all' && totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:14 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === 1 ? '#ccc' : '#555', fontSize:14, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>◀</button>
          <span style={{ fontSize:14, color:'#666' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === totalPages ? '#ccc' : '#555', fontSize:14, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>▶</button>
        </div>
      )}

      {editing && (
        // 요청사항: "저장버튼을 우측 상단 닫기 버튼 옆으로 이동" — 예전에는 폼 맨 아래에 전체폭
        // 버튼으로 있었는데, Modal이 이미 지원하는 headerActions 슬롯(닫기 "✕" 버튼 바로 왼쪽에
        // 렌더링됨)에 저장 버튼을 넣어 헤더에서 바로 저장할 수 있게 한다.
        <Modal title={`👤 ${editing.name} 사원정보`} onClose={() => setEditing(null)} width={820}
          headerActions={(
            // 2026-08-29 이동 — "고용보험 적용제외" 체크박스를 필드 그리드에서 헤더(제목
            // 오른쪽, 저장 버튼 왼쪽)로 옮겼다. 대표이사(임원) 여부에 직결되는 항목이라 폼을
            // 스크롤하지 않아도 바로 보이게 하기 위함(요청사항).
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'#555' }}>
                <input type="checkbox" checked={Number(form.employment_insurance_exempt) === 1} disabled={!canWrite}
                  onChange={e => setForm(f => ({ ...f, employment_insurance_exempt: e.target.checked ? 1 : 0 }))} />
                고용보험 적용제외(임원)
              </label>
              {canWrite && <button onClick={save} style={{ ...btn(C.primary), padding:'6px 16px', fontSize:13 }}>저장</button>}
            </div>
          )}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
            {/* 요청사항: "채용구분과 사번 위에 부서, 직급 추가" — 조직도에 등록된 부서/직급
                목록에서 드롭다운으로 선택한다(사원등록 폼의 부서/직급 select와 동일한 방식).
                저장 시 save()가 form 전체를 그대로 PUT으로 보내고, 백엔드가 department/position
                변경분을 users 테이블에도 반영한다(hr.js 참고) — 예전에는 이 값을 바꾸려면 별도의
                "계정관리" 모달의 부서변경/직급변경 버튼을 따로 써야 했다. */}
            <Field label="부서">
              <select style={{ ...input, width:'100%' }} value={form.department || ''} disabled={!canWrite}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
                <option value="">부서 선택</option>
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="직급">
              <select style={{ ...input, width:'100%' }} value={form.position || ''} disabled={!canWrite}
                onChange={e => {
                  const newPosition = e.target.value;
                  // ⚠️ 2026-08-29 추가 — "대표이사는 고용보험 적용제외" 버그 수정. 직급을 대표이사로
                  // 바꾸면 아래 체크박스를 자동으로 켠다(관리자가 직접 해제할 수도 있다 — 다른
                  // 직급으로 바뀌었을 때는 자동으로 끄지 않는다, 다른 임원도 수동으로 켤 수 있어야
                  // 하므로 "대표이사일 때만 자동 on"으로 제한한다).
                  setForm(f => ({ ...f, position: newPosition,
                    employment_insurance_exempt: newPosition === '대표이사' ? 1 : f.employment_insurance_exempt }));
                }}>
                <option value="">직급 선택</option>
                {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </Field>
            {/* ⚠️ 2026-08-29 추가 — 대표이사(임원)는 고용보험 적용제외 대상인데 예전에는 이 구분이
                없어 전 직원에게 고용보험을 동일하게 부과했다(실제 급여대장 대조로 발견된 버그).
                직급을 대표이사로 선택하면 위 헤더의 체크박스가 자동으로 켜지고, 다른 임원도
                필요하면 헤더에서 직접 체크할 수 있다(2026-08-29 재이동 — 요청사항: 제목 오른쪽
                위치로 이동, 위 headerActions 참고). */}
            {/* 요청사항: "입력칸 간격을 좌우 동일하게 수정" — 채용구분/사번을 예전처럼 한 div
                안에 40%/flex:1 비율로 묶지 않고, 이 모달의 기본 2열 그리드(1fr 1fr)를 그대로
                따르는 별개의 Field 두 개로 분리한다(= 좌우 폭이 항상 정확히 동일해짐).
                요청사항: "채용구분, 사번 분리해서 표시 (사번 : 드롭다운으로 선택)" — 사번은
                더 이상 자유 입력이 아니라, 현재 소속 부서의 사번설정 규칙(회사약어+부서약어+
                입사년도+순번)에 따라 다음에 올 수 있는 번호를 순차적으로 자동계산해 드롭다운
                후보로 보여준다(buildEmpNoCandidates). 부서에 아직 규칙이 없으면 예전처럼 자유
                입력(input)으로 남겨둔다.
                2026-08 재변경 — "사번: 사원등록으로 사번이 설정된 경우 표시 / 회원가입으로
                등록된 경우(사번 미할당) 미할당된 사번 10개 드롭다운으로 선택". 회원가입 시
                자동으로 만들어지는 직원현황 행은 사번 채번 로직을 타지 않아 employee_no가
                비어있다(routes/auth.js 참고). 이 화면에서 그런 직원을 다시 열었을 때만 사번을
                고를 수 있게 하고, 사원등록에서 이미 정식으로 배정받은 사번은(=값이 있으면)
                실수로 바뀌지 않도록 읽기전용으로 그대로 보여준다. */}
            <Field label="채용구분">
              <select style={{ ...input, width:'100%' }} value={form.employment_type || '정규직'} disabled={!canWrite}
                onChange={e => setForm(f => ({ ...f, employment_type: e.target.value }))}>
                {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="사번">
              {form.employee_no ? (
                <input style={{ ...input, width:'100%', background:'#f8fafc', color:'#555' }}
                  value={form.employee_no} disabled readOnly />
              ) : (() => {
                {/* 버그수정: 부서를 이 화면에서 새로 선택할 수 있게 되면서, 사번 후보도 원래
                    저장돼있던 editing.department가 아니라 지금 폼에서 선택 중인 form.department
                    기준으로 계산해야 부서를 바꾼 직후 바로 정확한 사번 후보가 보인다. */}
                const dept = departments.find(d => d.name === form.department);
                const candidates = buildEmpNoCandidates(dept, companyEmpnoPrefix, form.hire_date, form.employee_no, 10);
                return candidates ? (
                  <select style={{ ...input, width:'100%' }} value={form.employee_no || ''} disabled={!canWrite}
                    onChange={e => setForm(f => ({ ...f, employee_no: e.target.value }))}>
                    {!form.employee_no && <option value="">사번 선택</option>}
                    {candidates.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <input style={{ ...input, width:'100%', background:'#f8fafc', color:'#aaa' }}
                    value="자동부여 – 추후 재정의" disabled readOnly />
                );
              })()}
            </Field>

            <div style={{ gridColumn:'1 / -1' }}>
              <Field label={form.employment_type === '계약직' ? '입사일 / 퇴사일 / 계약만료일' : '입사일 / 퇴사일'}>
                <div style={{ display:'flex', gap:6 }}>
                  <input type="date" style={{ ...input, flex:1 }} value={form.hire_date || ''} disabled={!canWrite}
                    onChange={e => setForm(f => ({ ...f, hire_date: e.target.value }))} />
                  <input type="date" style={{ ...input, flex:1 }} value={form.resign_date || ''} disabled={!canWrite}
                    onChange={e => setForm(f => ({ ...f, resign_date: e.target.value }))} />
                  {form.employment_type === '계약직' && (
                    <input type="date" style={{ ...input, flex:1 }} value={form.contract_end_date || ''} disabled={!canWrite}
                      onChange={e => setForm(f => ({ ...f, contract_end_date: e.target.value }))} />
                  )}
                </div>
              </Field>
            </div>

            {/* 요청사항: "생년월일 -> 삭제" */}

            <div style={{ gridColumn:'1 / -1' }}>
              <Field label="급여통장 (은행 / 예금주 / 계좌)">
                <div style={{ display:'flex', gap:6 }}>
                  <input placeholder="은행" style={{ ...input, width:'25%' }} value={form.bank_name} disabled={!canWrite}
                    onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} />
                  <input placeholder="예금주" style={{ ...input, width:'25%' }} value={form.bank_holder || ''} disabled={!canWrite}
                    onChange={e => setForm(f => ({ ...f, bank_holder: e.target.value }))} />
                  <input placeholder="계좌번호" style={{ ...input, flex:1 }} value={form.bank_account} disabled={!canWrite}
                    onChange={e => setForm(f => ({ ...f, bank_account: e.target.value }))} />
                </div>
              </Field>
            </div>

            <div style={{ gridColumn:'1 / -1' }}>
              <Field label="비고"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={form.notes} disabled={!canWrite}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></Field>
            </div>

            <div style={{ gridColumn:'1 / -1', borderTop:'1px dashed #e5e7eb', margin:'8px 0 4px', paddingTop:14 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>📇 인사기록카드 상세정보</div>
            </div>
            <HrCardFields f={form} setF={setForm} disabled={!canWrite} employeeName={editing.name} />
          </div>
        </Modal>
      )}

      {/* 계정관리(구)에서 이관된 회원구분/부서/직급 변경 — 예전에는 사원정보 수정 모달 하단에
          붙어있었으나, 요청에 따라 별도의 독립된 모달(목록의 "계정관리" 버튼)로 분리했다.
          memberTypeDraft/deptDraft/posDraft는 이 모달을 열 때(openAcctManage) 대상 사원 기준으로
          다시 채워진다. */}
      {acctUser && (
        <Modal title={`👤 ${acctUser.name} 계정 관리`} onClose={() => setAcctUser(null)} width={420}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select value={memberTypeDraft} onChange={e => setMemberTypeDraft(e.target.value)}
                style={{ ...input, flex:1, padding:'7px 10px' }}>
                {MEMBER_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={changeMemberType} disabled={memberTypeDraft === toMemberType(acctUser)}
                style={{ ...btn(memberTypeDraft === toMemberType(acctUser) ? '#aaa' : '#0F6E56'), padding:'7px 12px', fontSize:12 }}>
                회원구분변경
              </button>
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select value={deptDraft} onChange={e => setDeptDraft(e.target.value)} style={{ ...input, flex:1, padding:'7px 10px' }}>
                <option value="">부서 미지정</option>
                {acctUser.department && !departments.some(d => d.name === acctUser.department) && (
                  <option value={acctUser.department}>{acctUser.department} (목록 외)</option>
                )}
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
              <button onClick={changeDepartmentAcct} disabled={deptDraft === (acctUser.department || '')}
                style={{ ...btn(deptDraft === (acctUser.department || '') ? '#aaa' : '#0F6E56'), padding:'7px 12px', fontSize:12 }}>
                부서변경
              </button>
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select value={posDraft} onChange={e => setPosDraft(e.target.value)} style={{ ...input, flex:1, padding:'7px 10px' }}>
                <option value="">직급 미지정</option>
                {acctUser.position && !positions.some(p => p.name === acctUser.position) && (
                  <option value={acctUser.position}>{acctUser.position} (목록 외)</option>
                )}
                {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
              <button onClick={changePositionAcct} disabled={posDraft === (acctUser.position || '')}
                style={{ ...btn(posDraft === (acctUser.position || '') ? '#aaa' : '#0F6E56'), padding:'7px 12px', fontSize:12 }}>
                직급변경
              </button>
            </div>
            <button onClick={resetAcctPassword} disabled={acctResetting}
              style={{ background:'#fff', color:'#dc2626', border:'1px solid #dc2626', borderRadius:8,
                padding:'8px 14px', fontSize:13, fontWeight:600, cursor:acctResetting?'not-allowed':'pointer',
                opacity:acctResetting?0.6:1 }}>
              {acctResetting ? '초기화 중...' : '🔑 비밀번호 초기화'}
            </button>
          </div>
        </Modal>
      )}

      {/* 사원등록 모달 — 로그인 계정(아이디/비밀번호)과 사원정보를 한번에 등록한다. */}
      {registering && (
        // 요청사항: "등록 버튼을 우측상단 닫기버튼 옆으로 이동" — 인사기록카드 상세보기-수정
        // 모달과 동일하게, 폼 맨 아래 전체폭 버튼을 Modal의 headerActions 슬롯(닫기 "✕" 버튼
        // 바로 왼쪽)으로 옮긴다.
        <Modal title="👤 사원등록" onClose={() => setRegistering(false)} width={820}
          headerActions={<button onClick={submitRegister} style={{ ...btn(C.primary), padding:'6px 16px', fontSize:13 }}>등록</button>}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
            <Field label="아이디 *"><input style={{ ...input, width:'100%' }} value={regForm.username}
              // 요청사항: E-mail은 별도 입력 없이 "아이디@ninegro.com" 형태로 자동입력된다.
              // 아이디가 바뀌면 이메일도 함께 갱신되도록 같은 핸들러에서 처리한다.
              onChange={e => { const v = e.target.value; setRegForm(f => ({ ...f, username: v, email: v ? `${v}@ninegro.com` : '' })); }} /></Field>
            <Field label="비밀번호 * (6자 이상)"><input type="password" style={{ ...input, width:'100%' }} value={regForm.password}
              onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))} /></Field>
            {/* 요청사항: 사원등록 입력폼 필드 순서 —
                이름·전화번호 / 이메일·비상연락처 / 부서·직급 / 채용구분·사번 / 입사일·퇴사일 /
                계약연봉·급여(실수령액) / 급여통장(은행/예금주/계좌번호) 순으로 각각 한 줄에 배치한다. */}
            <Field label="이름 *"><input style={{ ...input, width:'100%' }} value={regForm.name}
              onChange={e => setRegForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="전화번호"><input style={{ ...input, width:'100%' }} value={regForm.phone}
              onChange={e => setRegForm(f => ({ ...f, phone: e.target.value }))} /></Field>
            <Field label="E-mail"><input style={{ ...input, width:'100%', background:'#f8fafc', color:'#888' }}
              value={regForm.email} disabled readOnly /><SysMailGuideNote guideOnly /></Field>
            <Field label="비상연락처"><input style={{ ...input, width:'100%' }} value={regForm.emergency_phone}
              onChange={e => setRegForm(f => ({ ...f, emergency_phone: e.target.value }))} /></Field>
            {/* 부서/직급은 자유 텍스트가 아니라 조직도(조직구성)에 이미 등록된 목록 중에서
                드롭다운으로 선택한다 — "계정관리" 모달의 부서/직급 변경 select와 동일한
                departments/positions 상태를 재사용한다. 부서를 고르면 아래 EmployeeHrFields의
                사번 후보 드롭다운(buildEmpNoCandidates)도 함께 갱신된다. */}
            <Field label="부서">
              <select style={{ ...input, width:'100%' }} value={regForm.department}
                onChange={e => setRegForm(f => ({ ...f, department: e.target.value }))}>
                <option value="">부서 선택</option>
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="직급">
              <select style={{ ...input, width:'100%' }} value={regForm.position}
                onChange={e => setRegForm(f => ({ ...f, position: e.target.value }))}>
                <option value="">직급 선택</option>
                {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </Field>
            <div style={{ gridColumn:'1 / -1', borderTop:'1px dashed #e5e7eb', margin:'2px 0 4px', paddingTop:10 }} />
            <EmployeeHrFields f={regForm} setF={setRegForm} disabled={false}
              dept={departments.find(d => d.name === regForm.department)} companyPrefix={companyEmpnoPrefix} />
            {/* 버그수정: 계약연봉/급여(실수령액)를 EmployeeHrFields 뒤에 바로 이어붙이면,
                채용구분(정규직 등 계약직이 아닐 때) EmployeeHrFields가 마지막에 빈 <div/>로 열을
                하나 채우면서 2열 그리드의 좌우 정렬이 한 칸씩 밀려 계약연봉/급여가 한 줄에 나란히
                표시되지 않고 서로 다른 줄에 떨어져 보이는 문제가 있었다. 바깥 그리드의 정렬 상태와
                무관하게 항상 같은 줄에 붙도록, 이 두 필드만 별도의 2열 그리드로 감싸 전체 폭(1/-1)
                한 행에 고정한다. */}
            <div style={{ gridColumn:'1 / -1', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
              <Field label="계약연봉"><input placeholder="계약연봉" style={{ ...input, width:'100%' }} value={formatComma(regForm.salary_annual)}
                onChange={e => setRegForm(f => ({ ...f, salary_annual: onlyDigits(e.target.value) }))} /></Field>
              <Field label="급여(실수령액) - 자동계산">
                <input placeholder="계약연봉 입력시 자동계산" style={{ ...input, width:'100%', background:'#f8fafc', color:'#555' }}
                  value={calcingSalaryNet ? '계산 중...' : formatComma(regForm.salary_net)} disabled readOnly />
              </Field>
            </div>
            {/* 요청사항: "급여통장(은행/예금주/계좌번호) 이렇게 표현" — 인사기록카드 상세보기-수정
                화면과 동일한 한 줄 콤보 형태로 통일한다(예전에는 은행/계좌번호만 각각 별도 줄에
                있었고 예금주 입력칸이 아예 없었다). */}
            <div style={{ gridColumn:'1 / -1' }}>
              <Field label="급여통장 (은행 / 예금주 / 계좌)">
                <div style={{ display:'flex', gap:6 }}>
                  <input placeholder="은행" style={{ ...input, width:'25%',
                      ...(isLowConfidence(bankbookConfidence && bankbookConfidence.bank_name, regForm.bank_name) ? { border:'1.5px solid #ef4444', background:'#fff5f5' } : {}) }}
                    value={regForm.bank_name}
                    onChange={e => setRegForm(f => ({ ...f, bank_name: e.target.value }))} />
                  <input placeholder="예금주" style={{ ...input, width:'25%',
                      ...(isLowConfidence(bankbookConfidence && bankbookConfidence.account_holder, regForm.bank_holder) ? { border:'1.5px solid #ef4444', background:'#fff5f5' } : {}) }}
                    value={regForm.bank_holder}
                    onChange={e => setRegForm(f => ({ ...f, bank_holder: e.target.value }))} />
                  <input placeholder="계좌번호" style={{ ...input, flex:1,
                      ...(isLowConfidence(bankbookConfidence && bankbookConfidence.account_number, regForm.bank_account) ? { border:'1.5px solid #ef4444', background:'#fff5f5' } : {}) }}
                    value={regForm.bank_account}
                    onChange={e => setRegForm(f => ({ ...f, bank_account: e.target.value }))} />
                  {/* 요청사항: "계좌번호 입력란 우측에 급여통장을 가져올 수 있는 버튼을 추가" —
                      통장사본을 올리면 은행/예금주/계좌번호를 자동인식해 위 세 칸에 채워준다. */}
                  <BankbookOcrButton f={regForm} setF={setRegForm} disabled={false} onConfidence={setBankbookConfidence} />
                </div>
              </Field>
            </div>

            {/* 요청사항: "인사기록카드 상세정보 글자 우측끝에 등본으로 자동입력 버튼을 이쪽으로
                이동" — 예전에는 이 버튼이 HrCardFields 안쪽 주소 입력칸 옆에 있었는데, 라벨 줄
                우측 끝으로 옮기고 라벨 줄을 flex(space-between)로 바꿔 배치한다. */}
            <div style={{ gridColumn:'1 / -1', borderTop:'1px dashed #e5e7eb', margin:'8px 0 4px', paddingTop:14,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:13, fontWeight:700 }}>📇 인사기록카드 상세정보</div>
              <ResidentRegOcrButton f={regForm} setF={setRegForm} disabled={false} employeeName={regForm.name} allowNameFill
                onConfidence={setResidentRegConfidence} />
            </div>
            <HrCardFields f={regForm} setF={setRegForm} disabled={false} employeeName={regForm.name} allowNameFill
              confidence={residentRegConfidence} />
          </div>
        </Modal>
      )}

      {cardViewId && (() => {
        const cardEmp = employees.find(x => x.id === cardViewId);
        return (
          <EmployeeCardView
            userId={cardViewId}
            onClose={() => {
              setCardViewId(null);
              // 버그수정: 상세보기 안에서 근로계약(전자)을 생성하면 부서/직급이 사원정보
              // (users.department/position)에 즉시 반영되지만(hr.js applyOrgAssignment),
              // 이 목록(employees state)은 상세보기를 열 때 한 번만 불러온 값이라 모달을 닫아도
              // 자동으로 갱신되지 않아 목록에는 예전 부서가 계속 보였다. 상세보기를 닫을 때마다
              // 목록을 다시 불러와 최신 값이 즉시 반영되게 한다.
              reload(search);
            }}
            onEdit={canWrite ? () => { setCardViewId(null); if (cardEmp) openEdit(cardEmp); } : null}
            onDelete={(isSuperAdmin && cardEmp && !['superadmin','admin'].includes(cardEmp.role)) ? () => deleteEmployee(cardEmp) : null}
            deleteDisabled={['superadmin','admin'].includes(cardEmp?.role)}
          />
        );
      })()}
    </Section>

      {permUser && (
        <PermissionModal
          user={permUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === permUser.username)}
          onClose={() => setPermUser(null)}
          onSaved={() => { setPermUser(null); showMsg('권한이 저장되었습니다.'); loadPerms(); }}
          onOpenDetail={(menuKey, liveGranted) => {
            setDetailUser(permUser);
            setDetailMenuKey(menuKey);
            setDetailLiveOverride({ menu_key: menuKey, ...liveGranted });
          }}
        />
      )}

      {detailUser && (
        <DetailPermissionModal
          user={detailUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === detailUser.username)}
          initialDetails={permDetailRows.filter(d => d.username === detailUser.username)}
          initialMenu={detailMenuKey}
          liveOverride={detailLiveOverride}
          onClose={() => { setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null); }}
          onSaved={() => {
            setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null);
            showMsg('상세설정이 저장되었습니다.'); loadPerms();
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 🖥️ 생산현황 전용계정 관리 — 예전에는 인사관리 > 사원정보 탭 헤더에 있었으나, 요청에 따라
// 계정관리(마이페이지) 화면으로 이전했습니다. 버튼+모달이 완전히 자체완결형(state 포함)이라
// MyPage.js에서 그냥 <DisplayAccountManagerButton />만 놓으면 됩니다(최고관리자에게만 렌더).
// 회원가입 시 "구분"에서 "생산현황판 전용"을 선택해 만든 계정들 — 직원현황 목록에는 나타나지
// 않으며, 여기서만 승인/승인취소/권한설정/삭제를 처리한다.
// ═══════════════════════════════════════════════════════════════
export function DisplayAccountManagerButton({ showMsg }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  // 최고관리자가 아니어도 계정관리(mypage) > "전용계정 관리" 상세권한이 켜져 있으면 버튼/기능을
  // 사용할 수 있다(MyPage.js의 렌더 조건과 동일한 규칙). 백엔드도 admin.js의 approve/reject/
  // delete 라우트에서 동일한 권한을 별도로 재확인한다.
  const perm = usePermissions();
  const canManage = isSuperAdmin || perm.hasDetail('mypage', 'dedicated_account_manage');
  const notify = (text) => { if (typeof showMsg === 'function') showMsg(text); };

  const [modalOpen, setModalOpen] = useState(false);
  const [displayAccounts, setDisplayAccounts] = useState([]);
  const [permMenus, setPermMenus] = useState([]);
  const [permRows, setPermRows] = useState([]);
  const [permDetailRows, setPermDetailRows] = useState([]);
  const [permUser, setPermUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [detailMenuKey, setDetailMenuKey] = useState(null);
  const [detailLiveOverride, setDetailLiveOverride] = useState(null);

  const loadDisplayAccounts = () => API.get('/admin/users').then(r => setDisplayAccounts(r.data.filter(u => u.account_type === 'display_only'))).catch(() => {});
  const loadPerms = () => API.get('/permissions').then(r => {
    setPermMenus(r.data.menus || []); setPermRows(r.data.permissions || []); setPermDetailRows(r.data.details || []);
  }).catch(() => {});
  useEffect(() => { if (canManage) { loadDisplayAccounts(); loadPerms(); } }, [canManage]);

  const approveAccount = (id) => API.patch(`/admin/users/${id}/approve`)
    .then(() => { notify('승인 완료!'); loadDisplayAccounts(); })
    .catch(err => alert(err.response?.data?.message || '승인 처리 중 오류가 발생했습니다.'));
  const rejectAccount = (id) => API.patch(`/admin/users/${id}/reject`)
    .then(() => { notify('승인 취소되었습니다.'); loadDisplayAccounts(); })
    .catch(err => alert(err.response?.data?.message || '처리 중 오류가 발생했습니다.'));
  const deleteAccount = (u) => {
    if (!window.confirm(`${u.name}(@${u.username}) 전용계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    API.delete(`/admin/users/${u.id}`)
      .then(() => { notify('삭제되었습니다.'); loadDisplayAccounts(); })
      .catch(err => alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.'));
  };

  if (!canManage) return null;
  const pendingCount = displayAccounts.filter(u => u.approved === 0).length;

  return (
    <>
      <button onClick={() => setModalOpen(true)}
        style={{ background:'#0891b2', color:'#fff', border:'none', borderRadius:8, padding:'10px 16px',
          fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
        🖥️ 전용계정 관리{pendingCount > 0 ? ` (${pendingCount})` : ''}
      </button>

      {modalOpen && (
        <Modal title="🖥️ 생산현황 전용계정 관리" onClose={() => setModalOpen(false)} width={640}>
          <div style={{ fontSize:12, color:'#888', marginBottom:12, lineHeight:1.6 }}>
            회원가입 화면에서 "구분"을 "생산현황판 전용"으로 선택해 만든 계정 목록입니다. 실제 직원이 아니므로
            직원현황 목록에는 표시되지 않습니다. 승인 후 권한설정에서 "생산현황" 메뉴 읽기 권한을 부여해주세요.
          </div>
          {displayAccounts.length === 0 ? (
            <div style={{ textAlign:'center', color:'#aaa', fontSize:13, padding:'30px 0' }}>등록된 전용계정이 없습니다.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {displayAccounts.map(u => (
                <div key={u.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  background: u.approved === 0 ? '#fffbeb' : '#f8fafc',
                  border:`1px solid ${u.approved === 0 ? '#fef3c7' : '#e5e7eb'}`, borderRadius:10, padding:'10px 14px', gap:10, flexWrap:'wrap' }}>
                  <div style={{ fontSize:13 }}>
                    <span style={{ fontWeight:600 }}>{u.name}</span>
                    <span style={{ color:'#888', margin:'0 6px' }}>@{u.username}</span>
                    <span style={{ color:'#aaa' }}>{u.created_at?.slice(0, 10)}</span>
                    {u.approved === 0 ? (
                      <span style={{ marginLeft:8, padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:'#fef3c7', color:'#b45309' }}>승인대기</span>
                    ) : (
                      <span style={{ marginLeft:8, padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:'#dcfce7', color:'#16a34a' }}>승인됨</span>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    {u.approved === 0 ? (
                      <button onClick={() => approveAccount(u.id)} style={{ background:C.primary, color:'#fff', border:'none', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>✅ 승인</button>
                    ) : (
                      <button onClick={() => rejectAccount(u.id)} style={{ background:'#f0f0f0', color:'#555', border:'none', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>승인취소</button>
                    )}
                    <button onClick={() => setPermUser(u)}
                      style={{ background:'#fff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8,
                        padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                      🔐 권한설정
                    </button>
                    <button onClick={() => deleteAccount(u)} style={{ background:'#dc2626', color:'#fff', border:'none', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>삭제</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {permUser && (
        <PermissionModal
          user={permUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === permUser.username)}
          onClose={() => setPermUser(null)}
          onSaved={() => { setPermUser(null); notify('권한이 저장되었습니다.'); loadPerms(); }}
          onOpenDetail={(menuKey, liveGranted) => {
            setDetailUser(permUser);
            setDetailMenuKey(menuKey);
            setDetailLiveOverride({ menu_key: menuKey, ...liveGranted });
          }}
        />
      )}

      {detailUser && (
        <DetailPermissionModal
          user={detailUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === detailUser.username)}
          initialDetails={permDetailRows.filter(d => d.username === detailUser.username)}
          initialMenu={detailMenuKey}
          liveOverride={detailLiveOverride}
          onClose={() => { setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null); }}
          onSaved={() => {
            setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null);
            notify('상세설정이 저장되었습니다.'); loadPerms();
          }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 2) 조직도 — 부서/대표이사 박스를 화면에 자유 배치하고, 박스끼리 직선(직각 꺾은선)으로
//    임의 연결(네트워크형)한다. 일반적인 조직도(대표이사→부서 트리)에서 흔히 쓰는 형태를
//    참고해, 연결선은 항상 상하좌우 직선 구간으로만 구성된다(곡선 사용 안 함).
//    · 박스 헤더를 드래그하면 위치가 옮겨지고(pos_x/pos_y 저장), 연결된 박스와 좌표가
//      가까워지면 자동으로 스냅(정렬)되어 선이 곧게 정리된다.
//    · 박스 우측 하단의 🔗 손잡이를 다른 박스로 드래그하면 두 박스가 연결된다(다대다 자유 연결).
//    · 연결선을 클릭하면 삭제할 수 있다.
//    · 대표이사(경영진)는 부서와 별개의 특수 박스로, "🏢 조직구성" 모달의 "대표이사" 탭에서
//      등록/관리하며 조직도에서는 금색 박스로 구분되어 표시된다.
//    · 배치/연결 결과는 서버에 저장되어 모든 사용자에게 동일하게 보인다.
//    · 편집(이동/연결생성/연결삭제)은 최고관리자만 가능 — 일반 사용자는 결과만 열람한다.
// ═══════════════════════════════════════════════════════════════
const DEPT_CARD_WIDTH = 220;
const EXEC_CARD_WIDTH = DEPT_CARD_WIDTH + 40; // 대표이사 카드는 첨부 이미지처럼 좌측에 원형 아바타가 튀어나오는 형태라 더 넓게
const DEPT_PALETTE = [
  { header:'#F2B705', text:'#5c4200' },
  { header:'#2F6FED', text:'#ffffff' },
  { header:'#1F2937', text:'#ffffff' },
  { header:'#E4572E', text:'#ffffff' },
  { header:'#7C3AED', text:'#ffffff' },
  { header:'#0D9488', text:'#ffffff' },
];
const EXEC_STYLE = { header:'#C9A227', text:'#2b2205' };
const SNAP_PX = 14; // 카드 이동 중 연결된 카드와의 자동 정렬(스냅) 허용 오차
const CLICK_MOVE_PX = 5; // 이 픽셀 이하로만 움직였으면 "드래그"가 아니라 "클릭"으로 간주

// 박스 높이를 실제 DOM 측정 없이 대략 추정 — 연결선의 시작/끝 좌표 계산용(픽셀 완벽 정확도는 불필요).
const estimateBoxHeight = (node) => {
  if (node.type === 'exec') return 58; // 대표이사 카드는 원형 아바타 + 직함 + 성명 2줄, 높이 고정
  const memberCount = node.memberCount || 0;
  return 40 + Math.min(memberCount, 6) * 40 + (memberCount > 0 ? 10 : 0); // 사원 한 명당 미니카드 높이(40px)
};
const nodeCardWidth = (node) => node.type === 'exec' ? EXEC_CARD_WIDTH : DEPT_CARD_WIDTH;

// 조직도 카드(부서)를 클릭하면 뜨는 "소속 사원 관리" 모달 — 요청사항: "조직 이름은 조직구성으로
// 등록하지만, 사원과 조직의 부서 설정(즉 어떤 사원이 어느 부서 소속인지)은 조직도에서 한다."
// 실제 배정은 사원정보(users.department)를 바꾸는 것과 동일해서, 사원정보 탭에서 이미 쓰고 있는
// PATCH /api/admin/users/:id/department 를 그대로 재사용한다(새 API를 만들지 않음).
function DeptMembersModal({ dept, employees, onClose, onChanged }) {
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  // "대표이사" 부서 박스는 실제 department가 아니라 직급(position)으로 소속을 판정/배정한다
  // (2026-08 요청: +0 배지로도 대표이사 지정 가능해야 함)
  const isCeoDept = dept.name === '대표이사';

  const members = employees.filter(e => isCeoDept ? e.position === '대표이사' : (e.department || '') === dept.name);
  const q = search.trim();
  const others = employees
    .filter(e => isCeoDept ? e.position !== '대표이사' : (e.department || '') !== dept.name)
    .filter(e => !q || e.name.includes(q) || (e.department || '').includes(q) || (e.position || '').includes(q));

  const assign = (emp, targetName) => {
    setBusyId(emp.id);
    const req = isCeoDept
      ? API.patch(`/admin/users/${emp.id}/position`, { position: targetName ? '대표이사' : '' })
      : API.patch(`/admin/users/${emp.id}/department`, { department: targetName });
    req.then(() => onChanged())
      .catch(() => alert('처리 중 오류가 발생했습니다.'))
      .finally(() => setBusyId(null));
  };

  return (
    <Modal title={`🏢 ${dept.name} — 소속 사원 관리`} onClose={onClose} width={560}>
      <div style={{ fontSize:12, color:'#999', marginBottom:14 }}>
        사원정보에 등록된 사원을 이 부서로 배정하거나 제외할 수 있습니다. 여기서 바꾼 부서는 사원정보 목록에도 그대로 반영됩니다.
      </div>

      <Field label={`현재 소속 사원 (${members.length}명)`}>
        {members.length === 0 ? (
          <div style={{ color:'#aaa', fontSize:13, padding:'10px 0' }}>아직 배정된 사원이 없습니다. 아래에서 추가해주세요.</div>
        ) : (
          <div style={{ maxHeight:180, overflowY:'auto', border:`1px solid ${C.border}`, borderRadius:8 }}>
            {members.map(m => (
              <div key={m.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'8px 12px', borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
                <span>{m.name} <span style={{ color:C.sub }}>· {m.position || '-'}</span></span>
                <button disabled={busyId === m.id} onClick={() => assign(m, '')}
                  style={{ background:'none', border:'1px solid #f0b4b4', color:'#c0392b', borderRadius:6,
                    padding:'4px 10px', fontSize:12, cursor:'pointer' }}>제외</button>
              </div>
            ))}
          </div>
        )}
      </Field>

      <Field label="사원 추가">
        <input placeholder="이름/부서/직급 검색" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...input, width:'100%', marginBottom:8, boxSizing:'border-box' }} />
        <div style={{ maxHeight:220, overflowY:'auto', border:`1px solid ${C.border}`, borderRadius:8 }}>
          {others.length === 0 ? (
            <div style={{ color:'#aaa', fontSize:13, padding:'12px' }}>표시할 사원이 없습니다.</div>
          ) : others.map(m => (
            <div key={m.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'8px 12px', borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
              <span>{m.name} <span style={{ color:C.sub }}>· {m.department || '미배정'} · {m.position || '-'}</span></span>
              <button disabled={busyId === m.id} onClick={() => assign(m, dept.name)}
                style={{ ...btn(C.primary), padding:'5px 12px', fontSize:12 }}>+ 추가</button>
            </div>
          ))}
        </div>
      </Field>

      <div style={{ textAlign:'right', marginTop:6 }}>
        <button onClick={onClose} style={{ ...btn('#888'), padding:'9px 18px' }}>닫기</button>
      </div>
    </Modal>
  );
}

// 연결선은 항상 직선(수직/수평 구간으로만 이루어진 직각 꺾은선)으로 그린다 — 일반적인
// 조직도에서 흔히 쓰는 방식. 두 박스가 이미 상하 또는 좌우로 정렬돼 있으면 한 번도 꺾이지
// 않는 완전한 직선이 되고(스냅 기능과 맞물림), 그렇지 않으면 중간에서 한 번 꺾인다.
// elbowPathV: 세로 중간(midY)에서 한 번 꺾는다 — 두 카드를 상/하 변으로 연결할 때.
// elbowPathH: 가로 중간(midX)에서 한 번 꺾는다 — 두 카드를 좌/우측 변으로 연결할 때(신규).
const elbowPathV = (x1, y1, x2, y2) => {
  if (Math.abs(x1 - x2) < 1 || Math.abs(y1 - y2) < 1) return `M ${x1} ${y1} L ${x2} ${y2}`;
  const midY = (y1 + y2) / 2;
  return `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
};
const elbowPathH = (x1, y1, x2, y2) => {
  if (Math.abs(x1 - x2) < 1 || Math.abs(y1 - y2) < 1) return `M ${x1} ${y1} L ${x2} ${y2}`;
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
};

function OrgChartTab({ user, headerActionsEl, tabBarActionsEl }) {
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [connections, setConnections] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  // 사번설정(요청사항: "조직도 - 조직구성 버튼옆에 사번설정 버튼추가" / "부서별로 사번기준을
  // 설정할 수 있도록 구성" / "설정된 사번은 직원등록에 적용") — 부서별 접두어/자릿수/다음번호를
  // 관리하는 모달. 실제 적용은 인사관리 > 직원현황 > 사원등록에서 이뤄진다(assignEmployeeNo 참고).
  const [showEmpNoModal, setShowEmpNoModal] = useState(false);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [manageDeptKey, setManageDeptKey] = useState(null); // 클릭(드래그 아님)한 부서 카드 — "소속 사원 관리" 모달
  const [search, setSearch] = useState(''); // 부서명/대표이사명/소속 사원 이름·직급 검색 — 일치하지 않는 카드는 흐리게 표시

  const load = () => API.get('/hr/org-chart').then(r => {
    setEmployees(r.data.employees || []);
    setDepartments(r.data.departments || []);
    setExecutives(r.data.executives || []);
    setConnections(r.data.connections || []);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  // 대표이사로 등록된(org_executives.user_id로 연결된) 계정의 id 집합.
  const execUserIds = useMemo(() => new Set(executives.map(x => x.user_id).filter(Boolean)), [executives]);

  // "대표이사"라는 이름의 부서가 조직도에 이미 구성되어 있는 배포가 있다(회사마다 조직구성을
  // 자유롭게 만들 수 있으므로) — 2026-08 피드백: "대표이사 설정을 하면 현재 조직도에 구성되어
  // 있는 대표이사 탭으로 자동으로 적용", "조직도 - 대표이사 탭에 우측 +0을 눌러서도 대표이사
  // 지정을 할 수 있어야 함". 그런 배포에서는 org_executives 기반의 별도 카드를 새로 띄우는 대신,
  // 이미 있는 그 부서 박스를 대표이사 전용 창구로 그대로 쓴다(중복 표시 방지 + 기존 "소속 사원
  // 관리" +N 배지 클릭 UX를 그대로 재사용). users.department 값 자체는 여전히 비워둔 채로
  // 두므로(계정관리 등 다른 화면은 계속 "-" 표시), 아래 membersByDept 계산에서만 가상으로
  // 이 부서의 소속으로 묶어 보여준다 — DeptMembersModal도 이 부서일 때는 department가 아니라
  // position API로 배정/해제하도록 별도 분기되어 있다(아래 DeptMembersModal 참고).
  const ceoDeptExists = departments.some(d => d.name === '대표이사');

  const membersByDept = useMemo(() => {
    const map = {};
    employees.forEach(e => {
      const isCeo = e.position === '대표이사' || execUserIds.has(e.id);
      if (isCeo) {
        if (ceoDeptExists) {
          const key = '대표이사';
          if (!map[key]) map[key] = [];
          map[key].push(e);
        }
        // "대표이사" 부서가 따로 구성되어 있지 않은 배포에서는 대표이사 카드(exec)에서만
        // 표시하고 부서/미배정 카드에는 넣지 않는다(중복 방지, 기존 동작 유지).
        return;
      }
      const key = e.department || '';
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [employees, execUserIds, ceoDeptExists]);

  // "대표이사"라는 이름의 부서가 이미 있으면 그 부서 박스가 대표이사 표시를 전담하므로,
  // org_executives 기반의 대표이사 카드(제목이 정확히 "대표이사"인 것)는 캔버스에 중복으로
  // 띄우지 않는다. "부사장" 등 다른 직함으로 자유입력된 대표이사 탭 항목은 계속 별도 카드로
  // 표시한다(이 회사만의 특수 배치일 수 있으므로 건드리지 않음).
  const visibleExecutives = useMemo(
    () => executives.filter(x => !(ceoDeptExists && (x.title || '대표이사') === '대표이사')),
    [executives, ceoDeptExists]
  );

  const deptNames = new Set(departments.map(d => d.name));
  const unmatchedNames = Object.keys(membersByDept).filter(name => name && !deptNames.has(name));
  const unassignedMembers = membersByDept[''] || [];

  // 검색어와 일치하지 않는 카드는 흐리게(딤 처리) 표시한다 — 연결선/레이아웃은 그대로 유지한 채
  // 원하는 부서/대표이사/사원을 빠르게 찾을 수 있도록 한다.
  const searchQ = search.trim();
  const nodeMatchesSearch = (deptOrExecName, extraName, members) => {
    if (!searchQ) return true;
    if ((deptOrExecName || '').includes(searchQ) || (extraName || '').includes(searchQ)) return true;
    return (members || []).some(m => (m.name || '').includes(searchQ) || (m.position || '').includes(searchQ));
  };

  // 부서/대표이사를 하나의 노드 목록으로 통합 — key(`dept:1`, `exec:1`)로 구분해서 다룬다.
  const nodes = useMemo(() => ([
    ...departments.map(d => ({ type:'dept', id:d.id, key:`dept:${d.id}`, name:d.name, pos_x:d.pos_x, pos_y:d.pos_y, memberCount:(membersByDept[d.name] || []).length })),
    ...visibleExecutives.map(x => ({ type:'exec', id:x.id, key:`exec:${x.id}`, title:x.title || '대표이사', name:x.name || '', pos_x:x.pos_x, pos_y:x.pos_y })),
  ]), [departments, visibleExecutives, membersByDept]);
  const nodeByKey = (key) => nodes.find(n => n.key === key);

  const canvasRef = useRef(null);
  const regRef = useRef({}); // node key -> DOM el(박스 전체) — 클라이언트 좌표 히트테스트용
  const registerBody = (key) => (el) => { if (el) regRef.current[key] = el; else delete regRef.current[key]; };
  const didInitLayout = useRef(false);

  const layoutEndpoint = (n) => n.type === 'exec' ? `/org/executives/${n.id}/layout` : `/org/departments/${n.id}/layout`;

  // 사용자가 이번 화면 진입 중 직접 드래그해서 옮긴 노드의 key 집합. 아래 "기본 위치 배정" 로직이
  // 서버 응답 타이밍상 뒤늦게 도착해 방금 옮긴 좌표를 덮어써버리는 경합(회전문 버그: "이동은 되는데
  // 놓으면 원래 자리로 돌아간다")을 막기 위한 안전장치 — 서버는 이제 시작 시점에 기존 부서/대표이사의
  // 기본좌표를 한 번에 채워두므로(database.js seedOrgChartDefaultPositions) 이 경로는 주로 방금 새로
  // 추가된 부서/대표이사에만 해당된다.
  const manualPosRef = useRef(new Set());

  // 좌표가 아직 없는(한 번도 배치해본 적 없는) 노드에는 기본 위치를 배정해서 저장해둔다.
  // 대표이사는 맨 윗줄, 부서는 그 아래 격자로 — 일반적인 조직도의 상하 구조를 따른다.
  // 여러 노드를 동시에(병렬) PATCH하지 않고 하나씩 순서대로 처리해서, 사용자의 수동 드래그와
  // 뒤섞여 응답 순서가 꼬이는 일이 없도록 한다.
  useEffect(() => {
    if (!isSuperAdmin || didInitLayout.current || nodes.length === 0) return;
    const missingExec = visibleExecutives.filter(x => (x.pos_x === null || x.pos_x === undefined) && !manualPosRef.current.has(`exec:${x.id}`));
    const missingDept = departments.filter(d => (d.pos_x === null || d.pos_x === undefined) && !manualPosRef.current.has(`dept:${d.id}`));
    if (!missingExec.length && !missingDept.length) { didInitLayout.current = true; return; }
    didInitLayout.current = true;
    (async () => {
      for (let i = 0; i < missingExec.length; i++) {
        const pos_x = 24 + i * (DEPT_CARD_WIDTH + 40);
        try { await API.patch(`/org/executives/${missingExec[i].id}/layout`, { pos_x, pos_y: 24 }); } catch (e) {}
      }
      // 행(row)별 높이를 그 행에 배치될 부서들의 실제 인원수 기반 예상 높이 중 최댓값으로 계산해서
      // 다음 행이 그만큼 아래로 밀려나게 한다 — 인원이 많아 카드가 길어진 부서가 있어도 다음 줄
      // 카드와 겹치지 않도록 "자동 간격/높이"를 적용한다.
      const rowOffset = (visibleExecutives.length > 0 ? 170 : 24);
      const cols = 4;
      const rowCount = Math.ceil(missingDept.length / cols);
      const rowMaxH = new Array(rowCount).fill(0);
      missingDept.forEach((d, i) => {
        const row = Math.floor(i / cols);
        const h = estimateBoxHeight({ type:'dept', memberCount:(membersByDept[d.name] || []).length });
        if (h > rowMaxH[row]) rowMaxH[row] = h;
      });
      const rowY = [rowOffset];
      for (let r = 0; r < rowCount; r++) rowY.push(rowY[r] + (rowMaxH[r] || 220) + 24);
      for (let i = 0; i < missingDept.length; i++) {
        const col = i % cols, row = Math.floor(i / cols);
        const pos_x = 24 + col * (DEPT_CARD_WIDTH + 40);
        const pos_y = rowY[row];
        try { await API.patch(`/org/departments/${missingDept[i].id}/layout`, { pos_x, pos_y }); } catch (e) {}
      }
      load();
    })();
    // eslint-disable-next-line
  }, [nodes, isSuperAdmin]);

  // ── 박스 이동(드래그) / 클릭 구분 / 다중 선택 ────────────────────────────
  // 헤더를 누른 채로 일정 거리(CLICK_MOVE_PX) 이상 움직이면 "드래그"(위치 이동), 거의 움직이지
  // 않고 뗐으면 "클릭"으로 간주한다. 부서 카드를 클릭하면 소속 사원 관리 모달이 열린다
  // ("조직도 카드를 누르면 등록된 사원정보를 이용하여 추가할 수 있도록" 요청사항).
  // Ctrl(macOS는 ⌘)을 누른 채 카드를 클릭하면 이동 없이 다중선택 목록에 토글 추가/제거된다.
  // 선택된 카드 중 하나를 (Ctrl 없이) 드래그하면 선택된 카드 전체가 서로의 상대위치를 유지한 채
  // 함께 이동한다.
  const [dragKey, setDragKey] = useState(null); // 드래그를 시작한 "기준" 카드(스냅 계산 기준)
  const [dragLivePos, setDragLivePos] = useState(null); // { [nodeKey]: {x,y} } — 드래그 중인 모든 카드의 실시간 위치(렌더링용)
  const [selectedKeys, setSelectedKeys] = useState(() => new Set()); // Ctrl+클릭으로 다중선택된 카드 key 집합
  const [snapGuides, setSnapGuides] = useState({ x: null, y: null }); // 드래그 중 스냅된 기준선(안내선 표시용)
  // dragLivePosRef / groupStartPosRef / dragGroupKeysRef: onUp에서 "최종 좌표"를 읽어오는 용도.
  // state를 직접 읽으면 안 되는 이유 — 아래 useEffect는 dragKey가 바뀔 때(드래그 시작 시) 딱 한 번만
  // 실행되고, 그 안에서 만들어지는 onMove/onUp 클로저는 "그 순간의" state 값에 영원히 고정된다.
  // ref는 .current를 항상 최신값으로 유지하므로 이 문제가 없다(과거 "이동 후 원위치 복귀" 버그의 원인).
  const dragLivePosRef = useRef(null);
  const groupStartPosRef = useRef({});   // key -> 드래그 시작 시점의 {x,y}
  const dragGroupKeysRef = useRef([]);   // 이번 드래그로 함께 움직이는 모든 카드의 key 목록
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);

  const onBoxPointerDown = (node) => (e) => {
    if (!isSuperAdmin) return;
    e.preventDefault(); e.stopPropagation();

    // Ctrl/⌘ + 클릭: 위치 이동 없이 다중선택만 토글한다.
    if (e.ctrlKey || e.metaKey) {
      setSelectedKeys(prev => {
        const next = new Set(prev);
        if (next.has(node.key)) next.delete(node.key); else next.add(node.key);
        return next;
      });
      return;
    }

    const el = regRef.current[node.key];
    const r = el ? el.getBoundingClientRect() : null;
    dragOffsetRef.current = r ? { dx: e.clientX - r.left, dy: e.clientY - r.top } : { dx: 20, dy: 16 };
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;

    // 이미 다중선택(2개 이상)에 포함된 카드를 누른 경우에만 "그룹 이동"으로 취급하고, 그렇지
    // 않으면 선택을 이 카드 하나로 초기화한 뒤 기존과 동일하게 단일 카드만 이동한다.
    const groupKeys = (selectedKeys.has(node.key) && selectedKeys.size > 1) ? Array.from(selectedKeys) : [node.key];
    if (groupKeys.length === 1) setSelectedKeys(new Set());
    dragGroupKeysRef.current = groupKeys;
    const startPositions = {};
    groupKeys.forEach(k => {
      const n = nodeByKey(k);
      startPositions[k] = { x: n?.pos_x ?? 24, y: n?.pos_y ?? 24 };
    });
    groupStartPosRef.current = startPositions;
    dragLivePosRef.current = startPositions;
    setDragKey(node.key);
    setDragLivePos(startPositions);
  };

  useEffect(() => {
    if (dragKey == null) return;
    const canvasEl = canvasRef.current;
    const onMove = (e) => {
      if (!canvasEl) return;
      const dist = Math.hypot(e.clientX - pointerStartRef.current.x, e.clientY - pointerStartRef.current.y);
      if (dist > CLICK_MOVE_PX) movedRef.current = true;
      const cr = canvasEl.getBoundingClientRect();
      let x = Math.max(0, e.clientX - cr.left + canvasEl.scrollLeft - dragOffsetRef.current.dx);
      let y = Math.max(0, e.clientY - cr.top + canvasEl.scrollTop - dragOffsetRef.current.dy);

      const groupKeys = dragGroupKeysRef.current;
      const anchorNode = nodeByKey(dragKey);
      let guideX = null, guideY = null;

      // 상하좌우 자동 정렬(스냅): 연결 여부와 무관하게, 캔버스에 있는 다른 모든 카드의 좌측/중앙/
      // 우측(x축), 상단/중앙/하단(y축) 기준선에 가까워지면 그 선에 딱 맞춰서 정렬한다.
      if (anchorNode) {
        const w = nodeCardWidth(anchorNode), h = estimateBoxHeight(anchorNode);
        const others = nodes.filter(n => !groupKeys.includes(n.key));
        const xEdges = []; const yEdges = [];
        others.forEach(o => {
          const ox = o.pos_x ?? 24, ow = nodeCardWidth(o);
          xEdges.push(ox, ox + ow / 2, ox + ow);
          const oy = o.pos_y ?? 24, oh = estimateBoxHeight(o);
          yEdges.push(oy, oy + oh / 2, oy + oh);
        });
        for (const offset of [0, w / 2, w]) {
          const hit = xEdges.find(ex => Math.abs((x + offset) - ex) <= SNAP_PX);
          if (hit !== undefined) { x = hit - offset; guideX = hit; break; }
        }
        for (const offset of [0, h / 2, h]) {
          const hit = yEdges.find(ey => Math.abs((y + offset) - ey) <= SNAP_PX);
          if (hit !== undefined) { y = hit - offset; guideY = hit; break; }
        }
      }
      x = Math.max(0, x); y = Math.max(0, y);
      setSnapGuides({ x: guideX, y: guideY });

      // 스냅 적용된 기준 카드의 이동량(dx,dy)을 계산해서, 함께 선택된 나머지 카드에도 동일하게
      // 적용한다 — 이렇게 하면 그룹 내 카드들의 상대 배치가 그대로 유지된 채 다같이 이동한다.
      const start = groupStartPosRef.current[dragKey] || { x, y };
      const dx = x - start.x, dy = y - start.y;
      const nextPositions = {};
      groupKeys.forEach(k => {
        const s = groupStartPosRef.current[k] || { x: 0, y: 0 };
        nextPositions[k] = { x: Math.max(0, s.x + dx), y: Math.max(0, s.y + dy) };
      });
      dragLivePosRef.current = nextPositions;
      setDragLivePos(nextPositions);
    };
    const onUp = () => {
      const groupKeys = dragGroupKeysRef.current;
      if (movedRef.current) {
        const finalPositions = dragLivePosRef.current || {}; // ← 항상 최신값(ref)에서 읽는다
        groupKeys.forEach(k => manualPosRef.current.add(k));
        // 낙관적 갱신: 서버 응답/재조회(load) 타이밍과 무관하게 카드들이 그 자리에 곧바로
        // "고정"된 것처럼 보이도록 로컬 상태를 먼저 최종 좌표로 맞춘다.
        setExecutives(prev => prev.map(v => {
          const pos = finalPositions[`exec:${v.id}`];
          return pos ? { ...v, pos_x: pos.x, pos_y: pos.y } : v;
        }));
        setDepartments(prev => prev.map(v => {
          const pos = finalPositions[`dept:${v.id}`];
          return pos ? { ...v, pos_x: pos.x, pos_y: pos.y } : v;
        }));
        Promise.all(groupKeys.map(k => {
          const n = nodeByKey(k);
          const pos = finalPositions[k];
          if (!n || !pos) return Promise.resolve();
          return API.patch(layoutEndpoint(n), { pos_x: pos.x, pos_y: pos.y }).catch(() => {});
        })).then(load);
      }
      // 실제로는 움직이지 않은 순수 클릭이었어도 여기서는 더 이상 아무 동작도 하지 않는다 — 부서
      // 카드의 "소속 사원 관리" 모달은 이제 카드 우측의 인원수 배지(+N)를 직접 클릭했을 때만
      // 열린다(배지 자체의 onClick에서 처리, 아래 참고). 카드의 다른 부분을 클릭/드래그하면
      // 위치 이동만 될 뿐 모달은 열리지 않는다.
      setDragKey(null); setDragLivePos(null); dragLivePosRef.current = null;
      setSnapGuides({ x: null, y: null });
      dragGroupKeysRef.current = []; groupStartPosRef.current = {};
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    // eslint-disable-next-line
  }, [dragKey]);

  // ── 연결선 생성(🔗 손잡이 드래그) ────────────────────────────
  const [linkFrom, setLinkFrom] = useState(null);      // 연결 시작 노드 key
  const [linkPos, setLinkPos] = useState(null);         // 캔버스 기준 현재 포인터 좌표
  const [linkTargetKey, setLinkTargetKey] = useState(null);

  const onHandlePointerDown = (node) => (e) => {
    if (!isSuperAdmin) return;
    e.preventDefault(); e.stopPropagation();
    setLinkFrom(node.key);
    const canvasEl = canvasRef.current;
    if (canvasEl) {
      const cr = canvasEl.getBoundingClientRect();
      setLinkPos({ x: e.clientX - cr.left + canvasEl.scrollLeft, y: e.clientY - cr.top + canvasEl.scrollTop });
    }
  };

  useEffect(() => {
    if (linkFrom == null) return;
    const canvasEl = canvasRef.current;
    const hitTest = (clientX, clientY) => {
      let found = null;
      Object.keys(regRef.current).forEach((key) => {
        if (key === linkFrom) return;
        const el = regRef.current[key];
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) found = key;
      });
      return found;
    };
    const onMove = (e) => {
      if (canvasEl) {
        const cr = canvasEl.getBoundingClientRect();
        setLinkPos({ x: e.clientX - cr.left + canvasEl.scrollLeft, y: e.clientY - cr.top + canvasEl.scrollTop });
      }
      setLinkTargetKey(hitTest(e.clientX, e.clientY));
    };
    const onUp = (e) => {
      const target = hitTest(e.clientX, e.clientY);
      if (target && target !== linkFrom) {
        const from = nodeByKey(linkFrom), to = nodeByKey(target);
        if (from && to) {
          API.post('/org/connections', { from_dept_id: from.id, from_type: from.type, to_dept_id: to.id, to_type: to.type }).then(load)
            .catch(err => alert(err.response?.data?.message || '연결 실패'));
        }
      }
      setLinkFrom(null); setLinkPos(null); setLinkTargetKey(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    // eslint-disable-next-line
  }, [linkFrom]);

  const deleteConnection = (id) => {
    if (!isSuperAdmin) return;
    if (!window.confirm('이 연결을 삭제하시겠습니까?')) return;
    API.delete(`/org/connections/${id}`).then(load).catch(() => {});
  };

  // 카드의 상/하/좌/우 변 "중앙" 좌표 하나를 계산한다.
  const anchorPoint = (node, side) => {
    const x = node.pos_x ?? 24, y = node.pos_y ?? 24;
    const w = nodeCardWidth(node), h = estimateBoxHeight(node);
    if (side === 'top') return { x: x + w / 2, y };
    if (side === 'bottom') return { x: x + w / 2, y: y + h };
    if (side === 'left') return { x, y: y + h / 2 };
    return { x: x + w, y: y + h / 2 }; // 'right'
  };

  // 두 카드의 중심 좌표를 비교해서, 좌우로 더 멀리 떨어져 있으면 서로 마주보는 좌/우측 중앙끼리,
  // 위아래로 더 멀리 떨어져 있으면 상/하 중앙끼리 연결한다 — "연결선 중간에서 카드를 연결할 경우
  // 카드 좌우측 중간으로 연결" 요청사항 반영(기존엔 항상 상/하로만 연결됐음).
  const connectorAnchors = (a, b) => {
    const acx = (a.pos_x ?? 24) + nodeCardWidth(a) / 2, acy = (a.pos_y ?? 24) + estimateBoxHeight(a) / 2;
    const bcx = (b.pos_x ?? 24) + nodeCardWidth(b) / 2, bcy = (b.pos_y ?? 24) + estimateBoxHeight(b) / 2;
    const dx = bcx - acx, dy = bcy - acy;
    if (Math.abs(dx) > Math.abs(dy)) {
      return { p1: anchorPoint(a, dx > 0 ? 'right' : 'left'), p2: anchorPoint(b, dx > 0 ? 'left' : 'right'), axis: 'x' };
    }
    return { p1: anchorPoint(a, dy > 0 ? 'bottom' : 'top'), p2: anchorPoint(b, dy > 0 ? 'top' : 'bottom'), axis: 'y' };
  };

  // 연결선을 드래그해서 만드는 중(아직 상대 카드가 없고 포인터 좌표만 있는 상태)에도 같은 규칙으로
  // 카드의 어느 변에서 선이 시작할지 결정한다.
  const anchorTowards = (node, tx, ty) => {
    const w = nodeCardWidth(node), h = estimateBoxHeight(node);
    const ncx = (node.pos_x ?? 24) + w / 2, ncy = (node.pos_y ?? 24) + h / 2;
    const dx = tx - ncx, dy = ty - ncy;
    if (Math.abs(dx) > Math.abs(dy)) return { point: anchorPoint(node, dx > 0 ? 'right' : 'left'), axis: 'x' };
    return { point: anchorPoint(node, dy > 0 ? 'bottom' : 'top'), axis: 'y' };
  };

  // 연결선 그룹화(트리형 버스 연결) — 같은 부모(from) 카드에서 같은 방향(아래 또는 위)으로 2개
  // 이상의 자식이 연결돼 있으면(전형적인 조직도 형태) 각 연결을 따로 그리지 않고 "부모 → 공용
  // 가로 버스선 → 각 자식으로 수직 분기"하는 하나의 트리로 묶어서 그린다. 부모-자식 관계가 아닌
  // 좌우(1:1) 연결이나 자식이 1개뿐인 연결은 기존처럼 개별 꺾은선(상/하 또는 좌/우)으로 그린다.
  const connLines = (() => {
    const raw = connections.map(c => {
      const a = nodeByKey(`${c.from_type || 'dept'}:${c.from_dept_id}`);
      const b = nodeByKey(`${c.to_type || 'dept'}:${c.to_dept_id}`);
      if (!a || !b) return null;
      const dir = (b.pos_y ?? 24) >= (a.pos_y ?? 24) ? 'down' : 'up';
      return { id: c.id, a, b, dir };
    }).filter(Boolean);

    // 같은 부모(from)에서 같은 방향(dir)으로 나가는 연결이 2개 이상이면 "부모 1 : 자식 여러명"인
    // 트리 형태로 보고, 자식 카드 하나가 부모와 좌우로 더 멀리 떨어져 있어(가로 폭 > 세로 폭) 원래는
    // 좌/우 연결로 계산되더라도 항상 상/하(세로) 버스 연결로 통일해서 그린다 — 형제 카드들과 묶이지
    // 못하고 혼자 다른 모양(좌우 연결)의 선으로 삐져나오던 문제 수정.
    const fromDirCounts = {};
    raw.forEach(v => { const key = `${v.a.key}|${v.dir}`; fromDirCounts[key] = (fromDirCounts[key] || 0) + 1; });

    const valid = raw.map(v => {
      const key = `${v.a.key}|${v.dir}`;
      const forceVertical = fromDirCounts[key] >= 2;
      const { p1, p2, axis } = forceVertical
        ? { p1: anchorPoint(v.a, v.dir === 'down' ? 'bottom' : 'top'), p2: anchorPoint(v.b, v.dir === 'down' ? 'top' : 'bottom'), axis: 'y' }
        : connectorAnchors(v.a, v.b);
      return { id: v.id, a: v.a, b: v.b, p1, p2, axis, dir: v.dir };
    });

    const groups = {};
    valid.forEach(v => {
      if (v.axis !== 'y') return; // 좌우(1:1) 연결은 버스 그룹화 대상이 아니다
      const key = `${v.a.key}|${v.dir}`;
      (groups[key] = groups[key] || []).push(v);
    });

    const lines = [];
    const usedIds = new Set();
    Object.values(groups).forEach(g => {
      if (g.length < 2) return; // 자식이 1개뿐이면 굳이 묶지 않고 아래 개별 처리로 넘긴다
      const parentAnchor = g[0].p1; // 그룹 내 모두 같은 부모의 같은 변(상단 또는 하단) 중앙점
      const avgChildY = g.reduce((s, v) => s + v.p2.y, 0) / g.length;
      const busY = parentAnchor.y + (avgChildY - parentAnchor.y) / 2;
      const xs = [parentAnchor.x, ...g.map(v => v.p2.x)];
      lines.push({
        type: 'bus', key: `bus-${g[0].a.key}-${g[0].dir}`, parentAnchor, busY,
        minX: Math.min(...xs), maxX: Math.max(...xs),
        children: g.map(v => ({ id: v.id, x: v.p2.x, y: v.p2.y })),
      });
      g.forEach(v => usedIds.add(v.id));
    });
    valid.forEach(v => {
      if (usedIds.has(v.id)) return;
      lines.push({ type: 'single', key: `s-${v.id}`, id: v.id, p1: v.p1, p2: v.p2, axis: v.axis });
    });
    return lines;
  })();

  const printOrgChart = () => printListPopup({
    title: '조직도', menuLabel: '인사관리(조직도)',
    columns: ['구분', '이름/직함', '소속 인원'],
    rows: [
      ...visibleExecutives.map(x => [`${x.title || '대표이사'}`, x.name || '-', '-']),
      ...departments.map(d => [d.name, '부서', (membersByDept[d.name] || []).map(m => `${m.position ? m.position + ' ' : ''}${m.name}`).join(', ') || '-']),
    ],
  });

  return (
    <>
    <HeaderPortal target={headerActionsEl}>
      <button onClick={printOrgChart} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
      {isSuperAdmin && <button onClick={() => setShowOrgModal(true)} style={btn('#7C3AED')}>🏢 조직구성</button>}
      {isSuperAdmin && <button onClick={() => setShowEmpNoModal(true)} style={btn('#B45309')}>🔢 사번설정</button>}
      {isSuperAdmin && <button onClick={() => setShowDisclosureModal(true)} style={btn('#0F6E56')}>🎯 공개범위 설정</button>}
    </HeaderPortal>

    <HeaderPortal target={tabBarActionsEl}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="부서/대표이사/사원 이름 검색" style={{ ...input, width:220 }} />
    </HeaderPortal>

    <Section>
      <div ref={canvasRef} onPointerDown={(e) => { if (e.target === e.currentTarget && selectedKeys.size > 0) setSelectedKeys(new Set()); }}
        style={{ position:'relative', minHeight:700, maxHeight:'85vh', overflow:'auto',
        background:'#fafbfc', border:`1px dashed ${C.border}`, borderRadius:10 }}>
        {nodes.length === 0 && (
          <div style={{ color:'#aaa', fontSize:13, padding:20 }}>표시할 조직 정보가 없습니다. "🏢 조직구성"에서 부서(또는 대표이사)를 먼저 등록해주세요.</div>
        )}
        <svg style={{ position:'absolute', left:0, top:0, width:'100%', height:'100%', overflow:'visible', pointerEvents:'none' }}>
          {connLines.map(line => {
            if (line.type === 'single') {
              const d = line.axis === 'x' ? elbowPathH(line.p1.x, line.p1.y, line.p2.x, line.p2.y) : elbowPathV(line.p1.x, line.p1.y, line.p2.x, line.p2.y);
              return (
                <g key={line.key}>
                  <path d={d} fill="none" stroke="#cbd5e1" strokeWidth={2} />
                  {isSuperAdmin && (
                    <path d={d} fill="none" stroke="transparent" strokeWidth={14}
                      style={{ pointerEvents:'stroke', cursor:'pointer' }} onClick={() => deleteConnection(line.id)}>
                      <title>클릭하여 연결 삭제</title>
                    </path>
                  )}
                </g>
              );
            }
            // type === 'bus' — 부모에서 공용 가로 버스선을 거쳐 여러 자식으로 갈라지는 트리 형태
            const { parentAnchor, busY, minX, maxX, children } = line;
            return (
              <g key={line.key}>
                <path d={`M ${parentAnchor.x} ${parentAnchor.y} L ${parentAnchor.x} ${busY}`} fill="none" stroke="#cbd5e1" strokeWidth={2} />
                <path d={`M ${minX} ${busY} L ${maxX} ${busY}`} fill="none" stroke="#cbd5e1" strokeWidth={2} />
                {children.map(ch => {
                  const d = `M ${ch.x} ${busY} L ${ch.x} ${ch.y}`;
                  return (
                    <g key={`${line.key}-${ch.id}`}>
                      <path d={d} fill="none" stroke="#cbd5e1" strokeWidth={2} />
                      {isSuperAdmin && (
                        <path d={d} fill="none" stroke="transparent" strokeWidth={14}
                          style={{ pointerEvents:'stroke', cursor:'pointer' }} onClick={() => deleteConnection(ch.id)}>
                          <title>클릭하여 연결 삭제</title>
                        </path>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}
          {linkFrom != null && linkPos && (() => {
            const a = nodeByKey(linkFrom);
            if (!a) return null;
            const { point: p1, axis } = anchorTowards(a, linkPos.x, linkPos.y);
            const d = axis === 'x' ? elbowPathH(p1.x, p1.y, linkPos.x, linkPos.y) : elbowPathV(p1.x, p1.y, linkPos.x, linkPos.y);
            return <path d={d} fill="none" stroke="#185FA5" strokeWidth={2} strokeDasharray="6 4" />;
          })()}
          {/* 드래그 중 스냅된 기준선(안내선) — 다른 카드의 변/중앙에 맞춰졌을 때만 표시 */}
          {dragKey != null && snapGuides.x != null && (
            <line x1={snapGuides.x} y1={0} x2={snapGuides.x} y2={3000} stroke="#f43f5e" strokeWidth={1} strokeDasharray="4 4" />
          )}
          {dragKey != null && snapGuides.y != null && (
            <line x1={0} y1={snapGuides.y} x2={3000} y2={snapGuides.y} stroke="#f43f5e" strokeWidth={1} strokeDasharray="4 4" />
          )}
        </svg>

        {visibleExecutives.map(x => {
          const node = { type:'exec', id:x.id, key:`exec:${x.id}`, title:x.title || '대표이사', name:x.name || '', pos_x:x.pos_x, pos_y:x.pos_y };
          const isDragging = !!(dragLivePos && dragLivePos[node.key]);
          const pos = isDragging ? dragLivePos[node.key] : { x: x.pos_x ?? 24, y: x.pos_y ?? 24 };
          const isLinkTarget = linkTargetKey === node.key;
          const isSelected = selectedKeys.has(node.key);
          const dimmed = !nodeMatchesSearch(x.title, x.name, null);
          // 첨부 이미지의 대표이사(맨 위) 카드 스타일 참고: 좌측에 원형 아바타가 카드 테두리를
          // 살짝 넘어 튀어나오고, 그 옆에 직함(굵게)과 성명(작게)을 두 줄로 표시하는 "히어로 카드".
          return (
            <div key={node.key} ref={registerBody(node.key)} style={{
              position:'absolute', left:pos.x, top:pos.y, width:EXEC_CARD_WIDTH, minHeight:estimateBoxHeight(node),
              border: isLinkTarget ? `2px solid ${C.primary}` : `2px solid ${EXEC_STYLE.header}`,
              borderRadius:16, overflow:'visible', background:'#fff', opacity: isDragging ? 0.75 : (dimmed ? 0.25 : 1),
              boxShadow: isSelected ? `0 0 0 3px rgba(24,95,165,0.5), 0 3px 10px rgba(201,162,39,0.25)` : '0 3px 10px rgba(201,162,39,0.25)',
              transition: isDragging ? 'none' : 'border-color 0.1s',
              boxSizing:'border-box', padding:'9px 14px 9px 50px', display:'flex', flexDirection:'column', justifyContent:'center',
            }}>
              <div onPointerDown={onBoxPointerDown(node)}
                title={isSuperAdmin ? '드래그하여 위치 이동' : undefined}
                style={{ cursor: isSuperAdmin ? 'grab' : 'default', userSelect:'none', touchAction:'none' }}>
                <div style={{ position:'absolute', left:-6, top:'50%', transform:'translateY(-50%)', width:46, height:46,
                  borderRadius:'50%', background:EXEC_STYLE.header, color:'#fff', display:'flex', alignItems:'center',
                  justifyContent:'center', fontSize:21, border:'3px solid #fff', boxShadow:'0 2px 6px rgba(0,0,0,0.18)' }}>👑</div>
                <div style={{ fontSize:14, fontWeight:800, color:'#3a2c05' }}>{x.title || '대표이사'}</div>
                {x.name && <div style={{ fontSize:12, color:C.sub, marginTop:2 }}>{x.name}</div>}
              </div>
              {isSuperAdmin && (
                <div onPointerDown={onHandlePointerDown(node)} title="드래그하여 다른 박스와 연결"
                  style={{ position:'absolute', right:-10, bottom:-10, width:22, height:22, borderRadius:'50%',
                    background:C.primary, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:11, cursor:'crosshair', boxShadow:'0 1px 4px rgba(0,0,0,0.3)', touchAction:'none' }}>
                  🔗
                </div>
              )}
            </div>
          );
        })}

        {departments.map((d, i) => {
          const members = membersByDept[d.name] || [];
          const node = { type:'dept', id:d.id, key:`dept:${d.id}`, name:d.name, pos_x:d.pos_x, pos_y:d.pos_y, memberCount:members.length };
          const palette = DEPT_PALETTE[i % DEPT_PALETTE.length];
          const isDragging = !!(dragLivePos && dragLivePos[node.key]);
          const pos = isDragging ? dragLivePos[node.key] : { x: d.pos_x ?? 24, y: d.pos_y ?? 24 };
          const isLinkTarget = linkTargetKey === node.key;
          const isSelected = selectedKeys.has(node.key);
          const dimmed = !nodeMatchesSearch(d.name, null, members);
          return (
            <div key={node.key} ref={registerBody(node.key)} style={{
              position:'absolute', left:pos.x, top:pos.y, width:DEPT_CARD_WIDTH,
              border: isLinkTarget ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
              borderRadius:10, overflow:'hidden', background:'#fff', opacity: isDragging ? 0.75 : (dimmed ? 0.25 : 1),
              boxShadow: isSelected ? `0 0 0 3px rgba(24,95,165,0.5), 0 2px 6px rgba(0,0,0,0.08)` : '0 2px 6px rgba(0,0,0,0.08)',
              transition: isDragging ? 'none' : 'border-color 0.1s',
            }}>
              <div onPointerDown={onBoxPointerDown(node)}
                title={isSuperAdmin ? '드래그하여 위치 이동' : undefined}
                style={{ background:palette.header, color:palette.text, padding:'9px 12px', fontSize:13, fontWeight:700,
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  cursor: isSuperAdmin ? 'grab' : 'default', userSelect:'none', touchAction:'none' }}>
                <span>🏢 {d.name}</span>
                {/* 인원수 배지 — 이 배지를 "클릭"했을 때만 소속 사원 관리 모달이 열린다(그 외 카드
                    영역을 클릭/드래그하면 위치만 이동할 뿐 모달은 열리지 않는다). 배지 자체의
                    pointerdown에서 전파를 막아 헤더의 드래그 시작 핸들러가 함께 실행되지 않게 한다. */}
                <span onPointerDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); if (isSuperAdmin) setManageDeptKey(node.key); }}
                  title={isSuperAdmin ? '클릭: 소속 사원 관리' : undefined}
                  style={{ background:'rgba(255,255,255,0.3)', padding:'1px 8px', borderRadius:12, fontSize:11, fontWeight:800,
                    cursor: isSuperAdmin ? 'pointer' : 'default' }}>
                  +{members.length}
                </span>
              </div>
              {members.length > 0 && (
                <div style={{ padding:'6px', maxHeight:170, overflowY:'auto' }}>
                  {/* 첨부 이미지의 사원 미니카드 스타일 참고: 색상 원형 이니셜 아바타 + 이름 + 직급 */}
                  {members.map(m => (
                    <div key={m.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 7px', margin:'3px 0',
                      border:`1px solid ${C.border}`, borderRadius:8, background:'#fff',
                      opacity: m.employment_status && m.employment_status !== '재직' ? 0.45 : 1 }}>
                      <div style={{ width:22, height:22, borderRadius:'50%', background:palette.header, color:palette.text,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, flexShrink:0 }}>
                        {m.name ? m.name.slice(-2) : '?'}
                      </div>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {m.position ? `${m.position} ${m.name}` : m.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isSuperAdmin && (
                <div onPointerDown={onHandlePointerDown(node)} title="드래그하여 다른 박스와 연결"
                  style={{ position:'absolute', right:-10, bottom:-10, width:22, height:22, borderRadius:'50%',
                    background:C.primary, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:11, cursor:'crosshair', boxShadow:'0 1px 4px rgba(0,0,0,0.3)', touchAction:'none' }}>
                  🔗
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 부서 마스터에 없는(직접입력 등으로 남은) 부서명/미배정 인원은 연결 대상이 아닌 일반 카드로 별도 표시 */}
      {(unmatchedNames.length > 0 || unassignedMembers.length > 0) && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#888', marginBottom:8 }}>기타(부서 마스터 미등록)</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:14 }}>
            {unmatchedNames.map(name => (
              <div key={name} style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                <div style={{ background:'#64748b', color:'#fff', padding:'10px 14px', fontSize:13, fontWeight:700, display:'flex', justifyContent:'space-between' }}>
                  <span>🏢 {name}</span><span style={{ opacity:0.7 }}>{membersByDept[name].length}명</span>
                </div>
                <div style={{ padding:'8px 0' }}>
                  {membersByDept[name].map(m => (
                    <div key={m.id} style={{ display:'flex', justifyContent:'space-between', padding:'7px 14px', fontSize:13,
                      opacity: m.employment_status && m.employment_status !== '재직' ? 0.45 : 1 }}>
                      <span>{m.name}</span><span style={{ color:C.sub }}>{m.position || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {unassignedMembers.length > 0 && (
              <div style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
                <div style={{ background:'#94a3b8', color:'#fff', padding:'10px 14px', fontSize:13, fontWeight:700, display:'flex', justifyContent:'space-between' }}>
                  <span>🏢 미배정</span><span style={{ opacity:0.7 }}>{unassignedMembers.length}명</span>
                </div>
                <div style={{ padding:'8px 0' }}>
                  {unassignedMembers.map(m => (
                    <div key={m.id} style={{ display:'flex', justifyContent:'space-between', padding:'7px 14px', fontSize:13,
                      opacity: m.employment_status && m.employment_status !== '재직' ? 0.45 : 1 }}>
                      <span>{m.name}</span><span style={{ color:C.sub }}>{m.position || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Section>

      {showOrgModal && <OrgStructureModal onClose={() => { setShowOrgModal(false); load(); }} />}
      {/* 2026-08 변경 — EmpNoRuleModal이 이제 자체적으로 GET /org를 다시 호출해 부서 전체
          컬럼(empno_prefix/digits/next 포함)과 회사약어를 불러오므로, 이 화면(OrgChartTab)의
          departments state(=GET /hr/org-chart 응답, 사번 관련 컬럼이 없음)를 넘겨줄 필요가
          없다 — 예전에는 이 prop을 그대로 넘겨서 모달을 열 때마다 저장된 사번설정 값이 보이지
          않고 항상 기본값(미설정)으로 되돌아가 보이는 버그가 있었다. */}
      {showEmpNoModal && <EmpNoRuleModal onClose={() => { setShowEmpNoModal(false); load(); }} />}
      {showDisclosureModal && <DisclosureScopeModal onClose={() => setShowDisclosureModal(false)} />}
      {manageDeptKey && nodeByKey(manageDeptKey) && (
        <DeptMembersModal
          dept={nodeByKey(manageDeptKey)}
          employees={employees}
          onClose={() => setManageDeptKey(null)}
          onChanged={load}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 3) 근태관리
// ═══════════════════════════════════════════════════════════════
const WEEKDAYS = [
  { v:1, l:'월' }, { v:2, l:'화' }, { v:3, l:'수' }, { v:4, l:'목' },
  { v:5, l:'금' }, { v:6, l:'토' }, { v:0, l:'일' },
];
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (isNaN(h)) return null;
  return h * 60 + (m || 0);
};

// ── 근태관리 일 단위 목록 - "상태"/"통계" 컬럼 계산 ──
// 상태는 정상(정상출근+정상퇴근) / 외근 / 출장 / 휴가 / 연차 / 결근 / 휴무(비업무일) / '-'(아직 미확정,
// 오늘·미래 날짜에 아직 출근하지 않은 경우) 중 하나로만 표시한다. 지각 여부는 상태에 반영하지 않고
// (정상 상태를 유지하고) 통계란에서만 "지각 N분"으로 안내한다.
function computeAttendanceStatus(r, { isWorkDay, todayStr }) {
  if (r.leave_type) return r.leave_type === '연차' ? '연차' : '휴가';
  if (r.status === '외근') return '외근';
  if (r.status === '출장') return '출장';
  if (r.check_in) return '정상';
  if (!isWorkDay) return '휴무';
  if (r.date < todayStr) return '결근';
  return '-';
}
// 통계는 상태에 따라 서로 다른 문구를 보여준다:
//   지각(정상이지만 출근시각이 근무시작시각보다 늦음) → "지각 N분"
//   출장/휴가 → "YYYY-MM-DD ~ YYYY-MM-DD" (기간)
//   결근 → "무단결근"
//   연차 → "연차(잔여/총량)"
//   그 외(외근/휴무/'-'/정시출근) → '-'
function computeAttendanceStat(r, status, { workStartMin, leaveBalances }) {
  if (status === '연차') {
    const b = (leaveBalances && leaveBalances[r.user_id]) || { granted_days: 15, remaining_days: 15 };
    return `연차(${b.remaining_days}/${b.granted_days})`;
  }
  if (status === '휴가') return `${r.leave_start} ~ ${r.leave_end}`;
  if (status === '출장') return `${r.trip_start || r.date} ~ ${r.trip_end || r.date}`;
  if (status === '결근') return '무단결근';
  if (status === '정상' && r.check_in && workStartMin != null) {
    const lateMin = toMinutes(r.check_in) - workStartMin;
    if (lateMin > 0) return `지각 ${lateMin}분`;
  }
  return '-';
}

// 근태관리 전용 - 일/월/년 보기를 하나의 날짜 버튼으로 통합한 선택 위젯.
// 버튼(◀ [기간표기] ▶)을 클릭하면 팝업이 열리고, 팝업 상단의 일/월/년 탭으로 보기 단위를 바로 전환할 수 있다.
const periodArrowBtnStyle = { background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28, fontSize:13, cursor:'pointer', color:'#555' };
const periodJumpBtnStyle = { fontSize:12, padding:'5px 10px', background:'#E6F1FB', color:'#185FA5', border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontWeight:600 };

function AttendancePeriodPicker({ viewMode, setViewMode, selDate, setSelDate, selYear, selMonth, setSelYear, setSelMonth, shiftMonth }) {
  const now = new Date();
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(selYear);
  // 팝업 내부 단계 — 'month': 월 선택(+연도 전체보기), 'day': 월 선택 후 '상세'를 눌러 일자를 고르는 단계.
  // 기존에는 팝업 상단에 일/월/년 전환 버튼이 있었지만, 요청에 따라 이를 없애고 아래 흐름으로 통일한다.
  //   1) 월을 고른 뒤 [확인] → 월 단위로 적용
  //   2) 월을 고른 뒤 [상세] → 일자를 고르고 [확인] → 일 단위로 적용
  //   3) [OO년 전체보기] → 연 단위로 즉시 적용
  const [stage, setStage] = useState('month');
  const [pendingMonth, setPendingMonth] = useState(selMonth);
  const [pendingDay, setPendingDay] = useState(() => new Date(selDate).getDate());
  const ref = useRef(null);

  useEffect(() => {
    if (open) {
      setPickerYear(selYear);
      setStage('month');
      setPendingMonth(selMonth);
      setPendingDay(viewMode === 'day' ? new Date(selDate).getDate() : now.getDate());
    }
  }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const shiftDate = (delta) => {
    const d = new Date(selDate);
    d.setDate(d.getDate() + delta);
    setSelDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };
  const shiftPeriod = (delta) => {
    if (viewMode === 'day') shiftDate(delta);
    else if (viewMode === 'year') setSelYear(y => y + delta);
    else shiftMonth(delta);
  };

  const d = new Date(selDate);
  const label = viewMode === 'day' ? `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`
    : viewMode === 'year' ? `${selYear}년`
    : `${selYear}년 ${selMonth}월`;

  const isCurrent = viewMode === 'day' ? selDate === todayISO()
    : viewMode === 'year' ? selYear === now.getFullYear()
    : (selYear === now.getFullYear() && selMonth === now.getMonth() + 1);
  const jumpLabel = viewMode === 'day' ? '오늘' : viewMode === 'year' ? '올해' : '이번달';
  const jumpToNow = () => {
    if (viewMode === 'day') setSelDate(todayISO());
    else if (viewMode === 'year') setSelYear(now.getFullYear());
    else { setSelYear(now.getFullYear()); setSelMonth(now.getMonth() + 1); }
  };

  const daysInPendingMonth = new Date(pickerYear, pendingMonth, 0).getDate();
  const applyMonth = () => { setViewMode('month'); setSelYear(pickerYear); setSelMonth(pendingMonth); setOpen(false); };
  const applyDay = () => {
    const dateStr = `${pickerYear}-${String(pendingMonth).padStart(2, '0')}-${String(pendingDay).padStart(2, '0')}`;
    setViewMode('day'); setSelDate(dateStr); setOpen(false);
  };
  const applyYearFull = () => { setViewMode('year'); setSelYear(pickerYear); setOpen(false); };

  return (
    <div ref={ref} style={{ position:'relative', display:'flex', alignItems:'center', gap:8 }}>
      <button onClick={() => shiftPeriod(-1)} style={periodArrowBtnStyle}>◀</button>
      <span onClick={() => setOpen(v => !v)}
        style={{ fontSize:16, fontWeight:700, color:'#000', minWidth:130, textAlign:'center', cursor:'pointer' }}>
        {label}
      </span>
      <button onClick={() => shiftPeriod(1)} style={periodArrowBtnStyle}>▶</button>
      {!isCurrent && <button onClick={jumpToNow} style={periodJumpBtnStyle}>{jumpLabel}</button>}

      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, background:'#fff',
          border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 4px 14px rgba(0,0,0,0.12)', padding:12, zIndex:20, width:260 }}
          onClick={e => e.stopPropagation()}>

          {stage === 'month' ? (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <button onClick={() => setPickerYear(y => y - 1)} style={navBtnStyle}>◀</button>
                <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
                <button onClick={() => setPickerYear(y => y + 1)} style={navBtnStyle}>▶</button>
              </div>
              <button onClick={applyYearFull} style={{ width:'100%', padding:'7px 0', marginBottom:10,
                background:'#f5f7fa', color:'#555', border:'1px solid #ddd', borderRadius:6, fontSize:12.5, cursor:'pointer', fontWeight:600 }}>
                📅 {pickerYear}년 전체보기
              </button>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6, marginBottom:10 }}>
                {Array.from({ length:12 }, (_, i) => i + 1).map(m => {
                  const active = pendingMonth === m;
                  return (
                    <button key={m}
                      onClick={() => setPendingMonth(m)}
                      style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                        borderColor: active ? '#185FA5' : '#ddd',
                        background:  active ? '#E6F1FB' : '#fff',
                        color:       active ? '#185FA5' : '#555',
                        fontSize:14, cursor:'pointer' }}>
                      {m}월
                    </button>
                  );
                })}
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={() => setStage('day')} style={{ flex:1, padding:'8px 0',
                  background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:6, fontSize:13, cursor:'pointer', fontWeight:600 }}>
                  상세
                </button>
                <button onClick={applyMonth} style={{ flex:1, padding:'8px 0',
                  background:C.primary, color:'#fff', border:'none', borderRadius:6, fontSize:13, cursor:'pointer', fontWeight:700 }}>
                  확인
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <button onClick={() => setStage('month')} style={navBtnStyle}>◀</button>
                <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년 {pendingMonth}월</span>
                <span style={{ width:28 }} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:10 }}>
                {Array.from({ length:daysInPendingMonth }, (_, i) => i + 1).map(day => {
                  const active = pendingDay === day;
                  return (
                    <button key={day}
                      onClick={() => setPendingDay(day)}
                      style={{ padding:'5px 0', borderRadius:6, border:'1px solid',
                        borderColor: active ? '#185FA5' : '#ddd',
                        background:  active ? '#E6F1FB' : '#fff',
                        color:       active ? '#185FA5' : '#555',
                        fontSize:12.5, cursor:'pointer' }}>
                      {day}
                    </button>
                  );
                })}
              </div>
              <button onClick={applyDay} style={{ width:'100%', padding:'8px 0',
                background:C.primary, color:'#fff', border:'none', borderRadius:6, fontSize:13, cursor:'pointer', fontWeight:700 }}>
                확인
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AttendanceSettingsModal({ settings, onClose, onSaved, showMsg }) {
  const [form, setForm] = useState({
    work_start: settings.work_start || '09:00',
    work_end: settings.work_end || '18:00',
    work_days: new Set(String(settings.work_days || '1,2,3,4,5').split(',').map(s => Number(s.trim())).filter(n => !isNaN(n))),
    use_legal_holidays: !!settings.use_legal_holidays,
  });
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ label:'', month:1, day:1, repeat_yearly:false });
  const [addingHoliday, setAddingHoliday] = useState(false);

  const loadHolidays = () => API.get('/hr/attendance/custom-holidays').then(r => setHolidays(r.data || [])).catch(() => {});
  useEffect(() => { loadHolidays(); }, []);

  const toggleDay = (v) => setForm(f => {
    const s = new Set(f.work_days);
    if (s.has(v)) s.delete(v); else s.add(v);
    return { ...f, work_days: s };
  });
  const save = () => {
    setSaving(true);
    API.put('/hr/attendance/settings', {
      work_start: form.work_start, work_end: form.work_end,
      work_days: Array.from(form.work_days).sort().join(','),
      use_legal_holidays: form.use_legal_holidays,
    }).then(() => { showMsg('근태 설정이 저장되었습니다.'); onSaved(); })
      .catch(err => alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.'))
      .finally(() => setSaving(false));
  };
  const addHoliday = () => {
    if (!newHoliday.label.trim()) { alert('휴무일명을 입력해주세요.'); return; }
    setAddingHoliday(true);
    API.post('/hr/attendance/custom-holidays', newHoliday)
      .then(() => { setNewHoliday({ label:'', month:1, day:1, repeat_yearly:false }); loadHolidays(); })
      .catch(err => alert(err.response?.data?.message || '등록 중 오류가 발생했습니다.'))
      .finally(() => setAddingHoliday(false));
  };
  const deleteHoliday = (id) => {
    if (!window.confirm('이 휴무일을 삭제하시겠습니까?')) return;
    API.delete(`/hr/attendance/custom-holidays/${id}`).then(loadHolidays);
  };

  return (
    <Modal title="⚙️ 근태 설정" onClose={onClose} width={460}>
      <Field label="출근시간 / 퇴근시간">
        <div style={{ display:'flex', gap:8 }}>
          <input type="time" value={form.work_start} onChange={e => setForm(f => ({ ...f, work_start:e.target.value }))} style={{ ...input, flex:1 }} />
          <input type="time" value={form.work_end} onChange={e => setForm(f => ({ ...f, work_end:e.target.value }))} style={{ ...input, flex:1 }} />
        </div>
      </Field>
      <Field label="업무일">
        <div style={{ display:'flex', gap:6 }}>
          {WEEKDAYS.map(w => {
            const active = form.work_days.has(w.v);
            return (
              <button key={w.v} onClick={() => toggleDay(w.v)}
                style={{ flex:1, padding:'8px 0', borderRadius:6, border:'1px solid', cursor:'pointer', fontSize:13, fontWeight:600,
                  borderColor: active ? C.primary : '#ddd', background: active ? '#E6F1FB' : '#fff', color: active ? C.primary : '#888' }}>
                {w.l}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="휴무일 지정">
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13.5, color:'#333', cursor:'pointer', padding:'2px 0 10px' }}>
          <input type="checkbox" checked={form.use_legal_holidays}
            onChange={e => setForm(f => ({ ...f, use_legal_holidays:e.target.checked }))} style={{ width:16, height:16, cursor:'pointer' }} />
          법정공휴일(대체공휴일 포함)을 휴무일로 지정
        </label>

        <div style={{ fontSize:12.5, color:'#888', fontWeight:600, marginBottom:6 }}>기타 휴무일</div>
        <div style={{ border:'1px solid #eee', borderRadius:8, maxHeight:150, overflowY:'auto', marginBottom:8 }}>
          {holidays.length === 0 && <div style={{ padding:'12px 0', textAlign:'center', color:'#bbb', fontSize:12.5 }}>등록된 기타 휴무일이 없습니다.</div>}
          {holidays.map(h => (
            <div key={h.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderBottom:'1px solid #f5f5f5' }}>
              <span style={{ fontSize:12.5, color:'#555', width:44, flexShrink:0 }}>{h.month}월 {h.day}일</span>
              <span style={{ fontSize:13, color:'#333', flex:1 }}>{h.label}</span>
              <span style={{ fontSize:11, padding:'2px 6px', borderRadius:4, background: h.repeat_yearly ? '#E6F1FB' : '#f5f5f5', color: h.repeat_yearly ? C.primary : '#888' }}>
                {h.repeat_yearly ? '매년' : `${h.year}년만`}
              </span>
              <button onClick={() => deleteHoliday(h.id)} style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:12.5 }}>삭제</button>
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:6, marginBottom:6 }}>
          <input placeholder="휴무일명" value={newHoliday.label} onChange={e => setNewHoliday(h => ({ ...h, label:e.target.value }))}
            style={{ ...input, flex:1 }} />
          <select value={newHoliday.month} onChange={e => setNewHoliday(h => ({ ...h, month:Number(e.target.value) }))} style={{ ...input, width:64 }}>
            {Array.from({ length:12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
          </select>
          <select value={newHoliday.day} onChange={e => setNewHoliday(h => ({ ...h, day:Number(e.target.value) }))} style={{ ...input, width:64 }}>
            {Array.from({ length:31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}일</option>)}
          </select>
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5, color:'#555', cursor:'pointer' }}>
            <input type="checkbox" checked={newHoliday.repeat_yearly}
              onChange={e => setNewHoliday(h => ({ ...h, repeat_yearly:e.target.checked }))} style={{ width:14, height:14, cursor:'pointer' }} />
            매년 반복 (해제 시 이번 년도만 적용)
          </label>
          <button onClick={addHoliday} disabled={addingHoliday}
            style={{ padding:'6px 12px', borderRadius:6, border:'none', background:'#16a34a', color:'#fff', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
            + 추가
          </button>
        </div>
      </Field>
      <div style={{ display:'flex', gap:8, marginTop:18 }}>
        <button onClick={onClose} style={{ flex:1, ...btn('#f0f0f0', '#555') }}>취소</button>
        <button onClick={save} disabled={saving} style={{ flex:1, ...btn(C.primary) }}>{saving ? '저장중...' : '저장'}</button>
      </div>
    </Modal>
  );
}

// 월/년 보기에서 "상세" 클릭시 해당 사원의 기간 내 일별 출퇴근 내역을 보여주는 모달.
function AttendanceDetailModal({ row, onClose, canDelete, onReset }) {
  // 상세 모달 안에서도 일자별로 근태 기록을 초기화(삭제)할 수 있어야 한다는 요청에 따라,
  // 모달 로컬에 records 사본을 두고 초기화 시 즉시 화면에서도 지워지도록 한다(부모 목록은
  // onReset이 다시 불러온다).
  const [records, setRecords] = useState(row.records);
  const doReset = (r) => {
    if (!window.confirm(`${r.date} 근태 기록을 초기화하시겠습니까?`)) return;
    onReset(r.id);
    setRecords(prev => prev.filter(x => x.id !== r.id));
  };
  return (
    <Modal title={`📋 ${row.department || '-'} · ${row.name} 근태 상세`} onClose={onClose} width={620}>
      <div style={{ overflowX:'auto', maxHeight:420, overflowY:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['날짜','출근','퇴근','상태','비고',''].map((h,i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {records.length === 0 && <EmptyRow span={6} />}
            {records.map(r => (
              <tr key={r.id}>
                <td style={td}>{r.date}</td><td style={td}>{r.check_in || '-'}</td><td style={td}>{r.check_out || '-'}</td>
                <td style={td}>{r.display_status || r.status}</td><td style={td}>{r.note || '-'}</td>
                <td style={td}>{canDelete && <button onClick={() => doReset(r)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>초기화</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

const ATT_DAY_SORT_KEYS = ['employee_no', 'department', 'position', 'name', 'check_in', 'check_out', 'field_time', 'trip_time', '_status', '_stat', null];
const ATT_AGG_SORT_KEYS = ['employee_no', 'department', 'position', 'name', 'checkInCount', 'checkOutCount', 'status', null, null, null];

function AttendanceTab({ employees, perm, user, showMsg, headerActionsEl, tabBarActionsEl }) {
  const canWrite = perm.hasDetail('hr-management', 'attendance_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.attendance)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.attendance);
  const canDelete = perm.hasDetail('hr-management', 'delete_allowed')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.attendance)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.attendance);
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({ work_start:'09:00', work_end:'18:00', work_days:'1,2,3,4,5' });
  const [showSettings, setShowSettings] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('day'); // 'day' | 'month' | 'year' — 기본값은 오늘 날짜(일 단위)

  const now = new Date();
  const [selDate, setSelDate] = useState(todayISO());
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const month = `${selYear}-${String(selMonth).padStart(2, '0')}`;
  const shiftMonth = (delta) => {
    let m = selMonth + delta, y = selYear;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setSelMonth(m); setSelYear(y);
  };

  const loadSettings = () => API.get('/hr/attendance/settings').then(r => setSettings(r.data || {})).catch(() => {});
  useEffect(() => { loadSettings(); }, []);

  // 근태설정(법정공휴일 체크박스 + 기타 휴무일)에서 지정된 휴무일 — 출근률 등 통계의
  // "영업일수" 계산에서 제외하기 위해 선택된 연도의 날짜 목록을 조회해둔다.
  const [holidayDates, setHolidayDates] = useState(new Set());
  useEffect(() => {
    API.get('/hr/attendance/holidays', { params: { year: selYear } })
      .then(r => setHolidayDates(new Set((r.data || []).map(h => h.date))))
      .catch(() => setHolidayDates(new Set()));
  }, [selYear, settings.use_legal_holidays]); // eslint-disable-line

  const load = () => {
    const params = viewMode === 'day' ? { date: selDate } : viewMode === 'year' ? { year: selYear } : { month };
    API.get('/hr/attendance', { params }).then(r => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [viewMode, selDate, month, selYear]); // eslint-disable-line

  // AttendanceDetailModal(상세 보기 팝업) 안에서 쓰는 초기화 — 모달 쪽에서 이미 확인창을
  // 띄우므로 여기서는 바로 삭제 후 뒤에 깔린 목록만 새로고침한다.
  const resetRecord = (id) => {
    API.delete(`/hr/attendance/${id}`).then(() => { showMsg('초기화되었습니다.'); load(); }); };

  const isAgg = viewMode !== 'day';

  // ── 월/년 단위(직원별 집계) ──
  const workDaysSet = useMemo(() => new Set(String(settings.work_days || '1,2,3,4,5').split(',').map(s => Number(s.trim())).filter(n => !isNaN(n))), [settings.work_days]);
  const workStartMin = toMinutes(settings.work_start);
  const workEndMin = toMinutes(settings.work_end);

  // ── 일 단위(직원 전체 목록) ── 선택된 날짜가 업무일인지(요일+공휴일 기준), 그리고 그해의
  // 연차 잔여/총량(통계란 "연차(잔여/총량)" 표시용)을 함께 준비한다.
  const isWorkDay = useMemo(() => {
    if (viewMode !== 'day') return true;
    const dow = new Date(selDate).getDay();
    return workDaysSet.has(dow) && !holidayDates.has(selDate);
  }, [viewMode, selDate, workDaysSet, holidayDates]);

  const [leaveBalances, setLeaveBalances] = useState({});
  useEffect(() => {
    if (viewMode !== 'day') return;
    const y = new Date(selDate).getFullYear();
    API.get('/hr/leaves/balances', { params: { year: y } }).then(r => setLeaveBalances(r.data || {})).catch(() => setLeaveBalances({}));
  }, [viewMode, selDate]);

  const dayRowsComputed = useMemo(() => {
    if (isAgg) return [];
    const todayStr = todayISO();
    return rows.map(r => {
      const _status = computeAttendanceStatus(r, { isWorkDay, todayStr });
      const _stat = computeAttendanceStat(r, _status, { workStartMin, leaveBalances });
      return { ...r, _status, _stat };
    });
  }, [rows, isAgg, isWorkDay, workStartMin, leaveBalances]);

  const filteredDayRows = filterBySearch(dayRowsComputed, search, ['name', 'department']);
  const { sorted: sortedDayRows, sortKey: daySortKey, sortDir: daySortDir, toggleSort: dayToggleSort } = useSort(filteredDayRows, 'name', 'asc');
  const countBusinessDays = (from, to) => {
    let c = 0; const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (d <= end) {
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (workDaysSet.has(d.getDay()) && !holidayDates.has(dateStr)) c++;
      d.setDate(d.getDate() + 1);
    }
    return c;
  };

  const aggRows = useMemo(() => {
    if (!isAgg) return [];
    const map = {};
    rows.forEach(r => {
      if (!map[r.user_id]) map[r.user_id] = { user_id:r.user_id, department:r.department, employee_no:r.employee_no, name:r.name, records:[] };
      map[r.user_id].records.push(r);
    });
    let periodFrom, periodTo;
    if (viewMode === 'month') {
      periodFrom = new Date(selYear, selMonth - 1, 1);
      const lastDay = new Date(selYear, selMonth, 0);
      const isCurrentMonth = selYear === now.getFullYear() && selMonth === now.getMonth() + 1;
      periodTo = isCurrentMonth ? now : lastDay;
    } else {
      periodFrom = new Date(selYear, 0, 1);
      const isCurrentYear = selYear === now.getFullYear();
      periodTo = isCurrentYear ? now : new Date(selYear, 11, 31);
    }
    const businessDays = countBusinessDays(periodFrom, periodTo);

    return Object.values(map).map(u => {
      const recordsSorted = [...u.records].sort((a, b) => (a.date < b.date ? 1 : -1));
      const checkInCount = u.records.filter(r => r.check_in).length;
      const checkOutCount = u.records.filter(r => r.check_out).length;
      const lateRows = u.records.filter(r => r.status === '지각');
      const overtimeRows = u.records.filter(r => r.check_out && workEndMin != null && toMinutes(r.check_out) > workEndMin);
      const attendanceRate = businessDays > 0 ? Math.round((checkInCount / businessDays) * 100) : 0;
      const lateRate = checkInCount > 0 ? Math.round((lateRows.length / checkInCount) * 100) : 0;
      const overtimeRate = checkInCount > 0 ? Math.round((overtimeRows.length / checkInCount) * 100) : 0;
      const avgLateMin = lateRows.length ? Math.round(lateRows.reduce((s, r) => s + Math.max(0, toMinutes(r.check_in) - (workStartMin || 0)), 0) / lateRows.length) : 0;
      const avgOvertimeMin = overtimeRows.length ? Math.round(overtimeRows.reduce((s, r) => s + Math.max(0, toMinutes(r.check_out) - (workEndMin || 0)), 0) / overtimeRows.length) : 0;
      return {
        user_id: u.user_id, department: u.department, employee_no: u.employee_no, name: u.name,
        checkInCount, checkOutCount,
        status: recordsSorted[0]?.display_status || '-',
        statText: `지각 ${lateRows.length}회 / 야근 ${overtimeRows.length}회`,
        noteText: `출근률 ${attendanceRate}% · 지각률 ${lateRate}%(평균 ${avgLateMin}분) · 야근률 ${overtimeRate}%(평균 ${avgOvertimeMin}분)`,
        records: recordsSorted,
      };
    });
  }, [rows, isAgg, viewMode, selYear, selMonth, workDaysSet, workStartMin, workEndMin, holidayDates]); // eslint-disable-line

  const filteredAggRows = filterBySearch(aggRows, search, ['name', 'department']);
  const { sorted: sortedAggRows, sortKey: aggSortKey, sortDir: aggSortDir, toggleSort: aggToggleSort } = useSort(filteredAggRows, 'name', 'asc');

  useEffect(() => { setPage(1); }, [filteredDayRows.length, filteredAggRows.length, pageSize, viewMode]);
  const totalLen = isAgg ? sortedAggRows.length : sortedDayRows.length;
  const attTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalLen / pageSize));
  const pagedDayRows = pageSize === 'all' ? sortedDayRows : sortedDayRows.slice((page - 1) * pageSize, page * pageSize);
  const pagedAggRows = pageSize === 'all' ? sortedAggRows : sortedAggRows.slice((page - 1) * pageSize, page * pageSize);

  const periodLabel = viewMode === 'day' ? selDate : viewMode === 'year' ? `${selYear}년` : month;
  const printList = () => {
    if (isAgg) {
      printListPopup({
        title: `근태관리 (${periodLabel})`, menuLabel: '인사관리(근태관리)',
        columns: ['사번', '부서', '직급', '이름', '출근', '퇴근', '상태', '통계', '비고'],
        rows: sortedAggRows.map(r => [r.employee_no || '-', r.department || '-', r.position || '-', r.name, r.checkInCount, r.checkOutCount, r.status, r.statText, r.noteText]),
      });
    } else {
      printListPopup({
        title: `근태관리 (${periodLabel})`, menuLabel: '인사관리(근태관리)',
        columns: ['사번', '부서', '직급', '이름', '출근', '퇴근', '외근', '출장', '상태', '통계'],
        rows: sortedDayRows.map(r => [r.employee_no || '-', r.department || '-', r.position || '-', r.name, r.check_in || '-', r.check_out || '-', r.field_time || '-', r.trip_time || '-', r._status, r._stat]),
      });
    }
  };
  const downloadList = () => downloadListCsv(`/hr/attendance/export?month=${viewMode === 'year' ? `${selYear}-01` : month}`, `IMS_근태관리_${periodLabel}.csv`, '인사관리(근태관리)');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <HeaderPortal target={headerActionsEl}>
        <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
        <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
        {canWrite && <button onClick={() => setShowSettings(true)} style={btn('#64748B')}>⚙️ 설정</button>}
      </HeaderPortal>
      <HeaderPortal target={tabBarActionsEl}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:200 }} />
        <AttendancePeriodPicker viewMode={viewMode} setViewMode={setViewMode}
          selDate={selDate} setSelDate={setSelDate}
          selYear={selYear} selMonth={selMonth} setSelYear={setSelYear} setSelMonth={setSelMonth} shiftMonth={shiftMonth} />
        <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />
      </HeaderPortal>

      <Section>
        <div style={{ overflowX:'auto', maxHeight:480, overflowY:'auto' }}>
          {isAgg ? (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['사번','부서','직급','이름','출근','퇴근','상태','통계','비고','상세'].map((h, i) => (
                <SortTh key={i} label={h} sortKey={ATT_AGG_SORT_KEYS[i]} currentKey={aggSortKey} currentDir={aggSortDir} onSort={aggToggleSort} />
              ))}</tr></thead>
              <tbody>
                {pagedAggRows.length === 0 && <EmptyRow span={10} />}
                {pagedAggRows.map(r => (
                  <tr key={r.user_id}>
                    <td style={td}>{r.employee_no || '-'}</td><td style={td}>{r.department || '-'}</td><td style={td}>{r.position || '-'}</td><td style={td}>{r.name}</td>
                    <td style={td}>{r.checkInCount}회</td><td style={td}>{r.checkOutCount}회</td>
                    <td style={td}>{r.status}</td>
                    <td style={{ ...td, fontSize:12, color:'#888' }}>{r.statText}</td>
                    <td style={{ ...td, fontSize:12, color:'#888' }}>{r.noteText}</td>
                    <td style={td}><button onClick={() => setDetailRow(r)} style={{ background:'none', border:'none', color:C.primary, fontSize:12, cursor:'pointer', fontWeight:600 }}>상세</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['사번','부서','직급','이름','출근','퇴근','외근','출장','상태','통계','상세'].map((h, i) => (
                <SortTh key={i} label={h} sortKey={ATT_DAY_SORT_KEYS[i]} currentKey={daySortKey} currentDir={daySortDir} onSort={dayToggleSort} />
              ))}</tr></thead>
              <tbody>
                {pagedDayRows.length === 0 && <EmptyRow span={11} />}
                {pagedDayRows.map(r => (
                  <tr key={r.user_id}>
                    <td style={td}>{r.employee_no || '-'}</td><td style={td}>{r.department || '-'}</td><td style={td}>{r.position || '-'}</td><td style={td}>{r.name}</td>
                    <td style={td}>{r.check_in || '-'}</td><td style={td}>{r.check_out || '-'}</td>
                    <td style={td}>{r.field_time || '-'}</td><td style={td}>{r.trip_time || '-'}</td>
                    <td style={td}>{r._status}</td>
                    <td style={{ ...td, fontSize:12, color:'#888' }}>{r._stat}</td>
                    <td style={td}>
                      {r.attendance_id ? (
                        <button onClick={() => setDetailRow({
                          department: r.department, name: r.name,
                          records: [{ id: r.attendance_id, date: r.date, check_in: r.check_in, check_out: r.check_out, status: r.status, display_status: r._status, note: r.note }],
                        })} style={{ background:'none', border:'none', color:C.primary, fontSize:12, cursor:'pointer', fontWeight:600 }}>상세</button>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={attTotalPages} />}
      </Section>

      {showSettings && (
        <AttendanceSettingsModal settings={settings} showMsg={showMsg}
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); loadSettings(); load(); }} />
      )}
      {detailRow && <AttendanceDetailModal row={detailRow} onClose={() => setDetailRow(null)} canDelete={canDelete} onReset={resetRecord} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 4) 휴가관리
// ═══════════════════════════════════════════════════════════════
// 요청에 따라 개편: 신청/승인 목록이 아니라 "산정기간·부서·직급·이름 + 유형별 부여/합계/사용/
// 잔여/상태" 로스터를 메인 목록으로 보여준다. 유형별 부여는 연차(자동생성)/계절·명절·상조·결혼·
// 포상(휴가설정=구 수동부여)로 관리하고, 신청/승인/반려는 행을 클릭하면 뜨는 직원별 상세모달로
// 옮겼다(추후 전자결재에 휴가신청 워크플로우가 생기면 승인된 건이 여기 그대로 반영될 예정).
const LEAVE_ROSTER_TYPES = ['연차', '계절', '명절', '상조', '결혼', '포상'];
const LEAVE_TYPE_LABELS = { 연차:'연차휴가', 계절:'계절휴가', 명절:'명절휴가', 상조:'상조휴가', 결혼:'결혼휴가', 포상:'포상휴가' };
const LEAVE_ROSTER_SORT_KEYS = [null, 'employee_no', 'department', 'position', 'name',
  ...LEAVE_ROSTER_TYPES.map(t => `granted_${t}`), 'total_granted', 'total_used', 'total_remaining', 'status'];
const leaveStatusColor = (s) => s === '휴가' ? { bg:'#fef3c7', c:'#b45309' } : s === '출장' ? { bg:'#e0e7ff', c:'#4338ca' } : { bg:'#dcfce7', c:'#16a34a' };
// 산정기간이 연도 단위 고정이라 근태관리의 일/월/년 통합 팝업 대신, 동일한 화살표 버튼 스타일만
// 재사용한 단순 연도 선택기를 쓴다(같은 "◀ 기간표기 ▶" 시각언어를 그대로 적용).
function YearOnlyPicker({ year, setYear }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <button onClick={() => setYear(y => y - 1)} style={periodArrowBtnStyle}>◀</button>
      <div style={{ ...periodJumpBtnStyle, cursor:'default' }}>{year}년</div>
      <button onClick={() => setYear(y => y + 1)} style={periodArrowBtnStyle}>▶</button>
    </div>
  );
}

function LeaveTab({ employees, perm, user, showMsg, headerActionsEl, tabBarActionsEl }) {
  const canApprove = perm.hasDetail('hr-management', 'leave_approve')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.leave)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.leave);
  const [rows, setRows] = useState([]);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantForm, setGrantForm] = useState({ user_id:'', year:new Date().getFullYear(), leave_type:'계절', granted_days:1 });
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [detailUser, setDetailUser] = useState(null); // { user_id, name }

  const load = () => API.get('/hr/leaves/summary', { params:{ year } })
    .then(r => setRows((r.data || []).map(row => {
      const flat = { ...row };
      LEAVE_ROSTER_TYPES.forEach(t => { flat[`granted_${t}`] = row.byType[t].granted; });
      return flat;
    })))
    .catch(() => setRows([]));
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const grant = () => {
    if (!grantForm.user_id) return alert('대상자를 선택해주세요.');
    API.post('/hr/leaves/grant', grantForm).then(() => { showMsg(`${grantForm.leave_type} 휴가가 부여되었습니다.`); setShowGrantModal(false); load(); })
      .catch(err => alert(err.response?.data?.message || '부여 실패'));
  };
  // 입사일 기준 근로기준법 자동계산 — 재직중이고 입사일이 등록된 전체 사원을 대상으로 일괄 부여/갱신
  const autoGrantAll = () => {
    if (!window.confirm(`${year}년 기준으로 전체 사원의 연차를 입사일 기준(근로기준법)으로 자동 계산해서 부여하시겠습니까?\n기존 부여값은 새로 계산된 값으로 덮어씁니다.`)) return;
    API.post('/hr/leaves/auto-grant-all', { year })
      .then(r => { showMsg(r.data.message || '연차가 자동 생성되었습니다.'); load(); })
      .catch(err => alert(err.response?.data?.message || '자동생성 실패'));
  };

  const filteredRows = filterBySearch(rows, search, ['name', 'department']);
  const { sorted: sortedRows, sortKey: leaveSortKey, sortDir: leaveSortDir, toggleSort: leaveToggleSort } = useSort(filteredRows, 'name', 'asc');
  useEffect(() => { setPage(1); }, [filteredRows.length, pageSize]);
  const leaveTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const pagedRows = pageSize === 'all' ? sortedRows : sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const leaveHeaders = ['산정기간','사번','부서','직급','이름', ...LEAVE_ROSTER_TYPES.map(t => LEAVE_TYPE_LABELS[t]), '합계','사용','잔여','상태'];
  const printList = () => printListPopup({
    title: `휴가관리 (${year}년)`, menuLabel: '인사관리(휴가관리)',
    columns: leaveHeaders,
    rows: sortedRows.map(r => [r.period, r.employee_no || '-', r.department || '-', r.position || '-', r.name,
      ...LEAVE_ROSTER_TYPES.map(t => r.byType[t].granted), r.total_granted, r.total_used, r.total_remaining, r.status]),
  });
  const downloadList = () => downloadListCsv(`/hr/leaves/export-summary?year=${year}`, `IMS_휴가관리_${year}.csv`, '인사관리(휴가관리)');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <HeaderPortal target={headerActionsEl}>
        <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
        <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
        {canApprove && (
          <button onClick={autoGrantAll} style={btn('#7C3AED')} title="입사일 기준 근로기준법대로 전체 사원 연차를 자동 계산합니다.">🔄 연차 자동생성(전체)</button>
        )}
        {canApprove && (
          <button onClick={() => { setGrantForm(f => ({ ...f, year })); setShowGrantModal(true); }} style={btn('#16a34a')}>휴가설정</button>
        )}
      </HeaderPortal>

      <HeaderPortal target={tabBarActionsEl}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:200 }} />
        <YearOnlyPicker year={year} setYear={setYear} />
        <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />
      </HeaderPortal>

      <Section>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{leaveHeaders.map((h,i) => (
              <SortTh key={i} label={h} sortKey={LEAVE_ROSTER_SORT_KEYS[i]} currentKey={leaveSortKey} currentDir={leaveSortDir} onSort={leaveToggleSort} />
            ))}</tr></thead>
            <tbody>
              {pagedRows.length === 0 && <EmptyRow span={leaveHeaders.length} />}
              {pagedRows.map(r => {
                const sc = leaveStatusColor(r.status);
                return (
                  <tr key={r.user_id} onClick={() => setDetailUser({ user_id:r.user_id, name:r.name })} style={{ cursor:'pointer' }}>
                    <td style={td}>{r.period}</td>
                    <td style={td}>{r.employee_no || '-'}</td>
                    <td style={td}>{r.department || '-'}</td><td style={td}>{r.position || '-'}</td><td style={td}>{r.name}</td>
                    {LEAVE_ROSTER_TYPES.map(t => <td key={t} style={td}>{r.byType[t].granted || 0}</td>)}
                    <td style={{ ...td, fontWeight:700 }}>{r.total_granted}</td>
                    <td style={td}>{r.total_used}</td>
                    <td style={td}>{r.total_remaining}</td>
                    <td style={td}><span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:sc.bg, color:sc.c }}>{r.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={leaveTotalPages} />}
      </Section>

      {showGrantModal && (
        <Modal title="🌴 휴가설정" onClose={() => setShowGrantModal(false)}>
          <Field label="대상자"><EmpSelect employees={employees} value={grantForm.user_id} onChange={v => setGrantForm(f => ({ ...f, user_id:v }))} /></Field>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="휴가종류">
              <select style={{ ...input, width:'100%' }} value={grantForm.leave_type} onChange={e => setGrantForm(f => ({ ...f, leave_type:e.target.value }))}>
                {LEAVE_ROSTER_TYPES.map(t => <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field label="연도"><input type="number" style={{ ...input, width:'100%' }} value={grantForm.year} onChange={e => setGrantForm(f => ({ ...f, year:e.target.value }))} /></Field>
          </div>
          <Field label="부여일수"><input type="number" step="0.5" style={{ ...input, width:'100%' }} value={grantForm.granted_days} onChange={e => setGrantForm(f => ({ ...f, granted_days:e.target.value }))} /></Field>
          <div style={{ fontSize:12, color:'#aaa', marginBottom:12 }}>
            ※ 연차의 근로기준법 기준 일괄 계산은 상단 "🔄 연차 자동생성(전체)" 버튼을 이용하세요. 여기서는 특정 대상자에게 유형별 일수를 직접 지정해 부여합니다.
          </div>
          <button onClick={grant} style={{ ...btn(C.primary), width:'100%', padding:'10px 0' }}>부여</button>
        </Modal>
      )}

      {detailUser && (
        <LeaveEmployeeDetailModal userId={detailUser.user_id} name={detailUser.name} year={year}
          canApprove={canApprove} myUserId={user?.id} showMsg={showMsg}
          onClose={() => setDetailUser(null)} reload={load} />
      )}
    </div>
  );
}

// 휴가관리 로스터에서 행을 클릭하면 뜨는 직원별 상세 — 유형별 부여/사용/잔여 요약과, 그 직원의
// 휴가 신청/승인/반려 이력을 함께 보여준다. 본인 화면일 때만 "+ 휴가 신청" 버튼이 보인다(다른
// 직원 이름으로 대신 신청하면 실제로는 관리자 본인 이름으로 접수되는 것을 방지하기 위함).
function LeaveEmployeeDetailModal({ userId, name, year, canApprove, myUserId, onClose, showMsg, reload }) {
  const [history, setHistory] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leave_type:'연차', start_date:'', end_date:'', days:1, reason:'' });
  // 관리자등록: 관리자가 직원 대신 휴가 사용 내역을 직접 등록(등록 즉시 '승인' 상태)하는 폼.
  // 요청사항: "관리자가 임의로 휴가사용을 등록할 수 있는 폼", "폼에 사유도 포함되어야 함".
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [adminForm, setAdminForm] = useState({ leave_type:'연차', start_date:'', end_date:'', days:1, reason:'' });
  const mine = userId === myUserId;

  const load = () => API.get('/hr/leaves', { params:{ user_id:userId } }).then(r => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const refreshAll = () => { load(); reload(); };

  const submit = () => {
    if (!form.start_date || !form.end_date || !form.days) return alert('입력값을 확인해주세요.');
    API.post('/hr/leaves', form).then(() => { showMsg('휴가가 신청되었습니다.'); setShowForm(false);
      setForm({ leave_type:'연차', start_date:'', end_date:'', days:1, reason:'' }); refreshAll(); })
      .catch(err => alert(err.response?.data?.message || '신청 실패'));
  };
  const submitAdmin = () => {
    if (!adminForm.start_date || !adminForm.end_date || !adminForm.days) return alert('입력값을 확인해주세요.');
    if (!adminForm.reason || !adminForm.reason.trim()) return alert('사유를 입력해주세요.');
    API.post('/hr/leaves/admin', { ...adminForm, user_id:userId }).then(() => { showMsg('휴가 사용이 등록되었습니다.'); setShowAdminForm(false);
      setAdminForm({ leave_type:'연차', start_date:'', end_date:'', days:1, reason:'' }); refreshAll(); })
      .catch(err => alert(err.response?.data?.message || '등록 실패'));
  };
  const decide = (id, status) => {
    const reject_reason = status === '반려' ? window.prompt('반려 사유를 입력해주세요.') : undefined;
    if (status === '반려' && reject_reason === null) return;
    API.patch(`/hr/leaves/${id}/decide`, { status, reject_reason }).then(() => { showMsg(`${status}되었습니다.`); refreshAll(); });
  };
  const cancel = (id) => { if (!window.confirm('취소하시겠습니까?')) return;
    API.delete(`/hr/leaves/${id}`).then(() => { showMsg('취소되었습니다.'); refreshAll(); })
      .catch(err => alert(err.response?.data?.message || '취소 실패')); };

  const statusColor = (s) => s === '승인' ? { bg:'#dcfce7', c:'#16a34a' } : s === '반려' ? { bg:'#fee2e2', c:'#dc2626' } : s === '취소' ? { bg:'#f1f5f9', c:'#888' } : { bg:'#fef3c7', c:'#b45309' };

  return (
    <Modal title={`🌴 ${name}님 휴가 내역 (${year}년)`} width={620} onClose={onClose}
      headerActions={
        <div style={{ display:'flex', gap:6 }}>
          {mine && <button onClick={() => setShowForm(true)} style={{ ...btn(C.primary), padding:'5px 10px', fontSize:12 }}>+ 휴가 신청</button>}
          {canApprove && <button onClick={() => setShowAdminForm(true)} style={{ ...btn('#64748B'), padding:'5px 10px', fontSize:12 }}>🛠️ 관리자등록</button>}
        </div>
      }>
      <div style={{ overflowX:'auto', marginBottom:8 }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['종류','기간','일수','사유','상태',''].map((h,i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {history.length === 0 && <EmptyRow span={6} />}
            {history.map(r => {
              const sc = statusColor(r.status);
              return (
                <tr key={r.id}>
                  <td style={td}>{r.leave_type}</td>
                  <td style={td}>{r.start_date} ~ {r.end_date}</td><td style={td}>{r.days}</td>
                  <td style={td}>{r.reason || '-'}</td>
                  <td style={td}><span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:sc.bg, color:sc.c }}>{r.status}</span></td>
                  <td style={td}>
                    {r.status === '대기' && canApprove && (
                      <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                        <button onClick={() => decide(r.id, '승인')} style={{ ...btn('#16a34a'), padding:'4px 9px', fontSize:11 }}>승인</button>
                        <button onClick={() => decide(r.id, '반려')} style={{ ...btn('#dc2626'), padding:'4px 9px', fontSize:11 }}>반려</button>
                      </div>
                    )}
                    {r.status === '대기' && mine && !canApprove && (
                      <button onClick={() => cancel(r.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>취소</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:14, marginTop:10 }}>
          <Field label="종류">
            <select style={{ ...input, width:'100%' }} value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type:e.target.value }))}>
              {LEAVE_ROSTER_TYPES.map(t => <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>)}
            </select>
          </Field>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="시작일"><input type="date" style={{ ...input, width:'100%' }} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date:e.target.value }))} /></Field>
            <Field label="종료일"><input type="date" style={{ ...input, width:'100%' }} value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date:e.target.value }))} /></Field>
          </div>
          <Field label="일수"><input type="number" step="0.5" style={{ ...input, width:120 }} value={form.days} onChange={e => setForm(f => ({ ...f, days:e.target.value }))} /></Field>
          <Field label="사유"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={form.reason} onChange={e => setForm(f => ({ ...f, reason:e.target.value }))} /></Field>
          <button onClick={submit} style={{ ...btn(C.primary), width:'100%', padding:'10px 0' }}>신청</button>
        </div>
      )}

      {showAdminForm && (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:14, marginTop:10 }}>
          <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>관리자가 {name}님의 휴가 사용 내역을 직접 등록합니다. 등록 즉시 '승인' 상태로 반영됩니다.</div>
          <Field label="종류">
            <select style={{ ...input, width:'100%' }} value={adminForm.leave_type} onChange={e => setAdminForm(f => ({ ...f, leave_type:e.target.value }))}>
              {LEAVE_ROSTER_TYPES.map(t => <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>)}
            </select>
          </Field>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="시작일"><input type="date" style={{ ...input, width:'100%' }} value={adminForm.start_date} onChange={e => setAdminForm(f => ({ ...f, start_date:e.target.value }))} /></Field>
            <Field label="종료일"><input type="date" style={{ ...input, width:'100%' }} value={adminForm.end_date} onChange={e => setAdminForm(f => ({ ...f, end_date:e.target.value }))} /></Field>
          </div>
          <Field label="일수"><input type="number" step="0.5" style={{ ...input, width:120 }} value={adminForm.days} onChange={e => setAdminForm(f => ({ ...f, days:e.target.value }))} /></Field>
          <Field label="사유 (필수)"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={adminForm.reason} onChange={e => setAdminForm(f => ({ ...f, reason:e.target.value }))} /></Field>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={submitAdmin} style={{ ...btn('#64748B'), flex:1, padding:'10px 0' }}>등록</button>
            <button onClick={() => setShowAdminForm(false)} style={{ ...btn('#f0f0f0', '#555'), flex:1, padding:'10px 0' }}>취소</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════
// 5) 인사발령
// ═══════════════════════════════════════════════════════════════
const APPOINT_TYPES = ['신규채용','전보','승진','강등','휴직','복직','파견','퇴직'];
const APPOINT_COLORS = {
  '신규채용': { bg:'#e0f2fe', c:'#0369a1' }, '전보': { bg:'#ede9fe', c:'#7c3aed' },
  '승진': { bg:'#dcfce7', c:'#16a34a' }, '강등': { bg:'#ffedd5', c:'#c2410c' },
  '휴직': { bg:'#fef3c7', c:'#b45309' }, '복직': { bg:'#ccfbf1', c:'#0d9488' },
  '파견': { bg:'#e0e7ff', c:'#4338ca' }, '퇴직': { bg:'#fee2e2', c:'#dc2626' },
};

// 인사발령 — 구분별 필터 칩 + 색상 배지 + 부서/직급 변경 전후 한눈에 보기 형태로 개편.
// (요청사항 #5: "보기도 편하고 관리도 편한 화면으로 추천하여 구성")
const APPOINT_SORT_KEYS = ['effective_date','employee_no','name','appoint_type','to_department','to_position','reason', null];

function AppointmentTab({ employees, perm, showMsg, reloadEmployees, headerActionsEl, tabBarActionsEl }) {
  const canWrite = perm.hasDetail('hr-management', 'appointment_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.appointment)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.appointment);
  const canDelete = perm.hasDetail('hr-management', 'delete_allowed')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.appointment)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.appointment);
  const [rows, setRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState('전체');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { selYear, selMonth, setSelYear, setSelMonth, shiftMonth, month } = useMonthPicker();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id:'', appoint_type:'전보', from_department:'', to_department:'', from_position:'', to_position:'', effective_date:'', reason:'' });

  const load = () => API.get('/hr/appointments').then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = () => {
    if (!form.user_id || !form.effective_date) return alert('대상자와 발령일을 입력해주세요.');
    API.post('/hr/appointments', form).then(() => { showMsg('인사발령이 등록되었습니다.'); setShowForm(false); load(); reloadEmployees(); })
      .catch(err => alert(err.response?.data?.message || '등록 실패'));
  };
  const del = (id) => { if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/hr/appointments/${id}`).then(() => { showMsg('삭제되었습니다.'); load(); }); };

  // 대상자를 고르면 변경 전 부서/직급을 현재 사원정보로 자동 채워준다(관리 편의).
  const onPickTarget = (userId) => {
    const e = employees.find(x => String(x.id) === String(userId));
    setForm(f => ({ ...f, user_id: userId, from_department: e?.department || '', from_position: e?.position || '' }));
  };

  const monthFiltered = rows.filter(r => (r.effective_date || '').slice(0, 7) === month);
  const typeFilteredRows = typeFilter === '전체' ? monthFiltered : monthFiltered.filter(r => r.appoint_type === typeFilter);
  const filtered = filterBySearch(typeFilteredRows, search, ['name', 'from_department', 'to_department']);
  useEffect(() => { setPage(1); }, [filtered.length, pageSize, typeFilter, month]);

  const typeCount = (t) => monthFiltered.filter(r => r.appoint_type === t).length;

  const { sorted: sortedFiltered, sortKey: apptSortKey, sortDir: apptSortDir, toggleSort: apptToggleSort } = useSort(filtered, 'created_at', 'desc');
  const apptTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const apptPagedRows  = pageSize === 'all' ? sortedFiltered : sortedFiltered.slice((page - 1) * pageSize, page * pageSize);

  const printList = () => printListPopup({
    title: `인사발령 (${month})`, menuLabel: '인사관리(인사발령)',
    columns: ['발령일', '사번', '이름', '구분', '부서 변경', '직급 변경', '사유'],
    rows: sortedFiltered.map(r => [r.effective_date, r.employee_no || '-', r.name,  r.appoint_type,
      (r.from_department || r.to_department) ? `${r.from_department || '-'} → ${r.to_department || '-'}` : '-',
      (r.from_position || r.to_position) ? `${r.from_position || '-'} → ${r.to_position || '-'}` : '-', r.reason || '-']),
  });
  const downloadList = () => downloadListCsv('/hr/appointments/export', 'IMS_인사발령.csv', '인사관리(인사발령)');

  return (
    <>
    <HeaderPortal target={headerActionsEl}>
      <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
      <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
      {canWrite && <button onClick={() => setShowForm(true)} style={btn(C.primary)}>+ 인사발령</button>}
    </HeaderPortal>
    <HeaderPortal target={tabBarActionsEl}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:200 }} />
      <MonthPicker selYear={selYear} selMonth={selMonth} setSelYear={setSelYear} setSelMonth={setSelMonth} shiftMonth={shiftMonth} />
      <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />
    </HeaderPortal>
    <Section>
      {/* 구분별 필터 칩 */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
        {APPOINT_TYPES.filter(t => typeCount(t) > 0).map(t => {
          const c = APPOINT_COLORS[t];
          const active = typeFilter === t;
          return (
            <button key={t} onClick={() => setTypeFilter(active ? '전체' : t)}
              style={{ padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:700, cursor:'pointer', border:'1px solid transparent',
                background: active ? c.c : c.bg, color: active ? '#fff' : c.c }}>
              {t} {typeCount(t)}
            </button>
          );
        })}
      </div>

      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['발령일','사번','이름','구분','부서 변경','직급 변경','사유',''].map((h,i) => (
            <SortTh key={i} label={h} sortKey={APPOINT_SORT_KEYS[i]} currentKey={apptSortKey} currentDir={apptSortDir} onSort={apptToggleSort} />
          ))}</tr></thead>
          <tbody>
            {apptPagedRows.length === 0 && <EmptyRow span={8} />}
            {apptPagedRows.map(r => {
              const c = APPOINT_COLORS[r.appoint_type] || { bg:'#e8f4ff', c:C.primary };
              return (
                <tr key={r.id}>
                  <td style={td}>{r.effective_date}</td><td style={td}>{r.employee_no || '-'}</td><td style={{ ...td, fontWeight:600 }}>{r.name}</td>
                  <td style={td}><span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:c.bg, color:c.c }}>{r.appoint_type}</span></td>
                  <td style={td}>{r.from_department || r.to_department ? `${r.from_department || '-'} → ${r.to_department || '-'}` : '-'}</td>
                  <td style={td}>{r.from_position || r.to_position ? `${r.from_position || '-'} → ${r.to_position || '-'}` : '-'}</td>
                  <td style={td}>{r.reason || '-'}</td>
                  <td style={td}>{canDelete && <button onClick={() => del(r.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>삭제</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={apptTotalPages} />}

      {showForm && (
        <Modal title="📑 인사발령 등록" onClose={() => setShowForm(false)}>
          <Field label="대상자"><EmpSelect employees={employees} value={form.user_id} onChange={onPickTarget} /></Field>
          <Field label="구분">
            <select style={{ ...input, width:'100%' }} value={form.appoint_type} onChange={e => setForm(f => ({ ...f, appoint_type:e.target.value }))}>
              {APPOINT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="변경 전 부서"><input style={{ ...input, width:'100%' }} value={form.from_department} onChange={e => setForm(f => ({ ...f, from_department:e.target.value }))} /></Field>
            <Field label="변경 후 부서"><input style={{ ...input, width:'100%' }} value={form.to_department} onChange={e => setForm(f => ({ ...f, to_department:e.target.value }))} /></Field>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="변경 전 직급"><input style={{ ...input, width:'100%' }} value={form.from_position} onChange={e => setForm(f => ({ ...f, from_position:e.target.value }))} /></Field>
            <Field label="변경 후 직급"><input style={{ ...input, width:'100%' }} value={form.to_position} onChange={e => setForm(f => ({ ...f, to_position:e.target.value }))} /></Field>
          </div>
          <Field label="발령일"><input type="date" style={{ ...input, width:'100%' }} value={form.effective_date} onChange={e => setForm(f => ({ ...f, effective_date:e.target.value }))} /></Field>
          <Field label="사유"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={form.reason} onChange={e => setForm(f => ({ ...f, reason:e.target.value }))} /></Field>
          <button onClick={submit} style={{ ...btn(C.primary), width:'100%', padding:'10px 0' }}>등록</button>
        </Modal>
      )}
    </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 6) 급여관리
// ═══════════════════════════════════════════════════════════════
const PAYROLL_SORT_KEYS = ['year_month','employee_no','name','base_salary'];

// 급여명세 상세항목 — 기본급 그룹(식대/자가운전보조금/자녀보육수당) + 공제금액 그룹(4대보험+소득세/지방소득세).
// "기본급, 공제금액은 별도의 수정이 없으면 매달 동일하게 생성"되도록 서버(hrEmployeeSync.js)가
// 매달 직전월 값을 그대로 이어받아 초안을 만들어주고, 여기서는 필요할 때만 수정하면 된다.
// 2026-08-29 수정 — "차량보조금" 라벨을 "자가운전보조금"으로 변경(필드명 car_allowance는
// 하위호환을 위해 그대로 유지) + 옆에 "자녀보육수당"(child_care_allowance) 신규 비과세 항목 추가.
const PAYROLL_BASE_FIELDS = [
  ['base_salary', '기본급'], ['meal_allowance', '식대'], ['car_allowance', '자가운전보조금'], ['child_care_allowance', '자녀보육수당'],
];
const PAYROLL_DEDUCT_FIELDS = [
  ['national_pension', '국민연금'], ['health_insurance', '건강보험'], ['employment_insurance', '고용보험'],
  ['longterm_care_insurance', '장기요양보험'], ['income_tax', '소득세'], ['local_income_tax', '지방소득세'],
];
const PAYROLL_FORM_DEFAULT = { user_id:'', year_month:'', base_salary:0, meal_allowance:0, car_allowance:0, child_care_allowance:0,
  national_pension:0, health_insurance:0, employment_insurance:0, longterm_care_insurance:0,
  income_tax:0, local_income_tax:0, memo:'', status:'보류' };
// 급여등록 폼의 "계약구분" 드롭다운 — 실제 지급되는 기본급(월 단위, base_salary)을 입력받는 대신,
// 계약 방식(연봉/월급)에 맞는 금액을 고르면 자동으로 월 기본급으로 환산해 넣어준다.
// 연봉계약: 2026년 최저연봉(2,156,880원×12=25,882,560원)을 100만원 단위로 올림한 26,000,000원부터
// 3억원까지 100만원 단위. 금액계약(월급): 10만원부터 200만원까지 10만원 단위.
const PAYROLL_ANNUAL_MIN = 26000000, PAYROLL_ANNUAL_MAX = 300000000, PAYROLL_ANNUAL_STEP = 1000000;
const PAYROLL_MONTHLY_MIN = 100000, PAYROLL_MONTHLY_MAX = 2000000, PAYROLL_MONTHLY_STEP = 100000;
const payrollAmountOptions = (contractType) => {
  const [min, max, step] = contractType === '금액계약'
    ? [PAYROLL_MONTHLY_MIN, PAYROLL_MONTHLY_MAX, PAYROLL_MONTHLY_STEP]
    : [PAYROLL_ANNUAL_MIN, PAYROLL_ANNUAL_MAX, PAYROLL_ANNUAL_STEP];
  const opts = [];
  for (let v = min; v <= max; v += step) opts.push(v);
  return opts;
};
const payrollBaseTotal = (r) => Number(r.base_salary || 0) + Number(r.meal_allowance || 0) + Number(r.car_allowance || 0) + Number(r.child_care_allowance || 0);
const payrollDeductTotal = (r) => Number(r.national_pension || 0) + Number(r.health_insurance || 0) + Number(r.employment_insurance || 0)
  + Number(r.longterm_care_insurance || 0) + Number(r.income_tax || 0) + Number(r.local_income_tax || 0);
// 연봉 표기 — 사원정보(hr_profiles.salary_annual)에 값이 입력되어 있지 않은 사원이 많아 목록/명세에
// 연봉이 비어 보이던 문제를 해결하기 위해, 해당 급여행의 기본급 합계(기본급+식대+차량보조금) × 12로
// 계산해서 표시한다(직원현황 상세보기의 "연봉정보는 급여관리에서 가져옴" 방식과 동일한 기준).
// base_salary만으로 계산하면 계약금액에서 식대·차량보조금을 뺀 만큼 연봉이 실제보다 낮게 보이므로
// 반드시 기본급 합계(payrollBaseTotal) 기준으로 계산해야 한다.
const payrollAnnual = (r) => payrollBaseTotal(r) * 12;
const fmtWon = (n) => (n || 0).toLocaleString() + '원';
// 급여등록 폼의 금액 입력칸 — 사용자가 타이핑하는 동안 1000단위 콤마를 자동으로 붙여 보여주고,
// 실제 저장값(form 상태)은 콤마 없는 순수 숫자로 유지한다.
const fmtMoneyInput = (v) => (v === '' || v === null || v === undefined) ? '' : Number(v).toLocaleString('ko-KR');
const parseMoneyInput = (raw) => { const digits = String(raw).replace(/[^0-9]/g, ''); return digits === '' ? '' : Number(digits); };

// 급여 상태(지급/보류/지연) — 상세화면 버튼과 목록의 상태버튼이 공유하는 옵션/색상 정의.
const PAYROLL_STATUS_OPTIONS = ['지급', '보류', '지연'];
const PAYROLL_STATUS_COLOR = { 지급:'#16a34a', 보류:'#888888', 지연:'#dc2626' };

// 급여행 목록 하나를 "월별 합계 요약행" 형태로 집계하는 순수함수 — 원래 PayrollTab 안의
// payrollSummary useMemo에 인라인으로 있던 로직을 뽑아냈다. 이번 달 요약행뿐 아니라
// 2026-08-29 추가된 "연도 전체보기"(월별로 여러 줄 나열)에서도 월별로 동일한 함수를
// 재사용하기 위함 — 두 곳의 집계 방식이 어긋나지 않도록 단일 소스로 유지한다.
function summarizePayrollRows(list) {
  const sumField = (k) => list.reduce((s, r) => s + Number(r[k] || 0), 0);
  const base = {}; PAYROLL_BASE_FIELDS.forEach(([k]) => { base[k] = sumField(k); });
  const deduct = {}; PAYROLL_DEDUCT_FIELDS.forEach(([k]) => { deduct[k] = sumField(k); });
  const baseTotal = list.reduce((s, r) => s + payrollBaseTotal(r), 0);
  const deductTotal = list.reduce((s, r) => s + payrollDeductTotal(r), 0);
  const netTotal = sumField('net_pay');
  const statusCounts = PAYROLL_STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = list.filter(r => (r.status || '보류') === s).length; return acc;
  }, {});
  const paidCount = statusCounts['지급'] || 0;
  const paidDates = [...new Set(list.filter(r => r.status === '지급' && r.paid_at).map(r => r.paid_at))].sort();
  const payDate = paidDates.length === 0 ? '-' : paidDates.length === 1 ? paidDates[0] : `${paidDates[0]} ~ ${paidDates[paidDates.length - 1]}`;
  return { base, baseTotal, deduct, deductTotal, netTotal, statusCounts, paidCount, payDate, count: list.length };
}

// 목록의 상태버튼 — 클릭하면 지급/보류/지연 중 하나를 바로 선택할 수 있는 드롭다운(업무관리
// 상태 필터, 근태관리 기간선택과 동일한 "버튼+드롭다운" 패턴 재사용). 쓰기권한이 없으면
// 클릭할 수 없는 단순 뱃지로만 표시된다.
function PayrollStatusButton({ status, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);
  const color = PAYROLL_STATUS_COLOR[status] || PAYROLL_STATUS_COLOR['보류'];
  const label = status || '보류';
  if (disabled) {
    return <span style={{ fontSize:12, fontWeight:700, color, padding:'4px 10px', borderRadius:6, background:`${color}1a` }}>{label}</span>;
  }
  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }} onClick={e => e.stopPropagation()}>
      <button onClick={() => setOpen(v => !v)}
        style={{ fontSize:12, fontWeight:700, color:'#fff', background:color, border:'none', borderRadius:6, padding:'5px 10px', cursor:'pointer', whiteSpace:'nowrap' }}>
        {label} ▾
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', right:0, background:'#fff', border:'1px solid #e5e7eb',
          borderRadius:8, boxShadow:'0 4px 14px rgba(0,0,0,0.12)', zIndex:20, minWidth:76, overflow:'hidden' }}>
          {PAYROLL_STATUS_OPTIONS.map(s => (
            <div key={s} onClick={() => { onChange(s); setOpen(false); }}
              style={{ padding:'8px 12px', fontSize:12.5, fontWeight:600, cursor:'pointer', color:PAYROLL_STATUS_COLOR[s],
                background: s === status ? '#f8fafc' : '#fff' }}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 급여명세서 직접인쇄 — 상세화면 "인쇄" 버튼(지급 상태에서만 활성화)에서 호출. 목록 인쇄와
// 달리 사원 1명분의 급여명세서를 A4 1장 서식으로 그려서 새 창으로 인쇄한다.
function printPayslipPopup(r) {
  logPrint('인사관리(급여관리)', `${r.year_month} 급여명세서 - ${r.name}`);
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const baseRows = PAYROLL_BASE_FIELDS.map(([k, l]) => `<tr><th>${l}</th><td>${fmtWon(r[k])}</td></tr>`).join('');
  const deductRows = PAYROLL_DEDUCT_FIELDS.map(([k, l]) => `<tr><th>${l}</th><td>-${fmtWon(r[k])}</td></tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>급여명세서</title>
    <style>
      @page { size: A4 portrait; margin: 18mm; }
      body { font-family:'Malgun Gothic',sans-serif; color:#222; }
      h2 { font-size:20px; margin:0 0 4px; text-align:center; }
      .meta { font-size:12px; color:#888; text-align:center; margin-bottom:20px; }
      .info { display:flex; justify-content:space-between; font-size:13px; margin-bottom:16px; padding:10px 14px; background:#f8fafc; border-radius:6px; }
      table { width:100%; border-collapse:collapse; font-size:13px; margin-bottom:14px; }
      th, td { border:1px solid #ccc; padding:8px 10px; }
      th { background:#f1f5f9; text-align:left; width:40%; }
      td { text-align:right; }
      .grp th { background:#dbeafe; }
      .total { font-weight:700; font-size:16px; text-align:right; padding:14px 4px; border-top:2px solid #333; }
    </style></head>
    <body>
      <h2>급여명세서</h2>
      <div class="meta">출력일시: ${dateStr}</div>
      <div class="info"><span>귀속월: ${r.year_month}</span><span>부서: ${r.department || '-'}</span><span>성명: ${r.name}</span></div>
      <table><tr class="grp"><th colspan="2">기본급</th></tr>${baseRows}<tr><th>기본합계</th><td>${fmtWon(payrollBaseTotal(r))}</td></tr></table>
      <table><tr class="grp"><th colspan="2">공제금액</th></tr>${deductRows}<tr><th>공제합계</th><td>-${fmtWon(payrollDeductTotal(r))}</td></tr></table>
      <div class="total">실지급액: ${fmtWon(r.net_pay)}</div>
    </body></html>`;
  const pw = window.open('', '_blank', 'width=800,height=900');
  if (!pw) return;
  pw.document.write(html);
  pw.document.close();
  pw.focus();
  setTimeout(() => { pw.print(); pw.close(); }, 400);
}

// 급여 상세화면 — 목록에서 행을 클릭하면 열린다. 인쇄/전달/지급/보류/지연 5개 액션 버튼을
// 제공한다: 지급·보류·지연은 상태를 바꾸고 해당 직원 앞으로 알림을 등록하며(onChangeStatus),
// 지급 상태가 되어야만 인쇄/전달 버튼이 활성화된다. 전달(메일발송)은 SMTP 연동이 없는 현재
// 환경에서는 "준비 중" 안내로 대신한다(요청서에 명시된 대로 구현이 어려우면 보류 처리).
function PayrollDetailModal({ row, onClose, onChangeStatus, onEdit, onDelete, canWrite, canDelete }) {
  const isPaid = row.status === '지급';
  const handleDeliver = () => alert('전달(메일 발송) 기능은 준비 중입니다.\n직원별 이메일/SMTP 연동이 필요해 추후 제공될 예정입니다.');

  return (
    <Modal title={`💰 ${row.year_month} 급여명세 - ${row.name}`} width={520} onClose={onClose}
      headerActions={
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={() => printPayslipPopup(row)} disabled={!isPaid}
            style={{ ...btn(isPaid ? '#64748B' : '#e5e7eb', isPaid ? '#fff' : '#aaa'), padding:'5px 10px', fontSize:12, cursor: isPaid ? 'pointer' : 'not-allowed' }}
            title={isPaid ? '' : "'지급' 상태에서만 인쇄할 수 있습니다."}>
            🖨️ 인쇄
          </button>
          <button onClick={handleDeliver} disabled={!isPaid}
            style={{ ...btn(isPaid ? '#0891b2' : '#e5e7eb', isPaid ? '#fff' : '#aaa'), padding:'5px 10px', fontSize:12, cursor: isPaid ? 'pointer' : 'not-allowed' }}
            title={isPaid ? '' : "'지급' 상태에서만 전달할 수 있습니다."}>
            📧 전달
          </button>
        </div>
      }>
      <div style={{ display:'flex', justifyContent:'space-around', fontSize:14, color:'#666', marginBottom:14 }}>
        <span>직급: {row.position || '-'}</span><span>성명: {row.name}</span><span>연봉: {fmtWon(payrollAnnual(row))}</span>
      </div>

      <div style={{ fontSize:14, fontWeight:700, color:'#185FA5', margin:'6px 0' }}>기본급 (기본합계: {fmtWon(payrollBaseTotal(row))})</div>
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12 }}><tbody>
        {PAYROLL_BASE_FIELDS.map(([k, l]) => (
          <tr key={k}><td style={{ ...td, textAlign:'left', color:'#888' }}>{l}</td><td style={{ ...td, textAlign:'right' }}>{fmtWon(row[k])}</td></tr>
        ))}
      </tbody></table>

      <div style={{ fontSize:14, fontWeight:700, color:'#C1553A', margin:'6px 0' }}>공제금액 (공제합계: {fmtWon(payrollDeductTotal(row))})</div>
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12 }}><tbody>
        {PAYROLL_DEDUCT_FIELDS.map(([k, l]) => (
          <tr key={k}><td style={{ ...td, textAlign:'left', color:'#888' }}>{l}</td><td style={{ ...td, textAlign:'right', color:'#dc2626' }}>-{fmtWon(row[k])}</td></tr>
        ))}
      </tbody></table>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:14, fontWeight:700,
        padding:'10px 12px', background:'#f8fafc', borderRadius:8, marginBottom:6 }}>
        <span>실지급액</span><span>{fmtWon(row.net_pay)}</span>
      </div>
      {row.memo && <div style={{ fontSize:12.5, color:'#888', marginBottom:14 }}>비고: {row.memo}</div>}

      <div style={{ fontSize:12, fontWeight:700, color:'#666', margin:'14px 0 6px' }}>상태 변경</div>
      <div style={{ display:'flex', gap:6, marginBottom:14 }}>
        {PAYROLL_STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => canWrite && onChangeStatus(row, s)} disabled={!canWrite}
            style={{ flex:1, padding:'8px 0', borderRadius:6, border:'1px solid',
              borderColor: row.status === s ? PAYROLL_STATUS_COLOR[s] : '#ddd',
              background: row.status === s ? PAYROLL_STATUS_COLOR[s] : '#fff',
              color: row.status === s ? '#fff' : '#555',
              fontSize:13, fontWeight:600, cursor: canWrite ? 'pointer' : 'default' }}>
            {s}
          </button>
        ))}
      </div>

      {!isPaid && <div style={{ fontSize:11.5, color:'#aaa', marginBottom:14 }}>※ 인쇄/전달은 '지급' 상태에서만 가능합니다.</div>}

      <div style={{ display:'flex', gap:8, marginTop:isPaid ? 14 : 0 }}>
        {canWrite && <button onClick={() => onEdit(row)} style={{ flex:1, ...btn('#f0f0f0', '#555') }}>수정</button>}
        {canDelete && <button onClick={() => onDelete(row.id)} style={{ flex:1, ...btn('#fee2e2', '#dc2626') }}>삭제</button>}
        <button onClick={onClose} style={{ flex:1, ...btn(C.primary) }}>닫기</button>
      </div>
    </Modal>
  );
}

// 급여관리 "내보내기" 버튼 — 한때 연도/월/직원별 3옵션 드롭다운으로 개편했었으나 요청에 따라
// 원래대로(단일 버튼, 이번 달 급여대장만) 되돌린다. 대신 백엔드 응답 포맷을 CSV → 2시트
// (Sheet1 합계표 / Sheet2 개별) 엑셀(.xlsx)로 변경했다(아래 GET /hr/payrolls/export 참고).
function downloadPayrollExcel(month) {
  downloadListCsv(`/hr/payrolls/export?year_month=${month}`, `IMS_급여대장_${month}.xlsx`, '인사관리(급여관리)');
}

function PayrollTab({ employees, perm, user, showMsg, headerActionsEl, tabBarActionsEl, autoOpenIncomeTaxTable }) {
  // payroll_read(급여 전체조회 vs 본인만)는 손대지 않는 별개 개념 — 그대로 둔다.
  const canReadAll = perm.hasDetail('hr-management', 'payroll_read');
  const canWrite = perm.hasDetail('hr-management', 'payroll_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.payroll)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.payroll);
  const canDelete = perm.hasDetail('hr-management', 'delete_allowed')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.payroll)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.payroll);
  // 2026-08-28 추가 — "매년 공단 정보를 가져와서 갱신" 요청 구현. 4대보험 요율은 전 직원
  // 급여계산에 영향을 주는 회사 전역설정이라 백업/복구·사번설정 등과 동일하게 최고관리자만
  // 여닫을 수 있게 게이팅한다(실제 API 권한체계는 백엔드 isSuper 게이트가 최종 방어선).
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const [showRateModal, setShowRateModal] = useState(false);
  // 2026-08-28 추가 — 근로소득 간이세액표 리마인더에서 "지금 확인"으로 들어온 경우 요율 관리
  // 모달을 자동으로 열어준다(그 안의 근로소득 간이세액표 등록 모달까지는 PayrollRateModal에서 이어서 연다).
  useEffect(() => { if (autoOpenIncomeTaxTable) setShowRateModal(true); }, [autoOpenIncomeTaxTable]);
  const { selYear, selMonth, setSelYear, setSelMonth, shiftMonth, month } = useMonthPicker();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...PAYROLL_FORM_DEFAULT, year_month: month });
  const [detailRow, setDetailRow] = useState(null);
  // 계약구분(연봉계약/금액계약) + 선택한 금액 — 서버로 저장되는 값이 아니라, 이 두 값을 골라
  // form.base_salary(월 기본급)를 자동 계산해 채워주기 위한 화면 전용 상태다.
  const [contractType, setContractType] = useState('연봉계약');
  const [contractAmount, setContractAmount] = useState('');
  // 연봉 선택시 자동계산된 부양가족수(본인 포함) — 화면에 참고용으로만 표시한다.
  const [depCount, setDepCount] = useState(null);

  const load = () => API.get('/hr/payrolls', { params: { year_month: month } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [month]);

  // 연봉이 정해지면(계약구분/금액 선택 또는 재계산 버튼) 4대보험(국민연금/건강보험/고용보험/
  // 장기요양보험)과 소득세/지방소득세를 서버에서 계산해와 6개 공제항목에 미리 채워준다.
  // 대상자가 아직 선택되지 않았으면(부양가족수 조회 불가) 조용히 건너뛴다.
  const fetchDeductionPreview = (annualForCalc, userId) => {
    if (!userId || !annualForCalc || annualForCalc <= 0) return;
    API.get('/hr/payrolls/deduction-preview', { params: { user_id: userId, annual_salary: annualForCalc } })
      .then(r => {
        const { dependent_count, ...deduct } = r.data;
        setForm(f => ({ ...f, ...deduct }));
        setDepCount(dependent_count);
      })
      .catch(() => {});
  };

  // 계약금액(연봉/월금액)을 고르면 월 환산 목표금액(연봉계약: 연봉/12, 금액계약: 선택값 그대로)에서
  // 이미 입력되어 있는 식대·차량보조금을 뺀 나머지를 기본급으로 채운다.
  // 기본급 = (연봉/12) - 차량보조금 - 식대
  //
  // ⚠️ 2026-08-29 재수정 — 바로 위 2026-08 "절사로 통일" 판단이 틀렸던 것으로 확인됐다. 당시에는
  // "utils/payrollDeductions.js가 4대보험·소득세를 Math.floor(절사)로 계산하니 기본급도 절사가
  // 맞겠다"고 추론했지만, 이는 서로 다른 두 절사 규정을 혼동한 것이다: (1) 4대보험료 자체를 원
  // 단위 미만 절사하는 것(국민연금법 시행규칙 등, payrollDeductions.js가 올바르게 지키고 있음)과
  // (2) 연봉을 12개월로 나눠 "월 기본급"을 정하는 방식(법으로 절사/올림이 정해진 게 아니라 각
  // 회사·회계사무실의 급여 실무 관행일 뿐)은 별개의 문제다. 2026-08-29 회계사무실 실제
  // 급여대장(연봉 40,000,000원 사례)을 대조한 결과, 회계사무실은 기본급을 2,933,334원으로
  // 산정했다 — 이는 40,000,000÷12=3,333,333.33...원을 절사(2,933,333원)가 아니라 올림
  // (2,933,334원)한 값과 정확히 일치한다. 그래서 다시 Math.ceil(올림)로 되돌린다. (참고:
  // routes/hr.js의 autoRegisterPayrollFromContract()는 이 절사 변경의 영향을 받지 않고 계속
  // Math.ceil을 써왔다 — 그동안 이 화면의 수동 등록 경로만 절사로 어긋나 있었던 것.)
  const applyContractAmount = (type, amount) => {
    setContractType(type);
    setContractAmount(amount);
    if (amount === '') return;
    // 연봉/12가 1원 단위 이하로 나누어떨어지지 않는 경우 올림(Math.ceil) 처리한다 — 위 2026-08-29
    // 주석 참고, 회계사무실 실제 산출값과 일치시키기 위함.
    const target = type === '금액계약' ? Number(amount) : Math.ceil(Number(amount) / 12);
    const meal = Number(form.meal_allowance || 0);
    const car = Number(form.car_allowance || 0);
    const childCare = Number(form.child_care_allowance || 0);
    const baseSalary = Math.max(0, target - meal - car - childCare);
    setForm(f => ({ ...f, base_salary: baseSalary }));
    // 식대/차량보조금은 비과세 항목이라 4대보험·소득세 계산 대상에서 제외해야 한다 — 공제금액
    // 계산은 항상 기본급(과세대상)만 연 환산해서 넘긴다. (이전에는 목표금액 전체를 그대로
    // 연봉으로 넘겨 비과세 수당까지 과세대상에 포함되는 버그가 있었다.)
    fetchDeductionPreview(baseSalary * 12, form.user_id);
  };

  // 공제금액 영역의 "🔄 재계산" 버튼 — 대상자/기본급 기준 연봉으로 4대보험/소득세를 다시
  // 계산해 채운다(기본급을 직접 수정한 뒤 다시 맞출 때 사용). 식대/차량보조금은 비과세
  // 항목이므로 계산 기준(연봉)에서 제외한다 — base_salary만 12개월 환산해서 넘긴다.
  const recalcDeductions = () => {
    if (!form.user_id) return alert('대상자를 먼저 선택해주세요.');
    // ⚠️ 2026-08-29 버그수정 — 기존에는 이미 저장되어 있는 form.base_salary(기본급)를 그대로 12배해서
    // 공제금액만 다시 계산했다. 그런데 예전 버전(절사/Math.floor)으로 등록된 급여 행을 열어
    // "재계산"만 누르면, 기본급 자체는 그 예전 절사값(예: 2,933,333원)에 그대로 머물러 있고
    // 4대보험/소득세만 그 잘못된 기본급 기준으로 다시 계산되는 문제가 있었다(연봉 4천만원인데
    // 기본급이 2,933,333원으로 남아 국민연금 등이 미세하게 계속 어긋남). 계약구분(연봉계약)과
    // 선택된 연봉(contractAmount)을 알고 있으면 applyContractAmount()로 기본급 자체부터
    // 올림(Math.ceil) 기준으로 다시 산정한 뒤 공제금액을 계산하도록 위임한다 — 이렇게 해야
    // "재계산" 버튼 한 번으로 기본급 반올림 방식 수정분까지 함께 반영된다.
    if (contractType === '연봉계약' && contractAmount !== '' && contractAmount != null) {
      applyContractAmount(contractType, contractAmount);
      return;
    }
    const annual = Number(form.base_salary || 0) * 12;
    if (annual <= 0) return alert('연봉 정보가 없습니다. 계약구분/금액을 선택하거나 기본급을 입력해주세요.');
    fetchDeductionPreview(annual, form.user_id);
  };

  // 급여등록 폼의 금액 입력 공용 핸들러 — 콤마가 섞인 입력값을 순수 숫자로 바꿔 저장한다.
  // 식대/차량보조금은 입력한 금액만큼 기본급이 자동으로 줄어들도록(기본급+식대+차량보조금 합이
  // 그대로 유지되도록) 처리하고, 그 외 항목(기본급 자체, 공제항목)은 값만 그대로 반영한다.
  const onMoneyChange = (key) => (e) => {
    const value = parseMoneyInput(e.target.value);
    if (key === 'meal_allowance' || key === 'car_allowance' || key === 'child_care_allowance') {
      setForm(f => {
        const delta = (value === '' ? 0 : value) - Number(f[key] || 0);
        const newBase = Math.max(0, Number(f.base_salary || 0) - delta);
        return { ...f, [key]: value, base_salary: newBase };
      });
    } else {
      setForm(f => ({ ...f, [key]: value }));
    }
  };

  const openNew = () => { setForm({ ...PAYROLL_FORM_DEFAULT, year_month: month }); setContractType('연봉계약'); setContractAmount(''); setDepCount(null); };
  // 수정 화면을 열 때 계약구분/금액 드롭다운도 저장된 값(기본급+식대+차량보조금)에서 역산해
  // 가장 가까운 옵션으로 미리 채워둔다 — 그렇지 않으면 실제로는 값이 저장되어 있는데도
  // 드롭다운만 "선택"으로 비어 보여 마치 값이 사라진 것처럼 보이는 문제가 있었다.
  // (기본급 입력값 자체는 아래 setForm에서 그대로 불러오므로, 이 역산은 드롭다운 표시용일
  // 뿐 기본급을 다시 계산해 덮어쓰지 않는다.)
  const guessContract = (target) => {
    const annualGuess = Math.round((target * 12) / PAYROLL_ANNUAL_STEP) * PAYROLL_ANNUAL_STEP;
    if (annualGuess >= PAYROLL_ANNUAL_MIN && annualGuess <= PAYROLL_ANNUAL_MAX
      && Math.abs(Math.ceil(annualGuess / 12) - target) <= 6) {
      return { type: '연봉계약', amount: annualGuess };
    }
    const monthlyGuess = Math.round(target / PAYROLL_MONTHLY_STEP) * PAYROLL_MONTHLY_STEP;
    if (monthlyGuess >= PAYROLL_MONTHLY_MIN && monthlyGuess <= PAYROLL_MONTHLY_MAX && monthlyGuess === target) {
      return { type: '금액계약', amount: monthlyGuess };
    }
    return { type: '연봉계약', amount: '' };
  };
  const openEdit = (r) => {
    setForm({
      user_id: r.user_id, year_month: r.year_month, base_salary: r.base_salary || 0,
      meal_allowance: r.meal_allowance || 0, car_allowance: r.car_allowance || 0, child_care_allowance: r.child_care_allowance || 0,
      national_pension: r.national_pension || 0, health_insurance: r.health_insurance || 0,
      employment_insurance: r.employment_insurance || 0, longterm_care_insurance: r.longterm_care_insurance || 0,
      income_tax: r.income_tax || 0, local_income_tax: r.local_income_tax || 0,
      memo: r.memo || '', status: r.status || '보류',
    });
    const target = Number(r.base_salary || 0) + Number(r.meal_allowance || 0) + Number(r.car_allowance || 0) + Number(r.child_care_allowance || 0);
    const guess = guessContract(target);
    setContractType(guess.type); setContractAmount(guess.amount);
    setDepCount(null);
  };

  const submit = () => {
    if (!form.user_id) return alert('대상자를 선택해주세요.');
    API.post('/hr/payrolls', { ...form, year_month: form.year_month || month }).then(() => { showMsg('급여가 저장되었습니다.'); setShowForm(false); load(); })
      .catch(err => alert(err.response?.data?.message || '저장 실패'));
  };
  const del = (id) => { if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/hr/payrolls/${id}`).then(() => { showMsg('삭제되었습니다.'); setDetailRow(null); load(); }); };

  // 목록의 상태버튼 / 상세화면 버튼 공용 — 지급·보류·지연 상태를 바꾸면 서버가 해당 직원
  // 앞으로 알림을 한 건 등록하고(각 직원별 상태알림창), 소켓 연결 중이면 실시간으로도 전달한다.
  const changeStatus = (r, status) => {
    API.patch(`/hr/payrolls/${r.id}/status`, { status })
      .then(() => {
        showMsg(`상태가 '${status}'(으)로 변경되었습니다.`);
        setDetailRow(dr => (dr && dr.id === r.id) ? { ...dr, status } : dr);
        load();
      })
      .catch(err => alert(err.response?.data?.message || '상태 변경 중 오류가 발생했습니다.'));
  };

  // 당월지급 — 현재 선택된 월(month)에 등록되어 있는 급여행 중 아직 '지급' 상태가 아닌
  // 건을 모두 일괄로 '지급' 상태로 변경한다(각 건은 changeStatus와 동일하게 개별 알림 발송).
  const payAllThisMonth = () => {
    const targets = rows.filter(r => r.status !== '지급');
    if (!targets.length) return alert(`${month}에 지급 대기 중인 급여 내역이 없습니다.`);
    if (!window.confirm(`${month}에 등록된 ${targets.length}명의 급여를 일괄 '지급' 상태로 변경하시겠습니까?`)) return;
    Promise.all(targets.map(r => API.patch(`/hr/payrolls/${r.id}/status`, { status: '지급' })))
      .then(() => { showMsg(`${targets.length}명의 급여가 지급 처리되었습니다.`); load(); })
      .catch(err => alert(err.response?.data?.message || '일괄 지급 처리 중 오류가 발생했습니다.'));
  };

  const fmt = fmtWon;

  const filteredPayrollRows = filterBySearch(rows, search, ['name', 'department']);
  const { sorted: sortedPayrollRows, sortKey: payrollSortKey, sortDir: payrollSortDir, toggleSort: payrollToggleSort } = useSort(filteredPayrollRows, 'created_at', 'desc');
  useEffect(() => { setPage(1); }, [filteredPayrollRows.length, pageSize]);
  const payrollTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedPayrollRows.length / pageSize));
  const pagedPayrollRows = pageSize === 'all' ? sortedPayrollRows : sortedPayrollRows.slice((page - 1) * pageSize, page * pageSize);

  const printList = () => printListPopup({
    title: `급여대장 (${month})`, menuLabel: '인사관리(급여관리)',
    columns: ['귀속월','사번','이름','연봉','기본급','식대','자가운전보조금','자녀보육수당','기본합계',
      '국민연금','건강보험','고용보험','장기요양보험','소득세','지방소득세','공제합계','실지급액','상태'],
    rows: sortedPayrollRows.map(r => [r.year_month, r.employee_no || '-', r.name, fmt(payrollAnnual(r)),
      fmt(r.base_salary), fmt(r.meal_allowance), fmt(r.car_allowance), fmt(r.child_care_allowance), fmt(payrollBaseTotal(r)),
      fmt(r.national_pension), fmt(r.health_insurance), fmt(r.employment_insurance), fmt(r.longterm_care_insurance),
      fmt(r.income_tax), fmt(r.local_income_tax), '-' + fmt(payrollDeductTotal(r)), fmt(r.net_pay), r.status]),
  });
  const groupTh = { ...th, textAlign:'center' };
  // 2026-08-29 추가 — 급여관리 목록만 본문 글자크기를 14px→13px로 줄여달라는 요청. 전역 td
  // 상수(export const td, 다른 모든 탭이 공유)를 직접 수정하면 인사관리 전체 목록에 영향을
  // 주게 되므로, 이 표에서만 쓰는 로컬 오버라이드를 별도로 둔다.
  // 2026-08-31 재수정 — 13.5px에서 13px로 축소 요청 (요약행+개별목록 공통).
  const ptd = { ...td, fontSize:13 };
  // 2026-08-29 추가 — "연도 전체보기" 전용 표(월별 합계 리스트 + 펼침 상세)는 14px + 가운데정렬로
  // 별도 요청받아, td 자체가 이미 기본 가운데정렬이므로 fontSize만 다르게 오버라이드한다
  // (금액 컬럼도 우측정렬 대신 다른 표들과 달리 가운데정렬 유지 요청).
  const yptd = { ...td, fontSize:14 };
  const formBaseTotal = Number(form.base_salary || 0) + Number(form.meal_allowance || 0) + Number(form.car_allowance || 0) + Number(form.child_care_allowance || 0);
  const formDeductTotal = Number(form.national_pension || 0) + Number(form.health_insurance || 0) + Number(form.employment_insurance || 0)
    + Number(form.longterm_care_insurance || 0) + Number(form.income_tax || 0) + Number(form.local_income_tax || 0);

  // 2026-08-29 추가 — 탭메뉴/검색 영역과 급여 목록 사이에 넣을 "월별 합계 요약행". 검색조건에
  // 맞는(=아래 목록에 실제로 표시되는) 데이터를 기준으로 집계해야 화면과 요약이 어긋나지 않으므로
  // 페이지네이션 이전의 filteredPayrollRows를 사용한다(pagedPayrollRows를 쓰면 페이지를 넘길 때마다
  // 합계가 바뀌어 "월 전체 합계"라는 의미가 사라진다).
  const payrollSummary = useMemo(() => summarizePayrollRows(filteredPayrollRows), [filteredPayrollRows]);

  // 2026-08-29 추가 — "연도 전체보기" 모드. MonthPicker에서 "{연도}년 전체보기"를 누르면
  // viewMode가 'year'로 바뀌고 해당 연도 전체 급여행을 한 번에 받아와(yearRows) 귀속월별로
  // 그룹핑한 요약 리스트를 보여준다. 리스트의 특정 월 행을 클릭하면 그 달의 상세(직원별) 목록을
  // expandedMonth로 펼쳐서 아래에 보여준다(기존 월별 상세 표를 그대로 재사용).
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'year'
  const [yearViewYear, setYearViewYear] = useState(selYear);
  const [yearRows, setYearRows] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const openYearView = (y) => {
    setYearViewYear(y); setViewMode('year'); setExpandedMonth(null);
    API.get('/hr/payrolls', { params: { year: y } }).then(r => setYearRows(r.data)).catch(() => {});
  };
  const backToMonthView = () => { setViewMode('month'); setExpandedMonth(null); };
  const yearMonthGroups = useMemo(() => {
    const groups = new Map();
    yearRows.forEach(r => {
      if (!groups.has(r.year_month)) groups.set(r.year_month, []);
      groups.get(r.year_month).push(r);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, rows]) => ({ month, rows, summary: summarizePayrollRows(rows) }));
  }, [yearRows]);
  const expandedMonthRows = expandedMonth ? (yearMonthGroups.find(g => g.month === expandedMonth)?.rows || []) : [];

  return (
    <>
    <HeaderPortal target={headerActionsEl}>
      <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
      <button onClick={() => downloadPayrollExcel(month)} style={btn('#16a34a')}>📤 내보내기</button>
      {isSuperAdmin && <button onClick={() => setShowRateModal(true)} style={btn('#7C3AED')}>⚙️ 요율 관리</button>}
      {canWrite && <button onClick={payAllThisMonth} style={btn('#0891b2')}>💵 당월지급</button>}
      {canWrite && <button onClick={() => { openNew(); setShowForm(true); }} style={btn(C.primary)}>+ 급여 등록</button>}
    </HeaderPortal>
    <HeaderPortal target={tabBarActionsEl}>
      {viewMode === 'month' && <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:200 }} />}
      <MonthPicker selYear={selYear} selMonth={selMonth} setSelYear={setSelYear} setSelMonth={setSelMonth} shiftMonth={shiftMonth} onYearView={openYearView} />
      {viewMode === 'year'
        ? <button onClick={backToMonthView} style={btn('#f0f0f0', '#555')}>◀ 월별 보기로</button>
        : <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />}
    </HeaderPortal>
    {/* 2026-08-29 추가 — 탭메뉴(직원현황/조직도/근태관리 등)와 검색/월선택 컨트롤 바로 아래,
        급여 목록(귀속월/사번 등이 나오는 표) 바로 위에 배치하는 "월별 합계 요약행". 아래 목록에
        실제로 표시되는(=검색조건이 적용된) 데이터를 그대로 합산한다.
        2026-08-29 추가 — MonthPicker에서 "{연도}년 전체보기"를 누르면 아래 두 Section(월별 요약행 +
        직원별 목록) 대신 "연도 전체보기" 전용 뷰(월별 지급합계 리스트 + 클릭시 상세 펼침)를 보여준다. */}
    {viewMode === 'year' ? (
      <Section>
        <div style={{ fontSize:14, fontWeight:700, color:'#185FA5', marginBottom:8 }}>📊 {yearViewYear}년 월별 급여 지급합계</div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={th}>귀속월</th>
                <th style={th}>대상인원</th>
                <th style={th}>기본급 합계</th>
                <th style={th}>공제금액 합계</th>
                <th style={th}>실지급액 합계</th>
                <th style={th}>지급상태</th>
                <th style={th}>지급일</th>
              </tr>
            </thead>
            <tbody>
              {yearMonthGroups.length === 0 && <EmptyRow span={7} />}
              {yearMonthGroups.map(g => (
                <Fragment key={g.month}>
                  <tr onClick={() => setExpandedMonth(em => em === g.month ? null : g.month)}
                    style={{ cursor:'pointer', background: expandedMonth === g.month ? '#F5FAFF' : 'transparent' }}>
                    <td style={{ ...yptd, fontWeight:700 }}>{expandedMonth === g.month ? '▼ ' : '▶ '}{g.month}</td>
                    <td style={yptd}>{g.summary.count}명</td>
                    <td style={{ ...yptd, fontWeight:700 }}>{fmt(g.summary.baseTotal)}</td>
                    <td style={{ ...yptd, fontWeight:700, color:'#dc2626' }}>-{fmt(g.summary.deductTotal)}</td>
                    <td style={{ ...yptd, fontWeight:700, color:C.primary }}>{fmt(g.summary.netTotal)}</td>
                    <td style={{ ...yptd, fontWeight:700, color: g.summary.count > 0 && g.summary.paidCount === g.summary.count ? '#16a34a' : C.primary }}>
                      {g.summary.count === 0 ? '-' : `${g.summary.paidCount}/${g.summary.count}`}
                    </td>
                    <td style={yptd}>{g.summary.payDate}</td>
                  </tr>
                  {expandedMonth === g.month && (
                    <tr>
                      <td colSpan={7} style={{ padding:0, background:'#fafbfc' }}>
                        <div style={{ overflowX:'auto', padding:'10px 14px' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse' }}>
                            <thead>
                              <tr>
                                <th style={th}>사번</th><th style={th}>부서</th><th style={th}>직급</th><th style={th}>이름</th><th style={th}>연봉</th>
                                <th style={th}>기본합계</th><th style={th}>공제합계</th><th style={th}>실지급액</th><th style={th}>상태</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandedMonthRows.map(r => (
                                <tr key={r.id} onClick={() => setDetailRow(r)} style={{ cursor:'pointer' }}>
                                  <td style={yptd}>{r.employee_no || '-'}</td>
                                  <td style={yptd}>{r.department || '-'}</td>
                                  <td style={yptd}>{r.position || '-'}</td>
                                  <td style={{ ...yptd, fontWeight:600 }}>{r.name}</td>
                                  <td style={yptd}>{fmt(payrollAnnual(r))}</td>
                                  <td style={{ ...yptd, fontWeight:700 }}>{fmt(payrollBaseTotal(r))}</td>
                                  <td style={{ ...yptd, fontWeight:700, color:'#dc2626' }}>-{fmt(payrollDeductTotal(r))}</td>
                                  <td style={{ ...yptd, fontWeight:700 }}>{fmt(r.net_pay)}</td>
                                  <td style={yptd}><PayrollStatusButton status={r.status} onChange={s => changeStatus(r, s)} disabled={!canWrite} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    ) : (
      <>
    <Section>
      <div style={{ fontSize:13, fontWeight:700, color:'#185FA5', marginBottom:8 }}>📊 {month} 급여 합계 ({payrollSummary.count}명)</div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={th} rowSpan={2}>귀속월</th>
              <th colSpan={PAYROLL_BASE_FIELDS.length + 1} style={{ ...groupTh, background:'#eef5fb' }}>기본급 합계</th>
              <th colSpan={PAYROLL_DEDUCT_FIELDS.length + 1} style={{ ...groupTh, background:'#fdf1f0' }}>공제금액 합계</th>
              <th style={th} rowSpan={2}>실지급액 합계</th>
              <th style={th} rowSpan={2}>지급상태</th>
              <th style={th} rowSpan={2}>지급일</th>
            </tr>
            <tr>
              {PAYROLL_BASE_FIELDS.map(([k,l]) => <th key={k} style={{ ...th, fontSize:11.5, color:'#888' }}>{l}</th>)}
              <th style={{ ...th, fontSize:11.5, color:'#888' }}>기본합계</th>
              {PAYROLL_DEDUCT_FIELDS.map(([k,l]) => <th key={k} style={{ ...th, fontSize:11.5, color:'#888' }}>{l}</th>)}
              <th style={{ ...th, fontSize:11.5, color:'#888' }}>공제합계</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...ptd, fontWeight:700 }}>{month}</td>
              {PAYROLL_BASE_FIELDS.map(([k]) => <td key={k} style={{ ...ptd, textAlign:'right' }}>{fmt(payrollSummary.base[k])}</td>)}
              <td style={{ ...ptd, textAlign:'right', fontWeight:700 }}>{fmt(payrollSummary.baseTotal)}</td>
              {PAYROLL_DEDUCT_FIELDS.map(([k]) => <td key={k} style={{ ...ptd, textAlign:'right', color:'#dc2626' }}>-{fmt(payrollSummary.deduct[k])}</td>)}
              <td style={{ ...ptd, textAlign:'right', fontWeight:700, color:'#dc2626' }}>-{fmt(payrollSummary.deductTotal)}</td>
              <td style={{ ...ptd, textAlign:'right', fontWeight:700, color:C.primary }}>{fmt(payrollSummary.netTotal)}</td>
              <td style={{ ...ptd, fontWeight:700, color: payrollSummary.count > 0 && payrollSummary.paidCount === payrollSummary.count ? '#16a34a' : C.primary }}>
                {payrollSummary.count === 0 ? <span style={{ fontSize:11.5, color:'#bbb', fontWeight:400 }}>-</span> : `${payrollSummary.paidCount}/${payrollSummary.count}`}
              </td>
              <td style={ptd}>{payrollSummary.payDate}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
    <Section>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['귀속월','사번','이름','연봉'].map((h,i) => (
                <SortTh key={i} label={h} sortKey={PAYROLL_SORT_KEYS[i]} currentKey={payrollSortKey} currentDir={payrollSortDir} onSort={payrollToggleSort} rowSpan={2} />
              ))}
              {/* ⚠️ 2026-08-29 버그수정 — colSpan을 4로 하드코딩해뒀던 탓에, 자녀보육수당 추가로
                  PAYROLL_BASE_FIELDS가 4개(기본급/식대/자가운전보조금/자녀보육수당)+기본합계=5칸이
                  됐는데도 그룹 헤더는 여전히 4칸만 차지해 아래 데이터 행과 한 칸씩 밀려 보이는
                  문제가 있었다. PAYROLL_BASE_FIELDS/PAYROLL_DEDUCT_FIELDS 배열 길이 기준으로
                  항상 정확히 맞도록 동적 계산으로 변경(이후 항목이 추가/삭제돼도 자동으로 맞음). */}
              <th colSpan={PAYROLL_BASE_FIELDS.length + 1} style={{ ...groupTh, background:'#eef5fb' }}>기본급</th>
              <th colSpan={PAYROLL_DEDUCT_FIELDS.length + 1} style={{ ...groupTh, background:'#fdf1f0' }}>공제금액</th>
              <SortTh label="실지급액" sortKey="net_pay" currentKey={payrollSortKey} currentDir={payrollSortDir} onSort={payrollToggleSort} rowSpan={2} />
              <th style={th} rowSpan={2}>상태</th>
            </tr>
            <tr>
              {PAYROLL_BASE_FIELDS.map(([k,l]) => <th key={k} style={{ ...th, fontSize:11.5, color:'#888' }}>{l}</th>)}
              <th style={{ ...th, fontSize:11.5, color:'#888' }}>기본합계</th>
              {PAYROLL_DEDUCT_FIELDS.map(([k,l]) => <th key={k} style={{ ...th, fontSize:11.5, color:'#888' }}>{l}</th>)}
              <th style={{ ...th, fontSize:11.5, color:'#888' }}>공제합계</th>
            </tr>
          </thead>
          <tbody>
            {pagedPayrollRows.length === 0 && <EmptyRow span={17} />}
            {pagedPayrollRows.map(r => (
              <tr key={r.id} onClick={() => setDetailRow(r)} style={{ cursor:'pointer' }}>
                <td style={ptd}>{r.year_month}</td><td style={ptd}>{r.employee_no || '-'}</td><td style={{ ...ptd, fontWeight:600 }}>{r.name}</td>
                <td style={{ ...ptd, textAlign:'right' }}>{fmt(payrollAnnual(r))}</td>
                {PAYROLL_BASE_FIELDS.map(([k]) => <td key={k} style={{ ...ptd, textAlign:'right' }}>{fmt(r[k])}</td>)}
                <td style={{ ...ptd, textAlign:'right', fontWeight:700 }}>{fmt(payrollBaseTotal(r))}</td>
                {PAYROLL_DEDUCT_FIELDS.map(([k]) => <td key={k} style={{ ...ptd, textAlign:'right', color:'#dc2626' }}>-{fmt(r[k])}</td>)}
                <td style={{ ...ptd, textAlign:'right', fontWeight:700, color:'#dc2626' }}>-{fmt(payrollDeductTotal(r))}</td>
                <td style={{ ...ptd, textAlign:'right', fontWeight:700 }}>{fmt(r.net_pay)}</td>
                <td style={ptd}><PayrollStatusButton status={r.status} onChange={s => changeStatus(r, s)} disabled={!canWrite} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={payrollTotalPages} />}
      {!canReadAll && <div style={{ fontSize:12, color:'#aaa', marginTop:10 }}>※ 본인 급여만 조회됩니다. 전체 조회는 최고관리자에게 권한을 요청해주세요.</div>}
    </Section>
    </>
    )}
      {detailRow && (
        <PayrollDetailModal row={detailRow} onClose={() => setDetailRow(null)} onChangeStatus={changeStatus}
          canWrite={canWrite} canDelete={canDelete}
          onEdit={(r) => { openEdit(r); setDetailRow(null); setShowForm(true); }}
          onDelete={del} />
      )}

      {showForm && (
        <Modal title="💰 급여 등록" width={520} onClose={() => setShowForm(false)}>
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1 }}><Field label="대상자"><EmpSelect employees={employees} value={form.user_id} onChange={v => {
              setForm(f => ({ ...f, user_id:v }));
              // 연봉(계약금액)을 먼저 골라둔 상태에서 대상자를 나중에 선택하는 경우에도
              // 부양가족수를 반영해 공제금액을 자동계산해준다.
              if (v && contractAmount !== '') {
                const annualForCalc = contractType === '금액계약' ? Number(contractAmount) * 12 : Number(contractAmount);
                fetchDeductionPreview(annualForCalc, v);
              }
            }} /></Field></div>
            <div style={{ flex:1 }}><Field label="귀속월"><input type="month" style={{ ...input, width:'100%' }} value={form.year_month} onChange={e => setForm(f => ({ ...f, year_month:e.target.value }))} /></Field></div>
          </div>

          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1 }}>
              <Field label="계약구분">
                <select style={{ ...input, width:'100%' }} value={contractType}
                  onChange={e => applyContractAmount(e.target.value, '')}>
                  <option value="연봉계약">연봉계약</option>
                  <option value="금액계약">금액계약</option>
                </select>
              </Field>
            </div>
            <div style={{ flex:1 }}>
              <Field label={contractType === '금액계약' ? '월 금액' : '연봉'}>
                <select style={{ ...input, width:'100%' }} value={contractAmount}
                  onChange={e => applyContractAmount(contractType, e.target.value)}>
                  <option value="">선택</option>
                  {payrollAmountOptions(contractType).map(v => (
                    <option key={v} value={v}>{v.toLocaleString()}원</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div style={{ fontSize:12, fontWeight:700, color:'#185FA5', margin:'14px 0 6px' }}>기본급 (기본합계: {fmt(formBaseTotal)})</div>
          <div style={{ display:'flex', gap:8 }}>
            {PAYROLL_BASE_FIELDS.map(([k,l]) => (
              <Field key={k} label={l}><input type="text" inputMode="numeric" style={{ ...input, width:'100%', textAlign:'right' }} value={fmtMoneyInput(form[k])} onChange={onMoneyChange(k)} /></Field>
            ))}
          </div>

          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', margin:'14px 0 6px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#C1553A' }}>공제금액 (공제합계: {fmt(formDeductTotal)})</div>
            <button type="button" onClick={recalcDeductions} style={{ ...btn('#f0f0f0', '#555'), padding:'3px 10px', fontSize:11.5 }}>🔄 재계산</button>
          </div>
          <div style={{ fontSize:11, color:'#999', margin:'-2px 0 8px', lineHeight:1.5 }}>
            연봉을 선택(또는 재계산)하면 4대보험은 요율 기준으로, 소득세/지방소득세는 근사식으로 자동
            계산되어 채워집니다. 소득세는 홈택스 "근로소득 간이세액표"와 차이가 있을 수 있으니 지급 전
            반드시 확인해주세요.{depCount != null && ` (부양가족수: 본인 포함 ${depCount}명 — 인사기록카드 가족사항의 '등록' 체크 기준)`}
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:8 }}>
            {PAYROLL_DEDUCT_FIELDS.slice(0, 3).map(([k,l]) => (
              <Field key={k} label={l}><input type="text" inputMode="numeric" style={{ ...input, width:'100%', textAlign:'right' }} value={fmtMoneyInput(form[k])} onChange={onMoneyChange(k)} /></Field>
            ))}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {PAYROLL_DEDUCT_FIELDS.slice(3, 6).map(([k,l]) => (
              <Field key={k} label={l}><input type="text" inputMode="numeric" style={{ ...input, width:'100%', textAlign:'right' }} value={fmtMoneyInput(form[k])} onChange={onMoneyChange(k)} /></Field>
            ))}
          </div>

          <div style={{ fontSize:13, fontWeight:700, margin:'10px 0 14px', padding:'8px 10px', background:'#f8fafc', borderRadius:8 }}>
            {fmt(formBaseTotal)} - {fmt(formDeductTotal)} : {fmt(formBaseTotal - formDeductTotal)}
          </div>

          <Field label="상태">
            <select style={{ ...input, width:'100%' }} value={form.status} onChange={e => setForm(f => ({ ...f, status:e.target.value }))}>
              {PAYROLL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="비고"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={form.memo} onChange={e => setForm(f => ({ ...f, memo:e.target.value }))} /></Field>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={submit} style={{ ...btn(C.primary), flex:1 }}>저장</button>
            <button onClick={() => setShowForm(false)} style={{ ...btn('#f0f0f0', '#555'), flex:1 }}>취소</button>
          </div>
        </Modal>
      )}

      {showRateModal && <PayrollRateModal onClose={() => setShowRateModal(false)} showMsg={showMsg} autoOpenIncomeTaxModal={autoOpenIncomeTaxTable} />}
    </>
  );
}

// 2026-08-28 신규 — "매년 공단 정보를 가져와서 갱신"(요청사항) 관리자 UI.
// 현재 적용중(active)인 요율, 검토 대기중인 초안(draft, 자동조회 결과 포함), 지난 이력
// (archived)을 한 화면에서 보여주고, "지금 조회"(자동조회 시도)와 수동입력/수정, "적용" 버튼을
// 제공한다. 백엔드 라우트(routes/hr.js의 /payroll-rates*)와 1:1로 대응한다 — 자동조회 결과는
// 절대 이 화면을 거치지 않고는 실제 급여계산에 반영되지 않는다(반드시 "적용" 버튼 클릭 필요).
const RATE_FIELD_DEFS = [
  ['national_pension_rate', '국민연금요율(근로자부담, %)', 100],
  ['national_pension_min_base', '국민연금 기준소득월액 하한액(원)', 1],
  ['national_pension_max_base', '국민연금 기준소득월액 상한액(원)', 1],
  ['health_insurance_rate', '건강보험요율(근로자부담, %)', 100],
  ['longterm_care_rate', '장기요양보험료율(건강보험료 대비, %)', 100],
  ['employment_insurance_rate', '고용보험요율(근로자부담, %)', 100],
];
const RATE_STATUS_LABEL = { active: '적용중', draft: '검토대기(초안)', archived: '지난이력' };
const RATE_STATUS_COLOR = { active: '#16a34a', draft: '#d97706', archived: '#999' };

function PayrollRateModal({ onClose, showMsg, autoOpenIncomeTaxModal }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const [editRow, setEditRow] = useState(null); // draft 행 수정 폼(또는 신규 수동입력이면 { isNew:true, ... })
  const [saving, setSaving] = useState(false);
  const [showIncomeTaxModal, setShowIncomeTaxModal] = useState(false); // 2026-08-28 추가 — 근로소득 간이세액표 등록
  const [applyingAll, setApplyingAll] = useState(false); // 2026-08-31 추가 — "전체적용" 버튼(진행중 여부)
  // 2026-08-28 추가 — 리마인더 "지금 확인"으로 들어온 경우 근로소득 간이세액표 등록 모달까지 자동으로 연다.
  useEffect(() => { if (autoOpenIncomeTaxModal) setShowIncomeTaxModal(true); }, [autoOpenIncomeTaxModal]);

  const load = () => {
    setLoading(true);
    API.get('/hr/payroll-rates').then(r => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const active = rows.find(r => r.status === 'active');

  const runFetch = () => {
    setFetching(true);
    setFetchResult(null);
    API.post('/hr/payroll-rates/fetch')
      .then(r => { setFetchResult(r.data); load(); })
      .catch(err => setFetchResult({ success:false, notes:[err.response?.data?.message || '자동조회 요청 중 오류가 발생했습니다.'] }))
      .finally(() => setFetching(false));
  };

  // 화면 입력은 항상 %(퍼센트, 예: 4.75) 단위로 받고, editRow 상태에도 처음부터 그 단위로
  // 저장해둔다 — DB의 decimal(0.0475)과 화면 표시값을 매 렌더마다 다시 판별할 필요 없이,
  // "폼을 열 때 1회 변환 → 제출할 때 1회 역변환"만 하면 되도록 단순화한 것이다.
  const toPercentForm = (src, extra) => {
    const row = { ...src, ...extra };
    RATE_FIELD_DEFS.forEach(([k, , scale]) => {
      if (scale === 100 && row[k] !== '' && row[k] != null) row[k] = Number(row[k]) * 100;
    });
    return row;
  };
  const openManualNew = () => {
    setEditRow(toPercentForm(active || {
      national_pension_rate: '', national_pension_min_base: '', national_pension_max_base: '',
      health_insurance_rate: '', longterm_care_rate: '', employment_insurance_rate: '',
    }, { isNew: true, year: new Date().getFullYear(), effective_from: '', source_note: '' }));
  };
  const openEditDraft = (row) => setEditRow(toPercentForm(row, { isNew: false }));

  const submitEdit = () => {
    if (!editRow) return;
    setSaving(true);
    const body = { ...editRow };
    // 화면에서는 %(퍼센트) 단위로 입력받지만 DB/계산 로직은 소수(요율) 단위를 쓰므로,
    // 저장 직전에만 되돌린다 — RATE_FIELD_DEFS의 세 번째 값(scale=100)이 % 필드 표시다.
    RATE_FIELD_DEFS.forEach(([k, , scale]) => {
      if (scale === 100 && body[k] !== '' && body[k] != null) body[k] = Number(body[k]) / 100;
      else if (body[k] !== '' && body[k] != null) body[k] = Number(body[k]);
    });
    const req = editRow.isNew
      ? API.post('/hr/payroll-rates', body)
      : API.put(`/hr/payroll-rates/${editRow.id}`, body);
    req.then(() => { showMsg(editRow.isNew ? '요율이 등록·적용되었습니다.' : '초안이 수정되었습니다.'); setEditRow(null); load(); })
      .catch(err => alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.'))
      .finally(() => setSaving(false));
  };

  const applyRow = (row) => {
    if (!window.confirm(`${row.year}년 요율을 실제 급여계산에 즉시 적용하시겠습니까?\n(현재 적용중인 요율은 '지난 이력'으로 이동합니다.)`)) return;
    API.post(`/hr/payroll-rates/${row.id}/apply`)
      .then(() => { showMsg('적용되었습니다.'); load(); })
      .catch(err => alert(err.response?.data?.message || '적용 중 오류가 발생했습니다.'));
  };
  const deleteRow = (row) => {
    if (!window.confirm('이 요율 이력을 삭제하시겠습니까?')) return;
    API.delete(`/hr/payroll-rates/${row.id}`)
      .then(() => { showMsg('삭제되었습니다.'); load(); })
      .catch(err => alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.'));
  };

  // 2026-08-31 추가 — "전체적용" 버튼. 이미 등록된 모든 급여명세를 하나씩 열어 "수정 → 🔄 재계산
  // → 저장"을 반복하던 것을, 현재 활성 요율/간이세액표 기준으로 서버에서 한 번에 재계산·저장한다
  // (기본급/식대 등 이미 입력된 금액은 그대로 두고 공제금액 6종만 갱신).
  const applyAllPayrolls = () => {
    if (!window.confirm('현재 적용중인 요율·근로소득 간이세액표 기준으로, 이미 등록된 모든 급여명세의 공제금액(4대보험·소득세·지방소득세)을 다시 계산해 저장하시겠습니까?\n(기본급/식대 등 다른 항목은 변경되지 않습니다.)')) return;
    setApplyingAll(true);
    API.post('/hr/payrolls/recalc-all')
      .then(r => showMsg(r.data.message || '전체 적용되었습니다.'))
      .catch(err => alert(err.response?.data?.message || '전체적용 중 오류가 발생했습니다.'))
      .finally(() => setApplyingAll(false));
  };

  const pct = (v) => (v == null || v === '') ? '-' : `${(Number(v) * 100).toFixed(3).replace(/\.?0+$/, '')}%`;
  const won = (v) => (v == null || v === '') ? '-' : Number(v).toLocaleString() + '원';

  return (
    <Modal title="⚙️ 4대보험 요율 관리" width={720} onClose={onClose}
      headerActions={
        <button onClick={applyAllPayrolls} disabled={applyingAll} style={{ ...btn('#16a34a'), padding:'6px 12px', fontSize:12.5 }}>
          {applyingAll ? '적용 중...' : '⚡ 전체적용'}
        </button>
      }>
      <div style={{ fontSize:12, color:'#888', lineHeight:1.7, marginBottom:14, background:'#f8fafc', borderRadius:8, padding:'10px 12px' }}>
        {/* 2026-08-31 재수정 — 안내문구를 5개 항목으로 재정리(요청사항 그대로 반영). */}
        <div>1. 국민연금·건강보험·고용보험 요율은 공단이 보통 매년 초에 새로 고시합니다. "지금 조회"를 누르면 공식 안내 페이지에서 자동으로 값을 가져와 <b>초안(draft)</b>으로만 저장합니다.</div>
        <div>2. 근로소득 간이세액표 등록은 홈택스에서 근로소득 간이세액표 조회를 검색한 다음 엑셀 파일을 다운받아 첨부하시면 적용이 가능합니다.</div>
        <div>3. 매년 1월에는 서버가 자동으로도 1회 조회를 시도해 초안을 만들어둡니다.</div>
        <div>4. 실제 급여계산에는 요율관리에 등록후 <b>전체적용</b> 버튼을 누르면 설정한 요율대로 전체 반영됩니다.</div>
        <div>5. 사내망 인터넷 차단 등으로 자동조회가 실패할 수 있으니, 그 경우 "수동으로 새 요율 입력"을 이용해주세요.</div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:14 }}>
        <button onClick={runFetch} disabled={fetching} style={{ ...btn('#0891b2'), flex:1 }}>{fetching ? '조회 중...' : '🔎 지금 조회(자동)'}</button>
        <button onClick={openManualNew} style={{ ...btn(C.primary), flex:1 }}>✏️ 수동으로 새 요율 입력</button>
        <button onClick={() => setShowIncomeTaxModal(true)} style={{ ...btn('#059669'), flex:1 }}>📄 근로소득 간이세액표 등록</button>
      </div>

      {fetchResult && (
        <div style={{ fontSize:12, lineHeight:1.6, marginBottom:14, padding:'10px 12px', borderRadius:8,
          background: fetchResult.success ? '#f0fdf4' : '#fef2f2', color: fetchResult.success ? '#166534' : '#991b1b' }}>
          {(fetchResult.notes || []).map((n, i) => <div key={i}>{n}</div>)}
          {(fetchResult.sourceNotes || []).map((n, i) => <div key={'s'+i} style={{ color:'#888' }}>· {n}</div>)}
        </div>
      )}

      {active && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#16a34a', marginBottom:6 }}>✅ 현재 적용중 ({active.year}년)</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 16px', fontSize:12.5, background:'#f0fdf4', borderRadius:8, padding:'10px 12px' }}>
            {RATE_FIELD_DEFS.map(([k, l, scale]) => (
              <div key={k}><span style={{ color:'#888' }}>{l}</span> : <b>{scale === 100 ? pct(active[k]) : won(active[k])}</b></div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize:13, fontWeight:700, margin:'14px 0 6px' }}>이력</div>
      {loading && <div style={{ fontSize:12, color:'#888' }}>불러오는 중...</div>}
      {!loading && (
        <div style={{ maxHeight:280, overflowY:'auto', border:'1px solid #eee', borderRadius:8 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                <th style={{ ...th, fontSize:11.5 }}>연도</th><th style={{ ...th, fontSize:11.5 }}>상태</th>
                <th style={{ ...th, fontSize:11.5 }}>출처</th><th style={{ ...th, fontSize:11.5 }}>등록/조회일</th>
                <th style={{ ...th, fontSize:11.5 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, color:'#bbb' }}>등록된 요율 이력이 없습니다.</td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.year}</td>
                  <td style={{ ...td, color:RATE_STATUS_COLOR[r.status], fontWeight:600 }}>{RATE_STATUS_LABEL[r.status] || r.status}</td>
                  <td style={td}>{r.source === 'auto' ? '자동조회' : '수동입력'}</td>
                  {/* 2026-08-31 수정 — 등록/조회일 글자크기 11px→14px */}
                  <td style={{ ...td, fontSize:14 }}>{(r.fetched_at || r.applied_at || r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td style={{ ...td, whiteSpace:'nowrap' }}>
                    {r.status !== 'active' && <button onClick={() => applyRow(r)} style={{ ...btn('#16a34a'), padding:'3px 8px', fontSize:11, marginRight:4 }}>적용</button>}
                    {r.status === 'draft' && <button onClick={() => openEditDraft(r)} style={{ ...btn('#f0f0f0', '#555'), padding:'3px 8px', fontSize:11, marginRight:4 }}>수정</button>}
                    {r.status !== 'active' && <button onClick={() => deleteRow(r)} style={{ ...btn('#fee2e2', '#dc2626'), padding:'3px 8px', fontSize:11 }}>삭제</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editRow && (
        <Modal title={editRow.isNew ? '✏️ 요율 수동입력 (등록 즉시 적용됩니다)' : '✏️ 초안 수정'} width={480} onClose={() => setEditRow(null)}>
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1 }}><Field label="연도"><input type="number" style={{ ...input, width:'100%' }} value={editRow.year} disabled={!editRow.isNew}
              onChange={e => setEditRow(r => ({ ...r, year:e.target.value }))} /></Field></div>
            {editRow.isNew && (
              <div style={{ flex:1 }}><Field label="시행일(선택)"><input type="date" style={{ ...input, width:'100%' }} value={editRow.effective_from || ''}
                onChange={e => setEditRow(r => ({ ...r, effective_from:e.target.value }))} /></Field></div>
            )}
          </div>
          {RATE_FIELD_DEFS.map(([k, l, scale]) => (
            <Field key={k} label={l}>
              <input type="number" step="0.001" style={{ ...input, width:'100%' }}
                value={editRow[k]}
                onChange={e => setEditRow(r => ({ ...r, [k]: e.target.value }))} />
            </Field>
          ))}
          <div style={{ fontSize:11, color:'#999', margin:'-4px 0 10px' }}>※ 요율(%) 필드는 실제 백분율 숫자(예: 4.75)로 입력해주세요. DB에는 자동으로 소수(0.0475)로 변환되어 저장됩니다.</div>
          <Field label="비고(출처 메모)"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={editRow.source_note || ''}
            onChange={e => setEditRow(r => ({ ...r, source_note:e.target.value }))} /></Field>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={submitEdit} disabled={saving} style={{ ...btn(C.primary), flex:1 }}>{saving ? '저장 중...' : (editRow.isNew ? '등록 및 즉시 적용' : '저장')}</button>
            <button onClick={() => setEditRow(null)} style={{ ...btn('#f0f0f0', '#555'), flex:1 }}>취소</button>
          </div>
        </Modal>
      )}

      {showIncomeTaxModal && <IncomeTaxTableModal onClose={() => setShowIncomeTaxModal(false)} showMsg={showMsg} />}
    </Modal>
  );
}

// 2026-08-28 신규 — "근로소득 간이세액표 등록" 기능. 4대보험 요율과 달리 이 표는 국세청이
// 배포하는 완성된 문서이므로 draft 검토 단계 없이, 업로드하면 즉시 파싱해 active로 적용한다.
// 등록해두면 급여계산(calcPayrollDeductions)이 근사식 대신 이 표를 그대로 조회해서 소득세를
// 계산하므로 회계사무실 실제 원천징수액과 정확히 일치하게 된다.
//
// ⚠️ 자녀세액공제 조정 토글에 대한 안내: 2026-08-28 실제 급여 데이터로 재검증한 결과(20세
// 미만 자녀 다수 + 배우자 가구에서 소득세/지방소득세가 0원으로 표시되는 문제 포함), 이 회사는
// 이 조정을 실무상 적용하지 않는 것으로 확정 확인되었다 — 그래서 기본값을 "미적용(꺼짐)"으로
// 바꿨다. 법령상 원칙(시행령 별표2 3호)은 "적용"이므로, 만약 회계사무실이 실제로 이 조정을
// 반영해서 원천징수하고 있다면 아래 체크박스를 켜서 되돌릴 수 있다.
function IncomeTaxTableModal({ onClose, showMsg }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [applyChildCredit, setApplyChildCredit] = useState(false);
  const [settingLoaded, setSettingLoaded] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/hr/income-tax-table').then(r => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    API.get('/hr/income-tax-table/child-credit-setting')
      .then(r => setApplyChildCredit(r.data?.applyChildCreditAdjustment === true))
      .catch(() => {})
      .finally(() => setSettingLoaded(true));
  }, []);

  const active = rows.find(r => r.status === 'active');

  const handleUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`"${file.name}" 파일을 근로소득 간이세액표로 등록하시겠습니까?\n등록 즉시 급여계산에 적용됩니다(기존 표는 지난 이력으로 이동).`)) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    API.post('/hr/income-tax-table/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => { showMsg(`근로소득 간이세액표가 등록되었습니다. (${r.data.rowCount}개 구간)`); load(); })
      .catch(err => alert(err.response?.data?.message || '엑셀 파일을 처리하는 중 오류가 발생했습니다.'))
      .finally(() => setUploading(false));
  };

  const deleteRow = (row) => {
    if (!window.confirm('이 등록 이력을 삭제하시겠습니까?')) return;
    API.delete(`/hr/income-tax-table/${row.id}`)
      .then(() => { showMsg('삭제되었습니다.'); load(); })
      .catch(err => alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.'));
  };

  const toggleChildCredit = (checked) => {
    setApplyChildCredit(checked);
    API.put('/hr/income-tax-table/child-credit-setting', { applyChildCreditAdjustment: checked })
      .then(() => showMsg('설정이 저장되었습니다.'))
      .catch(err => { alert(err.response?.data?.message || '설정 저장 중 오류가 발생했습니다.'); setApplyChildCredit(!checked); });
  };

  return (
    <Modal title="📄 근로소득 간이세액표 등록" width={640} onClose={onClose}>
      <div style={{ fontSize:12, color:'#888', lineHeight:1.6, marginBottom:14, background:'#f8fafc', borderRadius:8, padding:'10px 12px' }}>
        국세청 홈택스(hometax.go.kr)에서 배포하는 공식 "근로소득 간이세액표"(소득세법 시행령 별표2)
        엑셀 파일을 그대로 업로드하면, 이후 급여관리의 소득세/지방소득세 자동계산이 근사식 대신
        이 표의 조회값을 그대로 사용합니다 — 회계사무실 원천징수액과 정확히 일치합니다.
      </div>

      <div style={{ marginBottom:14 }}>
        <label style={{ ...btn(C.primary), display:'inline-block', width:'100%', textAlign:'center', boxSizing:'border-box', cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
          {uploading ? '업로드 및 등록 중...' : '📤 간이세액표 엑셀 파일 등록'}
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} disabled={uploading} style={{ display:'none' }} />
        </label>
      </div>

      {active && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#16a34a', marginBottom:6 }}>✅ 현재 적용중</div>
          <div style={{ fontSize:12.5, background:'#f0fdf4', borderRadius:8, padding:'10px 12px', lineHeight:1.8 }}>
            <div><span style={{ color:'#888' }}>파일명</span> : <b>{active.source_filename || '-'}</b></div>
            <div><span style={{ color:'#888' }}>구간 수</span> : <b>{active.row_count != null ? `${active.row_count}개` : '-'}</b></div>
            <div><span style={{ color:'#888' }}>시행일</span> : <b>{active.effective_date || '-'}</b></div>
            <div><span style={{ color:'#888' }}>등록자/등록일</span> : <b>{active.uploaded_by || '-'} · {(active.uploaded_at || '').slice(0, 16).replace('T', ' ')}</b></div>
          </div>
        </div>
      )}
      {!active && !loading && (
        <div style={{ fontSize:12.5, color:'#d97706', background:'#fffbeb', borderRadius:8, padding:'10px 12px', marginBottom:14 }}>
          등록된 표가 없습니다. 표를 등록하기 전까지는 기존 근사식으로 소득세를 계산합니다.
        </div>
      )}

      {settingLoaded && (
        <div style={{ marginBottom:14, background:'#fffbeb', borderRadius:8, padding:'10px 12px' }}>
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
            <input type="checkbox" checked={applyChildCredit} onChange={e => toggleChildCredit(e.target.checked)} />
            표 조회값에서 자녀세액공제 조정(월)을 추가로 차감
          </label>
          <div style={{ fontSize:11, color:'#92600e', lineHeight:1.6, marginTop:6 }}>
            법령상(시행령 별표2 3호) 원칙은 "적용"이지만, 실제 급여 데이터와 대조한 결과 이 회사는
            이 조정을 적용하지 않는 것으로 확인되어 기본값을 꺼둔 상태입니다(20세 미만 자녀가 많은
            가구에서 이 조정을 적용하면 소득세가 0원까지 깎이는 문제가 있었습니다). 담당
            회계사무실이 실제로 이 조정을 반영해서 원천징수하고 있다면 체크해서 켜주세요.
          </div>
        </div>
      )}

      <div style={{ fontSize:13, fontWeight:700, margin:'14px 0 6px' }}>등록 이력</div>
      {loading && <div style={{ fontSize:12, color:'#888' }}>불러오는 중...</div>}
      {!loading && (
        <div style={{ maxHeight:220, overflowY:'auto', border:'1px solid #eee', borderRadius:8 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                <th style={{ ...th, fontSize:11.5 }}>파일명</th><th style={{ ...th, fontSize:11.5 }}>상태</th>
                <th style={{ ...th, fontSize:11.5 }}>구간수</th><th style={{ ...th, fontSize:11.5 }}>등록일</th>
                <th style={{ ...th, fontSize:11.5 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, color:'#bbb' }}>등록 이력이 없습니다.</td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.source_filename || '-'}</td>
                  <td style={{ ...td, color:RATE_STATUS_COLOR[r.status] || '#999', fontWeight:600 }}>{r.status === 'active' ? '적용중' : '지난이력'}</td>
                  <td style={td}>{r.row_count != null ? r.row_count : '-'}</td>
                  <td style={{ ...td, fontSize:11 }}>{(r.uploaded_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td style={{ ...td, whiteSpace:'nowrap' }}>
                    {r.status !== 'active' && <button onClick={() => deleteRow(r)} style={{ ...btn('#fee2e2', '#dc2626'), padding:'3px 8px', fontSize:11 }}>삭제</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════
// 8) 평가관리
// ═══════════════════════════════════════════════════════════════
const EVAL_SORT_KEYS = ['period','employee_no','department','name','ai_score','manager_status','manager_grade','total_score', null];

// 시스템평가(AI 자동평가) 뷰어 — 평가관리 목록에서 배지 클릭시 해당 ai_reports 내용을 모달로 보여준다.
// AiAssistant.js의 ReportBody와 같은 발상이지만, 인사관리 권한만으로 접근 가능하도록 전용
// 엔드포인트(GET /hr/system-eval/:id)를 사용한다(AI 어시스턴트 메뉴 권한 불필요).
function SystemEvalModal({ reportId, onClose }) {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    API.get(`/hr/system-eval/${reportId}`)
      .then(r => setReport(r.data))
      .catch(e => setErr(e.response?.data?.message || '조회에 실패했습니다.'));
  }, [reportId]);
  return (
    <Modal title="🤖 시스템평가 (AI 자동평가 결과)" onClose={onClose}>
      {err && <div style={{ color:'#dc2626', fontSize:13 }}>{err}</div>}
      {!err && !report && <div style={{ fontSize:13, color:'#888' }}>불러오는 중...</div>}
      {report && (
        <div style={{ maxWidth:560 }}>
          <div style={{ fontSize:13, color:'#888', marginBottom:8 }}>
            {report.title} · {report.created_at?.slice(0, 16).replace('T', ' ')}
          </div>
          <div style={{ whiteSpace:'pre-wrap', fontSize:13, lineHeight:1.6, maxHeight:420, overflowY:'auto' }}>
            {report.content}
          </div>
        </div>
      )}
    </Modal>
  );
}

// AI평가 실행 모달 — 직원을 여러 명 체크박스로 선택한 뒤 일괄 실행한다. 완료되면 각 직원의
// 결과(참고점수/추천등급 또는 실패 사유)를 보여주고, 목록(hr_evaluations)에는 이미 새 평가
// 항목으로 자동 등록되어 있어 닫으면 바로 반영된 목록을 볼 수 있다.
function AiEvalModal({ employees, onClose, onDone }) {
  const nowY = new Date().getFullYear();
  const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => nowY - 4 + i); // nowY-4 ~ nowY+1
  const QUARTER_OPTIONS = ['상반기', '하반기', '1분기', '2분기', '3분기', '4분기'];
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [evalYear, setEvalYear] = useState(nowY);
  const [evalQuarter, setEvalQuarter] = useState(QUARTER_OPTIONS[0]);
  const [includeAssistant, setIncludeAssistant] = useState(false);
  const filtered = employees.filter(e => !search || (e.name || '').includes(search) || (e.department || '').includes(search));
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map(e => e.id));
  const run = () => {
    if (!selected.length) return alert('평가할 직원을 선택해주세요.');
    setRunning(true);
    const period = `${evalYear}년 ${evalQuarter}`;
    API.post('/hr/evaluations/ai-run', { userIds: selected, period, includeAssistant })
      .then(r => { setResult(r.data.results || []); onDone(); })
      .catch(e => alert(e.response?.data?.message || 'AI평가 실행에 실패했습니다.'))
      .finally(() => setRunning(false));
  };
  return (
    <Modal title="🤖 AI평가 실행" onClose={onClose}>
      {!result ? (
        <>
          <div style={{ fontSize:12, color:'#888', marginBottom:8, lineHeight:1.5 }}>
            선택한 직원의 최근 90일 업무/근태 데이터를 바탕으로 AI가 평가 초안(시스템평가)과 참고점수·추천등급(A~E)을 생성합니다.
            인원이 많으면 시간이 걸릴 수 있습니다.
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:8 }}>
            <Field label="평가기간(년도)">
              <select style={{ ...input, width:'100%' }} value={evalYear} onChange={e => setEvalYear(Number(e.target.value))}>
                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
            </Field>
            <Field label="평가기간(분기)">
              <select style={{ ...input, width:'100%' }} value={evalQuarter} onChange={e => setEvalQuarter(e.target.value)}>
                {QUARTER_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </Field>
          </div>
          <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6, cursor:'pointer', marginBottom:8, color:'#555' }}>
            <input type="checkbox" checked={includeAssistant} onChange={e => setIncludeAssistant(e.target.checked)} />
            AI어시스턴트의 직원평가/업무능력평가도 함께 분석 (선택 해제 시 선택항목만 실행)
          </label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:'100%', marginBottom:8 }} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <label style={{ fontSize:14, display:'flex', alignItems:'center', gap:4, cursor:'pointer' }}>
              <input type="checkbox" checked={filtered.length > 0 && selected.length === filtered.length} onChange={toggleAll} /> 전체선택
            </label>
            <span style={{ fontSize:14, color:'#888' }}>{selected.length}명 선택됨</span>
          </div>
          <div style={{ maxHeight:300, overflowY:'auto', border:'1px solid #eee', borderRadius:8 }}>
            {filtered.map(e => (
              <label key={e.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', fontSize:13, borderBottom:'1px solid #f5f5f5', cursor:'pointer' }}>
                <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggle(e.id)} />
                <span style={{ color:'#888', width:80 }}>{e.department || '-'}</span>
                <span style={{ fontWeight:600 }}>{e.name}</span>
              </label>
            ))}
            {filtered.length === 0 && <div style={{ padding:16, textAlign:'center', color:'#bbb', fontSize:12 }}>대상 직원이 없습니다.</div>}
          </div>
          <button onClick={run} disabled={running} style={{ ...btn(C.primary), width:'100%', padding:'10px 0', marginTop:12, opacity: running ? 0.6 : 1 }}>
            {running ? `AI평가 실행 중... (${selected.length}명)` : `선택 직원 AI평가 실행 (${selected.length}명)`}
          </button>
        </>
      ) : (
        <div>
          <div style={{ fontSize:13, marginBottom:8, fontWeight:600 }}>실행 결과</div>
          <div style={{ maxHeight:300, overflowY:'auto' }}>
            {result.map(r => (
              <div key={r.userId} style={{ display:'flex', justifyContent:'space-between', padding:'6px 4px', borderBottom:'1px solid #f5f5f5', fontSize:13 }}>
                <span>{r.name || r.userId}</span>
                {r.ok
                  ? <span style={{ color:'#16a34a' }}>완료 (점수 {r.score}, 추천등급 {r.grade})</span>
                  : <span style={{ color:'#dc2626' }}>실패: {r.message}</span>}
              </div>
            ))}
          </div>
          <button onClick={onClose} style={{ ...btn(C.primary), width:'100%', padding:'10px 0', marginTop:12 }}>닫기</button>
        </div>
      )}
    </Modal>
  );
}

// 평가기준 설정 모달 — 2026-08 개편: 좌측은 AI평가기준(기존 구성 그대로 — 참고점수를 A~E
// 추천등급으로 환산하는 등급별 최소 점수), 우측은 관리자평가기준(AI/관리자 반영비중(%) +
// 관리자평가 체크리스트)으로 좌우 분할한다. 저장 버튼 하나로 양쪽을 함께 저장한다
// (서버 GET/PUT /hr/evaluations/ai-criteria + /hr/evaluations/settings).
function AiCriteriaModal({ onClose, showMsg }) {
  const [criteria, setCriteria] = useState({ A:90, B:75, C:60, D:45, E:0 });
  const [weights, setWeights] = useState({ aiWeight:60, managerWeight:40, checklist:[] });
  const [newItem, setNewItem] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([
      API.get('/hr/evaluations/ai-criteria'),
      API.get('/hr/evaluations/settings'),
    ]).then(([r1, r2]) => { setCriteria(r1.data); setWeights(r2.data); }).finally(() => setLoading(false));
  }, []);
  const set = (g, v) => setCriteria(c => ({ ...c, [g]: v }));
  const addChecklistItem = () => {
    const v = newItem.trim();
    if (!v) return;
    setWeights(w => ({ ...w, checklist: [...w.checklist, v] }));
    setNewItem('');
  };
  const removeChecklistItem = (idx) => setWeights(w => ({ ...w, checklist: w.checklist.filter((_, i) => i !== idx) }));
  const updateChecklistItem = (idx, v) => setWeights(w => ({ ...w, checklist: w.checklist.map((c, i) => i === idx ? v : c) }));
  const save = () => {
    setSaving(true);
    Promise.all([
      API.put('/hr/evaluations/ai-criteria', criteria),
      API.put('/hr/evaluations/settings', weights),
    ])
      .then(() => { showMsg('평가기준이 저장되었습니다.'); onClose(); })
      .catch(e => alert(e.response?.data?.message || '저장에 실패했습니다.'))
      .finally(() => setSaving(false));
  };
  const gradeColor = { A:'#16a34a', B:'#185FA5', C:'#b45309', D:'#dc2626', E:'#6b7280' };
  // 2026-08 요청: AI/관리자 반영비중(%) 입력칸을 우측 "관리자평가기준" 블록에서 모달 제목
  // 옆(헤더)으로 이동. 제목에 이어 "( AI 평가 [__]% : 관리자평가 [__]% )" 형태로 붙인다.
  const modalTitle = (
    <span style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:6 }}>
      <span>⚙️ 평가기준 설정</span>
      <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:14, fontWeight:400, color:'#666' }}>
        ( AI 평가
        <input type="number" min={0} max={100} style={{ ...input, width:56, padding:'3px 6px', fontSize:14 }} value={weights.aiWeight}
          onChange={e => setWeights(w => ({ ...w, aiWeight:Number(e.target.value) }))} />
        % : 관리자평가
        <input type="number" min={0} max={100} style={{ ...input, width:56, padding:'3px 6px', fontSize:14 }} value={weights.managerWeight}
          onChange={e => setWeights(w => ({ ...w, managerWeight:Number(e.target.value) }))} />
        % )
      </span>
    </span>
  );
  return (
    <Modal title={modalTitle} onClose={onClose} width={640}>
      {loading ? <div style={{ fontSize:13, color:'#888' }}>불러오는 중...</div> : (
        <div style={{ display:'flex', gap:20 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>AI평가기준 <span style={{ fontWeight:400, color:'#888', fontSize:11 }}>(반영 {weights.aiWeight}%)</span></div>
            <div style={{ fontSize:12, color:'#888', marginBottom:12, lineHeight:1.5 }}>
              AI평가가 계산한 참고점수(0~100)를 등급별 최소 점수 기준으로 A~E 추천등급으로 환산합니다.
            </div>
            {['A','B','C','D','E'].map(g => (
              <div key={g} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <span style={{ width:16, fontWeight:800, color: gradeColor[g] }}>{g}</span>
                <input type="number" min={0} max={100} style={{ ...input, width:64 }} value={criteria[g]} onChange={e => set(g, Number(e.target.value))} />
                <span style={{ fontSize:12, color:'#888' }}>점 이상</span>
              </div>
            ))}
          </div>
          <div style={{ width:1, background:'#eee' }} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, color:'#888', marginBottom:6 }}>관리자평가 체크리스트</div>
            <div style={{ maxHeight:180, overflowY:'auto', marginBottom:8 }}>
              {weights.checklist.map((c, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                  <input style={{ ...input, flex:1, padding:'6px 8px', fontSize:13 }} value={c} onChange={e => updateChecklistItem(i, e.target.value)} />
                  <button onClick={() => removeChecklistItem(i)} style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:13 }}>삭제</button>
                </div>
              ))}
              {weights.checklist.length === 0 && <div style={{ fontSize:12, color:'#bbb', padding:'8px 0' }}>등록된 항목이 없습니다.</div>}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <input style={{ ...input, flex:1, padding:'6px 8px', fontSize:13 }} placeholder="체크리스트 항목 추가"
                value={newItem} onChange={e => setNewItem(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }} />
              <button onClick={addChecklistItem} style={{ ...btn('#f0f0f0', '#555'), padding:'6px 12px', fontSize:13 }}>+ 추가</button>
            </div>
          </div>
        </div>
      )}
      {!loading && <button onClick={save} disabled={saving} style={{ ...btn(C.primary), width:'100%', padding:'10px 0', marginTop:16 }}>저장</button>}
    </Modal>
  );
}

// 관리자평가 모달 — 평가관리 목록의 "관리자평가(%)" 열에서 "평가대기"를 클릭하면 열린다.
// 기준설정에서 정의한 체크리스트를 보여주고(이미 평가한 적이 있으면 그 상태를 그대로 불러옴),
// 체크 비율로 점수를 산정해 서버(POST /hr/evaluations/:id/manager-eval)에 제출한다.
// 2026-08 개편: 항목별 체크(선택/미선택) 대신 A~E 등급을 선택하는 방식. 등급별 점수를
// 환산(백엔드 MANAGER_GRADE_SCORE와 동일 값)해 평균 내어 예상 점수를 미리 보여준다.
const MANAGER_GRADE_SCORE = { A:100, B:80, C:60, D:40, E:20 };
const MANAGER_GRADE_COLOR = { A:'#16a34a', B:'#185FA5', C:'#b45309', D:'#dc2626', E:'#6b7280' };
const MANAGER_GRADES = ['A', 'B', 'C', 'D', 'E'];

function ManagerEvalModal({ evalRow, defaultChecklist, onClose, onDone, showMsg }) {
  const initial = (Array.isArray(evalRow.manager_checklist) && evalRow.manager_checklist.length)
    ? evalRow.manager_checklist.map(c => ({ label: c.label, grade: MANAGER_GRADE_SCORE[c.grade] ? c.grade : null }))
    : (defaultChecklist || []).map(label => ({ label, grade:null }));
  const [checklist, setChecklist] = useState(initial);
  const [comments, setComments] = useState(evalRow.manager_comments || '');
  const [saving, setSaving] = useState(false);
  const setGrade = (i, g) => setChecklist(cl => cl.map((c, idx) => idx === i ? { ...c, grade:g } : c));
  const gradedCount = checklist.filter(c => c.grade).length;
  const previewScore = gradedCount
    ? Math.round(checklist.reduce((sum, c) => sum + (MANAGER_GRADE_SCORE[c.grade] || 0), 0) / gradedCount)
    : 0;
  const submit = () => {
    if (!checklist.length) return alert('체크리스트 항목이 없습니다. 기준설정에서 항목을 추가해주세요.');
    if (checklist.some(c => !c.grade)) return alert('모든 항목의 등급(A~E)을 선택해주세요.');
    setSaving(true);
    API.post(`/hr/evaluations/${evalRow.id}/manager-eval`, { checklist, comments })
      .then(() => { showMsg('관리자평가가 등록되었습니다.'); onDone(); onClose(); })
      .catch(e => alert(e.response?.data?.message || '저장에 실패했습니다.'))
      .finally(() => setSaving(false));
  };
  return (
    <Modal title={`📝 관리자평가 - ${evalRow.name}`} onClose={onClose}>
      <div style={{ marginBottom:12, maxHeight:260, overflowY:'auto' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr repeat(5, 32px)', alignItems:'center', gap:4, marginBottom:4 }}>
          <div />
          {MANAGER_GRADES.map(g => (
            <div key={g} style={{ textAlign:'center', fontSize:13, fontWeight:700, color:MANAGER_GRADE_COLOR[g] }}>{g}</div>
          ))}
        </div>
        {checklist.map((c, i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr repeat(5, 32px)', alignItems:'center', gap:4, padding:'7px 0', borderBottom:'1px solid #f1f3f5' }}>
            <div style={{ fontSize:13 }}>{c.label}</div>
            {MANAGER_GRADES.map(g => (
              <div key={g} style={{ display:'flex', justifyContent:'center' }}>
                <button type="button" onClick={() => setGrade(i, g)} title={`${c.label} - ${g}등급`}
                  style={{
                    width:22, height:22, borderRadius:4, cursor:'pointer', padding:0,
                    border:`1.5px solid ${c.grade === g ? MANAGER_GRADE_COLOR[g] : '#d0d5dd'}`,
                    background: c.grade === g ? MANAGER_GRADE_COLOR[g] : '#fff',
                  }} />
              </div>
            ))}
          </div>
        ))}
        {checklist.length === 0 && <div style={{ fontSize:12, color:'#bbb' }}>체크리스트 항목이 없습니다. 기준설정에서 추가해주세요.</div>}
      </div>
      <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>예상 점수: <b>{previewScore}점</b> ({gradedCount}/{checklist.length} 선택)</div>
      <Field label="의견"><textarea rows={4} style={{ ...input, width:'100%', resize:'vertical' }} value={comments} onChange={e => setComments(e.target.value)} /></Field>
      <button onClick={submit} disabled={saving} style={{ ...btn(C.primary), width:'100%', padding:'10px 0', marginTop:8 }}>
        {saving ? '저장 중...' : '평가 완료 등록'}
      </button>
    </Modal>
  );
}

function EvaluationTab({ employees, perm, showMsg, headerActionsEl, tabBarActionsEl }) {
  const canWrite = perm.hasDetail('hr-management', 'evaluation_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.evaluation)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.evaluation);
  const canDelete = perm.hasDetail('hr-management', 'delete_allowed')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.evaluation)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.evaluation);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id:'', period:'', score:'', grade:'B', comments:'' });
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const [sysEvalReportId, setSysEvalReportId] = useState(null);
  const [showAiEval, setShowAiEval] = useState(false);
  const [showAiCriteria, setShowAiCriteria] = useState(false);
  const [managerEvalRow, setManagerEvalRow] = useState(null);
  const [checklistDefaults, setChecklistDefaults] = useState([]);
  const { selYear, selMonth, setSelYear, setSelMonth, shiftMonth, month } = useMonthPicker();

  const load = () => API.get('/hr/evaluations').then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  // 관리자평가 모달을 열 때 기본 체크리스트가 즉시 있어야 하므로, 화면 진입 시 한 번 미리 받아둔다.
  useEffect(() => { API.get('/hr/evaluations/settings').then(r => setChecklistDefaults(r.data.checklist || [])).catch(() => {}); }, []);

  const submit = () => {
    if (!form.user_id || !form.period) return alert('대상자와 평가기간을 입력해주세요.');
    API.post('/hr/evaluations', form).then(() => { showMsg('평가가 등록되었습니다.'); setShowForm(false); load(); });
  };
  const del = (id) => { if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/hr/evaluations/${id}`).then(() => { showMsg('삭제되었습니다.'); load(); }); };

  const gradeColor = { S:'#7c3aed', A:'#16a34a', B:'#185FA5', C:'#b45309', D:'#dc2626', E:'#6b7280' };

  // 평가기간은 자유 텍스트라 달력(월)은 등록일(created_at) 기준으로 필터링한다.
  const monthEvalRows = rows.filter(r => (r.created_at || '').slice(0, 7) === month);
  const filteredEvalRows = filterBySearch(monthEvalRows, search, ['name', 'department']);
  const { sorted: sortedEvalRows, sortKey: evalSortKey, sortDir: evalSortDir, toggleSort: evalToggleSort } = useSort(filteredEvalRows, 'created_at', 'desc');
  useEffect(() => { setPage(1); }, [filteredEvalRows.length, pageSize]);
  const evalTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedEvalRows.length / pageSize));
  const pagedEvalRows = pageSize === 'all' ? sortedEvalRows : sortedEvalRows.slice((page - 1) * pageSize, page * pageSize);

  // 종합결과 열 — "합계등급 (점수) : AI평가 (등급-점수) + 관리자평가 (등급-점수)" 형식으로 표기.
  const renderTotalCell = (r) => {
    if (r.total_grade == null) return <span style={{ color:'#bbb', fontSize:14 }}>-</span>;
    const aiPart = `AI(${r.ai_grade || '-'}-${r.ai_score ?? '-'})`;
    const mgrPart = r.manager_status === '완료' ? `관리자(${r.manager_grade || '-'}-${r.manager_score ?? '-'})` : '관리자(평가대기)';
    return (
      <span style={{ fontSize:14 }}>
        <span style={{ fontWeight:800, color: gradeColor[r.total_grade] || '#555' }}>{r.total_grade}</span>
        {' '}({r.total_score ?? '-'}) : {aiPart} + {mgrPart}
      </span>
    );
  };

  const printList = () => printListPopup({
    title: `평가관리 (${month})`, menuLabel: '인사관리(평가관리)',
    columns: ['평가기간', '사번', '부서', '이름', 'AI평가(%)', 'AI등급', '관리자평가(%)', '관리자등급', '종합결과'],
    rows: sortedEvalRows.map(r => [
      r.period, r.employee_no || '-', r.department || '-', r.name,
      r.ai_score ?? '-', r.ai_grade || '-',
      r.manager_status === '완료' ? (r.manager_score ?? '-') : '평가대기',
      r.manager_grade || '-',
      r.total_grade ? `${r.total_grade} (${r.total_score ?? '-'})` : '-',
    ]),
  });
  const downloadList = () => downloadListCsv('/hr/evaluations/export', 'IMS_평가관리.csv', '인사관리(평가관리)');

  return (
    <>
    <HeaderPortal target={headerActionsEl}>
      <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
      <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
      {canWrite && <button onClick={() => setShowAiEval(true)} style={btn('#4338ca')}>🤖 AI평가</button>}
      {canWrite && <button onClick={() => setShowAiCriteria(true)} style={btn('#f0f0f0', '#555')}>⚙️ 기준설정</button>}
      {canWrite && <button onClick={() => setShowForm(true)} style={btn(C.primary)}>+ 평가 등록</button>}
    </HeaderPortal>
    <HeaderPortal target={tabBarActionsEl}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:200 }} />
      <MonthPicker selYear={selYear} selMonth={selMonth} setSelYear={setSelYear} setSelMonth={setSelMonth} shiftMonth={shiftMonth} />
      <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />
    </HeaderPortal>
    <Section>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['평가기간','사번','부서','이름','AI평가(%)','관리자평가(%)','등급','종합결과',''].map((h,i) => (
            <SortTh key={i} label={h} sortKey={EVAL_SORT_KEYS[i]} currentKey={evalSortKey} currentDir={evalSortDir} onSort={evalToggleSort} />
          ))}</tr></thead>
          <tbody>
            {pagedEvalRows.length === 0 && <EmptyRow span={9} />}
            {pagedEvalRows.map(r => (
              <tr key={r.id}>
                <td style={td}>{r.period}</td><td style={td}>{r.employee_no || '-'}</td><td style={td}>{r.department || '-'}</td><td style={{ ...td, fontWeight:600 }}>{r.name}</td>
                <td style={td}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, flexWrap:'wrap' }}>
                    {r.system_eval_report_id && (
                      <button onClick={() => setSysEvalReportId(r.system_eval_report_id)}
                        style={{ padding:'2px 8px', borderRadius:20, fontSize:14, fontWeight:700, background:'#eef2ff', color:'#4338ca', border:'none', cursor:'pointer' }}>
                        🤖 보기
                      </button>
                    )}
                    {r.ai_grade && <span style={{ fontWeight:800, fontSize:14, color: gradeColor[r.ai_grade] || '#555' }}>{r.ai_grade}</span>}
                    <span style={{ fontSize:14, color:'#888' }}>({r.ai_score ?? '-'})</span>
                  </div>
                </td>
                <td style={td}>
                  {r.manager_status === '완료' ? (
                    <span style={{ fontSize:14, fontWeight:600 }}>
                      {`${r.manager_evaluator_title || ''} ${r.manager_evaluator_name || ''}`.trim() || '-'}
                      {canWrite && (
                        <button onClick={() => setManagerEvalRow(r)} style={{ marginLeft:6, background:'none', border:'none', color:'#185FA5', fontSize:14, cursor:'pointer' }}>수정</button>
                      )}
                    </span>
                  ) : canWrite ? (
                    <button onClick={() => setManagerEvalRow(r)}
                      style={{ padding:'2px 10px', borderRadius:20, fontSize:14, fontWeight:700, background:'#fef3c7', color:'#b45309', border:'none', cursor:'pointer' }}>
                      평가대기
                    </button>
                  ) : (
                    <span style={{ fontSize:14, color:'#b45309', fontWeight:700 }}>평가대기</span>
                  )}
                </td>
                <td style={td}>{r.manager_grade ? <span style={{ fontWeight:800, color: gradeColor[r.manager_grade] || '#555' }}>{r.manager_grade}</span> : <span style={{ color:'#bbb', fontSize:14 }}>-</span>}</td>
                <td style={{ ...td, maxWidth:260 }}>{renderTotalCell(r)}</td>
                <td style={td}>{canDelete && <button onClick={() => del(r.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:14, cursor:'pointer', fontWeight:600 }}>삭제</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={evalTotalPages} />}

      {showForm && (
        <Modal title="📊 평가 등록" onClose={() => setShowForm(false)}>
          <Field label="대상자"><EmpSelect employees={employees} value={form.user_id} onChange={v => setForm(f => ({ ...f, user_id:v }))} /></Field>
          <Field label="평가기간"><input placeholder="예: 2026년 상반기" style={{ ...input, width:'100%' }} value={form.period} onChange={e => setForm(f => ({ ...f, period:e.target.value }))} /></Field>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="AI평가 점수"><input type="number" style={{ ...input, width:'100%' }} value={form.score} onChange={e => setForm(f => ({ ...f, score:e.target.value }))} /></Field>
            <Field label="AI평가 등급">
              <select style={{ ...input, width:'100%' }} value={form.grade} onChange={e => setForm(f => ({ ...f, grade:e.target.value }))}>
                {['S','A','B','C','D'].map(g => <option key={g}>{g}</option>)}
              </select>
            </Field>
          </div>
          <Field label="비고"><textarea rows={3} style={{ ...input, width:'100%', resize:'vertical' }} value={form.comments} onChange={e => setForm(f => ({ ...f, comments:e.target.value }))} /></Field>
          <button onClick={submit} style={{ ...btn(C.primary), width:'100%', padding:'10px 0' }}>등록</button>
        </Modal>
      )}
      {sysEvalReportId && <SystemEvalModal reportId={sysEvalReportId} onClose={() => setSysEvalReportId(null)} />}
      {showAiEval && <AiEvalModal employees={employees} onClose={() => setShowAiEval(false)} onDone={load} />}
      {showAiCriteria && <AiCriteriaModal showMsg={showMsg} onClose={() => setShowAiCriteria(false)} />}
      {managerEvalRow && (
        <ManagerEvalModal evalRow={managerEvalRow} defaultChecklist={checklistDefaults} showMsg={showMsg}
          onClose={() => setManagerEvalRow(null)} onDone={load} />
      )}
    </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 9) 교육관리
// ═══════════════════════════════════════════════════════════════
const TRAINING_SORT_KEYS = ['category','start_date','title','attendee_count','location','instructor','note'];

function TrainingTab({ employees, perm, user, showMsg, headerActionsEl, tabBarActionsEl }) {
  const canWrite = perm.hasDetail('hr-management', 'training_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.training)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.training);
  const canDelete = perm.hasDetail('hr-management', 'delete_allowed')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.training)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.training);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const EMPTY_TRAINING_FORM = { title:'', category:'직무교육', start_date:'', end_date:'', instructor:'', location:'', note:'', cost_type:'무상', cost_amount:'' };
  const [form, setForm] = useState(EMPTY_TRAINING_FORM);
  const [selected, setSelected] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const { selYear, selMonth, setSelYear, setSelMonth, shiftMonth, month } = useMonthPicker();

  // 2026-08 신규: 교육등록 시점에 참석자를 곧바로 여러 명 골라 넣을 수 있게 한다 — 부서/직급/이름
  // 순서로 표기되는 드롭다운(EmpSelect)에서 하나씩 골라 추가하면 아래 칩 목록에 쌓이고, 등록
  // 버튼을 누르면 교육과정 생성 직후 이 명단 그대로 참석자로 일괄 등록된다.
  const [pendingAttendeeId, setPendingAttendeeId] = useState('');
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const addPendingAttendee = () => {
    if (!pendingAttendeeId) return;
    if (selectedAttendees.some(a => String(a.id) === String(pendingAttendeeId))) { setPendingAttendeeId(''); return; }
    const emp = employees.find(e => String(e.id) === String(pendingAttendeeId));
    if (emp) setSelectedAttendees(prev => [...prev, emp]);
    setPendingAttendeeId('');
  };
  const removePendingAttendee = (id) => setSelectedAttendees(prev => prev.filter(a => String(a.id) !== String(id)));
  const closeForm = () => { setShowForm(false); setForm(EMPTY_TRAINING_FORM); setSelectedAttendees([]); setPendingAttendeeId(''); };
  const openForm = () => { setForm(EMPTY_TRAINING_FORM); setSelectedAttendees([]); setPendingAttendeeId(''); setShowForm(true); };

  const load = () => API.get('/hr/trainings').then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = () => {
    if (!form.title) return alert('교육명을 입력해주세요.');
    API.post('/hr/trainings', form).then(r => {
      const id = r.data.id;
      Promise.all(selectedAttendees.map(a => API.post(`/hr/trainings/${id}/attendees`, { user_id:a.id }).catch(() => {})))
        .finally(() => { showMsg('교육과정이 등록되었습니다.'); closeForm(); load(); });
    });
  };
  const del = (id) => { if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/hr/trainings/${id}`).then(() => { showMsg('삭제되었습니다.'); load(); if (selected?.id === id) setSelected(null); }); };

  const openAttendees = (t) => { setSelected(t); API.get(`/hr/trainings/${t.id}/attendees`).then(r => setAttendees(r.data)); };
  const [addUserId, setAddUserId] = useState('');
  const addAttendee = () => {
    if (!addUserId) return;
    API.post(`/hr/trainings/${selected.id}/attendees`, { user_id: addUserId }).then(() => {
      setAddUserId('');
      API.get(`/hr/trainings/${selected.id}/attendees`).then(r => setAttendees(r.data));
    });
  };
  const setAttStatus = (id, status) => API.patch(`/hr/training-attendees/${id}`, { status }).then(() =>
    API.get(`/hr/trainings/${selected.id}/attendees`).then(r => setAttendees(r.data)));
  const delAttendee = (id) => API.delete(`/hr/training-attendees/${id}`).then(() =>
    API.get(`/hr/trainings/${selected.id}/attendees`).then(r => setAttendees(r.data)));

  const attColor = { '신청':{bg:'#fef3c7',c:'#b45309'}, '수료':{bg:'#dcfce7',c:'#16a34a'}, '불참':{bg:'#fee2e2',c:'#dc2626'} };

  const monthTrainingRows = rows.filter(t => (t.start_date || '').slice(0, 7) === month);
  const filteredTrainingRows = filterBySearch(monthTrainingRows, search, ['title', 'instructor', 'location']);
  const { sorted: sortedTrainingRows, sortKey: trainingSortKey, sortDir: trainingSortDir, toggleSort: trainingToggleSort } = useSort(filteredTrainingRows, 'created_at', 'desc');
  useEffect(() => { setPage(1); }, [filteredTrainingRows.length, pageSize]);
  const trainingTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedTrainingRows.length / pageSize));
  const pagedTrainingRows = pageSize === 'all' ? sortedTrainingRows : sortedTrainingRows.slice((page - 1) * pageSize, page * pageSize);

  const printList = () => printListPopup({
    title: `교육과정 (${month})`, menuLabel: '인사관리(교육관리)',
    columns: ['구분','교육기간','교육명','참석자','장소','강사','비고'],
    rows: sortedTrainingRows.map(t => [t.category || '-', `${t.start_date || ''}~${t.end_date || ''}`, t.title, t.attendee_count ?? 0, t.location || '-', t.instructor || '-', t.note || '-']),
  });
  const downloadList = () => downloadListCsv('/hr/trainings/export', 'IMS_교육과정.csv', '인사관리(교육관리)');

  return (
    <div style={{ display:'grid', gridTemplateColumns: selected ? '1.1fr 1fr' : '1fr', gap:16 }}>
      <HeaderPortal target={headerActionsEl}>
        <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
        <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
        {canWrite && <button onClick={openForm} style={btn(C.primary)}>+ 교육 등록</button>}
      </HeaderPortal>
      <HeaderPortal target={tabBarActionsEl}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="교육명/강사/장소 검색" style={{ ...input, width:200 }} />
        <MonthPicker selYear={selYear} selMonth={selMonth} setSelYear={setSelYear} setSelMonth={setSelMonth} shiftMonth={shiftMonth} />
        <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />
      </HeaderPortal>
      <Section>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['구분','교육기간','교육명','참석자','장소','강사','비고',''].map((h,i) => (
              <SortTh key={i} label={h} sortKey={TRAINING_SORT_KEYS[i]} currentKey={trainingSortKey} currentDir={trainingSortDir} onSort={trainingToggleSort} />
            ))}</tr></thead>
            <tbody>
              {pagedTrainingRows.length === 0 && <EmptyRow span={8} />}
              {pagedTrainingRows.map(t => (
                <tr key={t.id} onClick={() => openAttendees(t)} style={{ cursor:'pointer', background: selected?.id === t.id ? '#f0f7ff' : 'transparent' }}>
                  <td style={td}>{t.category || '-'}</td>
                  <td style={td}>{t.start_date || '-'} ~ {t.end_date || '-'}</td>
                  <td style={{ ...td, fontWeight:600 }}>{t.title}</td>
                  <td style={{ ...td, textAlign:'center' }}>{t.attendee_count ?? 0}</td>
                  <td style={td}>{t.location || '-'}</td>
                  <td style={td}>{t.instructor || '-'}</td>
                  <td style={td}>{t.note || '-'}</td>
                  <td style={td} onClick={e => e.stopPropagation()}>{canDelete && <button onClick={() => del(t.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>삭제</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={trainingTotalPages} />}
      </Section>

      {selected && (
        <Section title={`참가자 — ${selected.title}`} action={<button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:'#999', cursor:'pointer', fontSize:13 }}>닫기 ✕</button>}>
          <div style={{ display:'flex', gap:6, marginBottom:12 }}>
            <div style={{ flex:1 }}><EmpSelect employees={employees} value={addUserId} onChange={setAddUserId} placeholder="참가자 추가" /></div>
            <button onClick={addAttendee} style={btn(C.primary)}>추가</button>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['부서','이름','상태',''].map((h,i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {attendees.length === 0 && <EmptyRow span={4} />}
                {attendees.map(a => (
                  <tr key={a.id}>
                    <td style={td}>{a.department || '-'}</td><td style={{ ...td, fontWeight:600 }}>{a.name}</td>
                    <td style={td}>
                      {canWrite ? (
                        <select value={a.status} onChange={e => setAttStatus(a.id, e.target.value)} style={{ ...input, padding:'3px 6px', fontSize:12 }}>
                          {['신청','수료','불참'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      ) : (() => { const c = attColor[a.status] || { bg:'#f1f5f9', c:'#888' };
                        return <span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:c.bg, color:c.c }}>{a.status}</span>; })()}
                    </td>
                    <td style={td}><button onClick={() => delAttendee(a.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>제외</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {showForm && (
        <Modal title="🎓 교육과정 등록" onClose={closeForm}>
          {/* 1. 구분 */}
          <Field label="구분">
            <select style={{ ...input, width:'100%' }} value={form.category} onChange={e => setForm(f => ({ ...f, category:e.target.value }))}>
              {['법정의무교육','직무교육','리더십','안전교육','기타'].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          {/* 2. 교육명 */}
          <Field label="교육명"><input style={{ ...input, width:'100%' }} value={form.title} onChange={e => setForm(f => ({ ...f, title:e.target.value }))} /></Field>
          {/* 3. 시작일/종료일 — flex:1 래핑으로 가로폭에 맞게 균등 배치 */}
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1 }}><Field label="시작일"><input type="date" style={{ ...input, width:'100%' }} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date:e.target.value }))} /></Field></div>
            <div style={{ flex:1 }}><Field label="종료일"><input type="date" style={{ ...input, width:'100%' }} value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date:e.target.value }))} /></Field></div>
          </div>
          {/* 4. 교육장소, 강사 순서 */}
          <div style={{ display:'flex', gap:8 }}>
            <div style={{ flex:1 }}><Field label="교육장소"><input style={{ ...input, width:'100%' }} value={form.location} onChange={e => setForm(f => ({ ...f, location:e.target.value }))} /></Field></div>
            <div style={{ flex:1 }}><Field label="강사"><input style={{ ...input, width:'100%' }} value={form.instructor} onChange={e => setForm(f => ({ ...f, instructor:e.target.value }))} /></Field></div>
          </div>
          {/* 5. 참석자 — 부서·직급·이름 순으로 표기되는 드롭다운에서 골라 여러 명 추가 */}
          <Field label="참석자">
            <div style={{ display:'flex', gap:6, marginBottom:6 }}>
              <div style={{ flex:1 }}><EmpSelect employees={employees} value={pendingAttendeeId} onChange={setPendingAttendeeId} placeholder="참석자 선택" /></div>
              <button type="button" onClick={addPendingAttendee} style={btn(C.primary)}>추가</button>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {selectedAttendees.length === 0 && <span style={{ fontSize:12, color:'#999' }}>추가된 참석자가 없습니다.</span>}
              {selectedAttendees.map(a => (
                <span key={a.id} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 8px', borderRadius:6, background:'#f1f5f9', fontSize:12 }}>
                  {a.department || '-'} · {a.position || '-'} · {a.name}
                  <button type="button" onClick={() => removePendingAttendee(a.id)} style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontWeight:700 }}>×</button>
                </span>
              ))}
            </div>
          </Field>
          {/* 6. 교육비 — 무상/유상 선택, 유상이면 우측에 금액 입력 */}
          <Field label="교육비">
            <div style={{ display:'flex', gap:8 }}>
              <select style={{ ...input, width: form.cost_type === '유상' ? 110 : '100%' }} value={form.cost_type}
                onChange={e => setForm(f => ({ ...f, cost_type:e.target.value, cost_amount: e.target.value === '무상' ? '' : f.cost_amount }))}>
                <option value="무상">무상</option>
                <option value="유상">유상</option>
              </select>
              {form.cost_type === '유상' && (
                <input type="number" placeholder="금액" style={{ ...input, flex:1 }} value={form.cost_amount} onChange={e => setForm(f => ({ ...f, cost_amount:e.target.value }))} />
              )}
            </div>
          </Field>
          {/* 7. 비고 */}
          <Field label="비고"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={form.note} onChange={e => setForm(f => ({ ...f, note:e.target.value }))} /></Field>
          <button onClick={submit} style={{ ...btn(C.primary), width:'100%', padding:'10px 0' }}>등록</button>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 10) 경조사관리
// ═══════════════════════════════════════════════════════════════
const EVENT_SORT_KEYS = ['event_date','employee_no','department','name','event_type','support_amount','note', null];

function EventTab({ employees, perm, showMsg, headerActionsEl, tabBarActionsEl }) {
  const canWrite = perm.hasDetail('hr-management', 'event_write')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.event)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.event);
  const canDelete = perm.hasDetail('hr-management', 'delete_allowed')
    && perm.hasListItem('hr-management', 'category_read_scope', HR_TAB_LABELS.event)
    && perm.hasListItem('hr-management', 'category_write_scope', HR_TAB_LABELS.event);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id:'', event_type:'결혼', event_date:'', support_amount:0, note:'' });
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);
  const { selYear, selMonth, setSelYear, setSelMonth, shiftMonth, month } = useMonthPicker();

  const load = () => API.get('/hr/events').then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = () => {
    if (!form.user_id || !form.event_date) return alert('대상자와 일자를 입력해주세요.');
    API.post('/hr/events', form).then(() => { showMsg('경조사가 등록되었습니다.'); setShowForm(false); load(); });
  };
  const del = (id) => { if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/hr/events/${id}`).then(() => { showMsg('삭제되었습니다.'); load(); }); };

  const monthEventRows = rows.filter(r => (r.event_date || '').slice(0, 7) === month);
  const filteredEventRows = filterBySearch(monthEventRows, search, ['name', 'department']);
  const { sorted: sortedEventRows, sortKey: eventSortKey, sortDir: eventSortDir, toggleSort: eventToggleSort } = useSort(filteredEventRows, 'created_at', 'desc');
  useEffect(() => { setPage(1); }, [filteredEventRows.length, pageSize]);
  const eventTotalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedEventRows.length / pageSize));
  const pagedEventRows = pageSize === 'all' ? sortedEventRows : sortedEventRows.slice((page - 1) * pageSize, page * pageSize);

  const printList = () => printListPopup({
    title: `경조사 내역 (${month})`, menuLabel: '인사관리(경조사관리)',
    columns: ['일자','사번','부서','이름','구분','지원금','비고'],
    rows: sortedEventRows.map(r => [r.event_date, r.employee_no || '-', r.department || '-', r.name, r.event_type, (r.support_amount || 0).toLocaleString() + '원', r.note || '-']),
  });
  const downloadList = () => downloadListCsv('/hr/events/export', 'IMS_경조사내역.csv', '인사관리(경조사관리)');

  return (
    <>
    <HeaderPortal target={headerActionsEl}>
      <button onClick={printList} style={btn('#f0f0f0', '#555')}>🖨️ 인쇄</button>
      <button onClick={downloadList} style={btn('#16a34a')}>📤 내보내기</button>
      {canWrite && <button onClick={() => setShowForm(true)} style={btn(C.primary)}>+ 경조사 등록</button>}
    </HeaderPortal>
    <HeaderPortal target={tabBarActionsEl}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서 검색" style={{ ...input, width:200 }} />
      <MonthPicker selYear={selYear} selMonth={selMonth} setSelYear={setSelYear} setSelMonth={setSelMonth} shiftMonth={shiftMonth} />
      <PageSizeSelect pageSize={pageSize} setPageSize={setPageSize} />
    </HeaderPortal>
    <Section>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['일자','사번','부서','이름','구분','지원금','비고',''].map((h,i) => (
            <SortTh key={i} label={h} sortKey={EVENT_SORT_KEYS[i]} currentKey={eventSortKey} currentDir={eventSortDir} onSort={eventToggleSort} />
          ))}</tr></thead>
          <tbody>
            {pagedEventRows.length === 0 && <EmptyRow span={8} />}
            {pagedEventRows.map(r => (
              <tr key={r.id}>
                <td style={td}>{r.event_date}</td><td style={td}>{r.employee_no || '-'}</td><td style={td}>{r.department || '-'}</td><td style={{ ...td, fontWeight:600 }}>{r.name}</td>
                <td style={td}>{r.event_type}</td><td style={{ ...td, textAlign:'right' }}>{(r.support_amount || 0).toLocaleString()}원</td>
                <td style={td}>{r.note || '-'}</td>
                <td style={td}>{canDelete && <button onClick={() => del(r.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>삭제</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageSize !== 'all' && <Pagination page={page} setPage={setPage} totalPages={eventTotalPages} />}

      {showForm && (
        <Modal title="🎉 경조사 등록" onClose={() => setShowForm(false)}>
          <Field label="대상자"><EmpSelect employees={employees} value={form.user_id} onChange={v => setForm(f => ({ ...f, user_id:v }))} /></Field>
          <Field label="구분">
            <select style={{ ...input, width:'100%' }} value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type:e.target.value }))}>
              {['결혼','출산','사망(본인·가족)','생일','입사기념일','기타'].map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <div style={{ display:'flex', gap:8 }}>
            <Field label="일자"><input type="date" style={{ ...input, width:'100%' }} value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date:e.target.value }))} /></Field>
            <Field label="지원금"><input type="number" style={{ ...input, width:'100%' }} value={form.support_amount} onChange={e => setForm(f => ({ ...f, support_amount:e.target.value }))} /></Field>
          </div>
          <Field label="비고"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={form.note} onChange={e => setForm(f => ({ ...f, note:e.target.value }))} /></Field>
          <button onClick={submit} style={{ ...btn(C.primary), width:'100%', padding:'10px 0' }}>등록</button>
        </Modal>
      )}
    </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 계정관리(구)에서 이관된 모달 — 일반설정(메뉴별 읽기/쓰기) + 세부설정
// (AdminUsers.js의 PermissionModal/DetailPermissionModal과 동일한 구현, 위치만 이동)
// 2026-08: 계정관리(마이페이지)의 회원 리스트 권한설정 버튼에서도 그대로 재사용하기 위해 export한다.
// ═══════════════════════════════════════════════════════════════
export function PermissionModal({ user, menus, initialPerms, onClose, onSaved, onOpenDetail }) {
  const initial = {};
  menus.forEach(m => {
    const found = initialPerms.find(p => p.menu_key === m.key);
    initial[m.key] = { can_read: !!found?.can_read, can_write: !!found?.can_write };
  });

  const [perms, setPerms]     = useState(initial);
  const [saving, setSaving]   = useState(false);

  // 2026-08: 비밀번호 초기화 버튼은 계정관리 모달(👤계정관리 → 🔑 비밀번호 초기화)로 이전했다.
  // 권한설정 화면에서는 삭제.

  const toggle = (key, field) =>
    setPerms(prev => ({ ...prev, [key]: { ...prev[key], [field]: !prev[key][field] } }));

  const setAll = (field, value) =>
    setPerms(prev => {
      const next = {};
      menus.forEach(m => {
        if (field === 'can_write' && NO_WRITE_MENUS.includes(m.key)) {
          next[m.key] = { ...prev[m.key], can_write: false };
        } else {
          next[m.key] = { ...prev[m.key], [field]: value };
        }
      });
      return next;
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = menus.map(m => ({
        menu_key: m.key,
        can_read: perms[m.key].can_read,
        can_write: perms[m.key].can_write,
      }));
      await API.put('/permissions/' + user.username, { permissions: payload });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:520, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🔐 권한설정</h3>
            <div style={{ fontSize:13, color:'#888', marginTop:4 }}>{formatUserLine(user)}</div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ padding:'16px 24px' }}>
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', justifyContent:'center' }}>
            <button onClick={() => setAll('can_read', true)}  style={quickBtn}>읽기 전체 켜기</button>
            <button onClick={() => setAll('can_read', false)} style={quickBtn}>읽기 전체 끄기</button>
            <button onClick={() => setAll('can_write', true)}  style={quickBtn}>쓰기 전체 켜기</button>
            <button onClick={() => setAll('can_write', false)} style={quickBtn}>쓰기 전체 끄기</button>
          </div>

          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                <th style={{ ...thStyle, padding:'8px 10px' }}>메뉴</th>
                <th style={{ ...thStyle, padding:'8px 10px', textAlign:'center', width:60 }}>읽기</th>
                <th style={{ ...thStyle, padding:'8px 10px', textAlign:'center', width:60 }}>쓰기</th>
                <th style={{ ...thStyle, padding:'8px 10px', textAlign:'center', width:96 }}>상세설정</th>
              </tr>
            </thead>
            <tbody>
              {menus.map(m => {
                const hasDetail = !!DETAIL_DEFS[m.key];
                const granted = perms[m.key].can_read || perms[m.key].can_write;
                return (
                <tr key={m.key}>
                  <td style={{ ...tdStyle, padding:'8px 10px' }}>{m.label}</td>
                  <td style={{ ...tdStyle, padding:'8px 10px', textAlign:'center' }}>
                    <input type="checkbox" checked={perms[m.key].can_read}
                      onChange={() => toggle(m.key, 'can_read')}
                      style={{ width:16, height:16, cursor:'pointer' }} />
                  </td>
                  <td style={{ ...tdStyle, padding:'8px 10px', textAlign:'center' }}>
                    {NO_WRITE_MENUS.includes(m.key) ? (
                      <span style={{ color:'#ccc' }}>-</span>
                    ) : (
                      <input type="checkbox" checked={perms[m.key].can_write}
                        onChange={() => toggle(m.key, 'can_write')}
                        style={{ width:16, height:16, cursor:'pointer' }} />
                    )}
                  </td>
                  <td style={{ ...tdStyle, padding:'8px 10px', textAlign:'center' }}>
                    {!hasDetail ? (
                      <span style={{ color:'#ccc' }}>-</span>
                    ) : (
                      <button
                        disabled={!granted}
                        onClick={() => onOpenDetail(m.key, perms[m.key])}
                        title={!granted ? '먼저 읽기 또는 쓰기를 켜주세요' : ''}
                        style={{
                          padding:'4px 10px', fontSize:12, fontWeight:600, borderRadius:6,
                          cursor: granted ? 'pointer' : 'not-allowed',
                          background: granted ? '#fff' : '#f5f5f5',
                          color: granted ? '#7C3AED' : '#bbb',
                          border: `1px solid ${granted ? '#7C3AED' : '#ddd'}`
                        }}>
                        상세설정
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:1, padding:'11px', background:saving?'#aaa':'#185FA5', color:'#fff',
              border:'none', borderRadius:8, cursor:saving?'not-allowed':'pointer', fontSize:14, fontWeight:600 }}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', background:'#f0f0f0', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

export function DetailPermissionModal({ user, menus, initialPerms, initialDetails, initialMenu, liveOverride, onClose, onSaved }) {
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [values, setValues]             = useState({});
  // list형 항목의 선택지 캐시 — optionsKey 단위(예: 'contracts','certs','storage','hr-management')로
  // 한 번만 조회해서 재사용한다(같은 메뉴의 읽기/쓰기 두 항목이 같은 목록을 공유하는 경우가 많음).
  const [listOptions, setListOptions]         = useState({});
  const [listOptionsLoading, setListOptionsLoading] = useState({});
  const [saving, setSaving]             = useState(false);
  // DETAIL_DEFS.home.read의 '출퇴근' 항목은 인사관리 > 사원정보 > 옵션에서 "🏠 홈 화면 근태체크
  // 위젯"이 켜져 있을 때만 이 상세설정 화면에 나타난다(꺼두면 홈화면 자체에 그 기능이 없으므로
  // 여기서도 설정할 게 없다고 보는 게 자연스럽다). requiresOption 값을 가진 항목은 모두 이 규칙을
  // 공용으로 따르도록 일반화했다(앞으로 같은 패턴이 또 생겨도 이 필터 하나로 처리된다).
  const [enabledOptions, setEnabledOptions] = useState(new Set());
  useEffect(() => {
    API.get('/hr/menu-options')
      .then(r => {
        const disabled = new Set((r.data || []).filter(o => !o.enabled).map(o => o.tab_key));
        // requiresOption에 등장하는 옵션 키들 중, 명시적으로 꺼진 것만 제외하고 나머지는 켜진
        // 것으로 취급한다(행이 없으면 기본 사용 — MenuOptionsModal과 동일한 규칙).
        const allOptionKeys = new Set(
          Object.values(DETAIL_DEFS).flatMap(d => [...(d.read || []), ...(d.write || [])])
            .filter(it => it.requiresOption).map(it => it.requiresOption)
        );
        setEnabledOptions(new Set([...allOptionKeys].filter(k => !disabled.has(k))));
      })
      .catch(() => {});
  }, []);
  // requiresOption이 걸린 항목은 해당 옵션이 켜져 있을 때만 통과시키는 필터.
  const visibleItems = (items) => (items || []).filter(it => !it.requiresOption || enabledOptions.has(it.requiresOption));

  useEffect(() => {
    if (initialMenu) selectMenu(initialMenu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMenu]);

  const getGranted = (menuKey) => {
    if (liveOverride && liveOverride.menu_key === menuKey) {
      return { canRead: !!liveOverride.can_read, canWrite: !!liveOverride.can_write };
    }
    const p = (initialPerms || []).find(x => x.menu_key === menuKey);
    return { canRead: !!p?.can_read, canWrite: !!p?.can_write };
  };

  const selectMenu = (menuKey) => {
    setSelectedMenu(menuKey || null);
    if (!menuKey) { setValues({}); return; }
    const menuDef = DETAIL_DEFS[menuKey];
    const listKeys = menuDef ? [...(menuDef.read || []), ...(menuDef.write || [])].filter(it => it.type === 'list').map(it => it.key) : [];
    const rows = (initialDetails || []).filter(d => d.menu_key === menuKey);
    const next = {};
    rows.forEach(r => {
      if (listKeys.includes(r.detail_key)) {
        try { next[r.detail_key] = JSON.parse(r.value || '[]'); } catch { next[r.detail_key] = []; }
      } else {
        next[r.detail_key] = r.value === '1';
      }
    });
    setValues(next);

    // 이 메뉴에 등장하는 list형 항목들의 optionsKey를 모아서, 아직 캐시에 없는 것만 조회한다.
    if (menuDef) {
      const optionsKeys = [...new Set(
        [...(menuDef.read || []), ...(menuDef.write || [])]
          .filter(it => it.type === 'list' && it.optionsKey)
          .map(it => it.optionsKey)
      )];
      optionsKeys.forEach(ok => {
        if (listOptions[ok] || listOptionsLoading[ok]) return;
        const fetcher = LIST_OPTION_FETCHERS[ok];
        if (!fetcher) return;
        setListOptionsLoading(prev => ({ ...prev, [ok]: true }));
        fetcher()
          .then(list => setListOptions(prev => ({ ...prev, [ok]: list })))
          .catch(() => setListOptions(prev => ({ ...prev, [ok]: [] })))
          .finally(() => setListOptionsLoading(prev => ({ ...prev, [ok]: false })));
      });
    }
  };

  const toggleBool = (key) => setValues(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleListItem = (key, name) => setValues(prev => {
    const cur = Array.isArray(prev[key]) ? prev[key] : [];
    const next = cur.includes(name) ? cur.filter(n => n !== name) : [...cur, name];
    return { ...prev, [key]: next };
  });

  const handleSave = async () => {
    if (!selectedMenu) return;
    setSaving(true);
    try {
      const def = DETAIL_DEFS[selectedMenu];
      const keys = [...def.read.map(d => d.key), ...def.write.map(d => d.key)];
      const details = {};
      keys.forEach(k => { details[k] = values[k] ?? (Array.isArray(values[k]) ? [] : false); });
      await API.put('/permissions/' + user.username + '/detail', { menu_key: selectedMenu, details });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const def = selectedMenu ? DETAIL_DEFS[selectedMenu] : null;
  const { canRead, canWrite } = selectedMenu ? getGranted(selectedMenu) : { canRead:false, canWrite:false };

  const renderListItem = (it) => {
    const options = listOptions[it.optionsKey] || [];
    const loading = !!listOptionsLoading[it.optionsKey];
    return (
      <div key={it.key} style={{ marginBottom:10 }}>
        <div style={{ fontSize:14, marginBottom:6 }}>{it.label}</div>
        {loading ? (
          <div style={{ fontSize:12, color:'#aaa' }}>목록을 불러오는 중...</div>
        ) : options.length === 0 ? (
          <div style={{ fontSize:12, color:'#aaa' }}>등록된 목록이 없습니다.</div>
        ) : (
          <div style={{ border:'1px solid #eee', borderRadius:8, padding:'8px 12px', maxHeight:180, overflowY:'auto' }}>
            {options.map(name => (
              <label key={name} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', fontSize:14, cursor:'pointer' }}>
                <input type="checkbox"
                  checked={(values[it.key] || []).includes(name)}
                  onChange={() => toggleListItem(it.key, name)}
                  style={{ width:16, height:16, cursor:'pointer' }} />
                {name}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (title, granted, items) => (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:13, fontWeight:600, color:'#555', marginBottom:8 }}>{title}</div>
      {!granted ? (
        <div style={{ fontSize:12, color:'#d97706', background:'#fffbeb', border:'1px solid #fef3c7',
          borderRadius:8, padding:'10px 12px' }}>
          먼저 권한설정에서 해당 권한을 켜야 상세설정을 사용할 수 있습니다.
        </div>
      ) : items.length === 0 ? (
        <div style={{ fontSize:12, color:'#aaa', padding:'4px 0' }}>이 항목에는 별도 상세설정이 없습니다.</div>
      ) : (
        items.map(it => it.type === 'list' ? renderListItem(it) : (
          <label key={it.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:14, cursor:'pointer' }}>
            <input type="checkbox" checked={!!values[it.key]} onChange={() => toggleBool(it.key)}
              style={{ width:16, height:16, cursor:'pointer' }} />
            {it.label}
          </label>
        ))
      )}
    </div>
  );

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2050 }}>
      <div style={{ background:'#fff', borderRadius:16, width:560, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🧩 상세설정</h3>
            <div style={{ fontSize:13, color:'#888', marginTop:4 }}>{formatUserLine(user)}</div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ padding:'16px 24px' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#185FA5', marginBottom:16 }}>
            {menus.find(m => m.key === selectedMenu)?.label || selectedMenu}
          </div>

          {selectedMenu && def && (
            <>
              {renderSection('읽기 상세설정', canRead, visibleItems(def.read))}
              {renderSection('쓰기 상세설정', canWrite, visibleItems(def.write))}
            </>
          )}
        </div>

        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          {selectedMenu && (
            <button onClick={handleSave} disabled={saving}
              style={{ flex:1, padding:'11px', background:saving?'#aaa':'#7C3AED', color:'#fff',
                border:'none', borderRadius:8, cursor:saving?'not-allowed':'pointer', fontSize:14, fontWeight:600 }}>
              {saving ? '저장 중...' : '저장'}
            </button>
          )}
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', background:'#f0f0f0', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            {selectedMenu ? '취소' : '닫기'}
          </button>
        </div>
      </div>
    </div>
  );
}
